// ══════════════════════════════════════════════════════════════════════════════
// JyotishPage.js — Vedic Astrology · Janma Rashi · Character Archetype
// Birth date → Moon Rashi → Gita/Ramayana character archetype + shareable card
// ══════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";
import TrueSelfReportTab from "./TrueSelfReportTab";
import ReportsStorePage from "./ReportsStorePage";
import { jsPDF } from "jspdf";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const LANG_LABELS = {
  rashi:     { te:"జన్మ రాశి", hi:"जन्म राशि", ta:"ஜன்ம ராசி", kn:"ಜನ್ಮ ರಾಶಿ", mr:"जन्म राशी", en:"Janma Rashi" },
  moon:      { te:"చంద్ర రాశి", hi:"चंद्र राशि", ta:"சந்திர ராசி", kn:"ಚಂದ್ರ ರಾಶಿ", mr:"चंद्र राशी", en:"Moon Sign" },
  archetype: { te:"మీ వేద అర్కటైప్", hi:"आपका वैदिक आदर्श", ta:"உங்கள் வேத குணம்", kn:"ನಿಮ್ಮ ವೈದಿಕ ಆದರ್ಶ", mr:"तुमचे वैदिक आदर्श", en:"Your Vedic Archetype" },
  you_are:   { te:"మీరు", hi:"आप हैं", ta:"நீங்கள்", kn:"ನೀವು", mr:"तुम्ही", en:"You are" },
  dharma:    { te:"మీ ధర్మ మార్గం", hi:"आपका धर्म मार्ग", ta:"உங்கள் தர்ம பாதை", kn:"ನಿಮ್ಮ ಧರ್ಮ ಮಾರ್ಗ", mr:"तुमचा धर्म मार्ग", en:"Your Dharma Path" },
  gita:      { te:"గీత సందేశం", hi:"गीता संदेश", ta:"கீதை செய்தி", kn:"ಗೀತಾ ಸಂದೇಶ", mr:"गीता संदेश", en:"Gita Message" },
  share:     { te:"షేర్ చేయండి", hi:"शेयर करें", ta:"பகிர்க", kn:"ಹಂಚಿಕೊಳ್ಳಿ", mr:"शेअर करा", en:"Share" },
  copied:    { te:"కాపీ అయింది!", hi:"कॉपी हो गया!", ta:"நகலெடுக்கப்பட்டது!", kn:"ನಕಲು ಆಯಿತು!", mr:"कॉपी झाले!", en:"Copied!" },
  add_birth: { te:"జన్మ వివరాలు చేర్చండి", hi:"जन्म विवरण जोड़ें", ta:"பிறப்பு விவரங்கள் சேர்க்க", kn:"ಹುಟ್ಟು ವಿವರ ಸೇರಿಸಿ", mr:"जन्म तपशील जोडा", en:"Add Birth Details" },
  approx:    { en:"* Approximate · add birth time for maximum accuracy", te:"* సుమారు గణన · ఖచ్చితత్వానికి జన్మ సమయం జోడించండి", hi:"* अनुमानित · सटीकता के लिए जन्म समय जोड़ें" },
};

const LANG_CHIPS = { te:"తె", hi:"हि", ta:"த", kn:"ಕ", mr:"म", en:"EN" };

function L(key, lang) {
  return (LANG_LABELS[key] || {})[lang] || LANG_LABELS[key]?.en || "";
}

// ── Shared light theme (the whole Jyotish section is light-only — see JyotishPage
//    render, "Light theme is locked on across the section"). Clean and plain:
//    flat white cards, thin neutral-gray borders, one plain sans-serif font for
//    everything (headings just get bigger/bolder, not a different typeface),
//    the brand orange used sparingly for buttons/links only. ─────────────────
const JT = {
  page:         "#ffffff",
  card:         "#ffffff",
  cardWarm:     "#ffffff",
  cardCream:    "#ffffff",
  border:       "#e2e2e2",
  borderStrong: "#c9c9c9",
  shadow:       "0 1px 3px rgba(0,0,0,0.08)",
  shadowLift:   "0 4px 12px rgba(0,0,0,0.1)",
  heading:      "'Inter',sans-serif",
  label:        "'Inter',sans-serif",
  body:         "'Inter',sans-serif",
  text:         "#1a1a1a",
  textDim:      "#5a5a5a",
  gold:         "#c2560f",
  goldDeep:     "#9a4508",
};

// ── Stars + Planets background ────────────────────────────────────────────────
const STAR_STYLE = {
  position: "absolute", borderRadius: "50%",
  background: "rgba(255,255,255,0.8)", animation: "twinkle 3s infinite",
};

function Stars({ planetOrbs = false }) {
  const stars = [
    {w:1,h:1,top:"4%",left:"12%",delay:"0s"},{w:2,h:2,top:"8%",left:"78%",delay:"0.4s"},
    {w:1,h:1,top:"11%",left:"35%",delay:"0.8s"},{w:3,h:3,top:"6%",left:"55%",delay:"1.2s"},
    {w:1,h:1,top:"16%",left:"90%",delay:"0.2s"},{w:2,h:2,top:"22%",left:"8%",delay:"1.6s"},
    {w:1,h:1,top:"18%",left:"68%",delay:"0.6s"},{w:1,h:1,top:"28%",left:"48%",delay:"2.0s"},
    {w:2,h:2,top:"32%",left:"22%",delay:"0.3s"},{w:1,h:1,top:"26%",left:"85%",delay:"1.0s"},
    {w:3,h:3,top:"38%",left:"5%",delay:"1.4s"},{w:1,h:1,top:"35%",left:"62%",delay:"0.7s"},
    {w:2,h:2,top:"42%",left:"40%",delay:"1.8s"},{w:1,h:1,top:"45%",left:"95%",delay:"0.1s"},
    {w:1,h:1,top:"50%",left:"16%",delay:"2.2s"},{w:2,h:2,top:"55%",left:"72%",delay:"0.9s"},
    {w:1,h:1,top:"58%",left:"30%",delay:"1.5s"},{w:3,h:3,top:"62%",left:"88%",delay:"0.5s"},
    {w:1,h:1,top:"65%",left:"50%",delay:"1.1s"},{w:2,h:2,top:"70%",left:"10%",delay:"2.4s"},
    {w:1,h:1,top:"72%",left:"65%",delay:"0.6s"},{w:1,h:1,top:"78%",left:"42%",delay:"1.7s"},
    {w:2,h:2,top:"80%",left:"80%",delay:"0.3s"},{w:1,h:1,top:"85%",left:"20%",delay:"1.3s"},
    {w:3,h:3,top:"88%",left:"58%",delay:"0.8s"},{w:1,h:1,top:"92%",left:"35%",delay:"2.1s"},
    {w:2,h:2,top:"14%",left:"28%",delay:"1.9s"},{w:1,h:1,top:"48%",left:"77%",delay:"0.4s"},
    {w:1,h:1,top:"75%",left:"92%",delay:"1.6s"},{w:2,h:2,top:"96%",left:"70%",delay:"0.2s"},
  ];
  const planets = planetOrbs ? [
    { size:60, top:"8%", right:"6%", color:"rgba(249,115,22,0.25)", shadow:"rgba(249,115,22,0.4)", delay:"0s" },
    { size:36, top:"45%", left:"2%", color:"rgba(139,92,246,0.2)", shadow:"rgba(139,92,246,0.35)", delay:"0.5s" },
    { size:28, bottom:"15%", right:"10%", color:"rgba(59,130,246,0.2)", shadow:"rgba(59,130,246,0.3)", delay:"1s" },
    { size:18, top:"30%", right:"18%", color:"rgba(245,158,11,0.25)", shadow:"rgba(245,158,11,0.35)", delay:"1.5s" },
  ] : [];
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
      {stars.map((s,i) => (
        <div key={i} style={{ ...STAR_STYLE, width:s.w, height:s.h, top:s.top, left:s.left, right:s.right, animationDelay:s.delay, opacity: 0.5 + Math.random()*0.5 }} />
      ))}
      {planets.map((p,i) => (
        <div key={`p${i}`} style={{
          position:"absolute", borderRadius:"50%",
          width:p.size, height:p.size,
          top:p.top, left:p.left, right:p.right, bottom:p.bottom,
          background:`radial-gradient(circle at 35% 35%, ${p.color.replace("0.2","0.5")} 0%, ${p.color} 50%, transparent 80%)`,
          boxShadow:`0 0 ${p.size}px ${p.shadow}`,
          animation:`float ${3 + i}s ease-in-out infinite`,
          animationDelay:p.delay,
        }} />
      ))}
    </div>
  );
}

// ── Birth details form ────────────────────────────────────────────────────────
function BirthForm({ userId, onSaved, isCorrection = false,
                     initialDob = "", initialTime = "", initialCity = "",
                     title = null, buttonLabel = null, inline = false }) {
  const [dob,    setDob]    = useState(initialDob);
  const [time,   setTime]   = useState(initialTime);
  const [city,   setCity]   = useState(initialCity);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!dob) return;
    setSaving(true);
    await fetch(`${API}/user/birthdate`, {
      method: "POST",
      headers: { "Content-Type":"application/json" },
      body: JSON.stringify({ user_id:userId, dob, birth_time:time, birth_city:city }),
    });
    setSaving(false);
    onSaved(dob);
  };

  const inputStyle = {
    width:"100%", boxSizing:"border-box", background:"#ffffff",
    border:`1px solid ${JT.borderStrong}`, borderRadius:"8px", padding:"clamp(9px,1.2vw,12px) 12px",
    color:JT.text, fontSize:"clamp(1rem,1.1vw,1.1rem)", fontFamily:"'Inter',sans-serif",
  };
  const labelStyle = { fontSize:"clamp(0.95rem,1.1vw,1.05rem)", color:JT.textDim, fontWeight:600, marginBottom:"4px" };
  const helperStyle = { fontSize:"0.85rem", color:JT.textDim, marginTop:"3px", lineHeight:1.4 };

  const fields = (
    <div style={{ display:"flex", flexDirection:"column", gap:"12px", maxWidth:340, margin:"0 auto" }}>
      <div style={{ textAlign:"left" }}>
        <div style={labelStyle}>Date of birth *</div>
        <input type="date" value={dob} onChange={e => setDob(e.target.value)} style={inputStyle} />
      </div>
      <div style={{ textAlign:"left" }}>
        <div style={labelStyle}>Birth time ⚡ (strongly recommended)</div>
        <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inputStyle} />
        {!time && (
          <div style={helperStyle}>Without birth time, noon (12:00) is assumed — may show wrong Rashi</div>
        )}
      </div>
      <div style={{ textAlign:"left" }}>
        <div style={labelStyle}>Birth city / nearest town (optional · for Lagna)</div>
        <input type="text" placeholder="e.g. Hyderabad, Nellore, Karimnagar…"
          value={city} onChange={e => setCity(e.target.value)} style={inputStyle} />
        <div style={helperStyle}>From a village? Enter your nearest town or district HQ — that's close enough.</div>
      </div>
      <button onClick={save} disabled={!dob || saving} style={{
        background: dob ? "#c2560f" : "#f0f0f0",
        border:"none", borderRadius:"10px", padding:"clamp(11px,1.4vw,14px)",
        color: dob ? "#fff" : "#999",
        fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:"clamp(1rem,1.2vw,1.1rem)",
        cursor: dob ? "pointer" : "default",
        boxShadow: dob ? "0 8px 24px rgba(194,86,15,0.35)" : "none",
        marginTop:"2px",
        transition:"all 0.2s",
      }}>
        {saving ? "Calculating..." : isCorrection ? "✨ Recalculate My Rashi" : (buttonLabel || "✨ Reveal My Archetype")}
      </button>
    </div>
  );

  // Inline mode: just the fields, no card wrapper (used inside hook card)
  if (inline) return (
    <div>
      {/* Why birth time matters — compact */}
      <div style={{
        background:"#ffffff", border:`1px solid ${JT.border}`,
        borderRadius:"8px", padding:"8px 12px", marginBottom:"12px",
        display:"flex", gap:"8px", alignItems:"flex-start",
      }}>
        <span style={{ fontSize:"0.95rem", flexShrink:0 }}>ℹ️</span>
        <div style={{ fontSize:"0.9rem", color:JT.textDim, lineHeight:1.5, fontFamily:"'Inter',sans-serif" }}>
          <b style={{ color:JT.text }}>Birth time matters.</b> Moon changes Rashi every ~2.5 days — without it the reading may be off.
        </div>
      </div>
      {fields}
    </div>
  );

  return (
    <div style={{
      background:"#ffffff", maxWidth:480,
      border:`1px solid ${JT.border}`, borderRadius:"16px",
      boxShadow:"0 10px 40px rgba(226,120,40,0.15), 0 2px 8px rgba(0,0,0,0.04)",
      padding:"clamp(18px,2.4vw,26px)", textAlign:"center",
    }}>
      <div style={{ fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:"clamp(1.05rem,1.6vw,1.3rem)",
        color:JT.text, marginBottom:"10px" }}>
        {isCorrection ? "Update Your Birth Details" : (title || "Discover Your Vedic Archetype")}
      </div>

      {/* Why birth time matters */}
      <div style={{
        background:"#ffffff", border:`1px solid ${JT.border}`,
        borderRadius:"8px", padding:"8px 12px", marginBottom:"12px", textAlign:"left",
        display:"flex", gap:"8px", alignItems:"flex-start",
      }}>
        <span style={{ fontSize:"0.95rem", flexShrink:0 }}>ℹ️</span>
        <div style={{ fontSize:"0.85rem", color:JT.textDim, lineHeight:1.5, fontFamily:"'Inter',sans-serif" }}>
          <b style={{ color:JT.text }}>Birth time is important for accuracy.</b> The Moon changes Rashi every ~2.5 days.
          Without your exact birth time, the Rashi shown may be off.
          If a pujari or jyotishi told you your Rashi, enter your birth time to confirm.
        </div>
      </div>

      {fields}
    </div>
  );
}

// Western zodiac symbols
const WEST_SYMBOL = ["♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓"];

// ── Archetype card ────────────────────────────────────────────────────────────
function ArchetypeCard({ data, lang, onShare }) {
  const { archetype, rashi, sun_rashi, western_sign, western_sign_idx, has_accurate_calc } = data;
  const accent   = archetype.color;
  const rgb      = hexToRgb(accent);
  const charName = archetype[lang] || archetype.en;
  const rashiName = rashi[lang] || rashi.en;
  const sunRashiName = sun_rashi ? (sun_rashi[lang] || sun_rashi.en) : "";

  return (
    <div style={{ animation:"fadeInUp 0.6s ease" }}>

      {/* ── Hero banner ── */}
      <div style={{
        position:"relative", overflow:"hidden",
        background:"#ffffff",
        border:`1px solid ${JT.border}`, borderRadius:"18px",
        marginBottom:"16px",
        boxShadow:JT.shadow,
      }}>
        {/* Accent line top */}
        <div style={{ height:4, background:accent }} />

        <div style={{ padding:"32px 28px 28px", textAlign:"center", position:"relative" }}>
          {/* Sign row — Moon Rashi + Sun Rashi + Western */}
          <div style={{ display:"flex", gap:"8px", justifyContent:"center",
            flexWrap:"wrap", marginBottom:"26px" }}>
            {/* Moon Rashi */}
            <div style={{
              display:"inline-flex", alignItems:"center", gap:"9px",
              background:`rgba(${rgb},0.15)`, border:`1px solid ${accent}55`,
              borderRadius:"30px", padding:"8px 18px",
            }}>
              <span style={{ fontSize:"1.1rem" }}>🌙</span>
              <div>
                <div style={{ fontSize:"0.95rem", color:`${accent}cc`,
                  fontFamily:JT.label, fontWeight:600 }}>Moon Sign</div>
                <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                  color:accent, fontWeight:700 }}>{rashiName}</div>
              </div>
            </div>
            {/* Vedic Sun Rashi */}
            {sunRashiName && (
              <div style={{
                display:"inline-flex", alignItems:"center", gap:"9px",
                background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.4)",
                borderRadius:"30px", padding:"8px 18px",
              }}>
                <span style={{ fontSize:"1.1rem" }}>☀️</span>
                <div>
                  <div style={{ fontSize:"0.95rem", color:"rgba(249,115,22,0.9)",
                    fontFamily:JT.label, fontWeight:600 }}>Vedic Sun</div>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                    color:"#fb923c", fontWeight:700 }}>{sunRashiName}</div>
                </div>
              </div>
            )}
            {/* Western Sun sign */}
            {western_sign && (
              <div style={{
                display:"inline-flex", alignItems:"center", gap:"9px",
                background:"rgba(100,116,139,0.15)", border:"1px solid rgba(100,116,139,0.4)",
                borderRadius:"30px", padding:"8px 18px",
              }}>
                <span style={{ fontSize:"1.1rem" }}>{WEST_SYMBOL[western_sign_idx] || "✦"}</span>
                <div>
                  <div style={{ fontSize:"0.95rem", color:"rgba(148,163,184,0.9)",
                    fontFamily:JT.label, fontWeight:600 }}>Western</div>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                    color:"#94a3b8", fontWeight:700 }}>{western_sign}</div>
                </div>
              </div>
            )}
          </div>

          {/* Floating emoji */}
          <div style={{ fontSize:"6.5rem", lineHeight:1, marginBottom:"18px" }}>
            {archetype.emoji}
          </div>

          {/* "You are" label */}
          <div style={{ fontSize:"0.95rem", color:JT.textDim,
            fontFamily:JT.label, fontWeight:600, marginBottom:"8px" }}>
            {L("you_are", lang)}
          </div>

          {/* Character name — BIG */}
          <div style={{
            fontFamily:JT.heading,
            fontSize: charName.length > 12 ? "2rem" : "2.8rem",
            color:JT.text, lineHeight:1.1, marginBottom:"6px", fontWeight:700,
          }}>
            {charName}
          </div>

          {/* Sanskrit / English subtitle */}
          {lang !== "en" && (
            <div style={{ fontSize:"1.1rem", color:accent,
              fontFamily:"'Inter',sans-serif", marginBottom:"20px" }}>
              {archetype.en}
            </div>
          )}

          {/* Trait chips */}
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap",
            justifyContent:"center", marginTop: lang !== "en" ? 0 : "20px" }}>
            {archetype.traits.map(t => (
              <div key={t} style={{
                background:`${accent}14`, border:`1px solid ${accent}50`,
                borderRadius:"20px", padding:"6px 16px",
                fontSize:"1.0rem", color:accent, fontWeight:600,
              }}>{t}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Dharma path ── */}
      <div style={{
        background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
        borderRadius:"18px", padding:"22px 24px", marginBottom:"14px",
        borderLeft:"4px solid #f97316",
      }}>
        <div style={{ fontFamily:JT.label, fontSize:"1rem", fontWeight:700,
          color:"#c2560f", marginBottom:"10px" }}>
          {L("dharma", lang)}
        </div>
        <div style={{ fontFamily:JT.body, fontSize:"1.35rem",
          color:JT.text, fontStyle:"italic", lineHeight:1.85 }}>
          "{archetype.dharma}"
        </div>
      </div>

      {/* ── Description ── */}
      <div style={{
        background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
        borderRadius:"18px", padding:"22px 24px", marginBottom:"14px",
      }}>
        <div style={{ fontFamily:JT.body, fontSize:"1.2rem",
          color:JT.text, lineHeight:1.85 }}>
          {archetype.desc}
        </div>
      </div>

      {/* ── Gita verse ── */}
      <div style={{
        background:JT.cardWarm, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
        borderRadius:"18px", padding:"20px 24px", marginBottom:"16px",
        display:"flex", gap:"14px", alignItems:"flex-start",
      }}>
        <div style={{ fontSize:"1.6rem", flexShrink:0, marginTop:"2px" }}>🕉️</div>
        <div>
          <div style={{ fontFamily:JT.label, fontSize:"1rem", fontWeight:700,
            color:"#c2560f", marginBottom:"8px" }}>
            {L("gita", lang)}
          </div>
          <div style={{ fontFamily:JT.body, fontSize:"1.15rem",
            color:JT.text, fontStyle:"italic", lineHeight:1.7 }}>
            {archetype.gita_ref}
          </div>
        </div>
      </div>

      {/* Vedic vs Western explanation */}
      {western_sign && sunRashiName && (
        <div style={{
          background:JT.cardCream, border:`1px solid ${JT.border}`,
          borderRadius:"14px", padding:"14px 18px", marginBottom:"14px",
          display:"flex", gap:"10px", alignItems:"flex-start",
        }}>
          <div style={{ fontSize:"1.1rem", flexShrink:0 }}>💡</div>
          <div style={{ fontSize:"1rem", color:JT.textDim, lineHeight:1.75, fontFamily:JT.body }}>
            <b style={{ color:JT.text }}>Why your Vedic Sun sign may differ from your Western sign:</b>{" "}
            Vedic astrology uses the sidereal zodiac — aligned to actual star positions — which sits ~24° behind the Western tropical zodiac.
            So a Western <b style={{ color:"#64748b" }}>{western_sign}</b> becomes Vedic{" "}
            <b style={{ color:"#c2560f" }}>{sunRashiName}</b>.
            Both are valid — Vedic is older and rooted in astronomy.
          </div>
        </div>
      )}

      {!has_accurate_calc && (
        <div style={{ fontSize:"1.0rem", color:JT.textDim, fontStyle:"italic",
          textAlign:"center", marginBottom:"12px" }}>
          {L("approx", lang) || L("approx", "en")}
        </div>
      )}

      {/* ── Share ── */}
      <button onClick={onShare} style={{
        width:"100%", padding:"16px",
        background:`linear-gradient(135deg,${accent},${accent}bb)`,
        border:"none", borderRadius:"14px", cursor:"pointer",
        fontFamily:JT.label, fontWeight:600, fontSize:"1.0rem",
        color:"#fff", letterSpacing:"0.06em",
        boxShadow:`0 6px 28px rgba(${rgb},0.45)`,
        display:"flex", alignItems:"center", justifyContent:"center", gap:"10px",
        transition:"transform 0.15s",
      }}
        onMouseEnter={e => e.currentTarget.style.transform="scale(1.02)"}
        onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}
      >
        <span style={{ fontSize:"1.2rem" }}>🔱</span>
        {L("share", lang)} — {charName}
      </button>
    </div>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ── Ruler → color mapping ─────────────────────────────────────────────────────
const RULER_COLOR = {
  Ketu:"#8b5cf6", Venus:"#ec4899", Sun:"#f97316", Moon:"#3b82f6",
  Mars:"#ef4444", Rahu:"#6366f1", Jupiter:"#f59e0b",
  Saturn:"#64748b", Mercury:"#22c55e",
};

// ── Shani Status Card ─────────────────────────────────────────────────────────
function ShaniStatusCard({ data }) {
  const [open, setOpen] = React.useState(null); // null | "remedies"
  const affected = data.is_affected;
  const ss = data.sade_sati;
  const dh = data.dhaiya;
  const active = ss || dh;
  const accent = affected ? (ss?.intensity === "high" ? "#ef4444" : "#f97316") : "#64748b";
  const rgb = affected ? (ss?.intensity === "high" ? "239,68,68" : "249,115,22") : "100,116,139";
  const intensityColor = { high:"#ef4444", moderate:"#f97316" };

  const fmtDate = iso => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { month:"short", year:"numeric" });
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>

      {/* Status hero */}
      <div style={{
        background: affected
          ? `linear-gradient(135deg,rgba(${rgb},0.2),rgba(${rgb},0.07))`
          : "linear-gradient(135deg,rgba(100,116,139,0.12),rgba(100,116,139,0.04))",
        border:`1px solid rgba(${rgb},0.45)`,
        borderRadius:"20px", padding:"26px 22px", textAlign:"center",
        boxShadow:`0 4px 24px rgba(${rgb},0.15)`,
      }}>
        <div style={{ fontSize:"3rem", marginBottom:"8px",
          filter:`drop-shadow(0 0 16px rgba(${rgb},0.6))` }}>🪐</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.3rem",
          color: accent, marginBottom:"6px", letterSpacing:"0.02em" }}>
          {affected ? (active.name || active.phase_name) : "Saturn Clear"}
        </div>
        <div style={{ fontSize:"0.95rem", color:JT.textDim,  fontFamily:JT.label, fontWeight:600, marginBottom:"10px" }}>
          {affected ? "Active Transit" : "No active Sade Sati or Dhaiya"}
        </div>
        {affected && active.intensity && (
          <span style={{
            display:"inline-block",
            background:`rgba(${rgb},0.18)`, border:`1px solid rgba(${rgb},0.4)`,
            borderRadius:"20px", padding:"3px 14px",
            fontSize:"0.95rem", color: intensityColor[active.intensity] || accent,
            fontFamily:JT.label, fontWeight:600, 
          }}>
            {active.intensity} intensity
          </span>
        )}
      </div>

      {/* Saturn position */}
      <div style={{
        background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
        borderRadius:"14px", padding:"16px 18px",
        display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px",
      }}>
        <div>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"4px" }}>Saturn Now</div>
          <div style={{ fontSize:"1.2rem", color:JT.text, fontFamily:JT.body }}>
            {data.saturn_rashi}
          </div>
          <div style={{ fontSize:"1rem", color:JT.textDim, marginTop:"2px" }}>
            {data.saturn_lon}° sidereal
          </div>
        </div>
        <div>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"4px" }}>Your Moon Rashi</div>
          <div style={{ fontSize:"1.2rem", color:JT.text, fontFamily:JT.body }}>
            {data.moon_rashi}
          </div>
          <div style={{ fontSize:"1rem", color:JT.textDim, marginTop:"2px" }}>
            Janma Rashi
          </div>
        </div>
      </div>

      {/* Active period details */}
      {affected && (
        <>
          {/* Feel */}
          <div style={{
            background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"14px", padding:"18px",
          }}>
            <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
               marginBottom:"8px" }}>What This Period Feels Like</div>
            <div style={{ color:JT.text, fontFamily:JT.body,
              fontSize:"1.2rem", lineHeight:1.7 }}>
              {active.feel}
            </div>
          </div>

          {/* Dates */}
          <div style={{
            background:`rgba(${rgb},0.07)`, border:`1px solid rgba(${rgb},0.25)`,
            borderRadius:"14px", padding:"16px 18px",
            display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px",
          }}>
            <div>
              <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
                 marginBottom:"4px" }}>Saturn Entered</div>
              <div style={{ fontSize:"1.2rem", color: accent, fontFamily:JT.body, fontWeight:600 }}>
                {fmtDate(data.current_sign_entry)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
                 marginBottom:"4px" }}>Saturn Exits</div>
              <div style={{ fontSize:"1.2rem", color: accent, fontFamily:JT.body, fontWeight:600 }}>
                {fmtDate(data.current_sign_exit)}
              </div>
              <div style={{ fontSize:"1rem", color:JT.textDim, marginTop:"2px" }}>
                ~{Math.round(data.days_left_in_sign / 30)} months left
              </div>
            </div>
          </div>

          {/* Remedies accordion */}
          <div style={{
            background:`rgba(${rgb},0.06)`, border:`1px solid rgba(${rgb},0.22)`,
            borderRadius:"14px", overflow:"hidden",
          }}>
            <div onClick={() => setOpen(o => o === "remedies" ? null : "remedies")}
              style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"16px 18px", cursor:"pointer" }}>
              <div style={{ fontFamily:JT.label, fontWeight:600, fontSize:"1.05rem",
                color: accent, letterSpacing:"0.02em" }}>
                🛡 Shani Remedies
              </div>
              <span style={{ color:JT.textDim, fontSize:"1rem" }}>
                {open === "remedies" ? "▲" : "▼"}
              </span>
            </div>
            {open === "remedies" && (
              <div style={{ padding:"0 18px 18px", display:"flex", flexDirection:"column", gap:8 }}>
                {(active.remedies || []).map((r, i) => (
                  <div key={i} style={{
                    display:"flex", gap:10, alignItems:"flex-start",
                    padding:"10px 12px", borderRadius:10,
                    background:`rgba(${rgb},0.1)`, border:`1px solid rgba(${rgb},0.22)`,
                    fontSize:"1.2rem", color:JT.text, lineHeight:1.5,
                  }}>
                    <span style={{ color:accent, flexShrink:0, fontWeight:700 }}>•</span>
                    {r}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Assurance */}
      <div style={{
        background:"linear-gradient(135deg,rgba(212,175,55,0.1),rgba(212,175,55,0.04))",
        border:"1px solid rgba(212,175,55,0.3)",
        borderRadius:"14px", padding:"18px",
      }}>
        <div style={{ fontSize:"0.95rem", color:JT.goldDeep,  marginBottom:"8px", fontFamily:JT.label, fontWeight:600 }}>
          The Gita's Teaching on Saturn
        </div>
        <div style={{ color:JT.text, fontFamily:JT.body,
          fontSize:"1.3rem", lineHeight:1.8, fontStyle:"italic" }}>
          {data.assurance}
        </div>
      </div>
    </div>
  );
}

// ── Ishta Devata Card ─────────────────────────────────────────────────────────
function IshtaDevataCard({ data }) {
  const p = data.primary_deity;
  const s = data.secondary_deity;
  const accent = RULER_COLOR[data.nakshatra_ruler] || "#c9a84c";
  const rgb = hexToRgb(accent);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
      {/* Header — deity symbol + name */}
      <div style={{
        background:`linear-gradient(135deg,rgba(${rgb},0.18),rgba(${rgb},0.07))`,
        border:`1px solid rgba(${rgb},0.4)`,
        borderRadius:"20px", padding:"28px 24px", textAlign:"center",
        boxShadow:`0 4px 24px rgba(${rgb},0.15)`,
      }}>
        <div style={{ fontSize:"3.5rem", marginBottom:"10px",
          filter:`drop-shadow(0 0 18px rgba(${rgb},0.6))` }}>
          {p?.symbol || "🪔"}
        </div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.5rem",
          color:accent, marginBottom:"6px", letterSpacing:"0.01em" }}>
          {p?.deity}
        </div>
        <div style={{ fontSize:"0.95rem", color:JT.textDim,  fontFamily:JT.label, fontWeight:600 }}>
          Your Ishta Devata
        </div>
        {data.unified && (
          <div style={{
            display:"inline-block", marginTop:"12px",
            background:`rgba(${rgb},0.18)`, border:`1px solid rgba(${rgb},0.4)`,
            borderRadius:"20px", padding:"4px 14px",
            fontSize:"0.95rem", color:accent, fontFamily:JT.label, fontWeight:600,
            letterSpacing:"0.08em",
          }}>
            ✦ Confirmed by both Nakshatra & Lagna Lord
          </div>
        )}
      </div>

      {/* Nakshatra + Lagna details */}
      <div style={{
        background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
        borderRadius:"14px", padding:"16px 18px",
        display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px",
      }}>
        <div>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"4px" }}>Lagna Nakshatra</div>
          <div style={{ fontSize:"1.2rem", color:JT.text, fontFamily:JT.body }}>
            {data.lagna_nakshatra}
          </div>
          <div style={{ fontSize:"1rem", color:accent, marginTop:"2px" }}>
            Ruled by {data.nakshatra_ruler}
          </div>
        </div>
        <div>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"4px" }}>Lagna Rashi</div>
          <div style={{ fontSize:"1.2rem", color:JT.text, fontFamily:JT.body }}>
            {data.lagna_rashi}
          </div>
          <div style={{ fontSize:"1rem", color:accent, marginTop:"2px" }}>
            Lord: {data.lagna_lord}
          </div>
        </div>
      </div>

      {/* Quality */}
      {p?.quality && (
        <div style={{
          background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
          borderRadius:"14px", padding:"16px 18px",
        }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"6px" }}>Divine Quality</div>
          <div style={{ color:JT.text, fontFamily:JT.body,
            fontSize:"1.2rem", lineHeight:1.6, fontStyle:"italic" }}>
            {p.quality}
          </div>
        </div>
      )}

      {/* Mantra */}
      {p?.mantra && (
        <div style={{
          background:`linear-gradient(135deg,rgba(${rgb},0.12),rgba(${rgb},0.05))`,
          border:`1px solid rgba(${rgb},0.35)`,
          borderRadius:"14px", padding:"20px 18px", textAlign:"center",
        }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"10px" }}>Your Mantra</div>
          <div style={{ fontFamily:"'Noto Serif Devanagari',serif",
            fontSize:"1.35rem", color:accent, lineHeight:1.7, letterSpacing:"0.02em" }}>
            {p.mantra}
          </div>
        </div>
      )}

      {/* Gita verse */}
      {p?.gita_ref && (
        <div style={{
          background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
          borderRadius:"14px", padding:"16px 18px",
        }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"6px" }}>
            Gita Wisdom · {p.gita_ref}
          </div>
          <div style={{ color:JT.text, fontFamily:JT.body,
            fontSize:"1.2rem", lineHeight:1.7, fontStyle:"italic" }}>
            "{p.gita_line}"
          </div>
        </div>
      )}

      {/* Sadhana */}
      {p?.sadhana && (
        <div style={{
          background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
          borderRadius:"14px", padding:"16px 18px",
        }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"6px" }}>Daily Sadhana</div>
          <div style={{ color:JT.text, fontFamily:JT.body,
            fontSize:"1.2rem", lineHeight:1.7 }}>
            🪔 {p.sadhana}
          </div>
        </div>
      )}

      {/* Secondary deity (when lagna lord → different deity) */}
      {s && (
        <div style={{
          background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
          borderRadius:"14px", padding:"16px 18px",
        }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, letterSpacing:"0.1em", fontFamily:JT.label,
             marginBottom:"6px" }}>
            Also Honored · via Lagna Lord ({data.lagna_lord})
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            <span style={{ fontSize:"1.6rem" }}>{s.symbol}</span>
            <div>
              <div style={{ color:JT.text, fontFamily:JT.body, fontSize:"1.2rem" }}>
                {s.deity}
              </div>
              <div style={{ fontSize:"1rem", color:JT.textDim, marginTop:"2px" }}>
                {s.mantra}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Nakshatra Deep Profile Card ───────────────────────────────────────────────
function NakshatraCard({ nak, lang, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const accent = RULER_COLOR[nak.ruler] || "#c9a84c";
  const rgb    = hexToRgb(accent);
  const name   = nak.name[lang] || nak.name.en;

  return (
    <div style={{ marginTop:"14px", animation:"fadeInUp 0.7s ease 0.15s both" }}>

      {/* ── Header card — always visible ── */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          background:"#ffffff",
          border:`1px solid ${JT.border}`, borderRadius:"16px",
          padding:"20px 22px", cursor:"pointer",
          boxShadow:JT.shadow,
          transition:"box-shadow 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow=JT.shadowLift}
        onMouseLeave={e => e.currentTarget.style.boxShadow=JT.shadow}
      >
        {/* Top accent line */}
        <div style={{ height:3, background:accent,
          marginBottom:"18px", borderRadius:2 }} />

        <div style={{ display:"flex", alignItems:"center", gap:"14px", marginBottom:"12px" }}>
          <div style={{ fontSize:"2.8rem", lineHeight:1 }}>{nak.symbol}</div>
          <div style={{ flex:1 }}>
            {/* Label */}
            <div style={{ fontSize:"1rem", color:accent,
              fontFamily:JT.label, fontWeight:700, marginBottom:"4px" }}>
              Janma Nakshatra · Birth Star
            </div>
            {/* Nakshatra name big */}
            <div style={{ fontFamily:JT.heading,
              fontSize: name.length > 14 ? "1.4rem" : "1.8rem",
              color:JT.text, lineHeight:1.1, fontWeight:700 }}>
              {name}
            </div>
            {lang !== "en" && (
              <div style={{ fontSize:"1.0rem", color:JT.textDim,
                fontFamily:JT.body }}>
                {nak.name.en}
              </div>
            )}
          </div>
          <div style={{ color:JT.textDim, fontSize:"1.4rem",
            transform: open ? "rotate(90deg)" : "rotate(0deg)", transition:"transform 0.3s" }}>
            ›
          </div>
        </div>

        {/* Ruler + Element + Life theme */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", marginBottom:"12px" }}>
          {[
            { label:"Ruler", value:nak.ruler },
            { label:"Element", value:nak.element },
            { label:"Deity", value:nak.deity.split("·")[0].trim() },
          ].map(item => (
            <div key={item.label} style={{
              background:`${accent}12`, border:`1px solid ${accent}40`,
              borderRadius:"20px", padding:"7px 14px",
              fontSize:"1.1rem", color:JT.text, fontWeight:600,
            }}>
              <span style={{ color:JT.textDim, fontWeight:400, marginRight:5 }}>{item.label}:</span>
              {item.value}
            </div>
          ))}
        </div>

        {/* Life theme */}
        <div style={{ fontFamily:JT.body, fontSize:"1.05rem",
          color:JT.textDim }}>
          {nak.life_theme}
        </div>

        {/* Strengths chips */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginTop:"12px" }}>
          {nak.strengths.map(s => (
            <div key={s} style={{
              background:"#f5f5f5", border:`1px solid ${JT.border}`,
              borderRadius:"16px", padding:"6px 13px",
              fontSize:"1.1rem", color:JT.text, fontWeight:500,
            }}>{s}</div>
          ))}
        </div>

        <div style={{ textAlign:"center", marginTop:"12px", fontSize:"1.0rem",
          color:JT.textDim }}>
          {open ? "Tap to collapse ↑" : "Tap to expand your full Nakshatra profile ↓"}
        </div>
      </div>

      {/* ── Expanded sections ── */}
      {open && (
        <div style={{ marginTop:"10px", display:"flex", flexDirection:"column", gap:"10px",
          animation:"fadeInUp 0.4s ease" }}>

          {/* Your gift */}
          <div style={{
            background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px", borderLeft:"4px solid #f97316",
          }}>
            <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
              color:"#c2560f", marginBottom:"10px" }}>
              Your Spiritual Gift
            </div>
            <div style={{ fontFamily:JT.body, fontSize:"1.1rem",
              color:JT.text, lineHeight:1.85 }}>
              {nak.gift}
            </div>
          </div>

          {/* Life pattern */}
          <div style={{
            background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px", borderLeft:"4px solid #f97316",
          }}>
            <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
              color:"#c2560f", marginBottom:"10px" }}>
              Your Life Pattern
            </div>
            <div style={{ fontFamily:JT.body, fontSize:"1.08rem",
              color:JT.text, lineHeight:1.85 }}>
              {nak.life_pattern}
            </div>
          </div>

          {/* Shadow — intentional red, semantic exception */}
          <div style={{
            background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px",
            borderLeft:"4px solid rgba(239,68,68,0.7)",
            display:"flex", gap:"12px", alignItems:"flex-start",
          }}>
            <div style={{ fontSize:"1.4rem", flexShrink:0 }}>🌑</div>
            <div>
              <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
                color:"#b91c1c", marginBottom:"8px" }}>
                Your Shadow
              </div>
              <div style={{ fontFamily:JT.body, fontSize:"1.05rem",
                color:JT.text, lineHeight:1.7 }}>
                {nak.shadow}
              </div>
            </div>
          </div>

          {/* Daily practice */}
          <div style={{
            background:JT.card, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px", borderLeft:"4px solid #f97316",
            display:"flex", gap:"12px", alignItems:"flex-start",
          }}>
            <div style={{ fontSize:"1.4rem", flexShrink:0 }}>🪔</div>
            <div>
              <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
                color:"#c2560f", marginBottom:"8px" }}>
                Your Nakshatra Practice
              </div>
              <div style={{ fontFamily:JT.body, fontSize:"1.05rem",
                color:JT.text, lineHeight:1.8 }}>
                {nak.practice}
              </div>
            </div>
          </div>

          {/* Mantra */}
          <div style={{
            background:JT.cardWarm, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px", borderLeft:"4px solid #f97316",
          }}>
            <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
              color:"#c2560f", marginBottom:"12px" }}>
              ॐ Your Nakshatra Mantra · Deity: {nak.deity.split("·")[0].trim()}
            </div>
            <div style={{ fontFamily:"'Noto Sans Devanagari',sans-serif", fontSize:"1.55rem",
              color:JT.text, lineHeight:1.6, marginBottom:"6px" }}>
              {nak.mantra}
            </div>
          </div>

          {/* Gita verse */}
          <div style={{
            background:JT.cardWarm, border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
            borderRadius:"18px", padding:"20px 22px", borderLeft:"4px solid #f97316",
            display:"flex", gap:"12px", alignItems:"flex-start",
            marginBottom:"6px",
          }}>
            <div style={{ fontSize:"1.4rem", flexShrink:0 }}>🕉️</div>
            <div>
              <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
                color:"#c2560f", marginBottom:"8px" }}>
                Gita Verse For Your Nakshatra — {nak.gita_ref[0]}
              </div>
              <div style={{ fontFamily:JT.body, fontSize:"1.08rem",
                color:JT.text, lineHeight:1.7 }}>
                "{nak.gita_ref[1]}"
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Layer 2: Personalized Daily Horoscope card ───────────────────────────────
function PersonalHoroscopeCard({ horoscope, loading, onUpgrade }) {
  if (loading) return (
    <div style={{ height:240, borderRadius:"20px", marginBottom:"20px",
      background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
      backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
  );

  const mahaColor = horoscope?.maha_color || "#c9a84c";
  const rgb = hexToRgb(mahaColor);

  // Paywall: no subscription → locked card
  if (!horoscope || onUpgrade) {
    return (
      <div style={{
        position:"relative", overflow:"hidden",
        background:"#ffffff",
        border:`1px solid ${JT.border}`, borderRadius:"22px",
        marginBottom:"20px",
        boxShadow:JT.shadow,
      }}>
        <div style={{ height:3, background:`linear-gradient(90deg,transparent,${mahaColor},transparent)` }} />

        {/* Blurred preview */}
        <div style={{ padding:"20px 22px", filter:"blur(4px)", userSelect:"none", pointerEvents:"none" }}>
          <div style={{ fontSize:"1.05rem", letterSpacing:"0.14em",
            color:mahaColor, fontFamily:JT.heading,
            marginBottom:"10px" }}>YOUR PERSONAL READING · DASHA + TRANSITS</div>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
            color:"#2b2b2b", lineHeight:1.7, marginBottom:"10px" }}>
            You are in a powerful chapter of your life where the planets are aligning
            specifically for your natal chart. Your dasha is activating deep patterns
            in relationships and career that require focused action this week.
          </div>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.0rem",
            color:"#2b2b2b", lineHeight:1.7, fontStyle:"italic" }}>
            BG 3.19 · Therefore, without attachment, always do what has to be done —
            for it is through action without attachment that one attains the Supreme.
          </div>
        </div>

        {/* Lock overlay */}
        <div style={{
          position:"absolute", inset:0,
          background:"linear-gradient(180deg,rgba(255,255,255,0) 0%,rgba(255,255,255,0.94) 45%)",
          display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"flex-end",
          padding:"24px",
        }}>
          <div style={{ fontSize:"1.8rem", marginBottom:"8px" }}>🔒</div>
          <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
            color:JT.text, marginBottom:"6px", textAlign:"center", fontWeight:700 }}>
            Your Personal Reading
          </div>
          <div style={{ fontSize:"1.08rem", color:JT.textDim,
            textAlign:"center", lineHeight:1.6, marginBottom:"16px" }}>
            Dasha-aware · Birth chart transits · Personal Gita guidance
          </div>
          <button onClick={onUpgrade} style={{
            // Was a pale gold gradient with white text — 2.2:1, unreadable.
            background:"linear-gradient(135deg,#C2560F,#A8450B)",
            border:"none", borderRadius:"14px", padding:"14px 28px", minHeight:48,
            color:"#fff", fontFamily:JT.heading, fontWeight:700,
            fontSize:"1.08rem", cursor:"pointer", letterSpacing:"0.02em",
            boxShadow:"0 4px 18px rgba(168,69,11,0.32)",
          }}>
            {/* No price here — pricing lives in one place, the plans section.
                Repeating ₹299 in two spots ~4500px apart read as nagging. */}
            See what's included
          </button>
          <div style={{ fontSize:"1.1rem", color:JT.textDim, marginTop:"6px", textAlign:"center", lineHeight:1.6 }}>
            Includes personalized horoscope · full dosha analysis · birth chart chat · weekly reading
          </div>
        </div>
      </div>
    );
  }

  // Unlocked — full reading
  return (
    <div style={{
      position:"relative", overflow:"hidden",
      background:`linear-gradient(160deg,#FFFDF8 60%,rgba(${rgb},0.12) 100%)`,
      border:`1px solid rgba(${rgb},0.28)`,
      borderRadius:"22px", marginBottom:"20px",
      boxShadow:`0 4px 20px rgba(${rgb},0.1)`,
    }}>
      <div style={{ height:3, background:`linear-gradient(90deg,${mahaColor},rgba(${rgb},0.4),${mahaColor})` }} />
      <div style={{ padding:"22px 24px" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
          <div>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.12em",
              color:mahaColor, fontFamily:JT.heading }}>
              Your Personal Reading
            </div>
            <div style={{ fontSize:"1.1rem", color:"#111", fontWeight:700, marginTop:"4px",
              fontFamily:JT.heading }}>
              {horoscope.maha_emoji} {horoscope.maha_planet?.charAt(0).toUpperCase() + horoscope.maha_planet?.slice(1)} Maha Dasha
              <span style={{ fontSize:"1rem", color:"#555", fontWeight:400, marginLeft:"8px" }}>
                · {horoscope.sub_emoji} {horoscope.sub_planet?.charAt(0).toUpperCase() + horoscope.sub_planet?.slice(1)} Antardasha
              </span>
            </div>
          </div>
          <div style={{ fontSize:"1.0rem", color:"#888", fontFamily:"'Inter',sans-serif",
            fontStyle:"italic", textAlign:"right" }}>
            {horoscope.dasha_remaining} left
          </div>
        </div>

        {/* Pills */}
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"18px" }}>
          {horoscope.moon_nakshatra && (
            <span style={{ background:`rgba(${rgb},0.1)`, border:`1px solid rgba(${rgb},0.3)`,
              borderRadius:"20px", padding:"5px 14px",
              fontSize:"1rem", color:mahaColor, fontFamily:"'Inter',sans-serif" }}>
              🌙 {horoscope.moon_nakshatra} Pada {horoscope.moon_pada}
            </span>
          )}
          {horoscope.tithi && (
            <span style={{ background:"rgba(0,0,0,0.04)", border:"1px solid rgba(0,0,0,0.1)",
              borderRadius:"20px", padding:"5px 14px",
              fontSize:"1rem", color:"#555", fontFamily:"'Inter',sans-serif" }}>
              {horoscope.tithi}
            </span>
          )}
        </div>

        {/* Life Chapter */}
        {horoscope.chapter && (
          <div style={{ marginBottom:"18px" }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:"#E8832A",
              fontFamily:JT.heading, marginBottom:"8px" }}>📖 Your Life Chapter</div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
              color:"#111", lineHeight:1.9 }}>
              {horoscope.chapter}
            </div>
          </div>
        )}

        {/* Today's Tone */}
        {horoscope.tone && (
          <div style={{ marginBottom:"18px" }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:mahaColor,
              fontFamily:JT.heading, marginBottom:"8px" }}>⚡ Today's Tone</div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
              color:"#111", lineHeight:1.9 }}>
              {horoscope.tone}
            </div>
          </div>
        )}

        {/* Guidance */}
        {horoscope.guidance && (
          <div style={{
            background:`rgba(${rgb},0.07)`, border:`1px solid rgba(${rgb},0.18)`,
            borderLeft:`4px solid ${mahaColor}`,
            borderRadius:"14px", padding:"18px 20px", marginBottom:"18px",
          }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.12em", color:"#E8832A",
              fontFamily:JT.heading, marginBottom:"10px" }}>🕉️ Guidance</div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.35rem",
              fontWeight:600, color:"#1a1108", lineHeight:2.0 }}>
              {horoscope.guidance}
            </div>
          </div>
        )}

        {/* Watch For */}
        {horoscope.watch && (
          <div style={{ display:"flex", gap:"12px", alignItems:"flex-start", marginBottom:"16px" }}>
            <span style={{ fontSize:"1.2rem", flexShrink:0 }}>👁️</span>
            <div>
              <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:"#ef4444",
                fontFamily:JT.heading, marginBottom:"6px" }}>Watch For</div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
                color:"#111", lineHeight:1.85 }}>
                {horoscope.watch}
              </div>
            </div>
          </div>
        )}

        {/* Verse */}
        {horoscope.verse && (
          <div style={{
            background:"#FFFDF8", border:`1px solid rgba(${rgb},0.22)`,
            borderLeft:`4px solid ${mahaColor}`,
            borderRadius:"12px", padding:"16px 18px",
            fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
            color:"#1a1108", fontStyle:"italic", lineHeight:1.9,
            textShadow:`0 0 14px rgba(${rgb},0.35)`,
          }}>
            {horoscope.verse}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Layer 2: Weekly Mood & Energy card ───────────────────────────────────────
function WeeklyReadingCard({ reading, loading, onUpgrade }) {
  if (loading) return (
    <div style={{ height:220, borderRadius:"20px", marginBottom:"20px",
      background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
      backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
  );

  const accent = reading?.maha_color || "#c9a84c";
  const rgb    = hexToRgb(accent);

  // Paywall — short teaser only; the full pitch already ran on PersonalHoroscopeCard right above this
  if (!reading || onUpgrade) {
    return (
      <div onClick={onUpgrade} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
        cursor:"pointer",
        background:"#ffffff",
        border:`1px solid ${JT.border}`, borderRadius:"14px",
        padding:"14px 18px", marginBottom:"20px",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:"1.2rem" }}>🔒</span>
          <span style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.05rem",
            color:JT.text, fontWeight:600 }}>
            Weekly Mood & Energy — included in your plan
          </span>
        </div>
        <span style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.0rem",
          color:accent, fontWeight:700, whiteSpace:"nowrap" }}>
          See plans ↑
        </span>
      </div>
    );
  }

  // Unlocked — full weekly reading
  const days = reading.days || [];
  const maxScore = Math.max(...days.map(d => d.score || 5), 1);

  return (
    <div style={{
      position:"relative", overflow:"hidden",
      background:`linear-gradient(160deg,#FFFDF8 60%,rgba(${rgb},0.12) 100%)`,
      border:`1px solid rgba(${rgb},0.28)`,
      borderRadius:"20px", marginBottom:"20px",
      boxShadow:`0 4px 20px rgba(${rgb},0.1)`,
    }}>
      <div style={{ height:3, background:`linear-gradient(90deg,${accent},rgba(${rgb},0.4),${accent})` }} />
      <div style={{ padding:"22px 24px" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
          marginBottom:"14px" }}>
          <div>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.12em", color:accent,
              fontFamily:JT.heading }}>
              Weekly Mood & Energy
            </div>
            <div style={{ fontSize:"1.1rem", color:"#111", fontWeight:700, marginTop:"4px",
              fontFamily:JT.heading }}>
              {reading.maha_emoji} {reading.maha_planet?.charAt(0).toUpperCase() + reading.maha_planet?.slice(1)} Dasha Week
            </div>
          </div>
          <div style={{ fontSize:"1.0rem", color:"#888", fontFamily:"'Inter',sans-serif",
            fontStyle:"italic", textAlign:"right", lineHeight:1.5 }}>
            {new Date(reading.week_start).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}
            {" – "}
            {new Date(reading.week_end).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}
          </div>
        </div>

        {/* Weekly theme */}
        {reading.theme && (
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
            color:"#111", lineHeight:1.9, marginBottom:"18px" }}>
            {reading.theme}
          </div>
        )}

        {/* 7-day energy bars */}
        {days.length > 0 && (
          <div style={{ marginBottom:"18px" }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:"#555",
              fontFamily:JT.heading, marginBottom:"12px" }}>Daily Energy</div>
            <div style={{ display:"flex", gap:"5px", alignItems:"flex-end", height:"68px" }}>
              {days.map((d, i) => {
                const h = Math.round((d.score / maxScore) * 44) + 8;
                const isToday = d.date === new Date().toISOString().split("T")[0];
                return (
                  <div key={i} style={{ flex:1, display:"flex", flexDirection:"column",
                    alignItems:"center", gap:"4px" }}>
                    <div title={`${d.day}: ${d.note || ""}`} style={{
                      width:"100%", height:`${h}px`, borderRadius:"4px 4px 0 0",
                      background: isToday
                        ? `linear-gradient(180deg,${accent},${accent}88)`
                        : `rgba(${rgb},${0.2 + (d.score/10)*0.5})`,
                      border: isToday ? `1px solid ${accent}` : "none",
                      cursor:"default", transition:"height 0.3s",
                    }} />
                    <div style={{ fontSize:"1.0rem", color: isToday ? accent : "#888",
                      fontWeight: isToday ? 700 : 400 }}>
                      {d.day?.slice(0,1)}
                    </div>
                    <div style={{ fontSize:"1.0rem", color: isToday ? accent : "#888",
                      fontWeight: isToday ? 700 : 400 }}>
                      {d.score}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Today's note */}
            {(() => {
              const todayStr = new Date().toISOString().split("T")[0];
              const todayDay = days.find(d => d.date === todayStr);
              return todayDay ? (
                <div style={{ marginTop:"12px", background:`rgba(${rgb},0.07)`,
                  border:`1px solid rgba(${rgb},0.18)`, borderRadius:"12px",
                  padding:"12px 14px" }}>
                  <div style={{ fontSize:"1.1rem", color:accent, marginBottom:"6px",
                    fontFamily:JT.heading }}>
                    🌙 {todayDay.nakshatra} Pada {todayDay.pada} — Today
                  </div>
                  <div style={{ fontSize:"1.2rem", color:"#111", lineHeight:1.85,
                    fontFamily:"'Inter',sans-serif" }}>
                    {todayDay.note}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Focus areas */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"18px" }}>
          {[
            { icon:"💼", label:"Career",        text:reading.career },
            { icon:"💞", label:"Relationships", text:reading.relationships },
            { icon:"🌿", label:"Health",        text:reading.health },
            { icon:"🕉️", label:"Sadhana",       text:reading.sadhana },
          ].filter(f => f.text).map(f => (
            <div key={f.label} style={{
              background:"rgba(0,0,0,0.03)", border:"1px solid rgba(0,0,0,0.08)",
              borderRadius:"14px", padding:"14px",
            }}>
              <div style={{ fontSize:"1.1rem", letterSpacing:"0.08em", color:"#E8832A",
                fontFamily:JT.heading, marginBottom:"6px" }}>
                {f.icon} {f.label}
              </div>
              <div style={{ fontSize:"1.2rem", color:"#111", lineHeight:1.7,
                fontFamily:"'Inter',sans-serif" }}>{f.text}</div>
            </div>
          ))}
        </div>

        {/* Verse */}
        {reading.verse && (
          <div style={{
            background:"#FFFDF8", border:`1px solid rgba(${rgb},0.22)`,
            borderLeft:`4px solid ${accent}`,
            borderRadius:"12px", padding:"16px 18px",
            fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
            color:"#1a1108", fontStyle:"italic", lineHeight:1.9,
            textShadow:`0 0 14px rgba(${rgb},0.35)`,
          }}>
            {reading.verse}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Daily Rashi Horoscope card ────────────────────────────────────────────────
function HoroscopeCard({ horoscope, rashi, loading }) {
  const today  = new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" });
  const accent = rashi?.color || "#f97316";
  const hexToRgb = h => {
    const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16);
    return `${r},${g},${b}`;
  };
  const rgb = accent.startsWith("#") ? hexToRgb(accent) : "249,115,22";

  if (loading) return (
    <div style={{ height:220, borderRadius:"20px", marginBottom:"20px",
      background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
      backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
  );

  if (!horoscope) return (
    <div style={{
      background:"#FFFFFF", border:"1px solid rgba(201,149,58,0.2)",
      borderRadius:"20px", padding:"20px 22px", marginBottom:"20px",
      textAlign:"center", color:"#3a3a3a", fontSize:"1.2rem",
      fontFamily:"'Inter',sans-serif",
    }}>
      Today's reading is being prepared — check back shortly.
    </div>
  );

  return (
    <div style={{
      position:"relative", overflow:"hidden",
      background:`linear-gradient(160deg, #FFFDF8 60%, rgba(${rgb},0.12) 100%)`,
      border:`1px solid rgba(${rgb},0.3)`,
      borderRadius:"22px", marginBottom:"20px",
      boxShadow:`0 4px 20px rgba(${rgb},0.12)`,
    }}>
      {/* Top accent bar */}
      <div style={{ height:3, background:`linear-gradient(90deg,${accent},rgba(${rgb},0.4),${accent})` }} />

      <div style={{ padding:"22px 24px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px" }}>
          <div style={{ fontSize:"1.8rem", filter:`drop-shadow(0 0 10px rgba(${rgb},0.4))` }}>
            {rashi?.emoji || "🪐"}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:"1.1rem", color:accent,  fontFamily:JT.heading }}>
              Today's Rashi Reading
            </div>
            <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:accent }}>
              {rashi?.vedic} · {rashi?.en}
            </div>
          </div>
          <div style={{ fontSize:"1.0rem", color:"#888", fontFamily:"'Inter',sans-serif",
            fontStyle:"italic", textAlign:"right", flexShrink:0 }}>
            {today}
          </div>
        </div>

        {/* Context pills */}
        {(horoscope.moon_nakshatra || horoscope.tithi || horoscope.day_ruler) && (
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"18px" }}>
            {horoscope.moon_nakshatra && (
              <span style={{
                background:`rgba(${rgb},0.1)`, border:`1px solid rgba(${rgb},0.3)`,
                borderRadius:"20px", padding:"5px 14px",
                fontSize:"1rem", color:accent, fontFamily:"'Inter',sans-serif",
              }}>
                🌙 {horoscope.moon_nakshatra} Pada {horoscope.moon_pada}
              </span>
            )}
            {horoscope.tithi && (
              <span style={{
                background:"rgba(0,0,0,0.04)", border:"1px solid rgba(0,0,0,0.1)",
                borderRadius:"20px", padding:"5px 14px",
                fontSize:"1rem", color:"#555", fontFamily:"'Inter',sans-serif",
              }}>
                {horoscope.tithi}
              </span>
            )}
            {horoscope.day_ruler && (
              <span style={{
                background:"rgba(0,0,0,0.04)", border:"1px solid rgba(0,0,0,0.1)",
                borderRadius:"20px", padding:"5px 14px",
                fontSize:"1rem", color:"#555", fontFamily:"'Inter',sans-serif",
              }}>
                {horoscope.day} · {horoscope.day_ruler}
              </span>
            )}
          </div>
        )}

        {/* Energy */}
        {horoscope.energy && (
          <div style={{ marginBottom:"18px" }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:accent,
              fontFamily:JT.heading, marginBottom:"10px" }}>
              ⚡ Today's Energy
            </div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
              color:"#111", lineHeight:1.9 }}>
              {horoscope.energy}
            </div>
          </div>
        )}

        {/* Guidance */}
        {horoscope.guidance && (
          <div style={{
            background:`rgba(${rgb},0.08)`,
            border:`1px solid rgba(${rgb},0.2)`,
            borderLeft:`4px solid ${accent}`,
            borderRadius:"14px", padding:"18px 20px", marginBottom:"18px",
          }}>
            <div style={{ fontSize:"1.1rem", letterSpacing:"0.12em", color:"#E8832A",
              fontFamily:JT.heading, marginBottom:"10px" }}>
              🕉️ Guidance
            </div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.35rem",
              fontWeight:600, color:"#1a1108", lineHeight:2.0 }}>
              {horoscope.guidance}
            </div>
          </div>
        )}

        {/* Watch for */}
        {horoscope.watch && (
          <div style={{ display:"flex", gap:"12px", alignItems:"flex-start", marginBottom:"16px" }}>
            <span style={{ fontSize:"1.2rem", flexShrink:0 }}>👁️</span>
            <div>
              <div style={{ fontSize:"1.1rem", letterSpacing:"0.1em", color:"#ef4444",
                fontFamily:JT.heading, marginBottom:"6px" }}>
                Watch For
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
                color:"#111", lineHeight:1.85 }}>
                {horoscope.watch}
              </div>
            </div>
          </div>
        )}

        {/* Verse */}
        {horoscope.verse && (
          <div style={{
            background:"#FFFDF8",
            border:`1px solid rgba(${rgb},0.22)`,
            borderLeft:`4px solid ${accent}`,
            borderRadius:"12px", padding:"16px 18px",
            fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
            color:"#1a1108", fontStyle:"italic", lineHeight:1.9,
            textShadow:`0 0 14px rgba(${rgb},0.35)`,
          }}>
            {horoscope.verse}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vimshottari Dasha Tab ─────────────────────────────────────────────────────
const _CONCERN_CARDS = [
  { id:"career",        icon:"💼", label:"Career & Purpose",  hook:"Your Dasha reveals exactly why career feels stuck — and when the cosmic shift arrives." },
  { id:"relationships", icon:"💔", label:"Relationships",      hook:"Relationship patterns in Dasha are precise. See what planet governs this chapter." },
  { id:"fear",          icon:"😰", label:"Fear & Anxiety",     hook:"Fear during Saturn or Rahu Dasha is a known pattern. Your chart shows the timeline." },
  { id:"grief",         icon:"🌿", label:"Grief & Loss",       hook:"Grief is a Dasha signature. Understanding it gives you the end-date — and the gift." },
  { id:"anger",         icon:"🔥", label:"Anger",              hook:"Mars Dasha, Rahu Dasha — both amplify fire. Knowing yours transforms how you meet it." },
  { id:"purpose",       icon:"🌟", label:"Finding My Path",    hook:"Dasha shows the exact period when your dharma activates. You may be in it right now." },
];

function DashaTab({ userId, noBirth, onNeedBirth, lightMode = true }) {
  const [data,            setData]           = useState(null);
  const [loading,         setLoading]        = useState(true);
  const [selectedConcern, setSelectedConcern] = useState(null);

  useEffect(() => {
    if (noBirth) { setLoading(false); return; }
    fetch(`${API}/jyotish/dasha/${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(d => { setData(d.error ? null : d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId, noBirth]);

  if (loading) return (
    <div style={{ height:300, borderRadius:"20px",
      background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
      backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
  );

  // ── No birth details — show problem-first concern picker ──────────────────
  if (noBirth) {
    const chosen = _CONCERN_CARDS.find(c => c.id === selectedConcern);
    return (
      <div style={{ padding:"32px 20px 28px", background:"var(--bg2)",
        borderRadius:"20px", border:"1px solid var(--border)",
        textAlign:"center" }}>

        {/* Heading */}
        <div style={{ fontFamily:JT.heading, color:"var(--gold)",
          fontSize:"1.05rem", marginBottom:"6px", letterSpacing:"0.03em" }}>
          What are you navigating right now?
        </div>
        <div style={{ fontSize:"1.05rem", color:"var(--text-dim)",
          fontFamily:"'Inter',sans-serif", marginBottom:"22px", lineHeight:1.6 }}>
          Your Dasha period shapes every area of life. Choose what's heaviest.
        </div>

        {/* 2×3 concern grid */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
          gap:"10px", marginBottom:"22px" }}>
          {_CONCERN_CARDS.map(card => {
            const active = selectedConcern === card.id;
            return (
              <button key={card.id}
                onClick={() => setSelectedConcern(card.id)}
                style={{
                  background: active
                    ? "linear-gradient(135deg,var(--saffron),var(--gold))"
                    : "var(--bg3)",
                  border: active
                    ? "1.5px solid var(--gold)"
                    : "1.5px solid var(--border)",
                  borderRadius:"14px",
                  padding:"14px 10px",
                  cursor:"pointer",
                  display:"flex", flexDirection:"column",
                  alignItems:"center", gap:"6px",
                  transition:"all 0.2s ease",
                  boxShadow: active ? "0 4px 18px rgba(212,160,23,0.25)" : "none",
                }}>
                <span style={{ fontSize:"1.6rem", lineHeight:1 }}>{card.icon}</span>
                <span style={{
                  fontFamily:"'Inter',sans-serif",
                  fontSize:"1.05rem",
                  color: active ? "#1a0f00" : "var(--text)",
                  fontWeight: active ? 700 : 500,
                  lineHeight:1.3,
                }}>{card.label}</span>
              </button>
            );
          })}
        </div>

        {/* Hook line + CTA — shown after selection */}
        {chosen && (
          <div style={{ animation:"fadeInUp 0.35s ease" }}>
            <div style={{
              background:"linear-gradient(135deg,rgba(212,160,23,0.08),rgba(212,160,23,0.03))",
              border:"1px solid rgba(212,160,23,0.25)",
              borderRadius:"14px",
              padding:"16px 18px",
              marginBottom:"18px",
              fontFamily:"'Inter',sans-serif",
              fontSize:"1.0rem",
              color:"var(--text)",
              lineHeight:1.65,
              fontStyle:"italic",
            }}>
              {chosen.hook}
            </div>
            <button
              onClick={onNeedBirth}
              style={{
                background:"linear-gradient(135deg,var(--saffron),var(--gold))",
                border:"none",
                borderRadius:"12px",
                padding:"13px 28px",
                fontFamily:JT.heading,
                fontSize:"1.05rem",
                color:"#1a0f00",
                fontWeight:700,
                cursor:"pointer",
                letterSpacing:"0.04em",
                boxShadow:"0 4px 20px rgba(212,160,23,0.35)",
                transition:"transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 24px rgba(212,160,23,0.45)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)";   e.currentTarget.style.boxShadow="0 4px 20px rgba(212,160,23,0.35)"; }}
            >
              See your Dasha →
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Has birth details but data load failed ────────────────────────────────
  if (!data) return (
    <div style={{ textAlign:"center", padding:"40px 20px",
      background:"var(--bg2)", borderRadius:"20px",
      border:"1px solid var(--border)" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"12px" }}>🌀</div>
      <div style={{ fontFamily:JT.heading, color:"var(--gold)",
        fontSize:"1rem", marginBottom:"8px" }}>Could Not Load Dasha</div>
      <div style={{ fontSize:"1.1rem", color:"var(--text-dim)", lineHeight:1.7 }}>
        Your birth details are saved but we could not compute your Dasha right now.
        Please try again in a moment.
      </div>
    </div>
  );

  const { maha_dasha, antardasha, timeline } = data;
  const accent = maha_dasha.color;
  const rgb    = hexToRgb(accent);
  const today  = new Date();

  return (
    <div style={{ animation:"fadeInUp 0.5s ease" }}>

      {/* ── Current Maha Dasha Hero ── */}
      <div style={{
        background: lightMode ? "#ffffff" : `linear-gradient(145deg,#100e0a,rgba(${rgb},0.2))`,
        border: lightMode ? "1px solid rgba(0,0,0,0.1)" : `1px solid ${accent}55`,
        borderRadius:"24px",
        overflow:"hidden", marginBottom:"14px",
        boxShadow: lightMode ? "0 4px 24px rgba(0,0,0,0.08)" : `0 12px 50px rgba(${rgb},0.2)`,
      }}>
        <div style={{ height:3, background:`linear-gradient(90deg,transparent,${accent},transparent)` }} />
        <div style={{ padding:"24px 22px" }}>
          {/* Header */}
          <div style={{ fontSize:"1.0rem", letterSpacing:"0.18em",
            color: lightMode ? "#f97316" : `${accent}88`,
            fontFamily:JT.heading, marginBottom:"14px" }}>
            YOUR VIMSHOTTARI MAHA DASHA
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:"16px", marginBottom:"20px" }}>
            <div style={{ fontSize:"3.5rem", filter:`drop-shadow(0 0 20px ${accent}80)`,
              animation:"float 3.5s ease-in-out infinite" }}>
              {maha_dasha.emoji}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:JT.heading,
                fontSize:"1.8rem", color: lightMode ? "#1a1a1a" : "#fff",
                textShadow: lightMode ? "none" : `0 0 30px ${accent}` }}>
                {maha_dasha.name?.en} Dasha
              </div>
              {maha_dasha.name?.hi && (
                <div style={{ fontSize:"1.1rem", color:`${accent}cc`,
                  fontFamily:"'Inter',sans-serif", fontStyle:"italic" }}>
                  {maha_dasha.name.hi} दशा
                </div>
              )}
            </div>
          </div>

          {/* Duration chips */}
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"18px" }}>
            {[
              { label:"Years in", value:`${maha_dasha.years_in} yrs` },
              { label:"Ends", value:new Date(maha_dasha.end).getFullYear() },
              { label:"Remaining", value:`${maha_dasha.years_left} yrs` },
              { label:"Full duration", value:`${maha_dasha.full_years} yrs` },
            ].map(c => (
              <div key={c.label} style={{
                background: lightMode ? "rgba(249,115,22,0.08)" : `rgba(${rgb},0.12)`,
                border: lightMode ? "1px solid rgba(249,115,22,0.25)" : `1px solid ${accent}33`,
                borderRadius:"20px", padding:"5px 14px",
                fontSize:"1.0rem",
              }}>
                <span style={{ color: lightMode ? "#555" : "var(--text-dim)" }}>{c.label}: </span>
                <span style={{ color: lightMode ? "#f97316" : accent, fontWeight:600 }}>{c.value}</span>
              </div>
            ))}
          </div>

          {/* Period feel */}
          <div style={{
            background: lightMode ? "rgba(249,115,22,0.05)" : `rgba(${rgb},0.08)`,
            border: lightMode ? "1px solid rgba(249,115,22,0.15)" : `1px solid ${accent}22`,
            borderRadius:"14px", padding:"14px 16px", marginBottom:"14px",
          }}>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem",
              color: lightMode ? "#333" : "var(--cream)", lineHeight:1.85, fontStyle:"italic" }}>
              "{maha_dasha.meaning?.period_feel}"
            </div>
          </div>

          {/* Gifts + Challenges */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"14px" }}>
            <div style={{ background:"rgba(34,197,94,0.07)", border:"1px solid rgba(34,197,94,0.2)",
              borderRadius:"12px", padding:"12px" }}>
              <div style={{ fontSize:"1.0rem", color: lightMode ? "#16a34a" : "#86efac",
                letterSpacing:"0.12em",
                fontFamily:JT.heading, marginBottom:"8px" }}>✨ GIFTS</div>
              {maha_dasha.meaning?.gifts?.map(g => (
                <div key={g} style={{ fontSize:"1.0rem", color: lightMode ? "#333" : "var(--cream)",
                  marginBottom:"5px", lineHeight:1.55 }}>· {g}</div>
              ))}
            </div>
            <div style={{ background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)",
              borderRadius:"12px", padding:"12px" }}>
              <div style={{ fontSize:"1.0rem", color: lightMode ? "#dc2626" : "#fca5a5",
                letterSpacing:"0.12em",
                fontFamily:JT.heading, marginBottom:"8px" }}>⚡ WATCH</div>
              {maha_dasha.meaning?.challenges?.map(c => (
                <div key={c} style={{ fontSize:"1.0rem", color: lightMode ? "#333" : "var(--cream)",
                  marginBottom:"5px", lineHeight:1.55 }}>· {c}</div>
              ))}
            </div>
          </div>

          {/* Guidance */}
          <div style={{
            borderLeft:`4px solid ${lightMode ? "#f97316" : accent}`, paddingLeft:"16px", marginBottom:"14px",
          }}>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.05rem",
              color: lightMode ? "#333" : "var(--cream)", lineHeight:1.85 }}>
              {maha_dasha.meaning?.guidance}
            </div>
          </div>

          {/* What to do */}
          <div style={{
            background: lightMode ? "#f8f4ee" : "var(--bg2)",
            border: lightMode ? "1px solid rgba(249,115,22,0.2)" : "1px solid var(--border)",
            borderRadius:"14px", padding:"14px 16px", marginBottom:"14px",
          }}>
            <div style={{ fontSize:"1.0rem", color:"#f97316", letterSpacing:"0.12em",
              fontFamily:JT.heading, marginBottom:"8px" }}>🪔 WHAT TO DO NOW</div>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
              color: lightMode ? "#333" : "var(--cream)", lineHeight:1.8 }}>
              {maha_dasha.meaning?.what_to_do}
            </div>
          </div>

          {/* Gita verse */}
          <div style={{
            background: lightMode ? "rgba(249,115,22,0.04)" : "linear-gradient(135deg,var(--bg2),rgba(232,131,42,0.06))",
            border:"1px solid rgba(232,131,42,0.25)", borderRadius:"14px",
            padding:"14px 16px", display:"flex", gap:"10px",
          }}>
            <div style={{ fontSize:"1.3rem", flexShrink:0 }}>🕉️</div>
            <div>
              <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
                color:"#f97316", letterSpacing:"0.1em", marginBottom:"6px" }}>
                {maha_dasha.meaning?.gita_ref?.[0]}
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color: lightMode ? "#333" : "var(--cream)", fontStyle:"italic", lineHeight:1.7 }}>
                "{maha_dasha.meaning?.gita_ref?.[1]}"
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Antardasha ── */}
      {antardasha && (() => {
        const aRgb = hexToRgb(antardasha.color);
        const aEnd = new Date(antardasha.end);
        const daysLeft = Math.max(0, Math.round((aEnd - today) / 86400000));
        const monthsLeft = (daysLeft / 30.44).toFixed(1);
        return (
          <div style={{
            background: lightMode ? "#ffffff" : `linear-gradient(135deg,var(--bg2),rgba(${aRgb},0.08))`,
            border: lightMode ? "1px solid rgba(0,0,0,0.1)" : `1px solid ${antardasha.color}44`,
            borderRadius:"18px",
            padding:"18px 20px", marginBottom:"14px",
            boxShadow: lightMode ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
          }}>
            <div style={{ fontSize:"1.0rem", letterSpacing:"0.14em",
              color: lightMode ? "#f97316" : `${antardasha.color}99`,
              fontFamily:JT.heading,
              marginBottom:"10px" }}>
              CURRENT ANTARDASHA (SUB-PERIOD)
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
              <span style={{ fontSize:"2rem", filter:`drop-shadow(0 0 10px ${antardasha.color}80)` }}>
                {antardasha.emoji}
              </span>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
                  color:antardasha.color }}>
                  {maha_dasha.name?.en}–{antardasha.name?.en} Antardasha
                </div>
                <div style={{ fontSize:"1.05rem", color: lightMode ? "#555" : "var(--text-dim)", marginTop:"2px" }}>
                  Ends {aEnd.toLocaleDateString("en-IN",{month:"long",year:"numeric"})} · {monthsLeft} months left
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Timeline ── */}
      <div style={{
        background: lightMode ? "#ffffff" : "var(--bg2)",
        border: lightMode ? "1px solid rgba(0,0,0,0.1)" : "1px solid var(--border)",
        borderRadius:"18px", padding:"18px 20px",
        boxShadow: lightMode ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
      }}>
        <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
          color: lightMode ? "#f97316" : "var(--gold)", letterSpacing:"0.12em", marginBottom:"14px" }}>
          YOUR DASHA TIMELINE
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
          {timeline?.filter(d => {
            // Show ±2 dashas around current
            const idx = timeline.indexOf(d);
            const curIdx = timeline.findIndex(t => t.is_current);
            return Math.abs(idx - curIdx) <= 2;
          }).map(d => {
            const isC = d.is_current;
            const dRgb = hexToRgb(d.color);
            return (
              <div key={d.start} style={{
                display:"flex", alignItems:"center", gap:"12px",
                padding:"10px 14px", borderRadius:"12px",
                background: isC ? (lightMode ? "rgba(249,115,22,0.06)" : `rgba(${dRgb},0.12)`) : "transparent",
                border: isC ? (lightMode ? "1px solid rgba(249,115,22,0.2)" : `1px solid ${d.color}44`) : "1px solid transparent",
              }}>
                <span style={{ fontSize:"1.4rem", opacity: isC ? 1 : 0.5 }}>{d.emoji}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"1rem", fontFamily:JT.heading,
                    color: isC ? (lightMode ? "#f97316" : d.color) : (lightMode ? "#888" : "var(--text-dim)"),
                    fontWeight: isC ? 600 : 400 }}>
                    {d.name?.en} Dasha {isC ? "← Now" : ""}
                  </div>
                  <div style={{ fontSize:"1.1rem", color: lightMode ? "#555" : "var(--text-dim)", marginTop:"2px" }}>
                    Age {d.age_start}–{d.age_end} · {new Date(d.start).getFullYear()}–{new Date(d.end).getFullYear()} · {d.years} yrs
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize:"1.05rem", color: lightMode ? "#888" : "var(--text-dim)", textAlign:"center",
          marginTop:"10px", fontStyle:"italic" }}>
          Showing dashas around your current period
        </div>
      </div>

      {/* ── Puja Recommendation ── */}
      <PujaCTA planet={maha_dasha.name?.en} color={accent} rgb={rgb} />
    </div>
  );
}

// ── Puja CTA — shown in Dasha + Sadhana ──────────────────────────────────────
const PUJA_INFO = {
  Ketu:    { puja:"Ketu Graha Shanti Puja",    deity:"Lord Ganesha",  benefit:"Clears confusion, brings spiritual clarity and protection from unseen obstacles." },
  Venus:   { puja:"Shukra Graha Shanti Puja",  deity:"Goddess Lakshmi",benefit:"Strengthens relationships, brings prosperity, harmony in love and creative work." },
  Sun:     { puja:"Surya Graha Shanti Puja",   deity:"Surya Dev",      benefit:"Improves career, authority, health of eyes and heart, relationship with father." },
  Moon:    { puja:"Chandra Graha Shanti Puja", deity:"Lord Shiva",     benefit:"Calms the mind, heals emotional wounds, strengthens intuition and mother bond." },
  Mars:    { puja:"Mangal Graha Shanti Puja",  deity:"Lord Hanuman",   benefit:"Reduces aggression, protects from accidents, builds courage and property gains." },
  Rahu:    { puja:"Rahu-Ketu Shanti Puja",     deity:"Goddess Durga",  benefit:"Removes obsession and confusion, brings sudden positive transformation." },
  Jupiter: { puja:"Guru Graha Shanti Puja",    deity:"Lord Vishnu",    benefit:"Expands wisdom, wealth, and family blessings. Removes obstacles to dharmic growth." },
  Saturn:  { puja:"Shani Graha Shanti Puja",   deity:"Shani Dev",      benefit:"Pacifies karmic debts, removes delays, brings justice and long-term stability." },
  Mercury: { puja:"Budha Graha Shanti Puja",   deity:"Lord Vishnu",    benefit:"Sharpens intellect, improves communication, removes obstacles in business and education." },
};

function PujaCTA({ planet, color, rgb }) {
  const info = PUJA_INFO[planet];
  if (!info) return null;

  return (
    <div style={{
      background:`linear-gradient(135deg,rgba(${rgb},0.08),${JT.card})`,
      border:`1px solid ${color}33`, borderRadius:"18px", boxShadow:JT.shadow,
      padding:"20px 22px", marginTop:"14px",
      borderTop:`3px solid ${color}66`,
    }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:"12px", marginBottom:"14px" }}>
        <div style={{ fontSize:"1.8rem" }}>🪔</div>
        <div>
          <div style={{ fontFamily:JT.label, fontWeight:700, fontSize:"1rem",
            color:color, marginBottom:"4px" }}>
            Recommended puja for your {planet} Dasha
          </div>
          <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
            color:JT.text }}>
            {info.puja}
          </div>
          <div style={{ fontSize:"1.0rem", color:JT.textDim, marginTop:"2px" }}>
            Deity: {info.deity}
          </div>
        </div>
      </div>

      <div style={{ fontFamily:JT.body, fontSize:"1.0rem",
        color:JT.text, lineHeight:1.75, marginBottom:"16px",
        fontStyle:"italic" }}>
        "{info.benefit}"
      </div>

      <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>
        <a href="/?page=puja"
          style={{
            flex:1, minWidth:140,
            background:`linear-gradient(135deg,${color},${color}bb)`,
            border:"none", borderRadius:"12px", padding:"12px 16px",
            color:"#fff", textDecoration:"none",
            fontFamily:JT.label, fontWeight:600, fontSize:"0.95rem",
            letterSpacing:"0.02em", textAlign:"center", cursor:"pointer",
            boxShadow:`0 4px 20px rgba(${rgb},0.35)`,
            display:"flex", alignItems:"center", justifyContent:"center", gap:"8px",
          }}>
          📱 Book Online Puja
        </a>
        <a href="/?page=puja"
          style={{
            flex:1, minWidth:140,
            background:JT.cardWarm, border:`1px solid ${color}44`,
            borderRadius:"12px", padding:"12px 16px",
            color:color, textDecoration:"none",
            fontFamily:JT.label, fontWeight:600, fontSize:"0.95rem",
            letterSpacing:"0.02em", textAlign:"center", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:"8px",
          }}>
          🏠 Pujari at Home
        </a>
      </div>
      <div style={{ fontSize:"1.0rem", color:JT.textDim, textAlign:"center",
        marginTop:"8px" }}>
        Online puja from ₹99 · Home visit available in your city
      </div>
    </div>
  );
}

// ── Kundali Milan Tab ─────────────────────────────────────────────────────────
const KOOTA_ICONS = {
  "Varna":        { icon:"🏛️", label:"Varna",        sub:"Spiritual values" },
  "Vashya":       { icon:"🤝", label:"Vashya",        sub:"Attraction & respect" },
  "Tara":         { icon:"⭐", label:"Tara",          sub:"Destiny & birth star" },
  "Yoni":         { icon:"🌙", label:"Yoni",          sub:"Physical harmony" },
  "Graha Maitri": { icon:"🪐", label:"Graha Maitri",  sub:"Mental bond" },
  "Gana":         { icon:"🧘", label:"Gana",          sub:"Nature & temperament" },
  "Bhakoot":      { icon:"♥️", label:"Bhakoot",       sub:"Emotional & family" },
  "Nadi":         { icon:"🩺", label:"Nadi",          sub:"Health & progeny" },
};

function GaugeRing({ percent, color }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width="130" height="130" style={{ transform:"rotate(-90deg)" }}>
      <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(125,29,63,0.12)" strokeWidth="10" />
      <circle cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="10"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition:"stroke-dashoffset 1.2s ease", filter:`drop-shadow(0 0 8px ${color}88)` }} />
    </svg>
  );
}

/* ── Kundali Milan palettes ── */
const MILAN_ROSE = {
  pageBg:      "#ffffff",
  cardBg:      "#ffffff",
  cardBgRose:  "#ffffff",
  cardBgGold:  "#ffffff",
  borderRose:  "#d4849a",
  borderSoft:  "rgba(0,0,0,0.10)",
  borderGold:  "#c9a84c",
  titleDeep:   "#7d1d3f",
  bodyText:    "#1a0a10",
  dimText:     "#4a1e30",
  gold:        "#9a6e1a",
  accentRose:  "#b5365a",
  inputBg:     "#ffffff",
  inputBorder: "#e8b4c4",
  trackBg:     "rgba(0,0,0,0.09)",
};

const MILAN_CRIMSON = {
  pageBg:      "#ffffff",
  cardBg:      "#ffffff",
  cardBgRose:  "#ffffff",
  cardBgGold:  "#ffffff",
  borderRose:  "#e89090",
  borderSoft:  "rgba(0,0,0,0.10)",
  borderGold:  "#c9a84c",
  titleDeep:   "#9b1a1a",
  bodyText:    "#1a0808",
  dimText:     "#5c1c1c",
  gold:        "#9a6e1a",
  accentRose:  "#c0392b",
  inputBg:     "#ffffff",
  inputBorder: "#f0b0b0",
  trackBg:     "rgba(0,0,0,0.09)",
};

const MILAN_THEMES = {
  rose:    MILAN_ROSE,
  crimson: MILAN_CRIMSON,
};

function KootaBar({ koota, lm, myName = "You", partnerName = "Partner" }) {
  const pct = koota.max > 0 ? (koota.score / koota.max) * 100 : 0;
  const meta = KOOTA_ICONS[koota.name] || { icon:"✨", label:koota.name, sub:"" };
  const barColor = pct >= 75 ? "#16a34a" : pct >= 50 ? "#d97706" : pct >= 25 ? "#ea580c" : "#dc2626";
  return (
    <div style={{ background:lm.cardBg, borderRadius:"14px", padding:"12px 14px",
      border:`1px solid ${lm.borderSoft}`,
      boxShadow:"0 2px 10px rgba(125,29,63,0.07)",
      borderLeft:`4px solid ${barColor}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        marginBottom:"6px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
          <span style={{ fontSize:"1.1rem" }}>{meta.icon}</span>
          <div>
            <div style={{ fontSize:"1.0rem", color:lm.bodyText, fontWeight:700 }}>
              {meta.label}
            </div>
            <div style={{ fontSize:"1.0rem", color:lm.dimText }}>{meta.sub}</div>
          </div>
        </div>
        <div style={{ fontSize:"1rem", fontWeight:700, color:barColor,
          fontFamily:JT.heading }}>
          {koota.score}/{koota.max}
        </div>
      </div>
      {/* Progress bar */}
      <div style={{ height:5, borderRadius:99, background:lm.trackBg, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:barColor,
          borderRadius:99, transition:"width 1s ease" }} />
      </div>
      {/* Extra info for specific kootas */}
      {(koota.symbol1 || koota.gana1 || koota.ruler1 || koota.nadi1) && (
        <div style={{ marginTop:"6px", fontSize:"1.1rem", color:lm.dimText,
          display:"flex", gap:"6px", flexWrap:"wrap" }}>
          {koota.symbol1 && <span>{myName}: {koota.symbol1}</span>}
          {koota.symbol2 && <span>· {partnerName}: {koota.symbol2}</span>}
          {koota.gana1 && <span>{myName}: {koota.gana1}</span>}
          {koota.gana2 && <span>· {partnerName}: {koota.gana2}</span>}
          {koota.ruler1 && <span>{myName}'s ruler: {koota.ruler1}</span>}
          {koota.ruler2 && <span>· {partnerName}'s ruler: {koota.ruler2}</span>}
          {koota.nadi1 && <span>{myName}: {koota.nadi1}</span>}
          {koota.nadi2 && <span>· {partnerName}: {koota.nadi2}</span>}
        </div>
      )}
    </div>
  );
}

const SEVERITY_COLOR = { high:"#ef4444", medium:"#f97316", low:"#f59e0b" };

function KundaliMilanTab({ userId, userDob, userBirthTime, userCity = "" }) {
  const [step,      setStep]      = useState("form");
  const [dob2,      setDob2]      = useState("");
  const [time2,     setTime2]     = useState("");
  const [name2,     setName2]     = useState("");
  const [city2,     setCity2]     = useState("");   // adds the Lagna to the Kuja dosha check
  const [iAm,       setIAm]       = useState("");    // must be picked: Varna, Vashya, Gana depend on who is the boy
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState("");
  const [theme,     setTheme]     = useState("crimson");
  const lm = MILAN_THEMES[theme];

  const ThemeToggle = () => (
    <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
      <span style={{ fontSize:"1.0rem", color:lm.dimText, fontWeight:500 }}>Theme</span>
      {[
        { key:"rose",    bg:"#7d1d3f", label:"Rose" },
        { key:"crimson", bg:"#9b1a1a", label:"Crimson" },
      ].map(t => (
        <button key={t.key} onClick={() => setTheme(t.key)} title={t.label} style={{
          width:22, height:22, borderRadius:"50%", background:t.bg, border:"none",
          cursor:"pointer", padding:0, flexShrink:0,
          boxShadow: theme === t.key ? `0 0 0 2px #fff, 0 0 0 4px ${t.bg}` : "0 1px 4px rgba(0,0,0,0.25)",
          transform: theme === t.key ? "scale(1.2)" : "scale(1)",
          transition:"all 0.2s ease",
        }} />
      ))}
    </div>
  );

  const calculate = async () => {
    if (!iAm) { setError("Please choose first: are you the boy or the girl?"); return; }
    if (!dob2) { setError("Please enter partner's date of birth."); return; }
    if (!userDob) { setError("Please add your own birth date first (go to Archetype tab)."); return; }
    setError("");
    setStep("loading");
    try {
      const res = await fetch(`${API}/jyotish/milan`, {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          dob1: userDob,
          birth_time1: userBirthTime || "12:00",
          dob2: dob2,
          birth_time2: time2 || "12:00",
          boy: iAm === "boy" ? 1 : 2,
          city1: userCity,
          city2: city2.trim(),
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResult({ ...data, name2: name2 || "Partner", boy: iAm === "boy" ? 1 : 2 });
      setStep("result");
    } catch (e) {
      setError(e.message || "Calculation failed.");
      setStep("form");
    }
  };

  const reset = () => { setStep("form"); setResult(null); setDob2(""); setTime2(""); setName2(""); setError(""); };

  if (step === "loading") return (
    <div style={{ textAlign:"center", padding:"60px 20px",
      background:lm.pageBg, borderRadius:"20px" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"16px",
        animation:"float 2s infinite ease-in-out" }}>💑</div>
      <div style={{ fontFamily:JT.heading, color:lm.titleDeep, fontSize:"1rem" }}>
        Reading the stars…
      </div>
      <div style={{ fontSize:"1.0rem", color:lm.dimText, marginTop:"8px" }}>
        Calculating Ashtakoota compatibility
      </div>
    </div>
  );

  if (step === "form") return (
    <div style={{ animation:"fadeInUp 0.4s ease",
      background:lm.pageBg, borderRadius:"20px", padding:"4px" }}>
      {/* Header card */}
      <div style={{
        background:`linear-gradient(135deg,#ffffff,${lm.cardBgRose})`,
        border:`2px solid ${lm.borderRose}`,
        borderTop:`4px solid ${lm.titleDeep}`,
        borderRadius:"20px", padding:"24px 20px", marginBottom:"20px", textAlign:"center",
        boxShadow:"0 4px 24px rgba(125,29,63,0.10)", position:"relative" }}>
        <div style={{ position:"absolute", top:"12px", right:"14px" }}><ThemeToggle /></div>
        <div style={{ fontSize:"2.2rem", marginBottom:"10px" }}>💑</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
          color:lm.titleDeep, marginBottom:"8px", letterSpacing:"0.06em" }}>
          Kundali Milan
        </div>
        <div style={{ fontSize:"1.15rem", color:lm.titleDeep, lineHeight:1.8,
          fontWeight:600,
          letterSpacing:"0.01em" }}>
          Ashtakoota matching — the ancient <span style={{ color:"#9a3a00", fontWeight:700 }}>36-point</span> Vedic compatibility system
          used in Hindu marriage across <span style={{ color:"#9a3a00", fontWeight:700 }}>thousands of years</span>
        </div>
      </div>

      {/* Who am I — asked first, no default, so nobody gets a wrong score by not noticing it */}
      <div style={{ background:lm.cardBg, borderRadius:"18px", padding:"20px",
        border:`2px solid ${iAm ? lm.borderRose : lm.titleDeep}`,
        boxShadow:"0 4px 20px rgba(125,29,63,0.08)", marginBottom:"16px" }}>
        <div style={{ fontSize:"1.2rem", color:"#1a1a1a", fontWeight:700, marginBottom:"6px" }}>
          Step 1: Are you the boy or the girl?
        </div>
        <div style={{ fontSize:"1rem", color:"#2b2b2b", marginBottom:"14px" }}>
          The score changes depending on who is the boy. Please tap one.
        </div>
        <div style={{ display:"flex", gap:"12px", flexWrap:"wrap" }}>
          {[["boy","👦 I am the boy"],["girl","👧 I am the girl"]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => { setIAm(v); setError(""); }} style={{
              flex:"1 1 180px", minHeight:"56px", padding:"12px", borderRadius:"12px", cursor:"pointer",
              fontSize:"1.15rem", fontWeight:700,
              background: iAm === v ? lm.titleDeep : "#ffffff",
              color: iAm === v ? "#fff" : "#1a1a1a",
              border:`2px solid ${iAm === v ? lm.titleDeep : lm.inputBorder}`,
            }}>{iAm === v ? "✓ " : ""}{label}</button>
          ))}
        </div>
        {iAm && (
          <div style={{ fontSize:"1rem", color:"#1a1a1a", marginTop:"12px" }}>
            Below, enter the details of the <b>{iAm === "boy" ? "girl (your bride)" : "boy (your groom)"}</b>.
          </div>
        )}
      </div>

      {/* Partner form */}
      <div style={{ background:lm.cardBg, borderRadius:"18px", padding:"20px",
        border:`1px solid ${lm.borderRose}`,
        boxShadow:"0 4px 20px rgba(125,29,63,0.08)", marginBottom:"16px" }}>
        <div style={{ fontSize:"1.1rem", color:lm.titleDeep, marginBottom:"16px",
          fontFamily:JT.heading, letterSpacing:"0.05em", fontWeight:700,
          borderBottom:`2px solid ${lm.borderRose}`, paddingBottom:"10px" }}>
          💕 Step 2: {iAm === "girl" ? "Boy's" : iAm === "boy" ? "Girl's" : "Partner's"} Details
        </div>

        <label style={{ fontSize:"1rem", color:lm.bodyText, display:"block", marginBottom:"6px", fontWeight:600 }}>
          Name (optional)
        </label>
        <input
          type="text"
          placeholder="Partner's name"
          value={name2}
          onChange={e => setName2(e.target.value)}
          style={{ width:"100%", background:lm.inputBg, border:`1px solid ${lm.inputBorder}`,
            borderRadius:"10px", padding:"11px 14px", color:lm.bodyText,
            fontSize:"1rem", marginBottom:"14px", boxSizing:"border-box" }}
        />

        <label style={{ fontSize:"1rem", color:lm.bodyText, display:"block", marginBottom:"6px", fontWeight:600 }}>
          Date of Birth *
        </label>
        <input
          type="date"
          value={dob2}
          onChange={e => setDob2(e.target.value)}
          style={{ width:"100%", background:lm.inputBg, border:`1px solid ${lm.inputBorder}`,
            borderRadius:"10px", padding:"11px 14px", color:lm.bodyText,
            fontSize:"1rem", marginBottom:"14px", boxSizing:"border-box" }}
        />

        <label style={{ fontSize:"1rem", color:lm.bodyText, display:"block", marginBottom:"6px", fontWeight:600 }}>
          ⚡ Birth Time <span style={{ color:lm.gold, fontWeight:400 }}>(strongly recommended)</span>
        </label>
        <input
          type="time"
          value={time2}
          onChange={e => setTime2(e.target.value)}
          style={{ width:"100%", background:lm.inputBg, border:`1px solid ${lm.inputBorder}`,
            borderRadius:"10px", padding:"11px 14px", color:lm.bodyText,
            fontSize:"1rem", marginBottom:"6px", boxSizing:"border-box" }}
        />
        <div style={{ fontSize:"1.0rem", color:lm.dimText, marginBottom:"18px" }}>
          Without birth time, 12:00 noon is assumed — nakshatra may differ near boundaries
        </div>

        <label style={{ fontSize:"1rem", color:lm.bodyText, display:"block", marginBottom:"6px", fontWeight:600 }}>
          Birth Place <span style={{ color:lm.gold, fontWeight:400 }}>(optional — for the Kuja dosha check)</span>
        </label>
        <input
          type="text"
          placeholder="Town or city"
          value={city2}
          maxLength={80}
          onChange={e => setCity2(e.target.value)}
          style={{ width:"100%", background:lm.inputBg, border:`1px solid ${lm.inputBorder}`,
            borderRadius:"10px", padding:"11px 14px", color:lm.bodyText,
            fontSize:"1rem", marginBottom:"18px", boxSizing:"border-box" }}
        />

        {error && (
          <div style={{ color:lm.titleDeep, fontSize:"1.0rem", marginBottom:"12px",
            background:`${lm.titleDeep}10`, border:`1px solid ${lm.titleDeep}33`,
            borderRadius:"10px", padding:"10px 14px" }}>
            {error}
          </div>
        )}

        <button onClick={calculate} style={{
          width:"100%", padding:"15px",
          background:`linear-gradient(135deg,${lm.titleDeep},${lm.accentRose})`,
          border:"none", borderRadius:"14px", cursor:"pointer",
          color:"#fff", 
          fontSize:"1.0rem", letterSpacing:"0.04em",
          boxShadow:`0 4px 20px ${lm.titleDeep}55`,
        }}>
          ✨ Calculate Compatibility
        </button>
      </div>

      <div style={{ fontSize:"1rem", color:lm.bodyText, textAlign:"center",
        lineHeight:1.85, padding:"0 10px" }}>
        Based on Janma Nakshatra using Lahiri Ayanamsha — same as traditional Jyotishis.
        The score shows <em>cosmic energy alignment</em>, not a guarantee of happiness.
        Real relationships are shaped by karma, effort, and free will — the stars only set the stage.
      </div>
    </div>
  );

  // ── RESULT VIEW ──────────────────────────────────────────────────────────────
  const { total, max, percent, verdict, summary, color, kootas, doshas,
          nakshatra1, nakshatra2, rashi1, rashi2, kuja } = result;

  return (
    <div style={{ animation:"fadeInUp 0.4s ease",
      background:lm.pageBg, borderRadius:"20px", padding:"4px" }}>
      {/* Score hero */}
      <div style={{
        background:`linear-gradient(135deg,#ffffff,${lm.cardBgRose})`,
        border:`2px solid ${lm.borderRose}`,
        borderTop:`4px solid ${lm.titleDeep}`,
        borderRadius:"22px", padding:"24px 20px", marginBottom:"18px",
        textAlign:"center", position:"relative", overflow:"hidden",
        boxShadow:`0 4px 24px ${lm.titleDeep}20` }}>
        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ fontSize:"1.0rem", color:lm.dimText, marginBottom:"12px",
            fontFamily:JT.heading, letterSpacing:"0.05em" }}>
            Ashtakoota Milan
          </div>

          {/* Gauge + score */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:"14px" }}>
            <div style={{ position:"relative", width:130, height:130 }}>
              <GaugeRing percent={percent} color={color} />
              <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
                alignItems:"center", justifyContent:"center" }}>
                <div style={{ fontSize:"2rem", fontWeight:800, color, lineHeight:1,
                  fontFamily:JT.heading }}>
                  {total}
                </div>
                <div style={{ fontSize:"1.1rem", color:lm.dimText, marginTop:"2px" }}>out of 36</div>
              </div>
            </div>
          </div>

          {/* Verdict badge */}
          <div style={{ display:"inline-block", padding:"8px 22px",
            background:`${color}18`, border:`2px solid ${color}55`,
            borderRadius:"20px", marginBottom:"14px" }}>
            <span style={{ color, fontFamily:JT.heading,
              fontSize:"1rem", fontWeight:700 }}>
              {verdict}
            </span>
          </div>

          {/* Names + nakshatras */}
          <div style={{ display:"flex", justifyContent:"center", gap:"16px",
            marginBottom:"14px", flexWrap:"wrap" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:"1.1rem", color:lm.dimText, marginBottom:"2px" }}>You{result.boy ? (result.boy === 1 ? " (boy)" : " (girl)") : ""}</div>
              <div style={{ fontSize:"1rem", color:lm.bodyText, fontWeight:700 }}>
                {nakshatra1?.symbol} {nakshatra1?.name}
              </div>
              <div style={{ fontSize:"1.0rem", color:lm.dimText }}>{rashi1}</div>
            </div>
            <div style={{ color:lm.titleDeep, fontSize:"1.6rem", alignSelf:"center" }}>💑</div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:"1.1rem", color:lm.dimText, marginBottom:"2px" }}>{result.name2}{result.boy ? (result.boy === 1 ? " (girl)" : " (boy)") : ""}</div>
              <div style={{ fontSize:"1rem", color:lm.bodyText, fontWeight:700 }}>
                {nakshatra2?.symbol} {nakshatra2?.name}
              </div>
              <div style={{ fontSize:"1.0rem", color:lm.dimText }}>{rashi2}</div>
            </div>
          </div>

          {/* Summary */}
          <div style={{ fontSize:"1rem", color:lm.bodyText, lineHeight:1.75,
            padding:"0 8px", }}>
            {summary}
          </div>
        </div>
      </div>

      {/* Dosha warnings */}
      {doshas.length > 0 && (
        <div style={{ marginBottom:"18px" }}>
          <div style={{ fontSize:"1rem", color:lm.titleDeep, marginBottom:"12px",
            fontFamily:JT.heading, letterSpacing:"0.04em" }}>
            ⚠️ Doshas Detected
          </div>
          {doshas.map((d, i) => (
            <div key={i} style={{
              background:lm.cardBg,
              border:`1px solid ${lm.borderSoft}`,
              borderLeft:`5px solid ${SEVERITY_COLOR[d.severity]}`,
              borderRadius:"14px", padding:"14px 16px", marginBottom:"10px",
              boxShadow:"0 2px 12px rgba(125,29,63,0.06)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                <span style={{ fontSize:"1rem" }}>{d.severity==="high"?"🔴":"🟠"}</span>
                <span style={{ color:SEVERITY_COLOR[d.severity], fontWeight:700,
                  fontSize:"1rem", fontFamily:JT.heading }}>
                  {d.name}
                </span>
                <span style={{ fontSize:"1.08rem", color:lm.dimText,
                  background:`${SEVERITY_COLOR[d.severity]}15`,
                  border:`1px solid ${SEVERITY_COLOR[d.severity]}30`,
                  padding:"3px 10px", borderRadius:"10px" }}>
                  {d.severity} severity
                </span>
              </div>
              <div style={{ fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.7,
                paddingLeft:"28px", }}>
                🪔 {d.remedy}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Kuja (Manglik) in each chart */}
      {kuja && (
        <div style={{ background:lm.cardBg, border:`1px solid ${lm.borderSoft}`, borderRadius:"14px",
          padding:"14px 16px", marginBottom:"18px", fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.7 }}>
          <div style={{ fontFamily:JT.heading, color:lm.titleDeep, fontWeight:700, marginBottom:"6px" }}>
            🔴 Kuja (Manglik) Dosha
          </div>
          {[["You", kuja.p1], [result.name2, kuja.p2]].map(([who, k]) => (
            <div key={who}>
              {who}: <b>{k.manglik ? "Manglik" : "Not Manglik"}</b>
              <span style={{ color:lm.dimText }}> — checked from {k.has_lagna ? "Lagna and Moon" : "Moon only (add birth place for Lagna)"}</span>
            </div>
          ))}
          <div style={{ marginTop:"6px" }}>
            {kuja.status === "balanced" ? "Both charts have Kuja dosha — it balances out, traditionally acceptable."
              : kuja.status === "one" ? "Kuja dosha is in only one chart. Cancellation rules must be checked before deciding."
              : "Neither chart has Kuja dosha."}
          </div>
        </div>
      )}

      {/* 8 Koota breakdown */}
      <div style={{ marginBottom:"18px" }}>
        <div style={{ fontSize:"1rem", color:lm.titleDeep, marginBottom:"12px",
          fontFamily:JT.heading, letterSpacing:"0.04em" }}>
          Koota Breakdown — 8 Factors
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
          {kootas.map((k, i) => <KootaBar key={i} koota={k} lm={lm} myName="You" partnerName={result.name2} />)}
        </div>
      </div>

      {/* Puja recommendation if low score */}
      {total < 24 && (
        <div style={{ background:lm.cardBgGold,
          border:`1px solid ${lm.borderGold}55`,
          borderLeft:`4px solid ${lm.gold}`,
          borderRadius:"18px", padding:"18px", marginBottom:"18px",
          boxShadow:"0 2px 16px rgba(184,137,46,0.10)" }}>
          <div style={{ display:"flex", gap:"12px", alignItems:"flex-start" }}>
            <div style={{ fontSize:"1.8rem", flexShrink:0 }}>🪔</div>
            <div>
              <div style={{ fontFamily:JT.heading, fontSize:"1rem",
                color:lm.gold, marginBottom:"8px" }}>
                Strengthen the Bond with a Puja
              </div>
              <div style={{ fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.8,
                marginBottom:"12px", }}>
                When compatibility needs strengthening, traditional remedies can help.
                A Vivah Puja or Navagraha Shanti performed together before marriage
                invokes divine blessings on the union.
              </div>
              <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>
                <a href="/?page=puja"
                  style={{ display:"inline-flex", alignItems:"center", gap:"6px",
                    padding:"9px 14px", borderRadius:"12px",
                    background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.35)",
                    color:"#15803d", fontSize:"1.0rem", textDecoration:"none",
                    }}>
                  📱 Book Online Puja
                </a>
                <a href="/?page=puja"
                  style={{ display:"inline-flex", alignItems:"center", gap:"6px",
                    padding:"9px 14px", borderRadius:"12px",
                    background:"rgba(184,137,46,0.12)", border:"1px solid rgba(184,137,46,0.3)",
                    color:lm.gold, fontSize:"1.0rem", textDecoration:"none",
                    }}>
                  🏠 Home Pujari Visit
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What this score means — important context */}
      <div style={{ background:lm.cardBg,
        border:`1px solid ${lm.borderGold}55`,
        borderLeft:`4px solid ${lm.borderGold}`,
        borderRadius:"16px", padding:"16px 18px", marginBottom:"16px",
        boxShadow:"0 2px 12px rgba(184,137,46,0.08)" }}>
        <div style={{ fontSize:"1rem", color:lm.gold, fontFamily:JT.heading,
          marginBottom:"10px" }}>
          📖 What This Score Means
        </div>
        <div style={{ fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.8, }}>
          Kundali Milan measures <strong style={{ color:lm.titleDeep }}>cosmic energy alignment</strong> between
          two birth stars — the planetary forces that support the union.
          It does <em>not</em> predict whether the relationship will be easy or difficult.
        </div>
        <div style={{ fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.8, marginTop:"8px", }}>
          A high score means the stars create a <strong style={{ color:lm.titleDeep }}>supportive foundation</strong>.
          But ego, communication, family pressures, and individual karma
          shape the actual relationship. Even a 36/36 match requires effort and dharmic living.
        </div>
        <div style={{ fontSize:"1.05rem", color:lm.bodyText, lineHeight:1.8, marginTop:"8px", }}>
          <strong style={{ color:lm.titleDeep }}>Note:</strong> This score is based on birth nakshatras
          which never change — so the number is the same before marriage and after.
          Different jyotishis may use different systems (some score /30, some /36).
        </div>
      </div>

      {/* Recalculate button */}
      <button onClick={reset} style={{
        width:"100%", padding:"13px",
        background:lm.cardBg, border:`1px solid ${lm.borderRose}`,
        borderRadius:"14px", cursor:"pointer",
        color:lm.titleDeep, 
        fontSize:"1.05rem", letterSpacing:"0.03em",
        boxShadow:`0 2px 8px ${lm.titleDeep}15`,
      }}>
        ← Check Another Match
      </button>

      <div style={{ height:20 }} />
    </div>
  );
}


// ── Kundali AI — Full Birth Chart ────────────────────────────────────────────

// Auto-detect chart style from birth city.
// South Indian style applies ONLY for AP / Telangana / Tamil Nadu / Karnataka / Kerala.
// Everyone else (North, West, East India) gets North Indian diamond format.
function getChartStyleForCity(city) {
  if (!city) return "north";
  const c = city.toLowerCase();
  const southKeywords = [
    // Andhra Pradesh / Telangana
    "hyderabad","warangal","vijayawada","visakhapatnam","vizag","guntur","nellore",
    "karimnagar","tirupati","rajahmundry","kurnool","anantapur","kadapa","eluru",
    "srikakulam","proddatur","ongole","khammam","nizamabad","mahbubnagar",
    "secunderabad","sangareddy","medak","adilabad","rangareddy",
    "jammikunta","jagtial","peddapalli","mancherial","nirmal","bhupalpally",
    "nalgonda","suryapet","yadadri","jangaon","siddipet","vikarabad","wanaparthy",
    "narayanpet","gadwal","jogulamba","nagarkurnool","mahabubnagar","asifabad",
    "kumuram bheem","rajanna sircilla","kamareddy","medchal","malkajgiri",
    "narsampet","mahabubabad","mulugu","bhadradri","kothagudem","khammam",
    "nandyal","sri sathya sai","palnadu","bapatla","konaseema","alluri",
    // Tamil Nadu
    "chennai","madras","coimbatore","madurai","salem","trichy","tiruchirappalli",
    "tirunelveli","vellore","erode","thoothukudi","tuticorin","thanjavur",
    "dindigul","kanchipuram","cuddalore","puducherry","pondicherry",
    // Karnataka
    "bangalore","bengaluru","mysore","mysuru","hubli","dharwad","mangalore",
    "mangaluru","belgaum","belagavi","davangere","davanagere","gulbarga","kalaburagi",
    "tumkur","shimoga","shivamogga","bidar","raichur","hassan","udupi",
    "bagalkot","ballari","bellary","chikkamagaluru","chitradurga","dharmasthala",
    "gadag","haveri","hospet","kolar","koppal","mandya","ranebennur","robertson pet",
    "tumakuru","vijayapura","bijapur","yadgir",
    // Kerala
    "kochi","cochin","thiruvananthapuram","trivandrum","kozhikode","calicut",
    "thrissur","kollam","palakkad","malappuram","kannur","alappuzha","alleppey",
    "kottayam","idukki","wayanad",
    // State keywords
    "andhra","telangana","tamilnadu","tamil nad","karnataka","kerala",
  ];
  return southKeywords.some(kw => c.includes(kw)) ? "south" : "north";
}

// South Indian chart: fixed rashi positions in a 4×4 grid (-1 = centre cell)
const SI_GRID = [
  [11, 0, 1, 2],
  [10,-1,-1, 3],
  [ 9,-1,-1, 4],
  [ 8, 7, 6, 5],
];

// North Indian chart: fixed house positions in a 4×4 grid (-1 = centre cell)
// House 1 (Lagna) is always top-center-left; rashis rotate based on lagna
const NI_GRID = [
  [12,  1,  2,  3],
  [11, -1, -1,  4],
  [10, -1, -1,  5],
  [ 9,  8,  7,  6],
];
const RASHI_SHORT = ["Ar","Ta","Ge","Ca","Le","Vi","Li","Sc","Sg","Cp","Aq","Pi"];

const HOUSE_MEANING = [
  "Self & Personality",
  "Wealth & Family",
  "Siblings & Courage",
  "Home & Mother",
  "Children & Intellect",
  "Health & Enemies",
  "Marriage & Partners",
  "Transformation",
  "Dharma & Fortune",
  "Career & Status",
  "Gains & Desires",
  "Liberation & Losses",
];

const P_ABBR  = { sun:"Su", moon:"Mo", mars:"Ma", mercury:"Me", jupiter:"Ju",
                  venus:"Ve", saturn:"Sa", rahu:"Ra", ketu:"Ke" };
/* Darker, high-contrast shades — legible as small text on a white background
   (pastel tones like light blue/gray read fine on dark UIs but wash out on white) */
const P_COLOR = { sun:"#b45309", moon:"#1d4ed8", mars:"#b91c1c", mercury:"#15803d",
                  jupiter:"#a16207", venus:"#be185d", saturn:"#6d28d9",
                  rahu:"#475569", ketu:"#c2410c" };

function formatDeg(lon) {
  const inRashi = lon % 30;
  const deg = Math.floor(inRashi);
  const min = Math.floor((inRashi - deg) * 60);
  return `${deg}°${String(min).padStart(2, "0")}′`;
}

// ── North Indian SVG chart ────────────────────────────────────────────────────
// Classic structure: 2 full corner diagonals + 4 adjacent midpoint lines = 12 houses.
// Matches AstroSage / standard printed kundali format.
function NorthIndianChartSVG({ planets, lagna, lightMode }) {
  const S = 300;
  const lagnaRashi = lagna ? lagna.rashi_idx : null;

  const byRashi = {};
  for (let i = 0; i < 12; i++) byRashi[i] = [];
  Object.entries(planets).forEach(([pid, pos]) => byRashi[pos.rashi_idx].push(pid));

  // Outer corners and edge midpoints
  const TL=[0,0],    TR=[S,0],   BR=[S,S],   BL=[0,S];
  const T=[S/2,0],   R=[S,S/2],  B=[S/2,S],  L=[0,S/2];
  const CE=[S/2,S/2]; // center (where diagonals cross)

  // Diagonal ∩ midpoint-line intersections (at S/4 grid)
  const p1=[S/4,S/4],   p2=[3*S/4,S/4];
  const p3=[3*S/4,3*S/4], p4=[S/4,3*S/4];

  // 6 structural lines only: 2 full diagonals + 4 adjacent midpoint connections
  const lines = [
    [TL, BR], [TR, BL],   // full diagonals
    [T, R], [R, B], [B, L], [L, T],  // adjacent midpoint connections (form inner diamond)
  ];

  // 12 house centroids — avg of region vertices, H1 at top going clockwise
  const avg = (...pts) => [
    pts.reduce((s,p) => s+p[0], 0) / pts.length,
    pts.reduce((s,p) => s+p[1], 0) / pts.length,
  ];
  const centroids = [
    avg(T, p2, CE, p1),    // H1  top quad (lagna)
    avg(TR, T, p2),         // H2  top-right corner
    avg(TR, R, p2),         // H3  right-upper
    avg(R, p3, CE, p2),    // H4  right quad
    avg(BR, R, p3),         // H5  right-lower
    avg(BR, B, p3),         // H6  bottom-right corner
    avg(B, p4, CE, p3),    // H7  bottom quad
    avg(BL, B, p4),         // H8  bottom-left corner
    avg(BL, L, p4),         // H9  left-lower
    avg(L, p1, CE, p4),    // H10 left quad
    avg(TL, L, p1),         // H11 left-upper
    avg(TL, T, p1),         // H12 top-left corner
  ];

  const rashiForHouse = (h) =>
    lagnaRashi != null ? (lagnaRashi + h - 1) % 12 : (h - 1) % 12;

  const borderColor = lightMode ? "#f97316" : "#c9a84c";
  const cellBg      = lightMode ? "#ffffff" : "#120d05";
  const houseNumCol = (isL) => isL ? "#9a6410" : (lightMode ? "#5a3e1b" : "rgba(220,190,120,0.95)");
  const rashiCol    = (isL) => isL ? "#9a6410" : (lightMode ? "#7a5220" : "rgba(180,150,90,0.7)");

  // H1 lagna highlight: the top quadrilateral
  const lagnaPolygon = `${T[0]},${T[1]} ${p2[0]},${p2[1]} ${CE[0]},${CE[1]} ${p1[0]},${p1[1]}`;

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* OM placed above chart, outside SVG */}
      <div style={{
        fontSize: "1.8rem", fontFamily: "serif", lineHeight: 1,
        marginBottom: "6px",
        color: lightMode ? "#f97316" : "#f97316",
        textShadow: lightMode
          ? "0 0 14px rgba(249,115,22,0.45)"
          : "0 0 18px rgba(249,115,22,0.75), 0 0 6px rgba(249,115,22,0.4)",
      }}>
        ॐ
      </div>
      <svg viewBox={`0 0 ${S} ${S}`}
           style={{
             width: "100%", maxWidth: 380, height: "auto", display: "block",
             filter: lightMode
               ? "drop-shadow(0 6px 24px rgba(249,115,22,0.22))"
               : "drop-shadow(0 4px 20px rgba(0,0,0,0.55))",
           }}>
        {/* Background */}
        <rect x="0" y="0" width={S} height={S} rx="4"
              fill={cellBg} stroke={borderColor} strokeWidth="2.5"/>

        {/* Lagna (H1) highlight */}
        {lagnaRashi != null && (
          <polygon
            points={lagnaPolygon}
            fill={lightMode ? "rgba(255,245,200,0.7)" : "rgba(201,168,76,0.13)"}
          />
        )}

        {/* 6 structural lines */}
        {lines.map(([a, b], i) => (
          <line key={i}
                x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                stroke={borderColor} strokeWidth="1.6" strokeOpacity="0.85"/>
        ))}

        {/* House labels + planets */}
        {Array.from({ length: 12 }, (_, i) => {
          const h    = i + 1;
          const r    = rashiForHouse(h);
          const isL  = h === 1 && lagnaRashi != null;
          const pids = byRashi[r] || [];
          const [tx, ty] = centroids[i];

          const lineH = 11;
          const startY = ty - (pids.length > 0 ? lineH : lineH / 2);

          return (
            <g key={h}>
              <text x={tx} y={startY} textAnchor="middle" dominantBaseline="middle"
                    fontSize="13" fontWeight="800" fontFamily="'Inter',sans-serif"
                    fill={houseNumCol(isL)}>
                {h}{isL ? "▲" : ""}
              </text>
              <text x={tx} y={startY + lineH} textAnchor="middle" dominantBaseline="middle"
                    fontSize="10.5" fontWeight="700" fontFamily="'Inter',sans-serif"
                    fill={rashiCol(isL)}>
                {RASHI_SHORT[r]}
              </text>
              {pids.slice(0, 4).map((pid, pi) => (
                <text key={pid}
                      x={tx} y={startY + lineH * 2 + pi * 11}
                      textAnchor="middle" dominantBaseline="middle"
                      fontSize="11.5" fontWeight="800" fontFamily="'Inter',sans-serif"
                      fill={P_COLOR[pid]}>
                  {P_ABBR[pid]}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Birth chart (South Indian + North Indian) ────────────────────────────────
function KundaliChart({ planets, lagna, chartStyle = "south", onStyleChange, lightMode = false }) {
  const lagnaRashi = lagna ? lagna.rashi_idx : null;

  // group planet ids by rashi index
  const byRashi = {};
  for (let i = 0; i < 12; i++) byRashi[i] = [];
  Object.entries(planets).forEach(([pid, pos]) => byRashi[pos.rashi_idx].push(pid));

  const isSouth = chartStyle === "south";

  // Render a single grid cell
  const renderCell = (key, ri, ci, cellValue) => {
    const isCenter = cellValue === -1;
    let rIdx, houseNum, isLagna;

    if (isSouth) {
      // South: cellValue = rashi index (fixed)
      rIdx     = isCenter ? null : cellValue;
      houseNum = lagnaRashi != null && !isCenter ? ((rIdx - lagnaRashi + 12) % 12) + 1 : null;
      isLagna  = !isCenter && rIdx === lagnaRashi;
    } else {
      // North: cellValue = house number (fixed), rashi rotates
      houseNum = isCenter ? null : cellValue;
      rIdx     = lagnaRashi != null && !isCenter ? (lagnaRashi + houseNum - 1) % 12 : null;
      // if lagna unknown, fall back: house 1 = rashi 0
      if (rIdx === null && !isCenter) rIdx = (houseNum - 1) % 12;
      isLagna  = !isCenter && houseNum === 1;
    }

    const pids = isCenter ? [] : byRashi[rIdx];

    const cellSize = isSouth ? 84 : 64;

    // Colors tuned per mode — strong contrast, no faint transparencies
    const rashiColor  = isLagna ? "#9a6410"
      : isSouth
        ? (lightMode ? "#5a3e1b"      : "rgba(255,235,180,0.55)")
        : (lightMode ? "#6b4c1e"      : "rgba(255,235,180,0.65)");
    const houseColor  = isLagna ? "#9a6410"
      : (lightMode ? "#7a5220"        : "rgba(201,168,76,0.75)");
    const borderColor = lightMode ? "rgba(160,110,30,0.5)" : "rgba(180,130,50,0.35)";

    return (
      <div key={key} style={{
        minHeight: cellSize, padding: isSouth ? "7px 6px 4px" : "3px 2px 2px",
        borderRight:  ci < 3 ? `1.5px solid ${borderColor}` : "none",
        borderBottom: ri < 3 ? `1.5px solid ${borderColor}` : "none",
        background: isCenter
          ? (lightMode ? "#ffffff" : "rgba(12,9,4,0.85)")
          : isLagna
            ? (lightMode ? "#fff8e1" : "rgba(201,168,76,0.18)")
            : (lightMode ? "#ffffff" : "var(--bg2)"),
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* North Indian: counter-rotate content so it reads upright */}
        <div style={{
          transform: isSouth ? "none" : "rotate(-45deg)",
          display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center",
          width:"100%", height:"100%", flex:1,
          minHeight: isSouth ? undefined : cellSize,
        }}>
          {isCenter ? (
            ((isSouth && ri === 1 && ci === 1) || (!isSouth && ri === 1 && ci === 1)) && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                height:"100%", fontSize: isSouth ? "2.2rem" : "1.5rem",
                color: "#f97316",
                textShadow: "0 0 14px rgba(249,115,22,0.55), 0 0 4px rgba(249,115,22,0.3)",
                userSelect:"none" }}>
                ॐ
              </div>
            )
          ) : (
            <>
              {isSouth ? (
                <div style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"flex-start", width:"100%" }}>
                  <span style={{ fontSize:"1.0rem", letterSpacing:"0.04em",
                    color: rashiColor, fontWeight: isLagna ? 700 : 600,
                    fontFamily:JT.heading }}>
                    {rIdx != null ? RASHI_SHORT[rIdx] : "—"}{isLagna ? " ▲" : ""}
                  </span>
                  {houseNum && (
                    <span style={{ fontSize:"1.0rem", fontWeight:700, color: houseColor }}>
                      {houseNum}
                    </span>
                  )}
                </div>
              ) : (
                /* North Indian: house# prominent, rashi below */
                <div style={{ textAlign:"center", lineHeight:1.25 }}>
                  {houseNum && (
                    <div style={{ fontSize:"1.0rem", fontWeight:800,
                      color: houseColor, fontFamily:JT.heading }}>
                      {houseNum}{isLagna ? "▲" : ""}
                    </div>
                  )}
                  <div style={{ fontSize:"1.0rem", fontFamily:JT.heading,
                    color: rashiColor, fontWeight:600 }}>
                    {rIdx != null ? RASHI_SHORT[rIdx] : "—"}
                  </div>
                </div>
              )}
              <div style={{ display:"flex", flexWrap:"wrap",
                gap:"1px 2px", marginTop: isSouth ? "4px" : "3px",
                flex: isSouth ? 1 : undefined,
                alignContent:"flex-start", justifyContent: isSouth ? "flex-start" : "center" }}>
                {pids.map(pid => (
                  <span key={pid} style={{ fontSize: isSouth ? "0.95rem" : "0.88rem",
                    fontFamily:"'Inter',sans-serif",
                    fontWeight:800, color:P_COLOR[pid], lineHeight:1.4 }}>
                    {P_ABBR[pid]}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const GRID = isSouth ? SI_GRID : NI_GRID;

  return (
    <div style={{ background: lightMode ? "#ffffff" : "transparent", borderRadius: lightMode ? "12px" : "0" }}>
      {/* Grid */}
      {isSouth ? (
        /* South Indian — fixed rashi square grid */
        <div style={{
          display:"grid", gridTemplateColumns:"repeat(4,1fr)",
          border: lightMode ? "2px solid rgba(180,130,50,0.55)" : "2px solid rgba(180,130,50,0.55)",
          borderRadius:"12px", overflow:"hidden", width:"100%", maxWidth:380, margin:"0 auto",
          boxShadow: lightMode
            ? "0 4px 24px rgba(180,130,50,0.15), inset 0 0 0 1px rgba(255,255,255,0.6)"
            : "0 4px 24px rgba(0,0,0,0.3)",
        }}>
          {GRID.map((row, ri) => row.map((cellValue, ci) =>
            renderCell(`${ri}-${ci}`, ri, ci, cellValue)
          ))}
        </div>
      ) : (
        /* North Indian — SVG triangular chart */
        <NorthIndianChartSVG planets={planets} lagna={lagna} lightMode={lightMode} />
      )}

      {/* Planet key */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 10px",
        marginTop:"10px", justifyContent:"center", padding:"0 4px" }}>
        {Object.entries(P_ABBR).map(([pid, abbr]) => (
          <span key={pid} style={{ fontSize:"1.0rem", color:P_COLOR[pid] }}>
            {abbr} — {pid.charAt(0).toUpperCase()+pid.slice(1)}
          </span>
        ))}
      </div>

      {/* House placements — only when Lagna is known */}
      {lagnaRashi != null && (
        <div style={{ marginTop:"16px" }}>
          <div style={{ fontSize:"1.05rem", color: lightMode ? "#5a5a5a" : "var(--text-dim)",
            fontWeight:600, fontFamily:"'Inter',sans-serif",
            textAlign:"center", marginBottom:"8px" }}>
            Planet House Placements
          </div>
          {Array.from({length:12}, (_, i) => {
            const rashi = (lagnaRashi + i) % 12;
            const pids  = byRashi[rashi];
            if (pids.length === 0) return null;
            return (
              <div key={i} style={{
                display:"flex", alignItems:"center", gap:"14px",
                padding:"12px 16px", borderRadius:"12px", marginBottom:"8px",
                background: lightMode ? "#ffffff" : "var(--bg2)",
                border: lightMode ? "1px solid #e5e7eb" : "1px solid var(--border)",
                borderLeft: `4px solid ${lightMode ? "#f97316" : "rgba(180,130,50,0.5)"}`,
                boxShadow: lightMode ? "0 1px 6px rgba(0,0,0,0.05)" : "none",
              }}>
                <div style={{ minWidth:38, textAlign:"center", flexShrink:0 }}>
                  <div style={{ fontSize:"1.1rem", fontWeight:800, color:"#d4a853",
                    fontFamily:JT.heading, lineHeight:1 }}>
                    {i+1}
                  </div>
                  <div style={{ fontSize:"0.95rem", color: lightMode ? "#c2560f" : "rgba(180,130,50,0.6)",
                    fontWeight:600, marginTop:"2px" }}>House</div>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:"1.05rem", color: lightMode ? "#444" : "var(--text-dim)",
                    marginBottom:"6px", fontFamily:"'Inter',sans-serif" }}>
                    {HOUSE_MEANING[i]}
                  </div>
                  <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>
                    {pids.map(pid => (
                      <span key={pid} style={{ fontSize:"1.1rem",
                        color:P_COLOR[pid], fontWeight:800 }}>
                        {P_ABBR[pid]}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          }).filter(Boolean)}
        </div>
      )}

      {/* Authenticity note */}
      <div style={{ marginTop:"12px", textAlign:"center", fontFamily:"'Inter',sans-serif",
        fontSize:"0.95rem", color: lightMode ? "#5a5a5a" : "var(--text-dim)", lineHeight:1.7, padding:"0 4px" }}>
        {isSouth ? "South Indian" : "North Indian"} chart · Lahiri Ayanamsha · Swiss Ephemeris (pyswisseph)
        {lagnaRashi == null && (
          <><br/>Add birth city (Archetype tab → Edit Birth Details) to see Lagna (▲) and house numbers</>
        )}
      </div>
      <ChartExplanation planets={planets} lagnaRashi={lagnaRashi} lightMode={lightMode} />
    </div>
  );
}

const PLANET_NATURE = {
  sun:     { name:"Surya (Sun)",     brings:"confidence, identity, authority, and your relationship with your father and government" },
  moon:    { name:"Chandra (Moon)",  brings:"emotions, intuition, the mind, and your relationship with your mother" },
  mars:    { name:"Mangal (Mars)",   brings:"drive, courage, ambition, physical energy, and the willingness to fight for what matters" },
  mercury: { name:"Budha (Mercury)", brings:"intelligence, sharp speech, analytical ability, business sense, and adaptability" },
  jupiter: { name:"Guru (Jupiter)",  brings:"wisdom, grace, expansion, spiritual growth, and blessings from the universe" },
  venus:   { name:"Shukra (Venus)",  brings:"love, beauty, pleasure, artistic gifts, and the capacity for deep relationships" },
  saturn:  { name:"Shani (Saturn)",  brings:"discipline, hard-earned lessons, karma, delays that teach, and eventual mastery through effort" },
  rahu:    { name:"Rahu",            brings:"unconventional ambition, obsession, worldly desire, and the hunger to experience something entirely new" },
  ketu:    { name:"Ketu",            brings:"spiritual depth, detachment from material things, and wisdom carried over from past lives" },
};

const HOUSE_CONTEXT = [
  { area:"Self & Personality",    plain:"how you appear to the world, your physical body, and your overall life direction" },
  { area:"Wealth & Family",       plain:"your finances, family relationships, what you eat and speak, and how you accumulate resources" },
  { area:"Courage & Siblings",    plain:"your courage, younger siblings, communication style, and short travels" },
  { area:"Home & Mother",         plain:"your home, mother, inner happiness, education, and sense of emotional security" },
  { area:"Children & Intellect",  plain:"your children, creative intelligence, romantic pleasures, and the karma you carry from past lives" },
  { area:"Health & Obstacles",    plain:"your daily health, debts, enemies, service to others, and the obstacles you must overcome" },
  { area:"Marriage & Partners",   plain:"your spouse or life partner, business partnerships, and all one-on-one relationships" },
  { area:"Transformation",        plain:"deep change, hidden matters, inheritance, longevity, and the fears you must face to grow" },
  { area:"Dharma & Fortune",      plain:"your higher purpose, fortune, father's influence, long journeys, and connection to the divine" },
  { area:"Career & Status",       plain:"your public reputation, career, authority, and the work you do in the world" },
  { area:"Gains & Friendships",   plain:"your income gains, elder siblings, friendships, social network, and desires fulfilled" },
  { area:"Liberation & Losses",   plain:"foreign lands, spiritual retreat, expenses, sleep, and your path toward final liberation" },
];

function ChartExplanation({ planets, lagnaRashi, lightMode = false }) {
  if (lagnaRashi == null) return (
    <div style={{ marginTop:"18px", background:"#ffffff",
      border:`1px solid ${JT.border}`, borderRadius:"14px",
      padding:"16px 18px", textAlign:"center" }}>
      <div style={{ fontSize:"1.2rem", marginBottom:"8px" }}>🏠</div>
      <div style={{ fontSize:"1.0rem", color:JT.text, marginBottom:"6px", fontWeight:600 }}>
        Add your birth city to unlock house meanings
      </div>
      <div style={{ fontSize:"1.08rem", color:JT.textDim, lineHeight:1.7 }}>
        With your birth city, we can calculate your Lagna (Ascendant) and tell you exactly which life area
        each planet is influencing — career, marriage, health, dharma, and more.
      </div>
    </div>
  );

  // group planet ids by house number (1-indexed)
  const byHouse = {};
  for (let h = 1; h <= 12; h++) byHouse[h] = [];
  Object.entries(planets).forEach(([pid, pos]) => {
    const h = ((pos.rashi_idx - lagnaRashi + 12) % 12) + 1;
    byHouse[h].push(pid);
  });

  const filledHouses = Object.entries(byHouse).filter(([,pids]) => pids.length > 0);

  return (
    <div style={{ marginTop:"20px",
      background: lightMode ? "#ffffff" : "transparent",
      borderRadius: lightMode ? "16px" : "0",
      padding: lightMode ? "4px 0" : "0" }}>
      <div style={{ fontSize:"1.15rem", fontFamily:JT.heading,
        color: lightMode ? "#c97416" : "var(--gold)", marginBottom:"8px" }}>
        What Your Chart Reveals
      </div>
      <div style={{ fontSize:"1rem", color: lightMode ? "#3d3222" : "var(--text-dim)",
        lineHeight:1.8, marginBottom:"18px" }}>
        Each planet in your chart sits in a "house" — a specific area of your life.
        Here is what the planets are saying about you:
      </div>
      {filledHouses.map(([hNum, pids]) => {
        const h = parseInt(hNum);
        const house = HOUSE_CONTEXT[h-1];
        return (
          <div key={h} style={{
            background: lightMode ? "#ffffff" : "var(--bg2)",
            border: lightMode ? "1px solid #e5e7eb" : "1px solid var(--border)",
            borderRadius:"16px", padding:"16px 18px", marginBottom:"12px",
            boxShadow: lightMode ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px" }}>
              <div style={{ background:"rgba(201,168,76,0.15)", border:"1px solid rgba(201,168,76,0.35)",
                borderRadius:"10px", padding:"6px 14px",
                fontFamily:JT.heading, fontSize:"1.0rem", color:"#c9882a",
                flexShrink:0 }}>
                House {h}
              </div>
              <div style={{ fontSize:"1.15rem", color: lightMode ? "#0d0a04" : "var(--cream)",
                fontWeight:700, fontFamily:JT.heading }}>
                {house.area}
              </div>
            </div>
            <div style={{ fontSize:"1.02rem", color: lightMode ? "#3a2e1e" : "var(--text-dim)",
              lineHeight:1.8, marginBottom:"14px", fontFamily:"'Inter',sans-serif" }}>
              This house governs {house.plain}.
            </div>
            {pids.map(pid => {
              const pn = PLANET_NATURE[pid];
              const pos = planets[pid];
              const st = pos.strength;
              const strengthNote = st === "exalted"
                ? " — exceptionally well-placed and powerful here"
                : st === "debilitated"
                ? " — under some strain here; requires conscious effort"
                : st === "own_sign"
                ? " — very comfortable and strong in its own sign"
                : "";
              return (
                <div key={pid} style={{
                  background: lightMode ? "#f9f9f9" : "var(--bg3)",
                  borderRadius:"12px", padding:"14px 16px", marginBottom:"10px",
                  borderLeft:`4px solid ${P_COLOR[pid]}`,
                }}>
                  <div style={{ fontSize:"1.08rem", color:P_COLOR[pid],
                    fontWeight:700, marginBottom:"8px", fontFamily:JT.heading }}>
                    {pn.name}{strengthNote}
                  </div>
                  <div style={{ fontSize:"1.05rem",
                    color: lightMode ? "#1e1810" : "var(--text)", lineHeight:1.9,
                    fontFamily:"'Inter',sans-serif" }}>
                    {pn.name} brings {pn.brings} into your <b style={{
                      color: lightMode ? "#0d0a04" : "var(--cream)"}}>
                    {house.area}</b> house. This shapes how you experience {house.plain}.
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

const PLANET_META = [
  {id:"sun",     label:"Surya",   emoji:"☀️",  color:"#f59e0b"},
  {id:"moon",    label:"Chandra", emoji:"🌙",  color:"#93c5fd"},
  {id:"mars",    label:"Mangal",  emoji:"🔴",  color:"#ef4444"},
  {id:"mercury", label:"Budha",   emoji:"💚",  color:"#22c55e"},
  {id:"jupiter", label:"Guru",    emoji:"🟡",  color:"#eab308"},
  {id:"venus",   label:"Shukra",  emoji:"🌸",  color:"#ec4899"},
  {id:"saturn",  label:"Shani",   emoji:"🪐",  color:"#8b5cf6"},
  {id:"rahu",    label:"Rahu",    emoji:"🐍",  color:"#64748b"},
  {id:"ketu",    label:"Ketu",    emoji:"🔥",  color:"#f97316"},
];

const STRENGTH_STYLE = {
  exalted:     {label:"Exalted ↑",    bg:"rgba(34,197,94,0.12)",    color:"#22c55e"},
  own_sign:    {label:"Own Sign ✦",   bg:"rgba(234,179,8,0.12)",    color:"#eab308"},
  neutral:     {label:"",             bg:"transparent",              color:"var(--text-dim)"},
  debilitated: {label:"Debilitated ↓",bg:"rgba(239,68,68,0.10)",    color:"#ef4444"},
};

function PlanetRow({ pid, pos, lagnaRashi, lightMode = false }) {
  const meta     = PLANET_META.find(m => m.id === pid);
  const st       = STRENGTH_STYLE[pos.strength] || STRENGTH_STYLE.neutral;
  const houseNum = lagnaRashi != null
    ? ((pos.rashi_idx - lagnaRashi + 12) % 12) + 1 : null;
  const degStr   = formatDeg(pos.lon);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"14px",
      padding:"16px 18px", borderRadius:"16px",
      background: lightMode
        ? "#ffffff"
        : (pos.strength !== "neutral" ? st.bg : "var(--bg2)"),
      border: lightMode
        ? `1.5px solid ${pos.strength !== "neutral" ? `${st.color}55` : "rgba(180,130,50,0.28)"}`
        : "1px solid var(--border)",
      boxShadow: lightMode
        ? (pos.strength !== "neutral"
            ? `0 2px 14px ${st.color}28, 0 1px 4px rgba(0,0,0,0.05)`
            : "0 2px 8px rgba(180,130,50,0.08)")
        : "none",
      marginBottom:"10px" }}>
      <span style={{ fontSize:"1.6rem", flexShrink:0 }}>{meta.emoji}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:"8px", flexWrap:"wrap", marginBottom:"4px" }}>
          <span style={{ fontSize:"1.15rem", color: lightMode ? "#0d0a04" : "var(--cream)",
            fontWeight:700, fontFamily:JT.heading }}>
            {meta.label}
          </span>
          {houseNum && (
            <span style={{ fontSize:"1.0rem", color:"#c97416", fontWeight:700 }}>
              House {houseNum} · {HOUSE_MEANING[houseNum-1]}
            </span>
          )}
        </div>
        <div style={{ fontSize:"1.05rem", color: lightMode ? "#3a2e1e" : "var(--text-dim)",
          marginTop:"2px", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>
          {pos.rashi_emoji} {pos.rashi} {degStr} · {pos.nakshatra} Pada {pos.pada}
        </div>
      </div>
      {pos.strength !== "neutral" && (
        <div style={{ fontSize:"1.1rem", color: lightMode ? "#fff" : st.color, fontWeight:700,
          background: lightMode ? st.color : `${st.color}18`,
          padding:"5px 12px", borderRadius:"10px",
          whiteSpace:"nowrap", flexShrink:0,
          boxShadow: lightMode ? `0 2px 8px ${st.color}55` : "none" }}>
          {st.label}
        </div>
      )}
    </div>
  );
}

function YogaCard({ yoga, lightMode = false }) {
  const [open, setOpen] = useState(false);
  const isRemedy = yoga.remedy;
  return (
    <div onClick={() => setOpen(o => !o)} style={{
      background: lightMode
        ? (isRemedy ? "rgba(239,68,68,0.03)" : "#ffffff")
        : (isRemedy ? "rgba(239,68,68,0.06)" : "rgba(201,168,76,0.07)"),
      border: lightMode
        ? `1.5px solid ${isRemedy ? "rgba(239,68,68,0.35)" : "rgba(180,130,50,0.3)"}`
        : `1px solid ${isRemedy ? "rgba(239,68,68,0.2)" : "rgba(201,168,76,0.2)"}`,
      borderRadius:"16px", padding:"18px 20px", marginBottom:"12px", cursor:"pointer",
      boxShadow: lightMode ? "0 2px 12px rgba(180,130,50,0.1)" : "none",
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:"1.15rem", color: isRemedy ? "#dc2626" : "#c97416",
          fontFamily:JT.heading, fontWeight:700 }}>
          {isRemedy ? "⚠️" : "✨"} {yoga.name}
        </div>
        <span style={{ fontSize:"1rem", color: lightMode ? "#a07830" : "var(--text-dim)" }}>
          {open ? "▲" : "▼"}
        </span>
      </div>
      {open && (
        <div style={{ marginTop:"16px", paddingTop:"16px",
          borderTop: lightMode ? "1px solid rgba(180,130,50,0.2)" : "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ fontSize:"1.2rem",
            color: lightMode ? "#1e1810" : "var(--text-dim)", lineHeight:1.9,
            fontFamily:"'Inter',sans-serif" }}>
            {yoga.desc}
          </div>
          {yoga.gita && (
            <div style={{ marginTop:"14px", padding:"14px 18px",
              background: lightMode ? "rgba(249,115,22,0.05)" : "rgba(201,168,76,0.07)",
              borderRadius:"12px", borderLeft:"4px solid #f97316",
              fontSize:"1.1rem", color: lightMode ? "#9a5c10" : "#c9a84c",
              fontStyle:"italic", fontFamily:"'Inter',sans-serif", lineHeight:1.85 }}>
              {yoga.gita[0]} — "{yoga.gita[1]}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AIReadingCard({ reading, lightMode = false }) {
  const [open, setOpen] = useState(true);
  if (!reading) return null;

  const cardBg     = lightMode
    ? "#ffffff"
    : "linear-gradient(135deg,rgba(201,168,76,0.08),rgba(180,130,50,0.06))";
  const cardBorder = lightMode ? "1.5px solid rgba(180,130,50,0.35)" : "1px solid rgba(180,130,50,0.2)";
  const bodyText   = lightMode ? "#0d0a04" : "var(--cream)";
  const dimText    = lightMode ? "#1e1810" : "var(--text-dim)";
  const labelColor = "#c97416";

  return (
    <div style={{ background:cardBg, border:cardBorder, borderRadius:"20px",
      padding:"26px 22px", marginBottom:"16px",
      boxShadow: lightMode ? "0 4px 24px rgba(180,130,50,0.12), 0 1px 4px rgba(0,0,0,0.06)" : "none" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display:"flex",
        justifyContent:"space-between", alignItems:"center",
        cursor:"pointer", marginBottom: open ? "22px" : 0 }}>
        <div style={{ fontFamily:JT.heading, fontSize:"1.25rem",
          color:"#c97416" }}>
          🔱 Dharmic Reading
        </div>
        <span style={{ fontSize:"1.0rem", color: lightMode ? "#a07830" : "var(--text-dim)" }}>
          {open ? "▲" : "▼"}
        </span>
      </div>

      {open && (
        <>
          {reading.soul_purpose && (
            <div style={{ marginBottom:"22px",
              background: lightMode ? "rgba(249,115,22,0.04)" : "rgba(255,255,255,0.03)",
              border: lightMode ? "1px solid rgba(201,168,76,0.25)" : "none",
              borderLeft:"4px solid #f97316",
              borderRadius:"14px", padding:"18px 20px" }}>
              <div style={{ fontSize:"1.0rem", color:labelColor,  marginBottom:"10px", fontWeight:700 }}>Soul Purpose</div>
              <div style={{ fontSize:"1.25rem", color:bodyText, lineHeight:1.9,
                fontFamily:"'Inter',sans-serif" }}>
                {reading.soul_purpose}
              </div>
            </div>
          )}

          {reading.strengths?.length > 0 && (
            <div style={{ marginBottom:"22px" }}>
              <div style={{ fontSize:"1.0rem", color:labelColor,  marginBottom:"14px", fontWeight:700 }}>Your Strengths</div>
              {reading.strengths.map((s, i) => (
                <div key={i} style={{ fontSize:"1.2rem", color:dimText,
                  lineHeight:1.85, marginBottom:"12px", fontFamily:"'Inter',sans-serif",
                  display:"flex", gap:"10px" }}>
                  <span style={{ color:"#c9a84c", flexShrink:0, marginTop:"2px" }}>✦</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          )}

          {reading.karmic_lessons?.length > 0 && (
            <div style={{ marginBottom:"22px" }}>
              <div style={{ fontSize:"1.0rem", color:labelColor,  marginBottom:"14px", fontWeight:700 }}>Karmic Lessons</div>
              {reading.karmic_lessons.map((s, i) => (
                <div key={i} style={{ fontSize:"1.2rem", color:dimText,
                  lineHeight:1.85, marginBottom:"12px", fontFamily:"'Inter',sans-serif",
                  display:"flex", gap:"10px" }}>
                  <span style={{ color:"#c9a84c", flexShrink:0, marginTop:"2px" }}>◈</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          )}

          {reading.gita_message && (
            <div style={{
              background: lightMode ? "rgba(249,115,22,0.05)" : "rgba(180,130,50,0.08)",
              borderRadius:"14px", padding:"20px 20px", marginBottom:"22px",
              borderLeft:"4px solid #f97316",
              boxShadow: lightMode ? "inset 0 1px 0 rgba(249,115,22,0.1)" : "none" }}>
              <div style={{ fontSize:"1.0rem", color:labelColor, marginBottom:"10px",
                 fontWeight:700 }}>
                Gita Speaks to Your Chart
              </div>
              <div style={{ fontSize:"1.25rem", color:bodyText, lineHeight:1.9,
                fontStyle:"italic", fontFamily:"'Inter',sans-serif" }}>
                {reading.gita_message}
              </div>
            </div>
          )}

          {reading.dharmic_guidance && (
            <div style={{ marginBottom:"22px" }}>
              <div style={{ fontSize:"1.0rem", color:labelColor,  marginBottom:"14px", fontWeight:700 }}>Dharmic Guidance</div>
              <div style={{ fontSize:"1.25rem", color:dimText, lineHeight:1.9,
                fontFamily:"'Inter',sans-serif" }}>
                {reading.dharmic_guidance}
              </div>
            </div>
          )}

          {reading.planet_to_watch && (
            <div style={{
              background: lightMode ? "rgba(249,115,22,0.06)" : "rgba(245,158,11,0.08)",
              borderRadius:"14px", padding:"16px 18px", border: lightMode ? "1px solid rgba(249,115,22,0.2)" : "none" }}>
              <span style={{ fontSize:"1.1rem", color:"#c97416", fontWeight:700 }}>🪐 Planet to watch: </span>
              <span style={{ fontSize:"1.1rem", color:dimText, fontFamily:"'Inter',sans-serif",
                lineHeight:1.8 }}>{reading.planet_to_watch}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KundaliTab({ userId, noBirth, onNeedBirth, lightMode = true }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [section,    setSection]    = useState("chart");  // chart | planets | yogas | reading
  const [chartStyle, setChartStyle] = useState("north"); // auto-set from birth city on load

  useEffect(() => {
    if (noBirth) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/jyotish/kundali/${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        setLoading(false);
        setChartStyle(getChartStyleForCity(d.birth_city || ""));
      })
      .catch(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div style={{ textAlign:"center", padding:"60px 20px", maxWidth:680, margin:"0 auto" }}>
      <div style={{ height:3, width:64, margin:"0 auto 22px",
        background:"#c2560f", borderRadius:3 }} />
      <div style={{ fontFamily:JT.heading, color:JT.text, fontWeight:700, fontSize:"1.3rem", marginBottom:"10px" }}>
        Reading your birth chart…
      </div>
      <div style={{ fontSize:"1.05rem", color:JT.textDim, lineHeight:1.75 }}>
        Calculating all 9 planets using Swiss Ephemeris.<br/>
        Preparing your personal reading from the Vedic texts.
      </div>
    </div>
  );

  if (noBirth || data?.error === "no_birthdate") return (
    <div style={{ textAlign:"center", padding:"40px 20px", maxWidth:680, margin:"0 auto" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"14px" }}>🌟</div>
      <div style={{ fontFamily:JT.heading, color:JT.text, fontWeight:700,
        marginBottom:"10px", fontSize:"1.2rem" }}>
        Birth Details Needed
      </div>
      <div style={{ fontSize:"1.05rem", color:JT.textDim, marginBottom:"20px",
        lineHeight:1.75 }}>
        Your full Kundali requires your date and time of birth.
        Go to the Archetype tab to add them.
      </div>
      <button onClick={onNeedBirth} style={{
        padding:"11px 22px", background:"#c2560f",
        border:"none", borderRadius:"14px", color:"#fff", cursor:"pointer",
        fontFamily:JT.heading, fontWeight:700, fontSize:"1.05rem",
      }}>
        Add Birth Details →
      </button>
    </div>
  );

  if (!data || data.error) return (
    <div style={{ color:"#f87171", padding:"20px", fontSize:"1.08rem" }}>
      Could not load chart. {data?.error}
    </div>
  );

  const { planets, lagna, yogas = [], ai_reading, has_location } = data;
  const lagnaRashi = lagna ? lagna.rashi_idx : null;

  return (
    <div style={{ animation:"fadeInUp 0.4s ease", maxWidth:680, margin:"0 auto" }}>
      {/* Header */}
      <div style={{
        background:"#ffffff",
        border:`1px solid ${JT.border}`,
        borderRadius:"18px", padding:"24px 22px 20px", marginBottom:"16px",
        boxShadow:JT.shadow,
      }}>
        <div style={{ fontSize:"1rem", color:JT.textDim, marginBottom:"6px",
          fontFamily:JT.heading, fontWeight:600 }}>Janma Kundali</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.7rem", fontWeight:700,
          color:JT.text, marginBottom:"16px", lineHeight:1.2 }}>
          Full Birth Chart
        </div>
        {/* Trust badges */}
        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:yogas.length > 0 ? "14px" : 0 }}>
          {[
            { icon:"✓", label:"Swiss Ephemeris", bg:"rgba(34,197,94,0.1)", border:"rgba(34,197,94,0.4)", color:"#166534", dot:"#22c55e" },
            { icon:"✓", label:"Lahiri Ayanamsha", bg:"rgba(180,130,50,0.1)", border:"rgba(180,130,50,0.4)", color:"#8B5E1A", dot:"#d4a853" },
            ...(lagna ? [{ icon:"✓", label:"Lagna Calculated", bg:"rgba(249,115,22,0.1)", border:"rgba(249,115,22,0.4)", color:"#9a3412", dot:"#f97316" }] : []),
          ].map(b => (
            <span key={b.label} style={{ fontSize:"1.0rem", background:b.bg,
              border:`1px solid ${b.border}`, borderRadius:"24px",
              padding:"6px 16px", color:b.color,
              fontWeight:600, display:"flex", alignItems:"center", gap:"6px" }}>
              <span style={{ color:b.dot }}>{b.icon}</span> {b.label}
            </span>
          ))}
        </div>
        {yogas.length > 0 && (
          <div style={{ display:"inline-block",
            background:"rgba(180,130,50,0.1)",
            border:"1px solid rgba(180,130,50,0.35)",
            borderRadius:"20px", padding:"7px 18px",
            fontSize:"1rem", color:"#8B5E1A",
            fontWeight:600 }}>
            ✨ {yogas.length} yoga{yogas.length > 1 ? "s" : ""} in your chart
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:"8px", marginBottom:"18px",
        background:"#f5f5f5",
        borderRadius:"16px", padding:"6px" }}>
        {[
          ["chart",  "📊 Chart"],
          ["planets","🪐 Planets"],
          ["yogas",  `✨ Yogas${yogas.length > 0 ? ` (${yogas.length})` : ""}`],
          ["reading","🔱 Reading"],
        ].map(([s,l]) => (
          <button key={s} onClick={() => setSection(s)} style={{
            flex:1, padding:"10px 4px",
            background: section===s ? "#c2560f" : "transparent",
            border: "none",
            borderRadius:"12px", cursor:"pointer",
            color: section===s ? "#fff" : JT.textDim,
            fontFamily:JT.heading, fontSize:"1.1rem",
            whiteSpace:"nowrap",
            transition:"all 0.18s",
          }}>{l}</button>
        ))}
      </div>

      {/* CHART section */}
      {section === "chart" && (
        <KundaliChart
          planets={planets} lagna={lagna}
          chartStyle={chartStyle}
          onStyleChange={() => {}}
          lightMode={lightMode}
        />
      )}

      {/* PLANETS section */}
      {section === "planets" && (
        <div style={{ padding:"0 6px" }}>
          {PLANET_META.map(meta => (
            <PlanetRow key={meta.id} pid={meta.id}
              pos={planets[meta.id]} lagnaRashi={lagnaRashi} lightMode={lightMode} />
          ))}
          <div style={{ marginTop:"12px", fontSize:"1rem", color:"var(--text-dim)",
            textAlign:"center", lineHeight:1.75, padding:"0 10px" }}>
            Sidereal positions · Lahiri Ayanamsha · Swiss Ephemeris (pyswisseph)
            {!has_location && (
              <><br/>Add birth city to see house numbers (Archetype tab → Edit Birth Details)</>
            )}
          </div>
        </div>
      )}

      {/* YOGAS section */}
      {section === "yogas" && (
        <div>
          <div style={{ fontSize:"1.1rem", lineHeight:1.9, marginBottom:"20px",
            padding:"18px 20px", borderRadius:"16px",
            background: lightMode ? "#ffffff" : "var(--bg2)",
            border: lightMode ? "1.5px solid rgba(180,130,50,0.3)" : "1px solid var(--border)",
            borderLeft: "4px solid #f97316",
            color: lightMode ? "#1e1810" : "var(--text-dim)",
            fontFamily:"'Inter',sans-serif",
            boxShadow: lightMode ? "0 2px 12px rgba(180,130,50,0.1)" : "none",
          }}>
            A <b style={{ color: lightMode ? "#0d0a04" : "var(--cream)" }}>Yoga</b> is a special
            combination of planets that creates a distinct life pattern — like a signature written
            in your chart at birth. Not all yogas are auspicious; some mark karmic challenges.
            The strength of a yoga depends on the planets' positions and dignity.
          </div>
          {yogas.length === 0 ? (
            <div style={{ textAlign:"center", padding:"30px 20px",
              fontSize:"1.05rem", color: lightMode ? "#3a2e1e" : "var(--text-dim)", lineHeight:1.8 }}>
              No major yogas detected in this chart.<br/>
              <span style={{ fontSize:"1.0rem" }}>
                This is normal — most charts carry their meaning through individual planet placements.
              </span>
            </div>
          ) : yogas.map((y, i) => <YogaCard key={i} yoga={y} lightMode={lightMode} />)}
        </div>
      )}

      {/* AI READING section */}
      {section === "reading" && (
        <div>
          {ai_reading
            ? <AIReadingCard reading={ai_reading} lightMode={lightMode} />
            : (
              <div style={{ textAlign:"center", padding:"30px 20px",
                fontSize:"1.08rem", color:"var(--text-dim)" }}>
                AI reading not available. Please try again.
              </div>
            )
          }
        </div>
      )}

      <div style={{ height:20 }} />
    </div>
  );
}


// ── Graha Sadhana — Planet selection ─────────────────────────────────────────
function PlanetSelector({ planets, onSelect }) {
  return (
    <div>
      <div style={{ fontFamily:JT.heading, fontSize:"1rem",
        color:"var(--gold)", marginBottom:"6px" }}>
        Choose Your Graha Sadhana
      </div>
      <div style={{ fontSize:"1.1rem", color:"var(--text-dim)", marginBottom:"20px", lineHeight:1.7 }}>
        Read each challenge below. Pick the one that resonates most with where you are right now.
        The sadhana gives you a real daily practice for 21 days — mantra, action, Gita verse, reflection.
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
        {planets.map(p => {
          const rgb = hexToRgb(p.color);
          return (
            <div key={p.key} onClick={() => onSelect(p.key)} style={{
              background:`linear-gradient(135deg,var(--bg2),rgba(${rgb},0.07))`,
              border:`1px solid ${p.color}33`, borderRadius:"16px",
              padding:"18px 20px", cursor:"pointer", transition:"all 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor=`${p.color}77`; e.currentTarget.style.transform="translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor=`${p.color}33`; e.currentTarget.style.transform="translateY(0)"; }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
                <div style={{ fontSize:"2.2rem", filter:`drop-shadow(0 0 10px ${p.color}80)` }}>{p.emoji}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
                    color:p.color, marginBottom:"5px" }}>
                    {p.name.en} · {p.name.hi || p.name.te}
                  </div>
                  <div style={{ fontSize:"1.1rem", color:"var(--text-dim)", lineHeight:1.6 }}>
                    {p.challenge}
                  </div>
                </div>
                <div style={{ color:"var(--text-dim)", fontSize:"1.2rem", flexShrink:0 }}>›</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Active Sadhana view ───────────────────────────────────────────────────────
const CAT_ICONS = {
  discipline:"⏰", order:"🧹", avoidance:"✅", fast:"🍃", seva:"🙏",
  saturday:"🪔", tuesday:"🪔", fast_tuesday:"🍃", reflection:"📝",
  invisible:"👁️", duty:"⚖️", restriction:"🔒", gratitude:"💌",
  hands:"🤲", witness:"🕯️", elderly:"👴", review:"🔍",
  sleep:"🌙", first:"🎯", reframe:"💎", pledge:"📜",
  breath:"🌬️", pause:"💧", body:"💪", mapping:"🗺️", challenge:"🔥",
  truth:"⚔️", sibling:"🤝", cold:"❄️", protect:"🛡️", create:"🎨",
  release:"🕊️", labour:"🛠️", courage:"🦁", forgive:"🌸",
  silence:"🤫", fear:"🕯️", small_fear:"🚪", offering:"🥥", outsider:"🫂",
  clarity:"🔍", meditation:"🧘", unplug:"📵", obsession:"🌀",
  margin:"🌿", pattern:"♾️", uncertainty:"🌫️", free:"🌈", simple:"🍚",
  tarpana:"💧", nature:"🌳", past:"📖", burn:"🔥", pratyahara:"🌑",
  sacred:"🏛️", worship:"⛪", letter:"✉️", purpose:"🔮", nothing:"∅",
  community:"🕉️", meditative:"☁️",
  moon:"🌙", screen:"📵", emotions:"❤️", mother:"💐", nourish:"🍲",
  water:"💧", inherited:"🧬", self_care:"🫂", water_sit:"🌊",
  peace:"🕊️", woman:"🌺", no_complain:"🙏", future:"⭐", forgive_mother:"🌸",
  moonlight:"🌕",
};

function ActiveSadhana({ sadhana, onComplete, onAbandon }) {
  const { planet_meta, day_number, done_days, today_done, practice, completed } = sadhana;
  const accent = planet_meta.color;
  const rgb    = hexToRgb(accent);
  const [confirming, setConfirming] = useState(false);

  if (completed) {
    return (
      <div style={{
        background:`linear-gradient(145deg,#100e0a,rgba(${rgb},0.2))`,
        border:`1px solid ${accent}55`, borderRadius:"24px",
        padding:"40px 28px", textAlign:"center",
        boxShadow:`0 12px 60px rgba(${rgb},0.2)`,
      }}>
        <div style={{ fontSize:"4rem", marginBottom:"16px" }}>🔱</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.5rem",
          color:accent, marginBottom:"12px" }}>
          Sadhana Complete
        </div>
        <div style={{ fontSize:"1rem", color:"var(--text-dim)", lineHeight:1.8, marginBottom:"24px" }}>
          You showed up for 21 days. That is not a small thing.<br/>
          {planet_meta.promise}
        </div>
        <button onClick={onAbandon} style={{
          background:"var(--bg3)", border:"1px solid var(--border)",
          borderRadius:"12px", padding:"12px 24px",
          color:"var(--text-dim)", cursor:"pointer", fontSize:"1.0rem",
        }}>
          Start a new Sadhana
        </button>
        <div style={{ marginTop:"20px" }}>
          <PujaCTA planet={planet_meta.name?.en} color={accent} rgb={rgb} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation:"fadeInUp 0.5s ease" }}>
      {/* Planet header */}
      <div style={{
        background:`linear-gradient(145deg,#100e0a,rgba(${rgb},0.15))`,
        border:`1px solid ${accent}44`, borderRadius:"20px",
        padding:"20px 24px", marginBottom:"14px",
        display:"flex", alignItems:"center", gap:"16px",
      }}>
        <div style={{ fontSize:"2.8rem", filter:`drop-shadow(0 0 16px ${accent}80)` }}>
          {planet_meta.emoji}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:accent }}>
            {planet_meta.name?.en} Sadhana · Day {day_number} of 21
          </div>
          <div style={{ fontSize:"1.05rem", color:"var(--text-dim)", marginTop:"3px" }}>
            {planet_meta.governs?.join(" · ")}
          </div>
        </div>
      </div>

      {/* 21-dot progress */}
      <div style={{ display:"flex", gap:"5px", flexWrap:"wrap", marginBottom:"20px", padding:"0 4px" }}>
        {Array.from({length:21},(_,i)=>{
          const d = i+1;
          const done = done_days.includes(d);
          const isToday = d === day_number;
          return (
            <div key={d} style={{
              width:28, height:28, borderRadius:"50%",
              background: done ? accent : isToday ? `${accent}30` : "var(--bg3)",
              border: `2px solid ${done ? accent : isToday ? accent : "var(--border)"}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"1.0rem", color: done ? "#fff" : isToday ? accent : "var(--text-dim)",
              fontWeight: isToday ? 700 : 400,
            }}>
              {done ? "✓" : d}
            </div>
          );
        })}
      </div>

      {/* Today's mantra */}
      <div style={{
        background:`linear-gradient(135deg,rgba(${rgb},0.12),rgba(${rgb},0.04))`,
        border:`1px solid ${accent}33`, borderRadius:"18px",
        padding:"22px 24px", marginBottom:"14px",
      }}>
        <div style={{ fontSize:"1.1rem", color:accent, letterSpacing:"0.12em",
          fontFamily:JT.heading, marginBottom:"12px" }}>
          ॐ MANTRA — CHANT {practice.mantra_count} TIMES
        </div>
        <div style={{ fontFamily:"'Noto Sans Devanagari',sans-serif", fontSize:"1.7rem",
          color:"var(--cream)", marginBottom:"8px", lineHeight:1.5 }}>
          {practice.mantra_sa}
        </div>
        <div style={{ fontSize:"1rem", color:accent, fontStyle:"italic",
          fontFamily:"'Inter',sans-serif", marginBottom:"6px" }}>
          {practice.mantra_roman}
        </div>
        <div style={{ fontSize:"1.08rem", color:"var(--text-dim)", lineHeight:1.6 }}>
          {practice.mantra_meaning}
        </div>
      </div>

      {/* Today's action */}
      <div style={{
        background:"var(--bg2)", border:"1px solid var(--border)",
        borderRadius:"18px", padding:"22px 24px", marginBottom:"14px",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"12px" }}>
          <span style={{ fontSize:"1.5rem" }}>{CAT_ICONS[practice.category] || "🌿"}</span>
          <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
            color:"var(--gold)", letterSpacing:"0.1em" }}>
            TODAY'S PRACTICE
          </div>
        </div>
        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.15rem",
          color:"var(--cream)", lineHeight:1.85 }}>
          {practice.action}
        </div>
      </div>

      {/* Gita verse */}
      <div style={{
        background:"linear-gradient(135deg,var(--bg2),rgba(232,131,42,0.06))",
        border:"1px solid rgba(232,131,42,0.25)", borderRadius:"18px",
        padding:"20px 24px", marginBottom:"14px",
        display:"flex", gap:"14px",
      }}>
        <div style={{ fontSize:"1.4rem", flexShrink:0 }}>🕉️</div>
        <div>
          <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
            color:"var(--gold)", letterSpacing:"0.1em", marginBottom:"8px" }}>
            BG {practice.gita_ref} — WEEK {practice.week} VERSE
          </div>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem",
            color:"var(--cream)", fontStyle:"italic", lineHeight:1.7 }}>
            {practice.gita_text}
          </div>
        </div>
      </div>

      {/* Complete / done state */}
      {today_done ? (
        <div style={{
          background:`${accent}15`, border:`1px solid ${accent}44`,
          borderRadius:"14px", padding:"16px", textAlign:"center",
          fontFamily:JT.heading, fontSize:"1.0rem", color:accent,
        }}>
          ✓ Practice complete for today · Return tomorrow for Day {day_number+1}
        </div>
      ) : (
        <button onClick={onComplete} style={{
          width:"100%", padding:"16px",
          background:`linear-gradient(135deg,${accent},${accent}bb)`,
          border:"none", borderRadius:"14px", cursor:"pointer",
          fontFamily:JT.heading, fontSize:"1.0rem",
          color:"#fff", letterSpacing:"0.06em",
          boxShadow:`0 6px 28px rgba(${rgb},0.4)`,
          transition:"transform 0.15s",
        }}
          onMouseEnter={e => e.currentTarget.style.transform="scale(1.02)"}
          onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}
        >
          ✓ Mark Today's Practice Complete
        </button>
      )}

      <button onClick={() => setConfirming(c=>!c)} style={{
        width:"100%", marginTop:"10px", padding:"10px",
        background:"none", border:"1px solid var(--border)",
        borderRadius:"10px", color:"var(--text-dim)",
        fontSize:"1.05rem", cursor:"pointer",
      }}>
        {confirming ? "Cancel" : "Abandon this Sadhana"}
      </button>
      {confirming && (
        <button onClick={onAbandon} style={{
          width:"100%", marginTop:"6px", padding:"10px",
          background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.3)",
          borderRadius:"10px", color:"#f87171",
          fontSize:"1.05rem", cursor:"pointer",
        }}>
          Yes, abandon — I will start fresh
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ── Feature cards (home landing) ─────────────────────────────────────────────

// ── Layer 2: Jyotish Chart Chat ───────────────────────────────────────────────
const CHAT_TOPICS = [
  { id:"marriage",  emoji:"💑", label:"Marriage & Love",    color:"#ec4899" },
  { id:"career",    emoji:"💼", label:"Career & Success",   color:"#f59e0b" },
  { id:"family",    emoji:"🏡", label:"Family & Children",  color:"#22c55e" },
  { id:"wealth",    emoji:"💰", label:"Wealth & Finance",   color:"#14b8a6" },
  { id:"spiritual", emoji:"🙏", label:"Spiritual Path",     color:"#c9a84c" },
  { id:"life",      emoji:"🌟", label:"Life Direction",     color:"#b8860b" },
];

const TOPIC_FOLLOWUP = {
  marriage: {
    q: "One quick question to personalize your reading — what's your current situation?",
    opts: [
      { label:"Single / looking",   key:"marital_status",    val:"single"       },
      { label:"In a relationship",  key:"marital_status",    val:"relationship"  },
      { label:"Already married",    key:"marital_status",    val:"married"       },
    ],
  },
  career: {
    q: "To give you the most accurate reading — where are you right now?",
    opts: [
      { label:"Currently employed",  key:"employment_status", val:"employed"  },
      { label:"Looking for a job",   key:"employment_status", val:"seeking"   },
      { label:"Running a business",  key:"employment_status", val:"business"  },
      { label:"Student",             key:"employment_status", val:"student"   },
    ],
  },
};

const TOPIC_OPENERS = {
  marriage: {
    single:       "Your chart is open for marriage timing. Ask me about your dasha windows, Venus strength, or what the stars say about your timeline.",
    relationship: "Your chart shows where this relationship could go. Ask about commitment timing, compatibility in your dasha, or what this phase means.",
    married:      "Since you're already married, I'll focus on relationship harmony, family timing, and partnership. What would you like to explore?",
    _default:     "Let's look at your love and relationship timing. What would you like to know?",
  },
  career: {
    employed:  "Let's look at what your chart says about growth, promotion windows, and your current dasha's effect on your work. What's on your mind?",
    seeking:   "Your chart holds real answers for job timing. I can tell you your strongest windows and what this dasha phase means for your career.",
    business:  "Your 10th house and Jupiter transits tell a lot about business timing. Ask about growth phases, partnership, or financial cycles.",
    student:   "Let's look at your chart for career direction and when your professional window opens. What would you like to know?",
    _default:  "Let's explore your career path through your chart. What would you like to know?",
  },
  family:   "Family is written in your 4th and 5th houses. Ask about children timing, family harmony, or what your chart shows about home life.",
  wealth:   "Wealth is shown through your 2nd and 11th houses and Jupiter transits. Ask about finances, gains, or prosperity windows.",
  spiritual:"Your chart shows a unique spiritual path. Ask about your karma, practices suited to your dasha, or your dharma.",
  life:     "Let's explore this chapter of your life — your current dasha, what it asks of you, and where you're headed.",
};

function _getOpener(topicId, statusVal) {
  const t = TOPIC_OPENERS[topicId];
  if (!t) return "Your chart is ready. What would you like to explore?";
  if (typeof t === "string") return t;
  return t[statusVal] || t._default || "Your chart is ready. What would you like to explore?";
}

const STARTER_QUESTIONS = {
  marriage: {
    single:       ["When will I get married?", "Is this a good period to meet someone?", "What does my chart say about my future partner?", "Which dasha is best for my marriage?"],
    relationship: ["When is the right time to get engaged?", "Will this relationship lead to marriage?", "What does my chart say about commitment timing?", "Is this a good period for our relationship?"],
    married:      ["When will I and my wife reunite?", "What does my chart say about family harmony?", "When is a good time to have a child?", "How can I strengthen my relationship during this dasha?"],
    _default:     ["When will I get married?", "What does Venus say about my love life?", "Is this a good period for relationships?"],
  },
  career: {
    employed:     ["When will I get a promotion?", "Is this a good time to switch jobs?", "What does my chart say about my career growth?", "Which period is best for leadership opportunities?"],
    seeking:      ["When will I get a job?", "What is my strongest career window?", "Is this a good period to apply for jobs?", "Which dasha supports career success?"],
    business:     ["When is a good time to expand my business?", "What does my chart say about financial growth?", "Is this a good period to take business risks?", "When will my business stabilize?"],
    student:      ["When will I get my first job?", "What career path suits my chart?", "Is this a good time to focus on studies?", "Which period supports my professional start?"],
    _default:     ["When will I get a promotion?", "What does my chart say about my career?", "Is this a good period for career growth?"],
  },
  family:   ["When will I have a child?", "What does my chart say about my family?", "Is this a good period to start a family?", "What does my chart say about my parents' health?"],
  wealth:   ["When will my financial situation improve?", "What does my chart say about wealth?", "Is this a good period to invest?", "When is my strongest period for financial gains?"],
  spiritual:["What spiritual practice suits my chart?", "What is my dharma path?", "What does my current dasha say about spiritual growth?", "How can I align with my chart's energy?"],
  life:     ["What is the theme of my current dasha?", "What does this period mean for me?", "What are my strongest areas right now?", "What should I focus on in the next 2 years?"],
};

function _getStarters(topicId, statusVal) {
  const s = STARTER_QUESTIONS[topicId];
  if (!s) return [];
  if (Array.isArray(s)) return s;
  return s[statusVal] || s._default || [];
}

// ── Muhurta Tab ───────────────────────────────────────────────────────────────

const MUHURTA_ACTIVITIES = [
  { id:"travel",    label:"Travel",    icon:"✈️" },
  { id:"interview", label:"Career",    icon:"💼" },
  { id:"business",  label:"Business",  icon:"🏢" },
  { id:"marriage",  label:"Marriage",  icon:"💍" },
  { id:"medical",   label:"Medical",   icon:"🏥" },
  { id:"education", label:"Education", icon:"📚" },
  { id:"property",  label:"Property",  icon:"🏠" },
];

function MuhurtaTab({ userId, noBirth, onNeedBirth, isSubscribed, onUpgrade }) {
  const [activity,     setActivity]     = useState("");
  const [recommended,  setRecommended]  = useState([]);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const fetch_ = async (act) => {
    if (noBirth) { onNeedBirth(); return; }
    setLoading(true); setError(null); setData(null);
    try {
      const url = act
        ? `${API}/jyotish/muhurta/personal/${userId}?activity=${act}&days=45`
        : `${API}/jyotish/muhurta/personal/${userId}?days=45`;
      const r = await fetch(url);
      const j = await r.json();
      if (j.error) setError(j.error === "no_birth_data" ? "Add birth details first" : j.error);
      else {
        setData(j);
        setActivity(j.activity);
        if (j.recommended) setRecommended(j.recommended);
      }
    } catch { setError("Could not load — please try again"); }
    setLoading(false);
  };

  useEffect(() => { fetch_(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchActivity = (act) => { setActivity(act); fetch_(act); };

  if (!isSubscribed) return (
    <div style={{ textAlign:"center", padding:"32px 16px" }}>
      <div style={{ fontSize:"2rem", marginBottom:12 }}>🕐</div>
      <div style={{ fontSize:"1rem", fontWeight:600, color:"var(--text)", marginBottom:8 }}>
        Personalised Muhurta
      </div>
      <div style={{ fontSize:"1.05rem", color:"var(--text-dim)", marginBottom:20, lineHeight:1.6 }}>
        See your best auspicious dates based on your natal chart — Tara Chakra, Chandra Bala, and Dasha timing personalised to you.
      </div>
      <button onClick={onUpgrade} style={{ padding:"10px 28px", borderRadius:22,
        background:"linear-gradient(135deg,#c9a84c,#d4a853)",
        border:"none", color:"#fff", fontSize:"1.08rem",
        fontWeight:600, cursor:"pointer" }}>
        Unlock — from ₹299/yr
      </button>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Recommended for you */}
      {recommended.length > 0 && (
        <div>
          <div style={{ fontSize:"1.2rem", color:"var(--text-dim)",
             marginBottom:7 }}>
            Relevant for you now
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:4, flexWrap:"wrap" }}>
            {recommended.map(rid => {
              const a = MUHURTA_ACTIVITIES.find(x => x.id === rid);
              if (!a) return null;
              return (
                <button key={a.id} onClick={() => switchActivity(a.id)}
                  style={{ display:"flex", alignItems:"center", gap:6,
                    padding:"10px 18px", borderRadius:22, border:"none", cursor:"pointer",
                    fontSize:"1.2rem", fontWeight:700,
                    background: activity === a.id
                      ? "linear-gradient(135deg,#c9a84c,#d4a853)"
                      : "rgba(201,168,76,0.15)",
                    color: activity === a.id ? "#fff" : "#d4a853",
                    border: activity === a.id ? "none" : "1px solid rgba(180,130,50,0.3)",
                    transition:"all 0.15s" }}>
                  <span style={{ fontSize:"1.2rem" }}>{a.icon}</span>{a.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* All activities */}
      <div>
        <div style={{ fontSize:"1.2rem", color:"var(--text-dim)",
           marginBottom:7 }}>
          All activities
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {MUHURTA_ACTIVITIES.filter(a => !recommended.includes(a.id)).map(a => (
            <button key={a.id} onClick={() => switchActivity(a.id)}
              style={{ display:"flex", alignItems:"center", gap:6,
                padding:"8px 14px", borderRadius:20, border:"none", cursor:"pointer",
                fontSize:"1.2rem", fontWeight: activity === a.id ? 700 : 400,
                background: activity === a.id
                  ? "linear-gradient(135deg,#c9a84c,#d4a853)"
                  : JT.card,
                color: activity === a.id ? "#fff" : JT.textDim,
                border: activity === a.id ? "none" : `1px solid ${JT.border}`,
                boxShadow: activity === a.id ? "none" : JT.shadow,
                transition:"all 0.15s" }}>
              <span>{a.icon}</span>{a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Context line */}
      {data && (
        <div style={{ fontSize:"1.2rem", color:JT.textDim, lineHeight:1.6,
          padding:"10px 14px", borderRadius:10, background:JT.cardWarm,
          border:`1px solid ${JT.border}` }}>
          Your <strong style={{ color:"#9b6f1f" }}>
            {data.maha_dasha?.charAt(0).toUpperCase() + data.maha_dasha?.slice(1)} Maha Dasha
          </strong> activates these life areas now · {data.lagna_rashi} Lagna · {data.days_scanned}-day scan
        </div>
      )}

      {loading && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ height:110, borderRadius:14, background:JT.cardWarm,
              animation:"shimmer 1.5s infinite alternate" }} />
          ))}
        </div>
      )}

      {error && (
        <div style={{ textAlign:"center", padding:"24px", color:"var(--text-dim)", fontSize:"1.2rem" }}>
          {error}
        </div>
      )}

      {!loading && data && data.results?.length === 0 && (
        <div style={{ textAlign:"center", padding:"24px 16px",
          color:"var(--text-dim)", fontSize:"1.2rem", lineHeight:1.6 }}>
          No strongly auspicious days found in the next {data.days_scanned} days for {data.activity_label?.toLowerCase()}.
          <br/>Try a different activity or check back later.
        </div>
      )}

      {/* Result cards */}
      {!loading && data?.results?.map((r, i) => {
        const dt     = new Date(r.date + "T12:00:00");
        const dayStr = dt.toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"short" });
        const pct    = Math.round(r.score);
        return (
          <div key={i} style={{ borderRadius:16, overflow:"hidden",
            border:`1px solid ${r.color}44`, boxShadow:JT.shadow,
            background:JT.card }}>

            {/* Header row */}
            <div style={{ display:"flex", alignItems:"center",
              justifyContent:"space-between", padding:"14px 16px 10px" }}>
              <div>
                <div style={{ fontSize:"1.2rem", fontWeight:700, color:JT.text }}>
                  {dayStr}
                </div>
                <div style={{ fontSize:"1.2rem", color:JT.textDim, marginTop:3 }}>
                  {r.panchang?.vara} · {r.panchang?.tithi} · {r.panchang?.nakshatra}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:"1.2rem", fontWeight:700, color:r.color }}>
                  {r.verdict}
                </div>
                <div style={{ fontSize:"1.2rem", color:JT.textDim }}>{pct}/100</div>
              </div>
            </div>

            {/* Score bar */}
            <div style={{ height:4, background:JT.cardCream, margin:"0 16px" }}>
              <div style={{ height:"100%", width:`${pct}%`,
                background:`linear-gradient(90deg,${r.color}88,${r.color})`,
                borderRadius:2, transition:"width 0.6s" }} />
            </div>

            {/* Personal factors */}
            {r.personal_factors?.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, padding:"10px 16px 6px" }}>
                {r.personal_factors.map((pf, pi) => (
                  <span key={pi} style={{ fontSize:"1.2rem", padding:"4px 10px",
                    borderRadius:10, fontWeight:600,
                    background: pf.positive ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                    color: pf.positive ? "#15803d" : "#b91c1c",
                    border: `1px solid ${pf.positive ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}` }}>
                    {pf.type === "tara" ? `${pf.name} Tara`
                      : pf.type === "chandra_bala" ? `Chandra Bala H${pf.house}`
                      : pf.type === "dasha" ? `${pf.planet?.charAt(0).toUpperCase() + pf.planet?.slice(1)} Dasha`
                      : pf.type}
                  </span>
                ))}
              </div>
            )}

            {/* Best times to start */}
            {r.best_times?.length > 0 && (
              <div style={{ padding:"8px 16px 4px" }}>
                <div style={{ fontSize:"1.2rem", color:JT.textDim,
                   marginBottom:6 }}>
                  Best time to start
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {r.best_times.map((bt, bti) => (
                    <div key={bti} style={{ display:"flex", alignItems:"center", gap:6,
                      padding:"6px 12px", borderRadius:10,
                      background:"rgba(34,197,94,0.1)",
                      border:"1px solid rgba(34,197,94,0.25)" }}>
                      <span style={{ fontSize:"1.2rem", fontWeight:700, color:"#15803d" }}>
                        {bt.start}–{bt.end}
                      </span>
                      <span style={{ fontSize:"1.2rem", color:"#15803d", opacity:0.8 }}>
                        {bt.name}
                      </span>
                    </div>
                  ))}
                  {r.abhijit && !r.abhijit.weakened && (
                    <div style={{ display:"flex", alignItems:"center", gap:6,
                      padding:"6px 12px", borderRadius:10,
                      background:"rgba(245,158,11,0.1)",
                      border:"1px solid rgba(245,158,11,0.25)" }}>
                      <span style={{ fontSize:"1.2rem", fontWeight:700, color:"#92400e" }}>
                        {r.abhijit.start}–{r.abhijit.end}
                      </span>
                      <span style={{ fontSize:"1.2rem", color:"#92400e", opacity:0.8 }}>Abhijit</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Positives + warnings */}
            <div style={{ padding:"8px 16px 14px", display:"flex", flexDirection:"column", gap:6 }}>
              {r.positives?.slice(0,3).map((pos, pi) => (
                <div key={pi} style={{ fontSize:"1.2rem", color:"#15803d",
                  display:"flex", gap:6, lineHeight:1.5 }}>
                  <span>✓</span><span>{typeof pos === "object" ? pos.text : pos}</span>
                </div>
              ))}
              {r.warnings?.slice(0,2).map((w, wi) => (
                <div key={wi} style={{ fontSize:"1.2rem", color:"#b91c1c",
                  display:"flex", gap:6, lineHeight:1.5 }}>
                  <span>⚠</span><span>{typeof w === "object" ? w.text : w}</span>
                </div>
              ))}
              {r.rahu_kalam?.start && (
                <div style={{ fontSize:"1.2rem", color:"#b91c1c", marginTop:2,
                  display:"flex", gap:6, alignItems:"flex-start", lineHeight:1.5 }}>
                  <span>🚫</span>
                  <span>Rahu Kalam {r.rahu_kalam.start}–{r.rahu_kalam.end} — avoid starting anything</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Dosha Tab ────────────────────────────────────────────────────────────────

function JyotishChatTab({ userId, noBirth, onNeedBirth, arcData, isSubscribed, initialMessage, onInitialMessageConsumed, onOpenReports, onUpgrade }) {
  const STORAGE_KEY = `jyotish_chat_${userId}`;
  const PROFILE_KEY = `jyotish_profile_${userId}`;

  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "{}"); }
    catch { return {}; }
  });
  const [topic,     setTopic]     = useState(null);
  const [chatPhase, setChatPhase] = useState(() => {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    // Only go to "chat" phase if there are actual user messages (not just opener)
    return stored.some(m => m.role === "user") ? "chat" : "topic";
  });

  const [input,       setInput]       = useState("");
  const [sending,     setSending]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [upsellDismissed, setUpsellDismissed] = useState(false);
  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);
  const dbLoaded    = useRef(false);

  const [activeSessionId, setActiveSessionId] = useState(null);
  const [dbSessions,      setDbSessions]      = useState([]);

  const FREE_LIMIT = 3;
  const FREE_Q_KEY = `jyotish_free_q_${userId}`;
  const [freeQUsed, setFreeQUsed] = useState(() =>
    Math.min(parseInt(localStorage.getItem(`jyotish_free_q_${userId}`) || "0", 10), 3)
  );

  const fetchSessions = () => {
    if (!userId) return;
    fetch(`${API}/jyotish/sessions/${userId}`)
      .then(r => r.json())
      .then(data => setDbSessions(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => { fetchSessions(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = async (sessionId) => {
    try {
      const r    = await fetch(`${API}/jyotish/sessions/${userId}/${sessionId}/messages`);
      const msgs = await r.json();
      if (Array.isArray(msgs) && msgs.length > 0) {
        setMessages(msgs.map(m => ({ role: m.role, text: m.text })));
        setActiveSessionId(sessionId);
        setChatPhase("chat");
        setShowHistory(false);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs.map(m => ({ role:m.role, text:m.text })))); } catch {}
      }
    } catch {}
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try { await fetch(`${API}/jyotish/sessions/${userId}/${sessionId}`, { method:"DELETE" }); } catch {}
    setDbSessions(prev => prev.filter(s => s.session_id !== sessionId));
    if (activeSessionId === sessionId) {
      setMessages([]);
      setActiveSessionId(null);
      setChatPhase("topic");
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-40))); }
    catch {}
  }, [messages, STORAGE_KEY]);

  // Save profile to localStorage + backend
  const saveProfile = (updates) => {
    const newProfile = { ...profile, ...updates };
    setProfile(newProfile);
    try { localStorage.setItem(PROFILE_KEY, JSON.stringify(newProfile)); } catch {}
    fetch(`${API}/jyotish/life-context`, {
      method: "POST", headers: { "Content-Type":"application/json" },
      body: JSON.stringify({ user_id: userId, ...updates }),
    }).catch(() => {});
  };

  // Start chat after topic (+optional followup) resolved
  const startChat = (t, statusVal) => {
    const opener = _getOpener(t.id, statusVal);
    setMessages([{ role:"assistant", text: opener }]);
    setChatPhase("chat");
  };

  // User clicks a topic card
  const selectTopic = (t) => {
    setTopic(t);
    const followup = TOPIC_FOLLOWUP[t.id];
    if (followup) {
      // Check if we already know their status
      const knownVal = t.id === "marriage" ? profile.marital_status
                     : t.id === "career"   ? profile.employment_status
                     : null;
      if (knownVal) {
        startChat(t, knownVal);
      } else {
        setChatPhase("followup");
      }
    } else {
      startChat(t, null);
    }
  };

  // User clicks a follow-up option
  const selectFollowup = (opt) => {
    saveProfile({ [opt.key]: opt.val });
    // If married → immediate local message (no API call needed)
    if (opt.key === "marital_status" && opt.val === "married") {
      // Also save to backend via life-context (already done in saveProfile)
      setMessages([{ role:"assistant", text: _getOpener(topic.id, "married") }]);
      setChatPhase("chat");
    } else {
      startChat(topic, opt.val);
    }
  };

  const TIMING_KEYWORDS = /when will i|exact date|which date|which month|which year|tell me the date|predict.*date|date of marriage|date of job|marry when|get job when/i;

  const clearChat = () => {
    setMessages([]);
    setTopic(null);
    setChatPhase("topic");
    setActiveSessionId(null);
    dbLoaded.current = false;
    setShowHistory(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    fetchSessions(); // refresh sidebar
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || sending) return;
    if (!isSubscribed && freeQUsed >= FREE_LIMIT) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    const isTimingQ = TIMING_KEYWORDS.test(msg);
    setMessages(prev => [...prev, { role:"user", text:msg }]);
    setSending(true);

    // Auto-create a session on first message
    let sid = activeSessionId;
    if (!sid) {
      try {
        const sr = await fetch(`${API}/jyotish/sessions/${userId}`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({}),
        });
        const sd = await sr.json();
        sid = sd.session_id || null;
        setActiveSessionId(sid);
      } catch {}
    }

    try {
      const r = await fetch(`${API}/jyotish/chat`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          user_id:    userId,
          message:    msg,
          history:    messages.slice(-8).map(m => ({ role:m.role, text:m.text })),
          session_id: sid || "",
        }),
      });
      const d = await r.json();
      setMessages(prev => [...prev, {
        role:"assistant",
        text: d.reply || "Something went wrong.",
        timingNote: isTimingQ,
      }]);
      if (!isSubscribed) {
        const next = Math.min(freeQUsed + 1, FREE_LIMIT);
        setFreeQUsed(next);
        try { localStorage.setItem(FREE_Q_KEY, String(next)); } catch {}
      }
      fetchSessions(); // update sidebar title after first exchange
    } catch {
      setMessages(prev => [...prev, { role:"assistant", text:"Could not reach the server. Please try again." }]);
    }
    setSending(false);
  };

  // Auto-send initialMessage when coming from dosha card
  useEffect(() => {
    if (!initialMessage || (!isSubscribed && freeQUsed >= FREE_LIMIT)) return;
    setChatPhase("chat");
    const t = setTimeout(() => {
      send(initialMessage);
      if (onInitialMessageConsumed) onInitialMessageConsumed();
    }, 300);
    return () => clearTimeout(t);
  }, [initialMessage]); // eslint-disable-line

  // Early returns — MUST be after all hooks
  if (noBirth) return (
    <div style={{ textAlign:"center", padding:"32px 16px" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"12px" }}>💬</div>
      <div style={{ color:"var(--text-dim)", fontSize:"1.0rem", marginBottom:"20px" }}>
        Add your birth details to use the chart-aware chat
      </div>
      <button onClick={onNeedBirth} style={{
        background:"var(--saffron)",
        border:"none", borderRadius:"12px", padding:"12px 28px",
        color:"#fff", fontFamily:JT.label, fontWeight:600, fontSize:"1.05rem", cursor:"pointer",
      }}>Add Birth Details</button>
    </div>
  );

  if (!isSubscribed && freeQUsed >= FREE_LIMIT) return (
    <div style={{
      position:"relative", overflow:"hidden",
      background:"#ffffff",
      border:`1px solid ${JT.border}`, borderRadius:"20px",
      minHeight:320,
      boxShadow:JT.shadow,
    }}>
      <div style={{ padding:"20px", filter:"blur(4px)", userSelect:"none", pointerEvents:"none" }}>
        {[
          { role:"user",      text:"Why have I been feeling stuck in my career lately?" },
          { role:"assistant", text:"Your Saturn dasha is restructuring your career path — not punishing you. Stop waiting for the right opportunity to appear. Saturn in house 10 from your natal Moon demands you act on what you already know needs to change. BG 18.41 — your nature is to lead through wisdom, not hustle." },
          { role:"user",      text:"Should I accept this job offer?" },
        ].map((m, i) => (
          <div key={i} style={{ display:"flex", flexDirection: m.role==="user" ? "row-reverse" : "row",
            gap:"8px", marginBottom:"14px", alignItems:"flex-start" }}>
            {m.role==="assistant" && (
              <div style={{ width:28, height:28, flexShrink:0, background:"var(--bg3)",
                border:"1px solid var(--border)",
                borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"1.0rem" }}>🔱</div>
            )}
            <div style={{
              background: m.role==="user" ? "var(--saffron)" : "#f5f5f5",
              border: m.role==="assistant" ? `1px solid ${JT.border}` : "none",
              borderRadius: m.role==="user" ? "14px 14px 4px 14px" : "4px 14px 14px 14px",
              padding:"10px 14px", maxWidth:"80%",
              fontSize:"1.08rem", color: m.role==="user" ? "#fff" : "#2b2b2b", lineHeight:1.65,
            }}>{m.text}</div>
          </div>
        ))}
      </div>
      <div style={{
        position:"absolute", inset:0,
        background:"linear-gradient(180deg,rgba(255,255,255,0) 15%,rgba(255,255,255,0.96) 55%)",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end",
        padding:"28px",
      }}>
        <div style={{ fontSize:"1.8rem", marginBottom:"8px" }}>🔱</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
          color:JT.text, marginBottom:"6px", fontWeight:700 }}>3 Free Questions Used</div>
        <div style={{ fontSize:"1.05rem", color:JT.textDim, textAlign:"center",
          lineHeight:1.6, marginBottom:"16px" }}>
          Your AI Pandit knows your full birth chart, current dasha, and today's transits.<br/>
          Up to 25 questions a day — ask about career, marriage, timing, remedies.
        </div>
        <button onClick={onUpgrade} style={{
          background:"var(--saffron)",
          border:"none", borderRadius:"12px", padding:"12px 28px",
          color:"#fff", fontFamily:JT.label, fontWeight:600,
          fontSize:"1.05rem", cursor:"pointer",
        }}>Continue with Guru Kripa — ₹699/yr</button>
        <div style={{ fontSize:"1.05rem", color:JT.textDim, marginTop:"6px" }}>
          Replaces a ₹500+ pandit visit · Ask every day
        </div>
      </div>
    </div>
  );

  // Highlight BG verse references inline
  const renderText = (text) => {
    // Split into paragraphs first, then highlight BG verses within each
    const paragraphs = text.split(/\n\n+/);
    return paragraphs.map((para, pi) => {
      const parts = para.split(/(BG \d+\.\d+[^—\n]*)/g);
      const content = parts.map((part, i) =>
        /^BG \d+\.\d+/.test(part) ? (
          <span key={i} style={{
            display:"inline", background:"rgba(251,191,36,0.1)",
            border:"1px solid rgba(251,191,36,0.25)", borderRadius:"5px",
            padding:"1px 7px", color:"var(--gold)",
            fontFamily:"'Inter',sans-serif", fontStyle:"italic", fontSize:"0.92em",
          }}>{part}</span>
        ) : part
      );
      return (
        <p key={pi} style={{
          margin: 0,
          marginBottom: pi < paragraphs.length - 1 ? "12px" : 0,
          lineHeight: 1.75,
          whiteSpace: "pre-wrap",
        }}>{content}</p>
      );
    });
  };

  // Group dbSessions by Today / This Week / Earlier
  const _groupedSessions = (() => {
    const todayStr  = new Date().toDateString();
    const weekAgo   = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const groups    = { "Today": [], "This Week": [], "Earlier": [] };
    dbSessions.forEach(s => {
      const d = new Date(s.created_at || s.updated_at || 0);
      if (d.toDateString() === todayStr)   groups["Today"].push(s);
      else if (d >= weekAgo)               groups["This Week"].push(s);
      else                                 groups["Earlier"].push(s);
    });
    return groups;
  })();

  // ── History view (full-panel replacement) ──────────────────────────────
  if (showHistory) return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:480 }}>
      {/* History header */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"12px 4px", marginBottom:"12px",
        borderBottom:"1px solid var(--border)",
      }}>
        <button onClick={() => setShowHistory(false)} style={{
          background:"none", border:"none", color:"var(--text-dim)",
          fontSize:"1.02rem", cursor:"pointer", padding:"4px 8px 4px 0",
          display:"flex", alignItems:"center", gap:"5px",
        }}>← Back</button>
        <span style={{ fontFamily:JT.heading, fontSize:"1.1rem", color:"var(--saffron)" }}>
          Reading History
        </span>
        <button onClick={clearChat} style={{
          background:"var(--saffron)",
          border:"none", borderRadius:"8px", padding:"5px 12px",
          color:"#fff", fontSize:"1.0rem", cursor:"pointer",
        }}>+ New</button>
      </div>

      {dbSessions.length === 0 ? (
        <div style={{ textAlign:"center", padding:"48px 0", color:"var(--text-dim)", fontSize:"1.05rem" }}>
          No past readings yet.<br/>Start a new conversation below.
        </div>
      ) : (
        <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:"4px",
          maxHeight:520, scrollbarWidth:"thin", scrollbarColor:"var(--border) transparent" }}>
          {Object.entries(_groupedSessions).map(([group, sessions]) =>
            sessions.length > 0 ? (
              <div key={group}>
                <div style={{ fontSize:"0.95rem", color:JT.textDim, fontFamily:JT.label, fontWeight:600,

                  padding:"10px 4px 6px", }}>
                  {group}
                </div>
                {sessions.map(s => (
                  <div key={s.session_id}
                    onClick={() => loadSession(s.session_id)}
                    style={{
                      display:"flex", alignItems:"center", gap:"10px",
                      padding:"10px 12px", borderRadius:"12px",
                      cursor:"pointer", transition:"background 0.15s",
                      background: s.session_id === activeSessionId
                        ? "rgba(232,131,42,0.1)"
                        : JT.card,
                      border: s.session_id === activeSessionId
                        ? "1px solid var(--saffron)"
                        : `1px solid ${JT.border}`,
                      marginBottom:"3px",
                    }}
                    onMouseEnter={e => { if (s.session_id !== activeSessionId) e.currentTarget.style.background=JT.cardWarm; }}
                    onMouseLeave={e => { if (s.session_id !== activeSessionId) e.currentTarget.style.background=JT.card; }}
                  >
                    <div style={{
                      width:28, height:28, flexShrink:0,
                      background:"var(--bg3)", border:"1px solid var(--border)",
                      borderRadius:"50%", display:"flex", alignItems:"center",
                      justifyContent:"center", fontSize:"1.0rem",
                    }}>🔱</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:"1.05rem", color:JT.text,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      }}>
                        {s.title && s.title !== "New Session" ? s.title : "Birth Chart Reading"}
                      </div>
                      <div style={{ fontSize:"1.0rem", color:JT.textDim, marginTop:"2px" }}>
                        {new Date(s.updated_at || s.created_at).toLocaleDateString("en-IN",
                          { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}
                      </div>
                    </div>
                    <button
                      onClick={e => deleteSession(e, s.session_id)}
                      style={{
                        background:"none", border:"none",
                        color:JT.textDim, fontSize:"1.02rem",
                        cursor:"pointer", padding:"4px 6px", borderRadius:"6px",
                        flexShrink:0, lineHeight:1,
                        transition:"color 0.15s, background 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color="#b91c1c"; e.currentTarget.style.background="rgba(239,68,68,0.12)"; }}
                      onMouseLeave={e => { e.currentTarget.style.color=JT.textDim; e.currentTarget.style.background="none"; }}
                      title="Delete this reading"
                    >✕</button>
                  </div>
                ))}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"0" }}>

      {/* Header */}
      <div style={{
        background:"#ffffff",
        border:`1px solid ${JT.border}`, borderRadius:"16px", boxShadow:JT.shadow,
        padding:"12px 16px", marginBottom:"14px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{
            width:36, height:36,
            background:"var(--bg3)", border:"1px solid var(--border)",
            borderRadius:"50%", flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:"1rem",
          }}>🔱</div>
          <div>
            <div style={{ fontSize:"1.08rem", color:JT.text, fontWeight:600, lineHeight:1.2 }}>
              Jyotish AI
            </div>
            <div style={{ fontSize:"1.0rem", color:JT.textDim, marginTop:"1px" }}>
              {arcData
                ? `${arcData.rashi?.en || ""} Moon · ${arcData.nakshatra?.name?.en || ""} nakshatra`
                : "Your personal Vedic astrologer"}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
          <button onClick={() => setShowHistory(true)} style={{
            background:"none", border:"1px solid var(--saffron)",
            borderRadius:"8px", padding:"4px 10px",
            color:"var(--saffron)", fontSize:"1.0rem", cursor:"pointer",
            display:"flex", alignItems:"center", gap:"4px",
          }}>
            <span>📜</span>
            <span style={{ position:"relative" }}>
              History
              {dbSessions.length > 0 && (
                <span style={{
                  position:"absolute", top:"-6px", right:"-14px",
                  background:"var(--saffron)", color:"#fff",
                  borderRadius:"50%", width:14, height:14,
                  fontSize:"1.0rem", display:"flex", alignItems:"center", justifyContent:"center",
                }}>{dbSessions.length > 9 ? "9+" : dbSessions.length}</span>
              )}
            </span>
          </button>
          {messages.some(m => m.role==="user") && (
            <button onClick={clearChat} style={{
              background:"none", border:`1px solid ${JT.border}`,
              borderRadius:"8px", padding:"4px 12px",
              color:JT.textDim, fontSize:"1.0rem", cursor:"pointer",
              transition:"border-color 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor=JT.borderStrong}
              onMouseLeave={e => e.currentTarget.style.borderColor=JT.border}
            >+ New</button>
          )}
        </div>
      </div>

      {/* ── Topic selection phase ────────────────────────────────────────── */}
      {chatPhase === "topic" && (
        <div style={{ marginBottom:"8px" }}>
          <div style={{ fontSize:"1.1rem", color:JT.text, fontFamily:JT.label, fontWeight:700,
             marginBottom:"10px", textAlign:"center" }}>
            What would you like clarity on?
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" }}>
            {CHAT_TOPICS.map(t => (
              <button key={t.id} onClick={() => selectTopic(t)} style={{
                background:JT.card,
                border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
                borderRadius:"14px", padding:"14px 12px",
                display:"flex", flexDirection:"column", alignItems:"center", gap:"6px",
                cursor:"pointer", transition:"all 0.18s",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `${t.color}18`;
                  e.currentTarget.style.borderColor = `${t.color}55`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = JT.card;
                  e.currentTarget.style.borderColor = JT.border;
                  e.currentTarget.style.transform = "none";
                }}
              >
                <span style={{ fontSize:"1.4rem" }}>{t.emoji}</span>
                <span style={{ fontSize:"1.1rem", color:JT.text, fontWeight:500, textAlign:"center", lineHeight:1.3 }}>
                  {t.label}
                </span>
              </button>
            ))}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", margin:"14px 0 0" }}>
            <div style={{ flex:1, height:1, background:JT.border }} />
            <span style={{ fontSize:"0.95rem", color:JT.textDim, fontFamily:JT.label, fontWeight:600 }}>Or type your question below</span>
            <div style={{ flex:1, height:1, background:JT.border }} />
          </div>
        </div>
      )}

      {/* ── Follow-up question phase ─────────────────────────────────────── */}
      {chatPhase === "followup" && topic && TOPIC_FOLLOWUP[topic.id] && (
        <div style={{
          background:JT.card,
          border:"1px solid var(--border)", boxShadow:JT.shadow,
          borderRadius:"16px",
          padding:"20px 18px", marginBottom:"12px",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px" }}>
            <div style={{
              width:32, height:32, background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"1.02rem", flexShrink:0,
            }}>🔱</div>
            <div style={{ fontSize:"1.08rem", color:JT.text, lineHeight:1.5 }}>
              {TOPIC_FOLLOWUP[topic.id].q}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"7px" }}>
            {TOPIC_FOLLOWUP[topic.id].opts.map(opt => (
              <button key={opt.val} onClick={() => selectFollowup(opt)} style={{
                background:JT.card,
                border:`1px solid ${JT.border}`,
                borderRadius:"10px", padding:"11px 16px",
                color:JT.text, fontSize:"1.05rem", cursor:"pointer",
                textAlign:"left", transition:"all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(232,131,42,0.08)"; e.currentTarget.style.borderColor="var(--saffron)"; }}
                onMouseLeave={e => { e.currentTarget.style.background=JT.card; e.currentTarget.style.borderColor=JT.border; }}
              >{opt.label}</button>
            ))}
          </div>
          <button onClick={() => { setTopic(null); setChatPhase("topic"); }} style={{
            background:"none", border:"none", color:JT.textDim,
            fontSize:"1.0rem", cursor:"pointer", marginTop:"10px", padding:"0",
          }}>← Back</button>
        </div>
      )}

      {/* Message thread */}
      {messages.length > 0 && (
        <div style={{
          display:"flex", flexDirection:"column", gap:"16px",
          maxHeight:440, overflowY:"auto", marginBottom:"14px",
          padding:"2px 2px 4px",
          scrollbarWidth:"thin", scrollbarColor:"var(--border) transparent",
        }}>
          {messages.map((m, i) => (
            <div key={i} style={{
              display:"flex",
              flexDirection: m.role==="user" ? "row-reverse" : "row",
              gap:"10px", alignItems:"flex-start",
            }}>
              {m.role==="assistant" && (
                <div style={{
                  width:32, height:32, flexShrink:0,
                  background:"var(--bg3)", border:"1px solid var(--border)",
                  borderRadius:"50%", marginTop:"2px",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"1.02rem",
                }}>🔱</div>
              )}
              <div style={{
                display:"flex", flexDirection:"column",
                alignItems: m.role==="user" ? "flex-end" : "flex-start",
                maxWidth:"82%",
              }}>
                <div style={{
                  background: m.role==="user" ? "var(--saffron)" : "var(--bg2)",
                  border: m.role==="assistant" ? "1px solid var(--border)" : "none",
                  borderRadius: m.role==="user" ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
                  padding:"12px 16px",
                  boxShadow: "var(--card-shadow)",
                  fontSize:"1.0rem", color: m.role==="user" ? "#fff" : "var(--text)", lineHeight:1.75,
                }}>
                  {m.role==="assistant" ? renderText(m.text) : m.text}
                </div>
                {m.timingNote && (
                  <div style={{
                    display:"flex", alignItems:"flex-start", gap:"6px",
                    marginTop:"6px",
                    background:"rgba(245,158,11,0.07)",
                    border:"1px solid rgba(245,158,11,0.2)",
                    borderRadius:"10px", padding:"8px 12px",
                  }}>
                    <span style={{ fontSize:"1.02rem", flexShrink:0 }}>⚠️</span>
                    <div style={{ fontSize:"1.1rem", color:"#b45309", lineHeight:1.55 }}>
                      Vedic astrology shows <b>windows of potential</b>, not fixed dates.
                      Exact dates depend on your free will, actions, and karma — not even the best jyotishi can predict them.
                      Use these insights for awareness, not certainty.
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div style={{ display:"flex", gap:"10px", alignItems:"flex-start" }}>
              <div style={{
                width:32, height:32, flexShrink:0,
                background:"var(--bg3)", border:"1px solid var(--border)",
                borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"1.02rem",
              }}>🔱</div>
              <div style={{
                background:"var(--bg2)",
                border:"1px solid var(--border)",
                borderRadius:"4px 18px 18px 18px",
                padding:"14px 18px",
                display:"flex", gap:"5px", alignItems:"center",
              }}>
                {[0,1,2].map(j => (
                  <div key={j} style={{
                    width:6, height:6, borderRadius:"50%",
                    background:"var(--saffron)",
                    animation:`chatDot 1.4s ease-in-out ${j*0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* In-chat report upsell — appears once real engagement is shown, not a real chat message */}
          {!isSubscribed && !upsellDismissed && messages.filter(m => m.role==="user").length >= 2 && (
            <div style={{
              background:"var(--bg2)", border:"1px solid var(--border)",
              borderLeft:"3px solid var(--saffron)", borderRadius:"0 14px 14px 0",
              padding:"14px 16px", boxShadow:"var(--card-shadow)",
              display:"flex", alignItems:"flex-start", gap:"10px",
            }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"1.02rem", color:"var(--text)", lineHeight:1.6, marginBottom:"8px" }}>
                  This is just the surface. Your full Janma Kundali has more — the exact dasha windows, remedies, and what changes when. Get it in writing →
                </div>
                <button onClick={onOpenReports} style={{
                  background:"none", border:"none", padding:0, cursor:"pointer",
                  color:"var(--saffron)", fontSize:"1.0rem", fontWeight:700,
                  fontFamily:"'Inter',sans-serif",
                }}>See your written reading</button>
              </div>
              <button onClick={() => setUpsellDismissed(true)} style={{
                background:"none", border:"none", cursor:"pointer",
                color:"var(--text-dim)", fontSize:"1.1rem", padding:0, lineHeight:1, flexShrink:0,
              }}>✕</button>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* ── Starter question chips — shown when chat is empty or after opener ── */}
      {!sending && messages.filter(m => m.role==="user").length === 0 && topic && (
        <div style={{ marginBottom:"10px" }}>
          <div style={{ fontSize:"0.95rem", color:JT.textDim, fontFamily:JT.label, fontWeight:600,
             marginBottom:"8px" }}>
            Try asking
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"7px" }}>
            {_getStarters(topic.id, profile?.marital_status || profile?.employment_status).map((q, i) => (
              <button key={i} onClick={() => send(q)}
                style={{
                  background:"rgba(232,131,42,0.06)",
                  border:"1px solid var(--saffron)",
                  borderRadius:"20px", padding:"6px 14px",
                  fontSize:"1.0rem", color:"var(--saffron)",
                  cursor:"pointer", transition:"all 0.15s", textAlign:"left",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "rgba(232,131,42,0.15)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(232,131,42,0.06)";
                }}
              >{q}</button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      {/* Free trial counter */}
      {!isSubscribed && (
        <div style={{
          marginBottom:8, padding:"8px 14px",
          background: freeQUsed >= FREE_LIMIT - 1
            ? "rgba(239,68,68,0.1)"
            : "var(--bg3)",
          border: `1px solid ${freeQUsed >= FREE_LIMIT - 1 ? "rgba(239,68,68,0.3)" : "var(--border)"}`,
          borderRadius:10,
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
        }}>
          <span style={{ fontSize:"1.02rem", color:JT.textDim }}>
            {FREE_LIMIT - freeQUsed > 0
              ? `${FREE_LIMIT - freeQUsed} free question${FREE_LIMIT - freeQUsed !== 1 ? "s" : ""} remaining`
              : "Free questions used"}
          </span>
          <span style={{
            display:"flex", gap:5,
          }}>
            {[...Array(FREE_LIMIT)].map((_, i) => (
              <span key={i} style={{
                width:8, height:8, borderRadius:"50%",
                background: i < freeQUsed ? "rgba(239,68,68,0.7)" : "var(--border)",
              }} />
            ))}
          </span>
        </div>
      )}

      <div style={{
        display:"flex", gap:"8px", alignItems:"flex-end",
        background:"var(--bg2)",
        border:"1px solid var(--border)",
        borderRadius:"16px",
        padding:"8px 8px 8px 14px",
        transition:"border-color 0.2s",
      }}
        onFocus={e => e.currentTarget.style.borderColor="var(--saffron)"}
        onBlur={e => e.currentTarget.style.borderColor="var(--border)"}
      >
        <textarea
          ref={textareaRef}
          value={input}
          rows={1}
          autoFocus
          onChange={e => {
            setInput(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
          }}
          onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type your question here… (Enter to send)"
          style={{
            flex:1, background:"none", border:"none",
            color:"var(--text)", fontSize:"1.05rem", outline:"none",
            resize:"none", lineHeight:1.6, padding:"4px 0",
            fontFamily:"inherit", maxHeight:120, overflowY:"auto",
          }}
        />
        <button onClick={() => send()} disabled={!input.trim() || sending} style={{
          width:36, height:36, flexShrink:0,
          background: input.trim() && !sending
            ? "var(--saffron)"
            : "var(--bg3)",
          border:"none", borderRadius:"10px",
          color: input.trim() && !sending ? "#fff" : "var(--text-dim)",
          cursor: input.trim() && !sending ? "pointer" : "default",
          fontSize:"1.1rem", transition:"all 0.2s",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow: "none",
        }}>↑</button>
      </div>
    </div>
  );
}

function DoshaTab({ userId, noBirth, onNeedBirth, isSubscribed, onChatAbout, onUpgrade, lightMode = true }) {
  const [data,     setData]     = useState(null);
  const [fullData, setFullData] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (noBirth) { setLoading(false); return; }
    // Free tier basic check
    fetch(`${API}/jyotish/doshas/${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
    // Layer 2 full analysis (fetched in background for paywall preview)
    fetch(`${API}/jyotish/doshas/full/${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(d => setFullData(d))
      .catch(() => {});
  }, [userId, noBirth]);

  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", padding:"40px" }}>
      <div style={{ width:32, height:32, borderRadius:"50%",
        border:"3px solid #ef4444", borderTopColor:"transparent",
        animation:"spin 0.8s linear infinite" }} />
    </div>
  );

  if (noBirth) return (
    <div style={{ textAlign:"center", padding:"32px 16px" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"12px" }}>🪐</div>
      <div style={{ color:"var(--text-dim)", fontSize:"1.0rem", marginBottom:"20px" }}>
        Add your birth details to check your doshas
      </div>
      <button onClick={onNeedBirth} style={{
        background:"linear-gradient(135deg,#ef4444,#dc2626)",
        border:"none", borderRadius:"12px", padding:"12px 28px",
        color:"#fff", fontFamily:JT.heading, fontSize:"1.05rem",
        cursor:"pointer",
      }}>Add Birth Details</button>
    </div>
  );

  if (!data || data.error) return (
    <div style={{ color:"var(--text-dim)", textAlign:"center", padding:"24px" }}>
      Could not load dosha data.
    </div>
  );

  const doshas = [
    {
      key: "manglik",
      name: "Manglik Dosha",
      icon: "♂",
      present: data.manglik,
      what: data.manglik
        ? "Mars in houses 1, 4, 7, 8, or 12 — directly delays and disrupts marriage. This is likely the root cause of relationship struggles not explained by anything else."
        : "Mars is not in a marriage-disrupting house. No Manglik Dosha in your chart.",
    },
    {
      key: "kaal_sarp",
      name: "Kaal Sarp Dosha",
      icon: "🐍",
      present: data.kaal_sarp,
      what: data.kaal_sarp
        ? "All 7 planets enclosed between Rahu and Ketu — the root cause of cycles that repeat, breakthroughs that stall, and fortune that slips away just as it arrives."
        : "Planets are not enclosed between Rahu and Ketu. No Kaal Sarp Dosha in your chart.",
    },
    {
      key: "sadhe_sati",
      name: "Sadhe Sati",
      icon: "♄",
      present: data.sadhe_sati,
      what: data.sadhe_sati
        ? `Saturn is transiting your Moon sign right now — ${data.sadhe_sati_phase} phase. This is the most intense 7.5-year cycle in Vedic astrology. What you do now shapes the next decade.`
        : "Saturn is not transiting your Moon sign. Sadhe Sati is not active for you right now.",
    },
  ];

  const presentCount = doshas.filter(d => d.present).length;

  /* ── Light-mode palette ── */
  const LM = {
    pageBg:       "#ffffff",
    cardBg:       "#ffffff",
    cardBgWarm:   "#ffffff",
    cardBgCream:  "#ffffff",
    borderPres:   "#c8241a",      /* deep red border */
    borderSafe:   "#16a34a",
    borderGold:   "#c9a84c",
    titleRed:     "#9b1c1c",
    bodyText:     "#1a1a1a",
    dimText:      "#5a5a5a",
    accentOrange: "#c2410c",
    gold:         "#c2560f",
  };

  return (
    <div style={{ background: lightMode ? LM.pageBg : "transparent",
      borderRadius:"20px", padding: lightMode ? "4px" : "0" }}>

      {/* ── Header ── */}
      <div style={{
        textAlign:"center", marginBottom:"20px",
        padding:"24px 20px",
        background: lightMode
          ? "#ffffff"
          : "linear-gradient(135deg,rgba(180,40,40,0.18),rgba(249,115,22,0.08))",
        borderRadius:"18px",
        border: lightMode ? `2px solid ${LM.borderPres}` : "1px solid rgba(239,68,68,0.25)",
        boxShadow: lightMode ? "0 4px 24px rgba(155,28,28,0.12)" : "none",
        /* saffron accent strip on top */
        borderTop: lightMode ? `4px solid ${LM.borderPres}` : undefined,
      }}>
        <div style={{ fontSize:"2.2rem", marginBottom:"10px" }}>🔭</div>
        <div style={{ fontFamily:JT.heading, fontSize:"1.15rem",
          color: lightMode ? LM.titleRed : "#fca5a5", letterSpacing:"0.07em", marginBottom:"8px" }}>
          Your Dosha Scan
        </div>
        <div style={{ fontSize:"1.2rem", color: lightMode ? LM.bodyText : "var(--cream)",
          lineHeight:1.7, marginBottom:"14px" }}>
          Planetary combinations in your birth chart that create specific life challenges.
        </div>
        {presentCount > 0 ? (
          <div style={{
            display:"inline-flex", alignItems:"center", gap:"6px",
            background: lightMode ? "#9b1c1c" : "rgba(239,68,68,0.22)",
            borderRadius:"30px", padding:"8px 20px", fontSize:"1rem", fontWeight:700,
            color: "#ffffff", letterSpacing:"0.03em",
            boxShadow: lightMode ? "0 2px 12px rgba(155,28,28,0.35)" : "none",
          }}>
            ⚠️ {presentCount} dosha{presentCount > 1 ? "s" : ""} found in your chart
          </div>
        ) : (
          <div style={{
            display:"inline-flex", alignItems:"center", gap:"6px",
            background: lightMode ? "#15803d" : "rgba(34,197,94,0.1)",
            border: lightMode ? "none" : "1px solid rgba(34,197,94,0.3)",
            borderRadius:"30px", padding:"8px 20px", fontSize:"1rem", fontWeight:700,
            color: lightMode ? "#ffffff" : "#86efac",
          }}>
            ✓ No active doshas found
          </div>
        )}
      </div>

      {/* ── Dosha cards ── */}
      <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"20px" }}>
        {doshas.map(d => (
          <div key={d.key} style={{
            background: lightMode
              ? (d.present ? LM.cardBgWarm : LM.cardBg)
              : (d.present ? "rgba(180,20,20,0.18)" : "var(--bg2)"),
            borderRadius:"14px", padding:"18px 20px",
            display:"flex", alignItems:"flex-start", gap:"16px",
            /* left border accent — the key depth signal in light mode */
            borderLeft: `5px solid ${d.present
              ? (lightMode ? LM.borderPres : "#ef4444")
              : (lightMode ? LM.borderSafe : "#22c55e")}`,
            borderTop:    lightMode ? "1px solid rgba(0,0,0,0.07)" : "none",
            borderRight:  lightMode ? "1px solid rgba(0,0,0,0.07)" : "none",
            borderBottom: lightMode ? "1px solid rgba(0,0,0,0.07)" : "none",
            boxShadow: lightMode
              ? (d.present ? "0 4px 20px rgba(155,28,28,0.1)" : "0 2px 12px rgba(0,0,0,0.07)")
              : (d.present ? "0 4px 20px rgba(180,20,20,0.2)" : "none"),
          }}>
            {/* Icon */}
            <div style={{
              width:48, height:48, borderRadius:"50%", flexShrink:0,
              background: d.present
                ? (lightMode ? "rgba(155,28,28,0.1)" : "rgba(239,68,68,0.15)")
                : (lightMode ? "rgba(21,128,61,0.1)"  : "rgba(34,197,94,0.1)"),
              border: `2px solid ${d.present
                ? (lightMode ? "rgba(155,28,28,0.25)" : "rgba(239,68,68,0.4)")
                : (lightMode ? "rgba(21,128,61,0.25)"  : "rgba(34,197,94,0.3)")}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"1.4rem",
              color: d.present ? (lightMode ? LM.titleRed : "#ef4444") : (lightMode ? "#15803d" : "#22c55e"),
            }}>
              {d.icon}
            </div>
            {/* Content */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                gap:"8px", marginBottom:"6px" }}>
                <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                  color: d.present ? (lightMode ? LM.titleRed : "#fca5a5") : (lightMode ? "#1c1208" : "var(--cream)") }}>
                  {d.name}
                </div>
                <div style={{
                  padding:"5px 14px", borderRadius:"20px", fontSize:"1.1rem",
                  fontWeight:700, letterSpacing:"0.04em", flexShrink:0,
                  background: d.present
                    ? (lightMode ? "#9b1c1c" : "rgba(239,68,68,0.18)")
                    : (lightMode ? "#15803d" : "rgba(34,197,94,0.12)"),
                  color: "#ffffff",
                }}>
                  {d.present ? "⚠ Present" : "✓ Clear"}
                </div>
              </div>
              <div style={{ fontSize:"1.2rem", color: lightMode ? LM.bodyText : "rgba(240,220,190,0.85)",
                lineHeight:1.75 }}>
                {d.what}
              </div>
              {/* Kaal Sarp transparency box */}
              {d.key === "kaal_sarp" && d.present && fullData?.kaal_sarp_rahu_rashi && (
                <div style={{
                  marginTop:"10px", padding:"12px 14px",
                  background: lightMode ? "rgba(180,130,50,0.06)" : "rgba(180,130,50,0.08)",
                  border: `1px solid ${lightMode ? "rgba(180,130,50,0.3)" : "rgba(180,130,50,0.25)"}`,
                  borderRadius:"10px", fontSize:"1.2rem",
                  color: lightMode ? LM.dimText : "rgba(220,200,160,0.8)",
                  lineHeight:1.7,
                }}>
                  <div style={{ fontWeight:700, marginBottom:"5px",
                    color: lightMode ? LM.bodyText : "rgba(240,220,190,0.9)" }}>
                    How we check this
                  </div>
                  <div>
                    All 7 main planets (Sun–Saturn) fall within the 180° arc from Rahu to Ketu. We use
                    the <b>strict classical rule</b> — all 7 must be fully enclosed (no partial exceptions).
                  </div>
                  <div style={{ marginTop:"6px" }}>
                    Rahu in <b style={{ color: lightMode ? "#6d28d9" : "#c4b5fd" }}>{fullData.kaal_sarp_rahu_rashi}</b>
                    {" · "}Ketu in <b style={{ color: lightMode ? "#c2410c" : "#fed7aa" }}>{fullData.kaal_sarp_ketu_rashi}</b>
                    {fullData.kaal_sarp_type && (
                      <span> · {fullData.kaal_sarp_type}</span>
                    )}
                  </div>
                </div>
              )}
              {/* Mars transparency box — only for Manglik */}
              {d.key === "manglik" && d.present && fullData?.manglik_mars_rashi && (
                <div style={{
                  marginTop:"10px", padding:"12px 14px",
                  background: lightMode ? "rgba(180,130,50,0.06)" : "rgba(180,130,50,0.08)",
                  border: `1px solid ${lightMode ? "rgba(180,130,50,0.3)" : "rgba(180,130,50,0.25)"}`,
                  borderRadius:"10px", fontSize:"1.2rem",
                  color: lightMode ? LM.dimText : "rgba(220,200,160,0.8)",
                  lineHeight:1.7,
                }}>
                  <div style={{ fontWeight:700, marginBottom:"5px",
                    color: lightMode ? LM.bodyText : "rgba(240,220,190,0.9)" }}>
                    How we check this
                  </div>
                  <div>
                    VedSanga uses the <b>broad school</b> — Mars checked from both Lagna and Moon sign,
                    as most modern Vedic astrologers practice.
                  </div>
                  <div style={{ marginTop:"6px" }}>
                    Mars position: <b style={{ color: lightMode ? "#9b1c1c" : "#fca5a5" }}>{fullData.manglik_mars_rashi}</b>
                    {fullData.manglik_mars_h_lagna != null && (
                      <span> · house <b>{fullData.manglik_mars_h_lagna}</b> from Lagna</span>
                    )}
                    {fullData.manglik_mars_h_moon != null && (
                      <span> · house <b>{fullData.manglik_mars_h_moon}</b> from Moon</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Full analysis cards (locked) ── */}
      <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
        {[
          {
            key:"manglik", label:"Manglik Dosha", present: data?.manglik,
            hook: "Your chart confirms why marriage has been delayed or troubled. The severity level and your specific 3-step remedy are ready — but they must be followed correctly to work.",
            lockLine: "Your Manglik severity level and the 3 remedies specific to your chart are locked above.",
            whatInside: ["Exact severity level — mild, moderate, or full", "Cancellation factors found in your chart", "Why your past relationships may have struggled", "Your 3-step remedy, specific to your Mars position"],
            previewLines: fullData?.manglik_impact ? [fullData.manglik_impact] : null,
            remedies: fullData?.manglik_remedies,
            extra: fullData?.manglik_cancellations?.length
              ? `${fullData.manglik_cancellations.length} cancellation factor${fullData.manglik_cancellations.length > 1 ? "s" : ""} found in your chart`
              : null,
            severity: fullData?.manglik_severity,
          },
          {
            key:"kaal_sarp", label: fullData?.kaal_sarp_type || "Kaal Sarp Dosha", present: data?.kaal_sarp,
            hook: "Every planet in your chart is trapped in the Rahu-Ketu axis. This is the specific reason certain life areas keep cycling — career gains reversed, relationships that end suddenly, fortune that arrives and disappears.",
            lockLine: "The exact axis type and which life areas are most affected by this pattern are locked above.",
            whatInside: ["Which Kaal Sarp type and the Rahu-Ketu axis in your chart", "Exactly which life areas are most disrupted", "Why the cycles repeat — and when they can break", "Specific temple remedies and mantra to neutralize"],
            previewLines: fullData?.kaal_sarp_impact ? [fullData.kaal_sarp_impact] : null,
            remedies: fullData?.kaal_sarp_remedies,
            severity: fullData?.kaal_sarp_severity,
          },
          {
            key:"sadhe_sati", label:"Sadhe Sati", present: data?.sadhe_sati,
            hook: "Saturn is transiting your Moon sign right now. This is one of the most consequential periods in a 30-year cycle. Your phase determines exactly what to expect — and what remedies apply.",
            lockLine: "Your exact Sadhe Sati phase, what it means for your life now, and when it ends are locked above.",
            whatInside: ["Which of the 3 phases you are in — and what each phase brings", "What this specific phase means for career, health, and relationships", "Exact end date of your current Sadhe Sati", "Saturn remedies for your phase"],
            previewLines: fullData?.sadhe_sati_phase_desc ? [fullData.sadhe_sati_phase_desc, fullData.sadhe_sati_assurance] : null,
            remedies: fullData?.sadhe_sati_remedies,
            extra: fullData?.sadhe_sati_end_estimate ? `Estimated end: ${fullData.sadhe_sati_end_estimate}` : null,
          },
        ].map(d => {
          if (!d.present) return null;
          return (
            <div key={d.key} style={{
              position:"relative", overflow:"hidden",
              background: lightMode ? LM.cardBg : "linear-gradient(160deg,rgba(140,15,15,0.35),rgba(80,5,5,0.28))",
              borderRadius:"18px",
              /* gold premium border in light mode */
              border: lightMode
                ? `2px solid ${LM.borderGold}`
                : "1.5px solid rgba(239,68,68,0.55)",
              boxShadow: lightMode
                ? "0 6px 32px rgba(180,130,50,0.18), 0 2px 8px rgba(0,0,0,0.08)"
                : "0 4px 24px rgba(180,10,10,0.25)",
            }}>
              {/* Premium header strip — light mode only */}
              {lightMode && (
                <div style={{
                  background:"linear-gradient(90deg,#9b1c1c,#c2410c,#d97706)",
                  padding:"12px 20px",
                  display:"flex", alignItems:"center", gap:"10px",
                }}>
                  <div style={{ fontSize:"1.3rem" }}>🔍</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                      color:"#fff", letterSpacing:"0.05em" }}>
                      Full Analysis — {d.label}
                    </div>
                  </div>
                  {d.severity && (
                    <span style={{
                      background:"rgba(255,255,255,0.2)", borderRadius:"6px",
                      padding:"4px 12px", fontSize:"1.08rem",
                      color:"#fff", fontWeight:700, letterSpacing:"0.05em",
                    }}>
                      {d.severity === "full" ? "Full" : (d.severity ? d.severity[0].toUpperCase()+d.severity.slice(1) : "")} severity
                    </span>
                  )}
                </div>
              )}

              <div style={{ padding: lightMode ? "18px 20px 26px" : "20px 20px 28px" }}>
                {/* Dark mode card header */}
                {!lightMode && (
                  <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px" }}>
                    <div style={{ fontSize:"1.6rem" }}>🔍</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                        color:"#fca5a5", letterSpacing:"0.04em" }}>
                        Full Analysis — {d.label}
                      </div>
                      {d.severity && (
                        <span style={{
                          display:"inline-block", marginTop:"4px",
                          background: d.severity === "severe" ? "rgba(239,68,68,0.22)" : "rgba(251,146,60,0.18)",
                          border: `1px solid ${d.severity === "severe" ? "rgba(239,68,68,0.5)" : "rgba(251,146,60,0.4)"}`,
                          borderRadius:"6px", padding:"4px 12px", fontSize:"1.1rem",
                          color: d.severity === "severe" ? "#fca5a5" : "#fdba74",
                          fontWeight:700, letterSpacing:"0.05em",
                        }}>
                          {d.severity === "full" ? "Full" : (d.severity ? d.severity[0].toUpperCase()+d.severity.slice(1) : "")} severity
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Hook line */}
                <div style={{
                  fontSize:"1.08rem",
                  color: lightMode ? LM.bodyText : "rgba(255,220,190,0.9)",
                  lineHeight:1.8, marginBottom:"16px",
                  fontFamily:"'Inter',sans-serif", fontStyle:"italic",
                  borderLeft: lightMode ? `3px solid ${LM.borderGold}` : "none",
                  paddingLeft: lightMode ? "12px" : "0",
                }}>
                  {d.hook}
                </div>

                {/* What you get — parchment box in light mode */}
                <div style={{
                  background: lightMode ? LM.cardBgCream : "rgba(0,0,0,0.25)",
                  borderRadius:"12px", padding:"16px 18px", marginBottom:"16px",
                  border: lightMode ? `1px solid rgba(180,130,50,0.3)` : "1px solid rgba(255,255,255,0.06)",
                  borderLeft: lightMode ? `4px solid ${LM.borderGold}` : undefined,
                }}>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.05rem", fontWeight:700,
                    color: lightMode ? LM.gold : "#fdba74",
                    marginBottom:"12px" }}>
                    ✦ What you get
                  </div>
                  {d.whatInside.map((item, i) => (
                    <div key={i} style={{
                      display:"flex", alignItems:"flex-start", gap:"8px",
                      fontSize:"1.02rem",
                      color: lightMode ? LM.bodyText : "rgba(240,220,190,0.85)",
                      lineHeight:1.7, marginBottom:"8px",
                    }}>
                      <span style={{ color: lightMode ? LM.gold : "#d4a853", flexShrink:0, marginTop:"2px" }}>◆</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Blurred preview — actual AI content */}
                {(d.previewLines || d.remedies) && (
                  <div style={{
                    filter: isSubscribed ? "none" : "blur(4px)",
                    userSelect: isSubscribed ? "auto" : "none",
                    pointerEvents: isSubscribed ? "auto" : "none",
                  }}>
                    {d.previewLines?.map((line, i) => (
                      <div key={i} style={{
                        fontSize:"1.05rem",
                        color: lightMode ? LM.bodyText : "rgba(240,215,185,0.9)",
                        lineHeight:1.8, marginBottom:"10px",
                        fontFamily:"'Inter',sans-serif",
                      }}>{line}</div>
                    ))}
                    {d.extra && (
                      <div style={{ fontSize:"1rem",
                        color: lightMode ? LM.accentOrange : "#fdba74",
                        fontWeight:700, marginBottom:"10px" }}>→ {d.extra}</div>
                    )}
                    {d.remedies && (
                      <div style={{
                        marginTop:"14px",
                        background: lightMode ? LM.cardBgCream : "transparent",
                        borderRadius: lightMode ? "10px" : "0",
                        padding: lightMode ? "14px 16px" : "0",
                        border: lightMode ? `1px solid rgba(180,130,50,0.2)` : "none",
                      }}>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                          color: lightMode ? LM.gold : "#fdba74",
                          letterSpacing:"0.12em", marginBottom:"10px" }}>🪔 REMEDIES</div>
                        {d.remedies.map((r, i) => (
                          <div key={i} style={{
                            fontSize:"1.02rem",
                            color: lightMode ? LM.bodyText : "rgba(240,215,185,0.9)",
                            lineHeight:1.75, marginBottom:"8px",
                          }}>• {r}</div>
                        ))}
                      </div>
                    )}
                    {isSubscribed && onChatAbout && (
                      <button
                        onClick={() => onChatAbout(d.label)}
                        style={{
                          marginTop:"16px", width:"100%", padding:"13px 16px",
                          background: lightMode ? "rgba(180,130,50,0.08)" : "rgba(201,168,76,0.1)",
                          border:`1px solid ${lightMode ? "rgba(180,130,50,0.4)" : "rgba(201,168,76,0.35)"}`,
                          borderRadius:"10px", cursor:"pointer",
                          color: lightMode ? LM.gold : "#d4a853",
                          fontSize:"1rem", fontWeight:600,
                          display:"flex", alignItems:"center", justifyContent:"center", gap:"8px",
                          transition:"all 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = lightMode ? "rgba(180,130,50,0.15)" : "rgba(201,168,76,0.18)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = lightMode ? "rgba(180,130,50,0.08)" : "rgba(201,168,76,0.1)"; }}
                      >
                        💬 Ask me anything about your {d.label}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Lock overlay */}
              {!isSubscribed && (
                <div style={{
                  position:"absolute", inset:0,
                  background: lightMode
                    ? "linear-gradient(180deg,transparent 20%,rgba(253,248,242,0.97) 55%)"
                    : "linear-gradient(180deg,transparent 25%,rgba(8,4,2,0.92) 62%)",
                  display:"flex", flexDirection:"column",
                  alignItems:"center", justifyContent:"flex-end",
                  padding:"20px 16px",
                }}>
                  <div style={{
                    fontSize:"1rem",
                    color: lightMode ? LM.dimText : "rgba(255,200,150,0.85)",
                    textAlign:"center", lineHeight:1.7, marginBottom:"16px",
                    fontFamily:"'Inter',sans-serif", fontStyle:"italic",
                  }}>
                    {d.lockLine || `Your full ${d.label} analysis — severity, exact impact, remedies, and guidance — is ready.`}
                  </div>
                  <button onClick={onUpgrade} style={{
                    background: lightMode
                      ? "linear-gradient(135deg,#9b1c1c,#c2410c)"
                      : "linear-gradient(135deg,#d4a853,#b8892e)",
                    border:"none", borderRadius:"12px", padding:"15px 28px",
                    color:"#fff", fontFamily:JT.heading, fontSize:"1rem",
                    cursor:"pointer", letterSpacing:"0.05em", width:"100%",
                    boxShadow: lightMode
                      ? "0 4px 20px rgba(155,28,28,0.4)"
                      : "0 4px 20px rgba(180,130,50,0.5)",
                  }}>
                    Unlock Remedies — Plans from ₹299/year
                  </button>
                  <div style={{ fontSize:"1.0rem",
                    color: lightMode ? LM.dimText : "rgba(200,180,140,0.7)",
                    marginTop:"8px", textAlign:"center", opacity:0.85 }}>
                    Doshas · Remedies · AI Pandit · Dasha · Full Chart · ₹299/year
                  </div>
                </div>
              )}
            </div>
          );
        }).filter(Boolean)}
      </div>
    </div>
  );
}

const JYOTISH_FEATURES = [
  {
    id:"kundali", icon:"🪐", color:"#c9a84c",
    title:"Birth Chart",
    subtitle:"Your personal sky map",
    desc:"See all 9 planets at the moment of your birth — your Kundali chart, yogas, and a dharmic AI reading of what it all means.",
  },
  {
    id:"archetype", icon:"🔱", color:"#d4a853",
    title:"Your Archetype",
    subtitle:"Who you are by the stars",
    desc:"Your Vedic soul character based on Moon sign — discover if you're an Arjuna, Hanuman, Krishna, or another great soul.",
    badge:"FREE",
  },
  {
    id:"nakshatra", icon:"⭐", color:"#f59e0b",
    title:"Nakshatra",
    subtitle:"Your birth star",
    desc:"The 27-star system reveals your inner nature and instincts — the subtle qualities the Rashi alone cannot show.",
    badge:"FREE",
  },
  {
    id:"dasha", icon:"🌀", color:"#22c55e",
    title:"Dasha Timeline",
    subtitle:"Which chapter of life you're in",
    desc:"Vedic planetary periods shaping your life right now — career, relationships, growth, or transformation.",
  },
  {
    id:"milan", icon:"💑", color:"#ec4899",
    title:"Compatibility",
    subtitle:"Kundali Milan with your partner",
    desc:"The 8-factor Ashtakoota matching used in traditional Vedic matchmaking. See your score out of 36.",
  },
  {
    id:"sadhana", icon:"🪔", color:"#f97316",
    title:"Graha Sadhana",
    subtitle:"Your 21-day planet practice",
    desc:"Pick your biggest life challenge. Get a daily mantra, action, and Gita verse — personalized to the planet that governs it.",
  },
  {
    id:"jyotish_weekly", icon:"📅", color:"#14b8a6",
    title:"Weekly Energy",
    subtitle:"7-day mood & energy forecast",
    desc:"Your personalized weekly energy forecast — 7 daily bars grounded in Moon nakshatra, your dasha theme, and focus areas for career, relationships, and health.",
    badge:"PREMIUM",
  },
  {
    id:"jyotish_chat", icon:"💬", color:"#c9a84c",
    title:"Chart Chat",
    subtitle:"Ask your birth chart anything",
    desc:"Talk to an AI Vedic astrologer who knows your full birth chart, current dasha, and today's transits. Ask about career, relationships, timing — anything.",
    badge:"PREMIUM",
  },
  {
    id:"dosha", icon:"⚠️", color:"#ef4444",
    title:"Dosha Check",
    subtitle:"Free · Manglik · Kaal Sarp · Sadhe Sati",
    desc:"Check the three major planetary doshas in your birth chart — Manglik, Kaal Sarp, and Sadhe Sati — instantly and free.",
    badge: "FREE",
  },
  {
    id:"ishta_devata", icon:"🪔", color:"#f59e0b",
    title:"Ishta Devata",
    subtitle:"Your personal deity by Lagna",
    desc:"The deity your soul has chosen for this lifetime — revealed through your Lagna nakshatra. A personal mantra, sadhana, and Gita verse await you.",
    badge:"PREMIUM",
  },
  {
    id:"shani", icon:"🪐", color:"#64748b",
    title:"Shani Status",
    subtitle:"Sade Sati & Shani Dhaiya",
    desc:"Is Saturn transiting your Moon sign? Know your Sade Sati phase or Shani Dhaiya, exact dates, intensity, and Gita-based remedies.",
    badge:"FREE",
  },
  {
    id:"puja_alerts", icon:"🌙", color:"#a78bfa",
    title:"Sacred Alerts",
    subtitle:"Ekadashi · Amavasya · Pradosham",
    desc:"Never miss a sacred day. Know each upcoming Ekadashi, Amavasya, Pradosham and Purnima with the ritual to perform — days in advance.",
  },
  {
    id:"planet_alerts", icon:"🪐", color:"#93c5fd",
    title:"Planet Alerts",
    subtitle:"Graha entries · Retrogrades · Transits",
    desc:"When planets change signs or go retrograde, your life energy shifts. Know every upcoming transit — and what it means for your Rashi.",
  },
  {
    id:"mantra", icon:"📿", color:"#93c5fd",
    title:"Daily Mantra",
    subtitle:"Your Nakshatra mantra for today",
    desc:"Your personal Nakshatra mantra — chant count, transliteration, meaning and practice guidance based on your birth star.",
    badge:"PREMIUM",
  },
  {
    id:"dasha_navigator", icon:"⏳", color:"#22c55e",
    title:"Dasha Navigator",
    subtitle:"Your life chapters — past, present & next",
    desc:"See every dasha period of your life mapped to your age. Know what's coming after your current chapter — and how to prepare now.",
  },
  {
    id:"dosha_remedy", icon:"💊", color:"#ef4444",
    title:"Dosha Remedies",
    subtitle:"Full remedy plans for every dosha",
    desc:"Manglik, Kaal Sarp, Pitra Dosha — severity, cancellation factors, and step-by-step remedies specific to your chart.",
  },
  {
    id:"shani_kavach", icon:"🛡️", color:"#64748b",
    title:"Shani Kavach",
    subtitle:"Full Sade Sati shield & weekly practice",
    desc:"Complete Saturn protection protocol — weekly ritual, kavach mantra, yantra guidance, and month-by-month Sade Sati navigation.",
  },
  {
    id:"eclipse", icon:"🌘", color:"#a855f7",
    title:"Eclipse Reports",
    subtitle:"Solar & lunar eclipse impact on your chart",
    desc:"Every eclipse hits certain houses in YOUR chart. Know which life areas get activated, what to avoid, and how to use eclipse energy wisely.",
  },
  {
    id:"retrograde", icon:"🔄", color:"#06b6d4",
    title:"Retrograde Guide",
    subtitle:"Mercury · Venus · Jupiter · Saturn retrogrades",
    desc:"When planets go retrograde, their energy turns inward. Know what each retrograde means for YOU — and what actions to take or avoid.",
  },
  {
    id:"festival", icon:"🎉", color:"#f97316",
    title:"Festival Guide",
    subtitle:"How Diwali · Navratri · Holi affect your chart",
    desc:"Major festivals create specific energy windows for each Rashi. Know which festivals are your power days — and which rituals to perform.",
  },
  {
    id:"varshaphal", icon:"📆", color:"#86efac",
    title:"Varshaphal",
    subtitle:"Your complete year ahead reading",
    desc:"The Vedic Solar Return chart — what this year holds for career, relationships, health, and dharma. Generated fresh each birthday.",
  },
  {
    id:"beej_mantra", icon:"📿", color:"#a78bfa",
    title:"Beej Mantra",
    subtitle:"Planetary seed mantras + 40-day sadhana",
    desc:"Each planet has a seed (beej) mantra that directly activates its energy. Get your planetary beej mantras with a 40-day initiation guide.",
  },
  {
    id:"gemstone", icon:"💎", color:"#34d399",
    title:"Gemstone Guide",
    subtitle:"Gems & yantras for your chart",
    desc:"Which gemstones strengthen your weak planets and which to avoid. Based on your birth chart — not generic sun sign advice.",
  },
  {
    id:"health_alerts", icon:"🏥", color:"#ef4444",
    title:"Health Alerts",
    subtitle:"Planetary body system warnings",
    desc:"Each planet governs specific body systems. Know which areas need attention each month based on transits through your chart.",
  },
  {
    id:"prakriti", icon:"🌿", color:"#22c55e",
    title:"Ayurvedic Prakriti",
    subtitle:"Your body-mind constitution from your chart",
    desc:"Vata, Pitta, or Kapha — your Ayurvedic Prakriti is encoded in your birth chart. Know your constitution and how to live in alignment.",
  },
  {
    id:"family", icon:"👨‍👩‍👧", color:"#ec4899",
    title:"Family Charts",
    subtitle:"Add parents · spouse · children",
    desc:"Store birth details for your family. See each person's Rashi, Nakshatra, current Dasha, and compatibility with your chart.",
  },
  {
    id:"puja_calendar", icon:"🙏", color:"#f59e0b",
    title:"Puja Calendar",
    subtitle:"Your annual puja plan by dosha",
    desc:"Which pujas to perform, when, and why — personalised to your doshas, current Dasha, and family occasion calendar.",
  },
  {
    id:"quarterly", icon:"📜", color:"#fbbf24",
    title:"Quarterly Report",
    subtitle:"Next 3 months — specific to your chart",
    desc:"A detailed 3-month prediction covering career, relationships, health and dharma — grounded in your Dasha, transits, and Varshaphal.",
  },
];

function FeatureCard({ feature: f, onClick }) {
  const [hovered, setHovered] = useState(false);
  const rgb = hexToRgb(f.color);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? `linear-gradient(135deg,var(--bg2),rgba(${rgb},0.12))`
          : `linear-gradient(135deg,var(--bg2),var(--bg3))`,
        border:`1px solid ${hovered ? f.color+"66" : f.color+"28"}`,
        borderRadius:"20px", padding:"22px 18px",
        cursor:"pointer", transition:"all 0.25s",
        position:"relative", overflow:"hidden",
      }}>
      <div style={{
        position:"absolute", top:-24, right:-24, width:100, height:100,
        borderRadius:"50%",
        background:`radial-gradient(circle,${f.color}20 0%,transparent 70%)`,
        opacity: hovered ? 1 : 0, transition:"opacity 0.25s", pointerEvents:"none",
      }}/>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div style={{ fontSize:"2.4rem", marginBottom:"12px" }}>{f.icon}</div>
        {f.badge && (
          <span style={{
            background: f.badge === "FREE" ? "rgba(34,197,94,0.15)" : "rgba(180,130,50,0.15)",
            border: `1px solid ${f.badge === "FREE" ? "rgba(34,197,94,0.35)" : "rgba(180,130,50,0.4)"}`,
            borderRadius:"6px", padding:"2px 7px", fontSize:"1.0rem",
            color: f.badge === "FREE" ? "#86efac" : "#c4b5fd",
            fontWeight:700, letterSpacing:"0.04em", flexShrink:0,
          }}>
            {f.badge}
          </span>
        )}
      </div>
      <div style={{ fontFamily:JT.heading, fontSize:"1rem",
        color: hovered ? f.color : "var(--saffron)",
        marginBottom:"4px", transition:"color 0.25s" }}>
        {f.title}
      </div>
      <div style={{ fontSize:"1.08rem", color:"var(--text-dim)",
        fontStyle:"italic", marginBottom:"10px" }}>
        {f.subtitle}
      </div>
      <div style={{ fontSize:"1.1rem", color:"var(--text)", lineHeight:1.65 }}>
        {f.desc}
      </div>
      <div style={{ marginTop:"14px", fontSize:"1.05rem",
        color: hovered ? f.color : "var(--text-dim)", transition:"color 0.25s" }}>
        Explore →
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Rashi metadata list (mirrors src/rashi_horoscope.py RASHIS)
const RASHIS_META = [
  {idx:0, en:"Aries",       vedic:"Mesha",      emoji:"♈", color:"#ef4444"},
  {idx:1, en:"Taurus",      vedic:"Vrishabha",  emoji:"♉", color:"#22c55e"},
  {idx:2, en:"Gemini",      vedic:"Mithuna",    emoji:"♊", color:"#22a677"},
  {idx:3, en:"Cancer",      vedic:"Karka",      emoji:"♋", color:"#f97316"},
  {idx:4, en:"Leo",         vedic:"Simha",      emoji:"♌", color:"#f59e0b"},
  {idx:5, en:"Virgo",       vedic:"Kanya",      emoji:"♍", color:"#ec4899"},
  {idx:6, en:"Libra",       vedic:"Tula",       emoji:"♎", color:"#ec4899"},
  {idx:7, en:"Scorpio",     vedic:"Vrischika",  emoji:"♏", color:"#a855f7"},
  {idx:8, en:"Sagittarius", vedic:"Dhanu",      emoji:"♐", color:"#14b8a6"},
  {idx:9, en:"Capricorn",   vedic:"Makara",     emoji:"♑", color:"#64748b"},
  {idx:10,en:"Aquarius",    vedic:"Kumbha",     emoji:"♒", color:"#06b6d4"},
  {idx:11,en:"Pisces",      vedic:"Meena",      emoji:"♓", color:"#3b82f6"},
];

const JYOTISH_FOCUSES = [
  { id:"love_family",   emoji:"❤️", title:"Love & Family",
    sub:"Marriage timing, relationship harmony, children" },
  { id:"career_wealth", emoji:"💼", title:"Career & Wealth",
    sub:"Job, business growth, financial prosperity" },
  { id:"life_dharma",   emoji:"🌟", title:"Life & Dharma",
    sub:"Purpose, spiritual path, inner direction" },
];

const CONCERN_SUBS = {
  marriage: [
    { id:"looking",      emoji:"💍", label:"Looking for marriage",            intro:"Your Vivah Yog — when your marriage window opens and what is currently blocking it" },
    { id:"relationship", emoji:"❤️", label:"In a relationship",               intro:"Your 7th house compatibility and what your chart says about your partnership" },
    { id:"married",      emoji:"👫", label:"Already married",                 intro:"Your marital harmony, household peace, and family growth" },
    { id:"family",       emoji:"👨‍👩‍👧", label:"For a family member's match",    intro:"General guidance — their birth chart gives full accuracy for a complete reading" },
  ],
  career: [
    { id:"job",          emoji:"💼", label:"Job or promotion is stuck",       intro:"Which planet is blocking your growth right now — and exactly when it clears" },
    { id:"business",     emoji:"🏢", label:"Business is blocked",             intro:"Your business dasha and the recovery window your chart shows" },
    { id:"change",       emoji:"🔄", label:"Thinking of a career change",     intro:"Your Lagna and 10th house reveal which direction is aligned with your dharma" },
  ],
  finance: [
    { id:"income",       emoji:"📈", label:"Income is not growing",           intro:"Your 11th house wealth period and when the flow is set to resume" },
    { id:"debt",         emoji:"⚖️", label:"Debt or loan pressure",           intro:"Your Saturn cycle and the planetary window for debt relief" },
    { id:"losses",       emoji:"📉", label:"Business or investment losses",   intro:"Recovery timing from your current dasha — what your chart shows" },
  ],
  health: [
    { id:"self",         emoji:"🌿", label:"My own health concerns",          intro:"Your 6th and 8th house health pattern, nakshatra body connection, and remedy" },
    { id:"family",       emoji:"🤲", label:"A family member's health",        intro:"General guidance from your chart — their birth details give a precise reading" },
  ],
  direction: [
    { id:"career_path",  emoji:"🧭", label:"No clarity on career path",       intro:"Your 10th house dharma and what your Lagna + Nakshatra say about your right direction" },
    { id:"purpose",      emoji:"🌟", label:"Life feels purposeless",          intro:"Your Nakshatra and Vedic Archetype reveal the function your soul came here to perform" },
    { id:"spiritual",    emoji:"🕉️", label:"Seeking spiritual growth",        intro:"Your Nakshatra deity, Ketu placement, and the sadhana path mapped for your chart" },
  ],
  family: [
    { id:"spouse",       emoji:"💑", label:"Tension with spouse",             intro:"Your 7th house and Venus transit pattern — what's creating friction and when it eases" },
    { id:"parent_child", emoji:"👨‍👩‍👦", label:"Parent-child tension",           intro:"Saturn and 4th house dynamics in your chart — the karmic pattern and remedy" },
    { id:"siblings",     emoji:"👥", label:"Sibling or relative conflict",    intro:"Mercury and 3rd house patterns — the planetary cause of the friction" },
    { id:"general",      emoji:"🏠", label:"Overall family stress",           intro:"Saturn's transit and your 4th house relief timing — when the heaviness lifts" },
  ],
};

const CONCERNS = [
  {
    id:"career", emoji:"💼",
    title:"Career is stuck",
    sub:"Job stagnation, business blocked, promotion not coming",
    hook:"Every career blockage traces back to one planet in your dasha. Most people spend years not knowing which graha is the blocker — and which year the window opens. Your birth chart shows it precisely.",
    urgent:"Thousands of people in this dasha period right now feel the same friction. Most don't know it ends.",
    cta:"Find My Career Window", lifeContext:"career_wealth", targetView:"dasha_navigator",
  },
  {
    id:"marriage", emoji:"❤️",
    title:"Love & Relationships",
    sub:"Marriage timing, partner harmony, family bonds — whatever is weighing on you",
    hook:"The 7th house in your Kundali governs every close relationship — marriage, partnership, harmony at home. Your dasha planet is shaping what is happening in this house right now, for better or worse.",
    urgent:"Venus and Jupiter's positions in your chart determine the quality of every relationship you have. Their current transit is active right now.",
    cta:"Read My Relationship Chart", lifeContext:"love_family", targetView:"milan",
  },
  {
    id:"finance", emoji:"💰",
    title:"Money isn't flowing",
    sub:"Financial stress, debt, savings stuck, expenses rising",
    hook:"The 2nd and 11th houses in your Kundali reveal when wealth flows — and when it's blocked. A single dasha shift can reverse your financial pressure within months. Most people don't see it coming.",
    urgent:"Your current dasha planet controls this. One remedy, timed correctly, can shift the pattern.",
    cta:"See My Wealth Period", lifeContext:"career_wealth", targetView:"dasha_navigator",
  },
  {
    id:"health", emoji:"🌿",
    title:"Health worries",
    sub:"Ongoing illness, low energy, fatigue, recovery doubts",
    hook:"The 6th and 8th houses govern health in Jyotish. Certain dasha periods weaken the body — knowing yours lets you prepare with the right remedies before symptoms worsen.",
    urgent:"Many health patterns are planetary, not random. Knowing the timing changes how you respond.",
    cta:"Check My Health Dasha", lifeContext:"life_dharma", targetView:"dosha_remedy",
  },
  {
    id:"direction", emoji:"🧭",
    title:"Feeling lost — no direction",
    sub:"Confusion about path, purpose, which way to go",
    hook:"Your Lagna (Ascendant) is the compass of your life. Your Nakshatra reveals your soul's function. These never lie — they show you exactly what you are built for, even when nothing makes sense.",
    urgent:"Confusion in this dasha phase is very common. There is a name for it in Jyotish — and a clear way through.",
    cta:"Find My Dharma Path", lifeContext:"life_dharma", targetView:"archetype",
  },
  {
    id:"family", emoji:"👨‍👩‍👧",
    title:"Family conflicts",
    sub:"Disputes, harmony issues, tension at home",
    hook:"Saturn transiting the 4th house is the leading planetary cause of family friction. Knowing the planetary cause tells you exactly when to push, when to wait, and what remedy eases it.",
    urgent:"The 4th house transit is time-bound. It passes. Knowing when helps you endure with clarity.",
    cta:"Check Family Harmony", lifeContext:"love_family", targetView:"dosha_remedy",
  },
];

// ── Concern Result Card — shown immediately after birth data entry via concern flow ──
const DASHA_NOTES = {
  career: {
    Sun:     "Sun Dasha brings government connections, authority roles, and leadership opportunities. If you've been overlooked, this period rewards those who step forward boldly.",
    Moon:    "Moon Dasha brings fluctuating career momentum — public perception matters most now. Careers involving people, care, communication, or creativity can advance strongly.",
    Mars:    "Mars Dasha brings drive and ambition but also conflict with authority. Entrepreneurship, engineering, and self-directed work thrive. Avoid ego clashes at the workplace.",
    Mercury: "Mercury Dasha strongly favors business, communication, and intellect. Writing, technology, trading, and consulting — multiple paths can open simultaneously.",
    Jupiter: "Jupiter Dasha is among the best periods for career expansion. Teaching, consulting, law, finance — Jupiter rewards ethical ambition. Opportunities come through teachers and elders.",
    Venus:   "Venus Dasha favors creative fields, luxury, hospitality, and relationship-based work. Collaborations and partnerships bring career breakthroughs now.",
    Saturn:  "Saturn Dasha demands patience and consistent effort — progress is slow but what you build now is permanent. Avoid shortcuts. Integrity and discipline are rewarded heavily.",
    Rahu:    "Rahu Dasha brings unconventional opportunities — foreign connections, technology, disruption. Career can leap forward through unexpected channels, but stay grounded.",
    Ketu:    "Ketu Dasha is introspective and research-oriented. Conventional career paths may stall, but spiritual, technical, or behind-the-scenes work can fulfill deeply.",
  },
  finance: {
    Sun:     "Sun Dasha brings income through positions of authority. Government-related work, senior roles, and leadership bring financial stability now.",
    Moon:    "Moon Dasha brings fluctuating income streams. Build savings systematically and avoid impulsive spending — the flow is there but not steady.",
    Mars:    "Mars Dasha can bring sudden gains through real estate, lending, or bold entrepreneurial moves. Equal risk of sudden losses — balance aggression with caution.",
    Mercury: "Mercury Dasha is excellent for business and trade. Multiple income streams are possible. Investments in information and education have strong returns now.",
    Jupiter: "Jupiter Dasha is the best period for wealth accumulation and financial growth. Investments made now — especially in gold, education, or property — tend to multiply.",
    Venus:   "Venus Dasha brings comfort and luxury through partnerships, art, and beauty businesses. Joint ventures and collaborative income are strong during this period.",
    Saturn:  "Saturn Dasha is slow for wealth but builds an unshakeable foundation. Focus on debt reduction, disciplined saving, and eliminating leakages — not new risks.",
    Rahu:    "Rahu Dasha can bring unexpected wealth through foreign sources or technology. Equally capable of sudden losses. Avoid speculative investments and get-rich-quick schemes.",
    Ketu:    "Ketu Dasha is lean for material accumulation. Focus on clearing old debts and reducing unnecessary expenditure. Spiritual merit built now returns materially in the next dasha.",
  },
  health: {
    Sun:     "Sun Dasha affects the heart, spine, and right eye. Support your vitality with early morning sunlight, regular walks, and foods that strengthen digestion.",
    Moon:    "Moon Dasha affects the mind, fluids, lungs, and emotional stability. Sleep quality, hydration, and emotional peace are your most important health levers right now.",
    Mars:    "Mars Dasha can bring inflammation, blood-related issues, accidents, or excess heat in the body. Cool foods, non-competitive exercise, and avoiding anger are protective.",
    Mercury: "Mercury Dasha affects the nervous system and skin. Mental overload and anxiety are the main risks. Pranayama, grounding routines, and reduced screen time are remedies.",
    Jupiter: "Jupiter Dasha can bring weight gain and liver-related issues from overindulgence. Moderate diet, regular movement, and avoiding excess are the key protective practices.",
    Venus:   "Venus Dasha affects the kidneys and reproductive system. Hydration, avoiding excess sugar, and maintaining relational harmony directly impact your physical wellbeing.",
    Saturn:  "Saturn Dasha affects joints, bones, knees, and teeth. Chronic conditions may surface. Consistent routine, warm sesame oil massage, and patience with the body are essential.",
    Rahu:    "Rahu Dasha can bring mysterious or hard-to-diagnose health issues, skin conditions, and anxiety. Avoid self-medication. Spiritual practice and clean diet are protective.",
    Ketu:    "Ketu Dasha affects the stomach and can bring spiritual-type ailments — fatigue without clear cause, fevers. Simplify diet, rest adequately, and maintain sadhana.",
  },
  marriage: {
    Sun:     "Sun is strong-willed and independent — relationship success depends on mutual respect for each other's authority. Ego conflicts are the main risk to watch.",
    Moon:    "Moon Dasha activates emotional needs strongly. Deep emotional compatibility and nurturing become essential. Relationships started now have strong domestic harmony.",
    Mars:    "Mars Dasha brings passion but also impulsiveness and conflict. Marriage can happen quickly but requires active effort to avoid power struggles and impatience.",
    Mercury: "Mercury Dasha favors intellectual compatibility. Communication and shared interests define the relationship. Long-distance or arranged marriages can work well now.",
    Jupiter: "Jupiter Dasha is highly auspicious for marriage. Jupiter blesses unions with wisdom, stability, and children. This is one of the best periods for marriage timing.",
    Venus:   "Venus Dasha is the most natural period for romance and marriage. Love relationships begun now carry deep affinity. Existing relationships deepen beautifully.",
    Saturn:  "Saturn Dasha delays marriage but when it happens, it is solid and long-lasting. Patience is required. Practicality and shared values matter more than romance now.",
    Rahu:    "Rahu Dasha can bring unconventional or unexpected relationships — cross-culture, age gaps, or whirlwind connections. Clarity of intent is essential before committing.",
    Ketu:    "Ketu Dasha is spiritually oriented — worldly relationships feel incomplete. Marriage is possible but requires one partner to be highly spiritually inclined.",
  },
  direction: {
    Sun:     "Sun Dasha calls you toward leadership, self-expression, and positions of authority. Your dharma involves shining — don't minimize yourself in this period.",
    Moon:    "Moon Dasha turns attention inward toward home, family, and emotional intelligence. Your path involves nurturing, intuition, and building something that lasts.",
    Mars:    "Mars Dasha calls you toward action, courage, and building — not waiting. Your dharma is to initiate, protect, and execute. Courage is your spiritual practice now.",
    Mercury: "Mercury Dasha calls you toward learning, communication, and sharing knowledge. Teaching, writing, and connecting people to information is your dharmic direction.",
    Jupiter: "Jupiter Dasha is the guru period — you are meant to grow through wisdom and to guide others. Study, teach, give back. Your expansion is tied to others' growth.",
    Venus:   "Venus Dasha calls you toward beauty, harmony, and creative expression. Your dharma involves creating beauty — in relationships, art, or the world around you.",
    Saturn:  "Saturn Dasha is the discipline period — karma is being settled, foundations are being built. Your dharma now is patience, service, and quiet, consistent effort.",
    Rahu:    "Rahu Dasha calls you toward new frontiers — unfamiliar territories, unconventional paths. Your direction is forward into the unknown. Conventional paths will feel empty.",
    Ketu:    "Ketu Dasha calls you inward — toward liberation, spiritual seeking, and releasing what no longer serves. Your dharma is to let go and discover what remains.",
  },
  family: {
    Sun:     "Sun Dasha can bring authority conflicts within family — father figures or authority clashes. Respect hierarchies while holding your ground with dignity.",
    Moon:    "Moon Dasha heightens emotional sensitivity in family dynamics. Nurturing communication and creating safe emotional space can transform difficult relationships.",
    Mars:    "Mars Dasha triggers anger-based conflicts and power struggles in the home. Conscious effort to pause before reacting and channel energy outward (exercise) is essential.",
    Mercury: "Mercury Dasha brings misunderstandings through miscommunication. Clear, honest conversation — not assumption — resolves most family friction now.",
    Jupiter: "Jupiter Dasha generally brings harmony and blessings to family. Any tensions are temporary. Wisdom, patience, and generosity heal most family wounds during this period.",
    Venus:   "Venus Dasha favors harmony in close relationships. Family tensions ease with beauty, shared meals, and acts of love. Creative expression together helps deeply.",
    Saturn:  "Saturn Dasha brings karmic family lessons — old wounds surface to be resolved. This is a period of settling ancestral karma. Service and patience with elders heal deeply.",
    Rahu:    "Rahu Dasha can bring sudden disruptions or separations in family. Clarity about boundaries and avoiding manipulation protect family bonds during this time.",
    Ketu:    "Ketu Dasha brings emotional detachment from family dynamics. This can feel isolating but creates space for spiritual growth. Seek to understand rather than react.",
  },
};

const DASHA_REMEDY = {
  Sun:     { action:"Offer water to the rising Sun every morning", mantra:"Om Suryaya Namah · 108 times on Sundays", color:"Red or saffron on Sundays" },
  Moon:    { action:"Fast on Mondays. Offer milk to a Shiva temple", mantra:"Om Chandraya Namah · 108 times on Mondays", color:"White or silver on Mondays" },
  Mars:    { action:"Visit Hanuman temple on Tuesdays. Offer red flowers", mantra:"Hanuman Chalisa daily · especially Tuesdays", color:"Red on Tuesdays" },
  Mercury: { action:"Feed green gram or grass to cows on Wednesdays", mantra:"Om Budhaya Namah · 108 times on Wednesdays", color:"Green on Wednesdays" },
  Jupiter: { action:"Offer yellow sweets to Vishnu or a temple on Thursdays. Serve your guru or a teacher", mantra:"Om Gurave Namah · 108 times on Thursdays", color:"Yellow on Thursdays" },
  Venus:   { action:"Offer white flowers to Lakshmi on Fridays. Donate sugar or milk", mantra:"Om Shukraya Namah · 108 times on Fridays", color:"White or pink on Fridays" },
  Saturn:  { action:"Light sesame oil lamp on Saturdays. Feed crows and serve the elderly", mantra:"Om Shanicharaya Namah · 108 times on Saturdays", color:"Dark blue or black on Saturdays" },
  Rahu:    { action:"Visit Durga temple. Donate dark-colored food on Saturdays", mantra:"Durga Chalisa · on Saturdays", color:"Dark blue on Saturdays" },
  Ketu:    { action:"Visit Ganesha temple. Donate blankets to the poor", mantra:"Om Ketave Namah · 108 times", color:"Grey or smoke color" },
};

const CONCERN_REMEDY = {
  career: {
    action:"Visit Hanuman or Shani temple on Saturday. Offer sesame oil lamp and donate black sesame to the poor",
    mantra:"Om Hanumate Namah · 108 times every morning for 21 consecutive days",
    extra:"Recite Sundara Kanda on Tuesdays — Hanuman removes obstacles in work and effort",
  },
  marriage: {
    action:"Chant Katyayani mantra for 21 days. Light a ghee lamp before Goddess Parvati on Fridays and offer white flowers",
    mantra:"Om Katyayanaye Vidmahe Kanyakubje Dhimahi Tanno Durgah Prachodayat",
    extra:"Wear white or light yellow on Fridays. Donate white sweets to a temple",
  },
  finance: {
    action:"Light a ghee lamp before Lakshmi every Friday evening. Keep your home entrance clean and clutter-free — Lakshmi does not enter untidy homes",
    mantra:"Om Shreem Mahalakshmiyei Namah · 108 times on Fridays for 21 days",
    extra:"Wear yellow or gold on Thursdays. Place a bowl of raw rice near your pooja — replace it weekly",
  },
  health: {
    action:"Chant Mahamrityunjaya mantra 108 times daily. Offer bel leaves and water to Shiva every Monday morning before eating",
    mantra:"Om Tryambakam Yajamahe Sugandhim Pushtivardhanam — Urvarukamiva Bandhanan Mrityor Mukshiya Maamritat",
    extra:"Eat sattvic food — reduce meat, excess oil, and spice. Sunrise walks for 21 days strengthen the Sun's healing energy",
  },
  direction: {
    action:"Sit in silence at sunrise for 10 minutes before looking at any screen. Ask internally: what am I built for? Write whatever comes",
    mantra:"Om Namah Shivaya · 108 times daily — the mantra of self-realization and dharma clarity",
    extra:"Read one verse of the Bhagavad Gita each morning — Chapter 3 (Karma Yoga) speaks directly to the dharma seeker",
  },
  family: {
    action:"Light sandalwood agarbatti at home every evening at sunset. Offer water to the Tulasi plant daily with a prayer for peace",
    mantra:"Om Shantih Shantih Shantih — chant 3 times slowly before any difficult family conversation",
    extra:"For 21 days, do not raise your voice inside the home. This single practice changes the energy of the space more than any ritual",
  },
};

// Sub-concern specific remedies — override CONCERN_REMEDY when a sub is selected
const CONCERN_SUB_REMEDY = {
  "finance.income": {
    action:"Light a ghee lamp before Lakshmi every Friday evening. Keep your north or east wall clutter-free — the wealth direction in Vastu",
    mantra:"Om Shreem Hreem Kleem Mahalakshmiyei Namah · 108 times on Fridays for 21 days",
    extra:"For income growth specifically: donate rice or lentils every Thursday. Jupiter rules income expansion and responds to generosity",
  },
  "finance.debt": {
    action:"Visit a Shani temple on Saturdays. Offer sesame oil and black sesame. Light a mustard oil lamp for Saturn — the karaka of debt and its release",
    mantra:"Om Shanaishcharaya Namah · 108 times every Saturday for 21 days — specifically ask for release from debt burden",
    extra:"Saturn rules debt cycles. Service to the elderly or poor on Saturdays shortens Saturn's karmic hold faster than any other remedy",
  },
  "finance.losses": {
    action:"Perform Ganesha puja on the 4th day of each lunar fortnight. Offer modak and 21 durva grass blades — Ganesha removes obstacles and prevents further loss",
    mantra:"Om Gam Ganapataye Namah · 108 times every morning — for restoration after financial setback",
    extra:"Avoid new financial commitments for the next 21 days. Use this time to close leakages first, then build. Mars/Rahu transits are the most common cause of sudden losses",
  },
  "career.job": {
    action:"Offer water to the rising Sun every morning for 21 days. Request clarity on your career path and professional recognition",
    mantra:"Om Suryaya Namah · 108 times on Sundays — Sun governs authority, recognition, and promotions",
    extra:"For promotion specifically: keep a small Sun yantra or red cloth on your work desk. Avoid speaking negatively about superiors",
  },
  "career.business": {
    action:"Visit Hanuman temple every Tuesday. Offer sindoor and jasmine oil. Hanuman removes obstacles in ventures and effort-based work",
    mantra:"Hanuman Chalisa daily, especially on Tuesdays — for business revival and removal of blocks",
    extra:"Mercury governs business transactions. Light a green candle or lamp on Wednesdays and state your business intention clearly",
  },
  "career.change": {
    action:"Meditate on your Nakshatra deity for 10 minutes daily. Write one sentence each morning: 'My work in this life is ___' — clarity emerges from repetition",
    mantra:"Om Namah Shivaya · 108 times — Shiva dissolves old forms so the true direction can emerge",
    extra:"Before any career change: consult your 10th house and current dasha. Rahu periods are excellent for bold pivots. Saturn periods favor steady transitions over leaps",
  },
  "health.self": {
    action:"Chant Mahamrityunjaya mantra 108 times daily. Offer bel leaves and water to Shiva every Monday before eating",
    mantra:"Om Tryambakam Yajamahe Sugandhim Pushtivardhanam — Urvarukamiva Bandhanan Mrityor Mukshiya Maamritat",
    extra:"Sunrise walks for 21 days strengthen Sun's healing energy. Your Nakshatra rules specific body zones — the full health report maps which system needs attention now",
  },
  "health.family": {
    action:"Offer bel leaves and water to Shiva on Mondays on behalf of your family member. Light a ghee lamp and pray specifically for their recovery",
    mantra:"Mahamrityunjaya mantra · chant 108 times visualising the family member surrounded by healing light",
    extra:"For another person's health: Pitru Dosha or ancestral karma often manifests through family illness. Performing Pitru tarpan on Amavasya (new moon) day helps the whole family line",
  },
  "direction.career_path": {
    action:"Write your strengths and natural gifts without editing or judging for 10 minutes. Do this 3 mornings in a row. Your Nakshatra already knows the answer",
    mantra:"Om Gam Ganapataye Namah · 108 times — Ganesha opens the right doors and closes the wrong ones",
    extra:"Your 10th house (dharma house) and Lagna together define your career dharma. The full report maps exactly which field is aligned with your planetary blueprint",
  },
  "direction.purpose": {
    action:"Sit in silence at sunrise for 10 minutes before any screen. Ask: 'What is my function in this life?' Write whatever comes without editing",
    mantra:"Om Namah Shivaya · 108 times — the mantra of self-realization and dissolving false identity",
    extra:"Ketu's placement in your chart shows where your soul has already mastered. Rahu shows where you are being pulled to grow. Together they define your life's arc",
  },
  "direction.spiritual": {
    action:"Choose one sadhana (Gita verse, mantra, or pranayama) and do it every day at the same time for 40 consecutive days without missing",
    mantra:"Om Namah Shivaya or the Gayatri mantra — 108 times at sunrise, facing east",
    extra:"Ketu's house in your chart is your natural spiritual zone — where spiritual practice lands fastest. Your Nakshatra deity is your personal guide; invoke them by name",
  },
  "family.spouse": {
    action:"Light sandalwood agarbatti every evening after sunset. Sit in silence together for 5 minutes before sleep — no phones, no discussion",
    mantra:"Om Shantih Shantih Shantih — chant slowly 3 times before any tense conversation with your spouse",
    extra:"Venus governs marital harmony. Light a ghee lamp before Lakshmi on Fridays and pray specifically for peace between you two. Avoid major decisions during retrograde periods",
  },
  "family.parent_child": {
    action:"Offer water to the Sun at sunrise on Sundays with gratitude for your father/mother energy. Saturn remedy on Saturdays heals karma between parent and child",
    mantra:"Om Shanaishcharaya Namah · 108 times on Saturdays — Saturn rules karmic bonds between generations",
    extra:"4th house rules the home and mother. Saturn's transit through your 4th house is the most common cause of parent-child tension. Service to elders accelerates its resolution",
  },
  "family.siblings": {
    action:"Feed green grass or green fodder to cows on Wednesdays — Mercury rules siblings and communication bonds",
    mantra:"Om Budhaya Namah · 108 times on Wednesdays for 21 days — for clarity and peace in sibling relationships",
    extra:"3rd house rules siblings. Mercury affliction (Rahu or Mars aspect) causes miscommunication and perceived slights. Avoid sending important messages during Mercury retrograde",
  },
  "family.general": {
    action:"Light sandalwood agarbatti at home every evening at sunset. Offer water to the Tulasi plant daily with a prayer for peace in your home",
    mantra:"Om Shantih Shantih Shantih — chant 3 times slowly before any difficult family conversation",
    extra:"Sadhe Sati (Saturn transiting Moon sign) is the single most common cause of household heaviness across all family types. The full family report identifies exactly which family member's chart is creating the friction",
  },
  "marriage.looking": {
    action:"Chant Katyayani mantra for 21 days. Light a ghee lamp before Goddess Parvati on Fridays and offer white flowers",
    mantra:"Om Katyayanaye Vidmahe Kanyakubje Dhimahi Tanno Durgah Prachodayat",
    extra:"Venus in your chart defines the spouse you attract. Jupiter's transit over your 7th house is the single strongest marriage timing signal — the full report identifies your exact window",
  },
  "marriage.relationship": {
    action:"Light a ghee lamp before Lakshmi on Fridays together if possible. Offer white flowers and pray for clarity on the path forward",
    mantra:"Om Shukraya Namah · 108 times on Fridays — Venus governs committed relationships and deepening of bonds",
    extra:"7th house compatibility and Venus-Mars interplay between charts determines long-term alignment. The compatibility report maps this precisely",
  },
  "marriage.married": {
    action:"Light sandalwood agarbatti every evening. Sit together for 5 minutes in silence before sleep — no phones, just presence",
    mantra:"Om Shantih Shantih Shantih — chant together 3 times before bed for 21 days for household peace",
    extra:"For deepening marital harmony: offer white flowers to Lakshmi every Friday as a couple. Avoid major decisions when Venus is retrograde",
  },
  "marriage.family": {
    action:"Chant Katyayani mantra for 21 days. Light a ghee lamp before Parvati on Fridays — the energy reaches the family member you are praying for",
    mantra:"Om Katyayanaye Vidmahe Kanyakubje Dhimahi Tanno Durgah Prachodayat",
    extra:"For a family member's match, their birth chart gives the most accurate reading. General Shukra (Venus) strengthening benefits everyone seeking a life partner",
  },
};

// Sub-concern specific extra context lines — appended to the main reading
const CONCERN_SUB_EXTRA = {
  "finance.income":  "Your 11th house (income house) and its current transits determine when the flow resumes. The full wealth report maps the exact period when income picks up.",
  "finance.debt":    "Debt cycles are ruled by Saturn and the 12th house. The wealth report identifies the planetary window when your debt burden begins to lift.",
  "finance.losses":  "Recovery from financial losses is tied to Mars and the 8th house. The report maps when the reversal begins and which sectors to re-enter first.",
  "career.job":      "Promotion timing is a 10th house and Sun analysis. Your dasha is the primary factor — the career report maps exact windows when authority recognizes your work.",
  "career.business": "Business recovery follows your Mercury and Jupiter dasha cycles. The career report maps when conditions align for your business to turn around.",
  "career.change":   "Career pivot success depends heavily on Rahu's current placement. Rahu periods are the most powerful windows for professional reinvention.",
  "health.self":     "Your Nakshatra governs specific body zones that are vulnerable in your chart. The health report maps which systems need attention and the protective sadhana for each.",
  "health.family":   null,
  "direction.career_path": "Your 10th house and Lagna lord together define your dharma path. The Dharma Report maps the precise career field your chart is built for.",
  "direction.purpose":     "Your Rahu-Ketu axis is your soul's evolutionary contract. The full Dharma Report explains what your soul came here to master.",
  "direction.spiritual":   "Your Ketu house is your deepest spiritual zone — where practice lands fastest. Your Nakshatra deity is your personal guide into that domain.",
  "family.spouse":         "Venus and the 7th house in your chart govern marital harmony. The Family Harmony Report identifies the planetary cause and when the friction eases.",
  "family.parent_child":   "4th house and Saturn dynamics reveal the karmic pattern between parent and child. The report maps the resolution timing.",
  "family.siblings":       "3rd house and Mercury affliction are the root. The report identifies which transit is causing the friction and the exact end date.",
  "family.general":        "The Family Harmony Report analyzes your 4th house, Saturn transit, and Moon sign to identify the primary source of tension and its resolution window.",
  "marriage.looking":      "Jupiter transiting your 7th house is the single strongest marriage timing signal. The Marriage Timing Report maps this window precisely for your chart.",
  "marriage.relationship": "Your 7th house compatibility and Venus-Mars interplay determine long-term alignment. The compatibility section of the Marriage Report maps this.",
  "marriage.married":      "Your 7th house and Venus cycle determine periods of deepening versus friction in marriage. The report maps what's ahead.",
  "marriage.family":       "For an accurate reading for the family member, their birth chart is needed. The Marriage Timing Report can be run for their details separately.",
};

const NAKSHATRA_HEALTH = {
  Sun:     "heart, spine, and vitality",
  Moon:    "mind, emotions, and fluid balance",
  Mars:    "blood, muscles, and inflammation",
  Mercury: "nervous system and skin",
  Jupiter: "liver, fat, and circulation",
  Venus:   "kidneys and reproductive health",
  Saturn:  "bones, joints, and chronic conditions",
  Rahu:    "nervous disorders and mysterious ailments",
  Ketu:    "stomach and immunity",
};

function ConcernResultCard({ concern, subConcern, arcData, summary, onDismiss, setView }) {
  const ctx    = CONCERNS.find(c => c.id === concern);
  const subs   = CONCERN_SUBS[concern] || [];
  const subCtx = subs.find(s => s.id === subConcern);
  if (!ctx) return null;

  // Still loading — show shimmer so the card is visible while data fetches
  if (!arcData || !summary) return (
    <div style={{ borderRadius:"22px", marginBottom:"20px", overflow:"hidden",
      background:"#ffffff",
      border:"1px solid rgba(212,175,55,0.2)" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#d4a853,transparent)" }} />
      <div style={{ padding:"22px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"18px" }}>
          <span style={{ fontSize:"2rem" }}>{ctx.emoji}</span>
          <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
            color:"#2b2b2b" }}>
            Calculating your {ctx.title} reading…
          </div>
        </div>
        {[180, 220, 140].map((w,i) => (
          <div key={i} style={{ height:14, borderRadius:8, marginBottom:12,
            width:`${w}px`, maxWidth:"100%",
            background:"linear-gradient(90deg,rgba(212,175,55,0.08) 25%,rgba(212,175,55,0.18) 50%,rgba(212,175,55,0.08) 75%)",
            backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
        ))}
      </div>
    </div>
  );

  const dasha     = summary.maha_dasha || "Jupiter";
  const antardasha = summary.antardasha;
  const nakshatra = summary.nakshatra_name || arcData.nakshatra?.name?.en || "";
  const nakRuler  = summary.nakshatra_ruler || "";
  const rashi     = summary.rashi_western || arcData.rashi?.en || "";
  const rashiSkt  = summary.rashi_sanskrit || "";
  const lagna     = summary.lagna_western || "";
  const dashaEmoji = summary.maha_dasha_emoji || "🪐";
  const sadhe     = summary.sadhe_sati;
  const manglik   = summary.manglik;

  const notes        = DASHA_NOTES[concern] || DASHA_NOTES.career;
  const note         = notes[dasha] || notes.Jupiter;
  const subKey       = subConcern ? `${concern}.${subConcern}` : null;
  const remedy       = (subKey && CONCERN_SUB_REMEDY[subKey]) || CONCERN_REMEDY[concern] || CONCERN_REMEDY.career;
  const subExtra     = subKey ? CONCERN_SUB_EXTRA[subKey] : null;
  const dashaRemedy  = DASHA_REMEDY[dasha] || DASHA_REMEDY.Jupiter;

  // Concern-specific extra context
  const extraCtx = {
    health:    nakRuler ? `Your Nakshatra (${nakshatra}) is ruled by ${nakRuler}, governing your ${NAKSHATRA_HEALTH[nakRuler] || "overall vitality"}.${sadhe ? " Shani Sadhe Sati is also active — this is the main source of depletion right now." : ""}` : "",
    marriage:  manglik  ? `⚠️ Manglik Dosha is present in your chart — Mars in a sensitive house. This does NOT prevent marriage, but the right match and timing matter enormously. A Manglik partner or specific remedies nullify this completely.` : `No Manglik Dosha detected — your chart is compatible with a wide range of partners.`,
    family:    sadhe    ? `⚠️ Shani Sadhe Sati is currently active. Saturn transiting your Moon sign is the leading planetary cause of family friction and emotional heaviness. This cycle is time-bound — it will pass.` : `Saturn is not transiting your Moon sign currently. The friction is likely tied to your ${dasha} Dasha dynamics within the home.`,
    direction: arcData.archetype ? `Your Vedic archetype is ${arcData.archetype.en} — ${arcData.archetype.dharma}.` : "",
    career:    lagna    ? `Your Lagna (Ascendant) is ${lagna} — this shapes how you approach career and how the world sees your ambitions.` : "",
    finance:   sadhe    ? `⚠️ Shani Sadhe Sati is active — Saturn transiting your Moon is a known period of financial tightening for many. This has a fixed end date; enduring it wisely matters more than fighting it.` : "",
  };

  const REPORT_CTA = {
    career:    { label:"Get Career Timing Report",     price:"₹99", desc:"Full dasha map · career peaks · planetary remedies" },
    marriage:  { label:"Get Marriage Timing Report",   price:"₹99", desc:"Vivah Yog window · compatibility traits · remedies" },
    finance:   { label:"Get Wealth & Prosperity Report", price:"₹99", desc:"Wealth periods · 2nd/11th house analysis · remedies" },
    health:    { label:"Get Health & Vitality Report", price:"₹99", desc:"6th/8th house analysis · nakshatra health · remedies" },
    direction: { label:"Get Full Dharma Report",       price:"₹149", desc:"Archetype depth · life purpose · Gita guidance" },
    family:    { label:"Get Family Harmony Report",    price:"₹99", desc:"4th house analysis · Saturn transit · peace remedies" },
  };
  const reportCta = REPORT_CTA[concern] || REPORT_CTA.career;

  const accent = "#d4a853";

  return (
    <div style={{ animation:"fadeInUp 0.4s ease", marginBottom:"20px" }}>
      {/* Card */}
      <div style={{
        position:"relative", overflow:"hidden",
        background:"#ffffff",
        border:"1px solid rgba(212,175,55,0.35)", borderRadius:"22px",
        boxShadow:JT.shadowLift,
      }}>
        <div style={{ height:3, background:"linear-gradient(90deg,transparent,#d4a853,#f5d17a,#d4a853,transparent)" }} />
        <div style={{ position:"relative", zIndex:1, padding:"22px 22px 26px" }}>

          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"18px" }}>
            <span style={{ fontSize:"2rem", filter:"drop-shadow(0 0 12px rgba(212,175,55,0.5))" }}>{subCtx ? subCtx.emoji : ctx.emoji}</span>
            <div>
              <div style={{ fontSize:"1.0rem", letterSpacing:"0.18em", color:"#d4a853",
                fontFamily:JT.heading,  }}>Your Reading</div>
              <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
                color:"#2b2b2b", marginTop:"2px" }}>{ctx.title}</div>
              {subCtx && (
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                  color:"#666666", marginTop:"3px" }}>
                  {subCtx.label}
                </div>
              )}
            </div>
          </div>

          {/* Chart context strip */}
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"20px" }}>
            {[
              { label:rashiSkt || rashi, icon:"🌙", sub:"Rashi" },
              { label:`${dashaEmoji} ${dasha}${antardasha ? ` → ${antardasha}` : ""}`, icon:null, sub:"Dasha" },
              nakshatra && { label:nakshatra, icon:"⭐", sub:"Nakshatra" },
            ].filter(Boolean).map((p,i) => (
              <div key={i} style={{
                background:"rgba(212,175,55,0.07)", border:"1px solid rgba(212,175,55,0.2)",
                borderRadius:"12px", padding:"7px 14px",
              }}>
                <div style={{ fontSize:"1.0rem", color:"rgba(212,175,55,0.6)",
                  fontFamily:JT.heading, letterSpacing:"0.1em", marginBottom:"2px" }}>{p.sub}</div>
                <div style={{ fontSize:"1rem", color:"#2b2b2b",
                  fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                  {p.icon && <span style={{ marginRight:4 }}>{p.icon}</span>}{p.label}
                </div>
              </div>
            ))}
          </div>

          {/* Main reading */}
          <div style={{
            background:JT.card, border:"1px solid rgba(212,175,55,0.15)", boxShadow:JT.shadow,
            borderLeft:"3px solid #d4a853",
            borderRadius:"14px", padding:"16px 18px", marginBottom:"16px",
          }}>
            <div style={{ fontSize:"1.0rem", letterSpacing:"0.14em", color:"#d4a853",
              fontFamily:JT.heading, marginBottom:"10px" }}>
              {concern === "marriage" && subConcern === "married"
                ? "🔭 Your Marital Harmony"
                : "🔭 What Your Chart Says"}
            </div>
            {subCtx && (
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color:"#b07818", fontStyle:"italic", marginBottom:"8px" }}>
                Based on what you shared: {subCtx.label}
              </div>
            )}
            {concern === "marriage" && subConcern === "married" ? (
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem",
                fontWeight:600, color:"#2b2b2b", lineHeight:1.9 }}>
                {/* Re-frame marriage note toward existing union harmony */}
                {note.replace(/marriage can happen/gi, "your bond can deepen")
                     .replace(/marriage is possible/gi, "your union continues")
                     .replace(/delays marriage/gi, "slows expansion but strengthens what is built")
                     .replace(/started now/gi, "in this period")}
              </div>
            ) : (
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem",
                fontWeight:600, color:"#2b2b2b", lineHeight:1.9 }}>
                {note}
              </div>
            )}
            {extraCtx[concern] && (
              <div style={{ marginTop:"12px", fontFamily:"'Inter',sans-serif", fontSize:"1.1rem",
                color:"#666666", lineHeight:1.75, fontStyle:"italic" }}>
                {extraCtx[concern]}
              </div>
            )}
            {concern === "health" && subConcern === "family" && (
              <div style={{ marginTop:"12px", fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color:"#b07818", lineHeight:1.7,
                borderTop:"1px solid rgba(212,175,55,0.12)", paddingTop:"10px" }}>
                ℹ️ This reading reflects patterns from your own chart. For a precise reading for your family member, their birth details give far greater accuracy.
              </div>
            )}
            {subExtra && concern !== "health" && subConcern !== "family" && (
              <div style={{ marginTop:"12px", fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color:"#b07818", lineHeight:1.7,
                borderTop:"1px solid rgba(212,175,55,0.12)", paddingTop:"10px" }}>
                {subExtra}
              </div>
            )}
          </div>

          {/* Remedy */}
          <div style={{
            background:"rgba(212,175,55,0.06)", border:"1px solid rgba(212,175,55,0.18)",
            borderRadius:"14px", padding:"16px 18px", marginBottom:"20px",
          }}>
            <div style={{ fontSize:"1.0rem", letterSpacing:"0.14em", color:"#d4a853",
              fontFamily:JT.heading, marginBottom:"12px" }}>
              🕉️ Remedy{subCtx ? ` — ${subCtx.label}` : ` for ${ctx.title}`}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
                fontWeight:600, color:"#2b2b2b", lineHeight:1.75 }}>
                <span style={{ color:"#b07818", fontSize:"1.0rem",
                  fontFamily:JT.heading, letterSpacing:"0.1em",
                  display:"block", marginBottom:"3px" }}>ACTION</span>
                {remedy.action}
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.15rem",
                fontWeight:500, color:"#2b2b2b", lineHeight:1.75,
                fontStyle:"italic" }}>
                <span style={{ color:"#b07818", fontSize:"1.0rem",
                  fontFamily:JT.heading, letterSpacing:"0.1em", fontStyle:"normal",
                  display:"block", marginBottom:"3px" }}>MANTRA</span>
                {remedy.mantra}
              </div>
              {remedy.extra && (
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.05rem",
                  color:"#666666", lineHeight:1.7,
                  borderTop:"1px solid rgba(212,175,55,0.12)", paddingTop:"10px" }}>
                  {remedy.extra}
                </div>
              )}
            </div>
            {/* Secondary: dasha planet remedy */}
            <div style={{ marginTop:"12px", borderTop:"1px solid rgba(212,175,55,0.1)",
              paddingTop:"10px" }}>
              <div style={{ fontSize:"1.0rem", letterSpacing:"0.1em", color:"rgba(212,175,55,0.5)",
                fontFamily:JT.heading, marginBottom:"5px" }}>
                ALSO — {dashaEmoji} {dasha} Dasha Remedy
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color:"#666666", lineHeight:1.6 }}>
                {dashaRemedy.action} · {dashaRemedy.mantra}
              </div>
            </div>
          </div>

          {/* Paid report CTA */}
          <div style={{
            background:"linear-gradient(135deg,rgba(212,175,55,0.12),rgba(180,130,50,0.08))",
            border:"1px solid rgba(212,175,55,0.3)", borderRadius:"16px",
            padding:"16px 18px", marginBottom:"18px",
            display:"flex", alignItems:"center", justifyContent:"space-between", gap:"12px",
          }}>
            <div>
              <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
                color:"#f5d17a", marginBottom:"4px" }}>{reportCta.label}</div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1rem",
                color:"#666666", lineHeight:1.5 }}>{reportCta.desc}</div>
            </div>
            <button onClick={() => setView("reports")} style={{
              background:"linear-gradient(135deg,#c9a84c,#d4a853)",
              border:"none", borderRadius:"12px", padding:"11px 20px",
              color:"#0d0a04", fontFamily:JT.heading,
              fontSize:"1.05rem", fontWeight:800, cursor:"pointer",
              whiteSpace:"nowrap", flexShrink:0,
              boxShadow:"0 4px 16px rgba(212,175,55,0.4)",
            }}>{reportCta.price}</button>
          </div>

          {/* Dismiss */}
          <button onClick={onDismiss} style={{
            width:"100%", background:JT.card,
            border:`1px solid ${JT.border}`, borderRadius:"12px",
            padding:"12px", color:JT.textDim,
            fontFamily:"'Inter',sans-serif", fontSize:"1rem",
            cursor:"pointer", transition:"all 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background=JT.cardWarm; e.currentTarget.style.color="#2b2b2b"; }}
            onMouseLeave={e => { e.currentTarget.style.background=JT.card; e.currentTarget.style.color=JT.textDim; }}
          >
            Explore all features →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Daily Retention — psychological triggers + return visitor awareness ───────
function DailyRetentionBanner({ summary, setView, user }) {
  const today = new Date().toISOString().slice(0, 10);
  const lastKey = `jyotish_last_${user.id}`;
  const isReturn = React.useMemo(() => {
    const v = localStorage.getItem(lastKey);
    return v && v !== today;
  }, []);

  React.useEffect(() => {
    localStorage.setItem(lastKey, today);
  }, []);

  // Shani transit alerts (dhaiya / small panoti) — separate from the natal
  // doshas below, and not covered by /jyotish/summary, so fetched here directly.
  const [shaniTransit, setShaniTransit] = React.useState(null);
  React.useEffect(() => {
    fetch(`${API}/jyotish/daily-briefing/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { if (d.shani_alert) setShaniTransit(d.shani_alert); })
      .catch(() => {});
  }, [user.id]);

  // Collect ALL active doshas — show everything, not just top priority
  const activeAlerts = [];

  if (shaniTransit && shaniTransit.type === "dhaiya") {
    activeAlerts.push({
      icon:"🪐", color:"#fbbf24", btn:"#B45309",
      bg:"rgba(251,191,36,0.08)", border:"rgba(251,191,36,0.35)",
      title:"Shani Dhaiya Active",
      phase:`${shaniTransit.days_left} days left in this cycle`,
      msg:"Saturn is transiting a house that brings friction and tests of patience — a 2.5-year period. Not as intense as Sadhe Sati, but real. Know the remedies.",
      cta:"Shani Remedies", view:"dosha",
    });
  }

  if (summary.sadhe_sati) {
    const phaseLabel = { peak:"PEAK PHASE", rising:"Rising Phase", setting:"Setting Phase" }[summary.sadhe_sati_phase] || "Active";
    const phaseDesc = {
      peak:"Saturn sits directly on your Moon sign — the most intense 2.5 years of the 7½-year cycle",
      rising:"Saturn enters your sky — a period of inner pressure and forced change begins",
      setting:"Saturn moves through your 2nd house — financial & family tests are common now",
    }[summary.sadhe_sati_phase] || "Saturn transiting near your Moon sign";
    const isPeak = summary.sadhe_sati_phase === "peak";
    activeAlerts.push({
      icon:"🪐", color: isPeak ? "#ef4444" : "#fbbf24",
      // Button needs a dark shade: white on #fbbf24 is ~1.9:1, unreadable.
      btn: isPeak ? "#B91C1C" : "#B45309",
      bg: isPeak ? "rgba(239,68,68,0.1)" : "rgba(251,191,36,0.08)",
      border: isPeak ? "rgba(239,68,68,0.4)" : "rgba(251,191,36,0.35)",
      title: isPeak ? "⚠ Sadhe Sati — Peak Phase Active" : "Shani Sadhe Sati Active",
      phase: `${phaseLabel} · ${phaseDesc}`,
      msg: isPeak
        ? "This is the most challenging phase of Saturn's 7½-year cycle. Most people feel its weight in career, health, and relationships simultaneously. Remedies matter most right now."
        : "Saturn's transit is reshaping your career, relationships, and inner life. Know how to navigate it with the right remedies.",
      cta: isPeak ? "Urgent — Get Remedies" : "Shani Remedies",
      view:"dosha",
    });
  }

  if (summary.manglik) activeAlerts.push({
    icon:"⚠️", color:"#f87171", btn:"#B91C1C",
    bg:"rgba(239,68,68,0.07)", border:"rgba(239,68,68,0.28)",
    title:"Manglik Dosha Detected",
    phase:"Affects marriage compatibility & relationship harmony",
    msg:"Mars occupies a sensitive house in your chart. This is the most searched dosha in Indian marriage astrology — critical to understand before any major relationship decision.",
    cta:"See Remedies", view:"dosha",
  });

  if (summary.kaal_sarp) activeAlerts.push({
    icon:"🐍", color:"#a78bfa", btn:"#6D28D9",
    bg:"rgba(139,92,246,0.07)", border:"rgba(139,92,246,0.28)",
    title:"Kaal Sarp Dosha — All Planets Between Rahu-Ketu",
    phase:"Intense karmic pattern — affects life path & recurring struggles",
    msg:"A rare and powerful configuration. People with this dosha often face repeating life patterns until they understand its root cause. See what yours reveals.",
    cta:"See Full Analysis", view:"dosha",
  });

  // Note: no dosha-free fallback here — current Dasha already lives in the
  // identity card above, so this banner only appears when there's an actual alert.

  const primary = activeAlerts[0];
  const extras  = activeAlerts.slice(1);

  return (
    <div style={{ marginBottom:18 }}>
      {isReturn && (
        <div style={{ textAlign:"center", fontSize:"1rem",
          color:"#5a5a5a", fontFamily:JT.label, fontWeight:600, marginBottom:12 }}>
          🌙 Welcome back — here's today's update
        </div>
      )}
      {primary && (
        <div style={{ marginBottom:12 }}>
          {activeAlerts.length > 1 && (
            <div style={{ fontSize:"2rem", fontFamily:JT.label, fontWeight:800,
              color:"#1a1a1a", letterSpacing:"-0.01em", lineHeight:1.15,
              marginBottom:12 }}>
              Happening in your chart right now
            </div>
          )}
          {/* Primary alert — prominent */}
          <div style={{ background:primary.bg, border:`1.5px solid ${primary.border}`,
            borderRadius:16, padding:"18px 20px", marginBottom: extras.length ? 10 : 0,
            boxShadow:JT.shadow }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
              <span style={{ fontSize:"1.9rem", flexShrink:0, marginTop:2 }}>{primary.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:JT.label, fontSize:"1.15rem",
                  color:"#1a1a1a", fontWeight:700, marginBottom:6, lineHeight:1.3 }}>
                  {primary.title}
                </div>
                <div style={{ fontSize:"1rem",
                  color:"#4a4a4a", fontFamily:JT.label, fontWeight:600, marginBottom:8, lineHeight:1.4 }}>
                  {primary.phase}
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1.05rem",
                  color:"#2b2b2b", lineHeight:1.6 }}>
                  {primary.msg}
                </div>
              </div>
            </div>
            <button onClick={() => setView(primary.view)}
              style={{ display:"block", width:"100%", maxWidth:340, margin:"0 auto",
                background:primary.btn || primary.color, border:"none",
                borderRadius:10, padding:"14px 18px", minHeight:48, color:"#fff",
                fontFamily:JT.label, fontSize:"1.05rem",
                cursor:"pointer", fontWeight:700,
                boxShadow:`0 4px 16px ${(primary.btn || primary.color)}44` }}>
              {primary.cta} →
            </button>
          </div>
          {/* Secondary alerts — compact rows */}
          {extras.map((a, i) => (
            <div key={i} onClick={() => setView(a.view)}
              style={{ background:a.bg, border:`1px solid ${a.border}`,
                borderRadius:10, padding:"12px 14px", marginBottom:6,
                display:"flex", alignItems:"center", gap:10, cursor:"pointer",
                transition:"all 0.15s" }}>
              <span style={{ fontSize:"1.2rem", flexShrink:0 }}>{a.icon}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:JT.label, fontSize:"1.05rem",
                  color:"#1a1a1a", fontWeight:700 }}>{a.title}</div>
                <div style={{ fontFamily:JT.label, fontSize:"1rem",
                  color:"#4a4a4a", lineHeight:1.3 }}>{a.phase}</div>
              </div>
              <span style={{ color:a.color, fontSize:"1.2rem", flexShrink:0 }}>→</span>
            </div>
          ))}
        </div>
      )}

      {/* Note: the old "Free tools" strip lived here. Every tile in it was
          repeated in the "Choose what to explore" grid below, so it was two
          menus for one set of features. Removed — the grid is the single
          place to browse features. */}
    </div>
  );
}

// ── Entry Summary Card ────────────────────────────────────────────────────────
function JyotishSummaryCard({ summary, setView, archetype, lang }) {
  const [collapsed, setCollapsed] = React.useState(() => {
    return localStorage.getItem("jyotish_snapshot_collapsed") !== "0";
  });
  const cardRef = React.useRef(null);
  const posterRef = React.useRef(null);

  if (!summary) return null;

  const downloadSnapshot = async () => {
    if (!posterRef.current) return;
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(posterRef.current, {
        backgroundColor: "#fdf6e0",
        scale: 3,
        useCORS: true,
        logging: false,
        removeContainer: true,
        allowTaint: false,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = "kundali-snapshot.png";
      a.click();
    } catch(e) {
      console.error("Snapshot download failed:", e);
    }
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("jyotish_snapshot_collapsed", next ? "1" : "0");
  };

  // Warm parchment, borrowed from the printed Kundali snapshot poster below so
  // the on-screen chart reads as the same artifact you can download.
  // Scoped to this hero card — the rest of the Jyotish theme stays white.
  // Auspicious palette only — turmeric-stained paper, gold, kumkum.
  // Never dark/black here: a janma kundali is a sacred object and black
  // reads as ashubh. Depth comes from saturation, not from darkness.
  const PARCHMENT  = "linear-gradient(158deg,#fff8e3 0%,#fbeec2 52%,#f6e0a6 100%)";
  const PARCH_LINE = "#c9902a";                 // gold leaf
  const PARCH_RULE = "rgba(155,32,32,0.28)";    // kumkum-red inner rule

  // Identity facts, in the order a pandit would read them out.
  const facts = [
    summary.rashi_sanskrit && {
      label: "Moon Rashi",
      value: summary.rashi_sanskrit,
      note:  summary.rashi_western,
    },
    summary.nakshatra_name && {
      label: "Nakshatra",
      value: summary.nakshatra_name,
      note:  summary.nakshatra_pada ? `Pada ${summary.nakshatra_pada}` : null,
    },
    summary.lagna_sanskrit && {
      label: "Lagna",
      value: summary.lagna_sanskrit,
      note:  summary.lagna_western,
    },
    archetype && {
      label: "Archetype",
      value: archetype[lang] || archetype.en,
      note:  null,
    },
  ].filter(Boolean);

  const doshas = [];
  if (summary.manglik)    doshas.push({ label:"Manglik",    active:true,  icon:"⚠️", tip:"Mars in sensitive house" });
  if (summary.kaal_sarp)  doshas.push({ label:"Kaal Sarp",  active:true,  icon:"🐍", tip:"All planets between Rahu-Ketu" });
  if (summary.sadhe_sati) doshas.push({ label:"Sadhe Sati", active:true,  icon:"🪐", tip:"Saturn transiting moon" });
  if (!summary.manglik)   doshas.push({ label:"No Manglik", active:false, icon:"✓",  tip:"Mars neutral" });
  if (!summary.kaal_sarp) doshas.push({ label:"No Kaal Sarp",active:false,icon:"✓", tip:"No Rahu-Ketu trap" });

  return (
    <div style={{ width:"100%", margin:"0 0 20px", animation:"fadeInUp 0.4s ease", position:"relative" }}>
      <div ref={cardRef} style={{
        background:"#ffffff",
        border:`1px solid ${JT.border}`,
        borderRadius:"16px", overflow:"hidden", position:"relative",
        boxShadow:JT.shadow,
      }}>
        {/* ── Card header (always visible) ── */}
        <div onClick={toggleCollapse} style={{ cursor:"pointer",
          padding:"16px 20px", position:"relative",
          borderBottom: collapsed ? "none" : `1px solid ${JT.border}`,
        }}>
          {/* Title row */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: collapsed ? 12 : 0 }}>
            <div>
              <div style={{ fontFamily:JT.label, fontSize:"1.3rem",
                color:JT.text, lineHeight:1.2, fontWeight:700 }}>
                Janma Kundli Snapshot
              </div>
              <div style={{ fontFamily:JT.label, fontSize:"1rem",
                color:JT.textDim, marginTop:4 }}>
                Your Vedic birth chart identity
              </div>
            </div>
            {/* Expand / Close affordance */}
            {collapsed ? (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                color:JT.textDim, flexShrink:0, marginLeft:8 }}>
                <div style={{ fontSize:"1.4rem", lineHeight:1 }}>›</div>
                <div style={{ fontFamily:JT.label, fontSize:"0.95rem", whiteSpace:"nowrap" }}>Open</div>
              </div>
            ) : (
              <div style={{
                display:"flex", alignItems:"center", gap:5,
                background:"#f5f5f5", border:`1px solid ${JT.border}`,
                borderRadius:8, padding:"5px 10px", cursor:"pointer",
                color:JT.textDim, flexShrink:0, marginLeft:8,
              }}>
                <span style={{ fontSize:"1.0rem" }}>✕</span>
                <span style={{ fontFamily:JT.label, fontSize:"0.95rem", fontWeight:700 }}>Close</span>
              </div>
            )}
          </div>

          {/* Collapsed hero: the real chart, then the facts it produced.
              The chart carries the authenticity — a text label never will. */}
          {collapsed && (
            <div style={{
              display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",
              gap:18, alignItems:"center", marginTop:4,
            }}>
              {/* Janma kundali, cast from the user's own birth details */}
              {summary.chart_planets && summary.chart_lagna && (
                <div style={{
                  background:PARCHMENT, border:`1px solid ${PARCH_LINE}`,
                  borderRadius:14, padding:"18px 16px 14px",
                  display:"flex", flexDirection:"column", alignItems:"center",
                  position:"relative",
                  boxShadow:"0 2px 16px rgba(160,110,20,0.12)",
                }}>
                  {/* Inner double-rule — the frame a printed kundali carries */}
                  <div style={{ position:"absolute", inset:6, borderRadius:10,
                    border:`1px solid ${PARCH_RULE}`, pointerEvents:"none" }} />
                  <div style={{ width:"100%", maxWidth:230 }}>
                    <NorthIndianChartSVG
                      planets={summary.chart_planets}
                      lagna={summary.chart_lagna}
                      lightMode={true}
                    />
                  </div>
                  <div style={{ fontFamily:JT.label, fontSize:"1rem",
                    color:"#4a3a1a", textAlign:"center", lineHeight:1.5, marginTop:10 }}>
                    Cast from your date, time and place of birth
                  </div>
                </div>
              )}

              {/* What the chart says about you */}
              <div>
                {facts.map((f, i) => (
                  <div key={f.label} style={{
                    display:"flex", alignItems:"baseline", justifyContent:"space-between",
                    gap:14, flexWrap:"wrap",
                    padding:"11px 0",
                    borderTop: i === 0 ? "none" : `1px solid ${JT.border}`,
                  }}>
                    <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim }}>
                      {f.label}
                    </span>
                    <span style={{ fontFamily:JT.label, fontSize:"1.2rem",
                      color:JT.text, fontWeight:700, textAlign:"right" }}>
                      {f.value}
                      {f.note && (
                        <span style={{ fontSize:"1rem", color:JT.textDim, fontWeight:400 }}>
                          {"  "}· {f.note}
                        </span>
                      )}
                    </span>
                  </div>
                ))}

                <button style={{
                  marginTop:14, width:"100%", maxWidth:420,
                  minHeight:48, padding:"13px 18px",
                  background:JT.gold, border:"none", borderRadius:10,
                  color:"#ffffff", fontFamily:JT.label,
                  fontSize:"1.05rem", fontWeight:700, cursor:"pointer",
                }}>
                  Open full kundali & download
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Expanded body ── */}
        {!collapsed && (
          <div style={{ padding:"12px 18px 18px" }}>

            {/* Janma Rashi */}
            {summary.rashi_western && (
              <div style={{ background:"#ffffff", border:`1px solid ${JT.border}`, borderRadius:10, padding:"14px 16px", marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:"1.1rem" }}>🌙</span>
                  <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, fontWeight:700 }}>Janma Rashi · Moon Sign</span>
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1.5rem", color:JT.text, fontWeight:700, lineHeight:1.1, marginBottom:8 }}>
                  {summary.rashi_sanskrit ? `${summary.rashi_sanskrit} · ${summary.rashi_western}` : summary.rashi_western}
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, lineHeight:1.55 }}>
                  The sign the Moon occupied at your birth — shapes emotion, instinct, and inner nature
                </div>
              </div>
            )}

            {/* Lagna */}
            <div style={{ background:"#ffffff", border:`1px solid ${JT.border}`, borderRadius:10, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                <span style={{ fontSize:"1rem" }}>▲</span>
                <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, fontWeight:700 }}>Lagna · Ascendant</span>
              </div>
              <div style={{ fontFamily:JT.label, fontSize:"1.5rem", color: summary.lagna_sanskrit ? JT.text : "#999", fontWeight:700, lineHeight:1.1, marginBottom: summary.lagna_sanskrit ? 8 : 0 }}>
                {summary.lagna_sanskrit ? `${summary.lagna_sanskrit} · ${summary.lagna_western}` : "Add birth city to unlock"}
              </div>
              {summary.lagna_sanskrit && (
                <div style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, lineHeight:1.55 }}>
                  Rising sign at the eastern horizon — shapes your outer personality and life path
                </div>
              )}
            </div>

            {/* Nakshatra */}
            {summary.nakshatra_name && (
              <div style={{ background:"#ffffff", border:`1px solid ${JT.border}`, borderRadius:10, padding:"14px 16px", marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:"1.1rem" }}>{summary.nakshatra_symbol || "⭐"}</span>
                  <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, fontWeight:700 }}>Janma Nakshatra · Birth Star</span>
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1.5rem", color:JT.text, fontWeight:700, lineHeight:1.1, marginBottom: summary.nakshatra_ruler ? 8 : 0 }}>
                  {summary.nakshatra_name}
                </div>
                {summary.nakshatra_ruler && (
                  <div style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, lineHeight:1.55 }}>
                    Ruled by {summary.nakshatra_ruler}{summary.nakshatra_pada ? ` · Pada ${summary.nakshatra_pada}` : ""} — the lunar mansion that shapes your deepest instincts
                  </div>
                )}
              </div>
            )}

            {/* Current Dasha */}
            {summary.maha_dasha && (
              <div style={{ background:"#ffffff", border:`1px solid ${JT.border}`, borderRadius:10, padding:"14px 16px", marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:"1.1rem" }}>{summary.maha_dasha_emoji || "🪐"}</span>
                  <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, fontWeight:700 }}>Current Dasha · Planetary Period</span>
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1.5rem", color:JT.text, fontWeight:700, lineHeight:1.1, marginBottom:8 }}>
                  {summary.maha_dasha} Dasha{summary.antardasha ? ` → ${summary.antardasha}` : ""}
                </div>
                <div style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, lineHeight:1.55 }}>
                  The ruling planet period shaping your life themes right now
                </div>
              </div>
            )}

            {/* Archetype */}
            {archetype && (
              <div onClick={() => setView("archetype")} style={{ background:"#ffffff", border:`1px solid ${JT.border}`, borderRadius:10, padding:"14px 16px", marginBottom:12, cursor:"pointer" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:"1.1rem" }}>{archetype.emoji || "🔱"}</span>
                  <span style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, fontWeight:700 }}>Archetype · Character</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                  <div>
                    <div style={{ fontFamily:JT.label, fontSize:"1.5rem", color:JT.text, fontWeight:700, lineHeight:1.1, marginBottom: archetype.dharma ? 8 : 0 }}>
                      {archetype[lang] || archetype.en || "—"}
                    </div>
                    {archetype.dharma && (
                      <div style={{ fontFamily:JT.label, fontSize:"1rem", color:JT.textDim, lineHeight:1.55 }}>
                        {archetype.dharma}
                      </div>
                    )}
                  </div>
                  <span style={{ color:JT.textDim, fontSize:"1rem", flexShrink:0 }}>›</span>
                </div>
              </div>
            )}

            {/* Branding */}
            <div style={{ padding:"10px 0 4px", textAlign:"center", borderTop:`1px solid ${JT.border}`, marginTop:4 }}>
              <div style={{ fontFamily:JT.label, fontSize:"0.95rem", color:JT.textDim, fontWeight:600 }}>
                Vedsanga · वेदसङ्ग — Vedic Astrology
              </div>
            </div>

            {/* Download button */}
            <div data-html2canvas-ignore="true" style={{ padding:"12px 0 0" }}>
              <button onClick={downloadSnapshot} style={{
                width:"100%", padding:"12px",
                background:"#ffffff",
                border:`1px solid ${JT.borderStrong}`, borderRadius:10,
                color:JT.text, fontFamily:JT.label,
                fontSize:"1.05rem", fontWeight:700, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              }}
                onMouseEnter={e => { e.currentTarget.style.background="#f5f5f5"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#ffffff"; }}
              >
                <span style={{ fontSize:"1rem" }}>📥</span>
                Download Kundali Snapshot
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Hidden premium download poster — captured for gallery-quality image ── */}
      <div ref={posterRef} style={{
        position:"absolute", top:0, left:"-9999px",
        width:"420px",
        background:"linear-gradient(165deg,#fffef8 0%,#fdf8e8 35%,#fefcf0 70%,#ffffff 100%)",
        border:"3px solid #c9902a",
        overflow:"hidden",
      }}>
        {/* Mandala watermark */}
        <div style={{
          position:"absolute", top:"50%", left:"50%",
          width:320, height:320,
          marginTop:-160, marginLeft:-160,
          borderRadius:"50%",
          border:"1.5px solid rgba(180,130,25,0.1)",
          pointerEvents:"none", zIndex:0,
        }}>
          <div style={{ position:"absolute", inset:36, borderRadius:"50%", border:"1.5px solid rgba(180,130,25,0.08)" }} />
          <div style={{ position:"absolute", inset:72, borderRadius:"50%", border:"1.5px solid rgba(180,130,25,0.07)" }} />
          <div style={{ position:"absolute", inset:108, borderRadius:"50%", border:"1px solid rgba(180,130,25,0.05)" }} />
          <div style={{ position:"absolute", top:"50%", left:0, right:0, height:1, background:"rgba(180,130,25,0.07)", marginTop:-0.5 }} />
          <div style={{ position:"absolute", left:"50%", top:0, bottom:0, width:1, background:"rgba(180,130,25,0.07)", marginLeft:-0.5 }} />
        </div>
        {/* Inner frame */}
        <div style={{ position:"absolute", inset:8, border:"1px solid rgba(180,130,25,0.22)", pointerEvents:"none", zIndex:1 }} />
        {/* Top gold bar */}
        <div style={{ height:8, background:"linear-gradient(90deg,#7a4808,#c9902a,#f5d17a,#fff0a0,#f5d17a,#c9902a,#7a4808)", position:"relative", zIndex:2 }} />

        {/* Header */}
        <div style={{ padding:"28px 32px 0", position:"relative", zIndex:2, textAlign:"center" }}>
          <div style={{
            display:"inline-flex", alignItems:"center", justifyContent:"center",
            width:62, height:62, borderRadius:"50%",
            background:"radial-gradient(circle at 40% 35%,#f8e8a8,#d4a030 50%,#9a6010 100%)",
            border:"2.5px solid #e0c050",
            fontSize:"1.6rem", color:"#fff8e0",
            fontFamily:"'Noto Sans Devanagari',sans-serif", fontWeight:700,
            boxShadow:"0 6px 20px rgba(140,90,10,0.4), inset 0 1px 3px rgba(255,240,160,0.5)",
            marginBottom:18,
          }}>ॐ</div>
          <div style={{
            fontFamily:JT.heading,
            fontSize:"1.6rem", color:"#5a2a04", fontWeight:900,
            letterSpacing:"0.04em", lineHeight:1.2, marginBottom:10,
          }}>Janma Kundli<br/>Snapshot</div>
          <div style={{
            fontFamily:JT.heading,
            fontSize:"1.0rem", color:"#9a6010",
             fontWeight:700,
            marginBottom:20,
          }}>Your Vedic Birth Chart Identity</div>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:24 }}>
            <div style={{ flex:1, height:1, background:"linear-gradient(90deg,transparent,rgba(180,130,25,0.6))" }} />
            <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#c9902a", letterSpacing:6 }}>✦ ✦ ✦</span>
            <div style={{ flex:1, height:1, background:"linear-gradient(90deg,rgba(180,130,25,0.6),transparent)" }} />
          </div>
        </div>

        {/* Data sections — card style, no italic, min 1.1rem detail */}
        <div style={{ padding:"0 24px", position:"relative", zIndex:2 }}>

          {/* Janma Rashi */}
          {summary.rashi_western && (
            <div style={{
              background:"rgba(139,37,0,0.05)", border:"1px solid rgba(139,37,0,0.18)",
              borderRadius:10, padding:"16px 18px", marginBottom:14,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, paddingBottom:10, borderBottom:"1px solid rgba(139,37,0,0.12)" }}>
                <span style={{ fontSize:"1.2rem" }}>🌙</span>
                <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#6a2004",  fontWeight:800 }}>
                  Janma Rashi · Moon Sign
                </span>
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.75rem", color:"#8B2500", fontWeight:700, lineHeight:1.1, marginBottom:10 }}>
                {summary.rashi_sanskrit ? `${summary.rashi_sanskrit} · ${summary.rashi_western}` : summary.rashi_western}
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem", color:"#5a3010", lineHeight:1.6, fontWeight:400 }}>
                The sign the Moon occupied at your birth — shapes emotion, instinct, and inner nature
              </div>
            </div>
          )}

          {/* Lagna */}
          {summary.lagna_sanskrit && (
            <div style={{
              background:"rgba(45,27,105,0.05)", border:"1px solid rgba(45,27,105,0.18)",
              borderRadius:10, padding:"16px 18px", marginBottom:14,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, paddingBottom:10, borderBottom:"1px solid rgba(45,27,105,0.12)" }}>
                <span style={{ fontSize:"1.1rem", color:"#2D1B69" }}>▲</span>
                <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#2D1B69",  fontWeight:800 }}>
                  Lagna · Ascendant
                </span>
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.75rem", color:"#2D1B69", fontWeight:700, lineHeight:1.1, marginBottom:10 }}>
                {summary.lagna_sanskrit} · {summary.lagna_western}
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem", color:"#2a1a5a", lineHeight:1.6, fontWeight:400 }}>
                Rising sign at the eastern horizon — shapes your outer personality and life path
              </div>
            </div>
          )}

          {/* Nakshatra */}
          {summary.nakshatra_name && (
            <div style={{
              background:"rgba(107,53,0,0.05)", border:"1px solid rgba(107,53,0,0.18)",
              borderRadius:10, padding:"16px 18px", marginBottom:14,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, paddingBottom:10, borderBottom:"1px solid rgba(107,53,0,0.12)" }}>
                <span style={{ fontSize:"1.2rem" }}>{summary.nakshatra_symbol || "⭐"}</span>
                <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#6B3500",  fontWeight:800 }}>
                  Janma Nakshatra · Birth Star
                </span>
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.75rem", color:"#6B3500", fontWeight:700, lineHeight:1.1, marginBottom:10 }}>
                {summary.nakshatra_name}
              </div>
              {summary.nakshatra_ruler && (
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem", color:"#5a2e00", lineHeight:1.6, fontWeight:400 }}>
                  Ruled by {summary.nakshatra_ruler}{summary.nakshatra_pada ? ` · Pada ${summary.nakshatra_pada}` : ""} — the lunar mansion that shapes your deepest instincts
                </div>
              )}
            </div>
          )}

          {/* Current Dasha */}
          {summary.maha_dasha && (
            <div style={{
              background:"rgba(26,74,46,0.05)", border:"1px solid rgba(26,74,46,0.18)",
              borderRadius:10, padding:"16px 18px", marginBottom:14,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, paddingBottom:10, borderBottom:"1px solid rgba(26,74,46,0.12)" }}>
                <span style={{ fontSize:"1.2rem" }}>{summary.maha_dasha_emoji || "🪐"}</span>
                <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#1A4A2E",  fontWeight:800 }}>
                  Current Dasha · Planetary Period
                </span>
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.75rem", color:"#1A4A2E", fontWeight:700, lineHeight:1.1, marginBottom:10 }}>
                {summary.maha_dasha} Dasha{summary.antardasha ? ` → ${summary.antardasha}` : ""}
              </div>
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem", color:"#0a3018", lineHeight:1.6, fontWeight:400 }}>
                The ruling planet period shaping your life themes right now
              </div>
            </div>
          )}

        </div>

        {/* Branding footer */}
        <div style={{ padding:"12px 24px 20px", position:"relative", zIndex:2 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
            <div style={{ flex:1, height:1, background:"linear-gradient(90deg,transparent,rgba(180,130,25,0.5))" }} />
            <span style={{ fontFamily:JT.heading, fontSize:"1.0rem", color:"#9a6e1a", letterSpacing:"0.2em", fontWeight:700 }}>Vedsanga</span>
            <span style={{ fontFamily:"'Noto Sans Devanagari',serif", fontSize:"1.0rem", color:"#b07818", fontWeight:600 }}>· वेदसङ्ग ·</span>
            <div style={{ flex:1, height:1, background:"linear-gradient(90deg,rgba(180,130,25,0.5),transparent)" }} />
          </div>
          <div style={{ textAlign:"center", fontFamily:JT.heading, fontSize:"1.0rem", color:"#9a6410",  fontWeight:700 }}>
            Vedic Astrology · Authentic Jyotish
          </div>
        </div>
        <div style={{ height:1, background:"linear-gradient(90deg,transparent,rgba(180,130,25,0.5),transparent)", margin:"0 16px 7px", position:"relative", zIndex:2 }} />
        {/* Bottom gold bar */}
        <div style={{ height:8, background:"linear-gradient(90deg,#7a4808,#c9902a,#f5d17a,#fff0a0,#f5d17a,#c9902a,#7a4808)", position:"relative", zIndex:2 }} />
      </div>

    </div>
  );
}

// ── Premium PDF Report Generator ─────────────────────────────────────────────
// jsPDF Helvetica cannot render emoji or non-Latin-1 chars — strip them
const pdfSafe = s => String(s)
  .replace(/—|–/g, "-")   // em/en dash → hyphen
  .replace(/•|·/g, "-")   // bullet → hyphen
  .replace(/[‘’]/g, "'")  // curly single quotes → apostrophe
  .replace(/[“”]/g, '"')  // curly double quotes → straight
  .replace(/[^\x00-\xFF]/g, "")     // drop everything outside Latin-1
  .trim();

function genJyotishPDF({ title, filename, userName, rashi, nakshatra, dob, badge = "", extraLines = [], sections }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const W = 210, M = 15, CW = W - 2 * M;
  let y = 0;
  const newPage = () => { doc.addPage(); y = 22; };
  const checkY = (need = 10) => { if (y + need > 278) newPage(); };

  // — Header bar
  doc.setFillColor(30, 10, 70);
  doc.rect(0, 0, W, 14, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.setTextColor(212, 175, 55);
  doc.text("VedSanga  -  Vedic Astrology & Jyotish", M, 9);
  if (badge) { doc.setTextColor(180, 160, 255); doc.text(pdfSafe(badge), W - M, 9, { align: "right" }); }

  // — Title
  y = 22;
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.setTextColor(15, 5, 40);
  doc.text(pdfSafe(title), M, y); y += 9;

  // — Divider
  doc.setDrawColor(180, 140, 255); doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y); y += 7;

  // — Meta block
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(50, 30, 90);
  const metaLines = [
    `Prepared for: ${pdfSafe(userName)}`,
    `Moon Sign (Janma Rashi): ${pdfSafe(rashi)}`,
    `Birth Nakshatra: ${pdfSafe(nakshatra)}`,
    ...(dob ? [`Date of Birth: ${pdfSafe(dob)}`] : []),
    ...extraLines.map(pdfSafe),
    `Generated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`,
  ];
  metaLines.forEach(l => { doc.text(l, M, y); y += 5.5; });
  y += 3;
  doc.setDrawColor(210, 200, 235); doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y); y += 9;

  // — Sections
  sections.forEach(sec => {
    checkY(22);
    // Section heading pill
    doc.setFillColor(30, 10, 70);
    doc.roundedRect(M, y - 6, CW, 10, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.setTextColor(212, 175, 55);
    doc.text(pdfSafe(sec.heading), M + 4, y); y += 8;

    // Section content
    (sec.lines || []).forEach(line => {
      checkY(8);
      if (line === "---") {
        doc.setDrawColor(210, 200, 235); doc.setLineWidth(0.2);
        doc.line(M + 2, y - 2, W - M - 2, y - 2); y += 4;
      } else if (typeof line === "object" && line.label) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(40, 20, 90);
        doc.text(pdfSafe(`${line.label}:`), M + 2, y);
        doc.setFont("helvetica", "normal"); doc.setTextColor(15, 15, 20);
        const vl = doc.splitTextToSize(pdfSafe(line.value), CW - 42);
        vl.forEach((v, vi) => { if (vi) checkY(7); doc.text(v, M + 40, y); y += 5.5; });
      } else {
        const raw = pdfSafe(String(line));
        const bullet = raw.startsWith("*") || raw.startsWith("-") || String(line).startsWith("  ");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(bullet ? 50 : 15, bullet ? 35 : 15, bullet ? 90 : 20);
        const wrapped = doc.splitTextToSize(raw, CW - (bullet ? 8 : 2));
        wrapped.forEach(wl => { checkY(7); doc.text(wl, M + (bullet ? 4 : 2), y); y += 5.5; });
      }
    });
    y += 6;
  });

  // — Footer on every page
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(150, 130, 180);
    doc.text("VedSanga - For spiritual & educational guidance only. Consult a qualified Jyotishi for important decisions.", M, 291);
    doc.text(`Page ${i} / ${total}`, W - M, 291, { align: "right" });
  }

  doc.save(`${filename}.pdf`);
}

function DlBtn({ onClick, label = "📥 Download My Report" }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ width:"100%", padding:"14px 20px", borderRadius:12, border:"none", cursor:"pointer",
        background: hover ? "linear-gradient(135deg,#2a1060,#3d1a7a)" : "linear-gradient(135deg,#1e0a52,#2d1464)",
        color:"#d4af37", fontFamily:JT.heading, fontSize:"1.1rem", fontWeight:700,
        letterSpacing:"0.06em", display:"flex", alignItems:"center", justifyContent:"center", gap:10,
        boxShadow: hover ? "0 6px 28px rgba(60,20,140,0.55)" : "0 4px 18px rgba(40,10,100,0.45)",
        transition:"all 0.18s" }}>
      {label}
    </button>
  );
}

// ── Simple welcome entry — one screen, one action ────────────────────────────
function JyotishWelcomeEntry({ userId, onSaved }) {
  const [dob,    setDob]    = useState("");
  const [time,   setTime]   = useState("");
  const [saving, setSaving] = useState(false);

  const reveal = async () => {
    if (!dob || saving) return;
    setSaving(true);
    try {
      await fetch(`${API}/user/birthdate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, dob, birth_time: time }),
      });
      onSaved(dob);
    } catch { setSaving(false); }
  };

  const inp = {
    width: "100%", boxSizing: "border-box",
    background: "#ffffff",
    border: `1px solid ${JT.borderStrong}`,
    borderRadius: "8px", padding: "clamp(9px,1.2vw,12px) 12px",
    color: JT.text, fontSize: "clamp(1rem,1.1vw,1.1rem)",
    fontFamily: "'Inter',sans-serif", outline: "none",
    WebkitAppearance: "none",
  };

  return (
    <div style={{
      background: "#ffffff", maxWidth: 520,
      border: `1px solid ${JT.border}`,
      borderRadius: "16px", padding: "clamp(22px,3vw,32px) clamp(20px,3vw,30px)",
      boxShadow: "0 10px 40px rgba(226,120,40,0.15), 0 2px 8px rgba(0,0,0,0.04)",
      textAlign: "center", marginBottom: "16px",
    }}>
      {/* Headline */}
      <div style={{
        fontFamily: "'Inter',sans-serif", fontWeight: 700,
        fontSize: "clamp(1.4rem,2.4vw,1.8rem)", color: JT.text,
        lineHeight: 1.25, marginBottom: "8px",
      }}>
        What do the stars say about you?
      </div>

      <div style={{
        fontSize: "clamp(0.95rem,1.2vw,1.05rem)", color: JT.textDim,
        fontFamily: "'Inter',sans-serif",
        lineHeight: 1.5, marginBottom: "18px",
      }}>
        Enter your date of birth — your Moon sign, Nakshatra, and Vedic archetype appear instantly.
      </div>

      {/* Date */}
      <div style={{ textAlign: "left", marginBottom: "10px" }}>
        <div style={{ fontSize: "0.95rem", color: JT.textDim,
          fontFamily: "'Inter',sans-serif", fontWeight: 600,
          marginBottom: "4px" }}>
          Date of birth *
        </div>
        <input type="date" value={dob} onChange={e => setDob(e.target.value)} style={inp} />
      </div>

      {/* Time */}
      <div style={{ textAlign: "left", marginBottom: "14px" }}>
        <div style={{ fontSize: "0.95rem",
          fontFamily: "'Inter',sans-serif", fontWeight: 600,
          marginBottom: "4px", display: "flex", alignItems: "baseline", gap: "6px",
          flexWrap: "wrap", color: JT.textDim }}>
          Birth time
          <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400,
            fontSize: "0.85rem", color: JT.textDim }}>
            optional — makes reading more accurate
          </span>
        </div>
        <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inp} />
      </div>

      {/* CTA */}
      <button onClick={reveal} disabled={!dob || saving} style={{
        width: "100%", padding: "clamp(12px,1.6vw,15px) 20px",
        background: dob ? "#c2560f" : "#f0f0f0",
        border: "none", borderRadius: "10px",
        color: dob ? "#ffffff" : "#999",
        fontFamily: "'Inter',sans-serif", fontWeight: 700,
        fontSize: "clamp(1rem,1.2vw,1.1rem)",
        cursor: dob ? "pointer" : "default",
        boxShadow: dob ? "0 8px 24px rgba(194,86,15,0.35)" : "none",
        marginBottom: "16px", transition: "all 0.2s",
      }}>
        {saving ? "Reading the stars…" : "Reveal My Stars →"}
      </button>

      {/* What you'll get */}
      <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "wrap" }}>
        {["Moon Sign","Nakshatra","Archetype","Daily Horoscope"].map((c,i) => (
          <div key={i} style={{
            background: "#ffffff", border: `1px solid ${JT.border}`,
            borderRadius: "20px", padding: "5px 12px",
            fontSize: "0.9rem", color: JT.textDim,
            fontFamily: "'Inter',sans-serif", fontWeight: 500,
          }}>{c}</div>
        ))}
      </div>

      <div style={{ marginTop: "12px", fontSize: "0.9rem",
        color: JT.textDim, fontFamily: "'Inter',sans-serif" }}>
        Free · No login needed to see your reading
      </div>
    </div>
  );
}

// 30/70 bridge: rendered at the end of every free reading. The free section
// gives the diagnosis (30%); this hands off into the written report (70%).
// Deliberately shows NO price — price appears only at the final generate step.
function ReportBridge({ heading, lines, onOpen }) {
  return (
    <div style={{ marginTop:22, borderRadius:18, padding:"22px 20px",
      background:"linear-gradient(160deg,#fff9ee,#fff3dd)",
      border:"1px solid rgba(201,149,58,0.35)",
      boxShadow:"0 6px 24px rgba(176,120,30,0.1)" }}>
      <div style={{ height:3, width:52, borderRadius:3, marginBottom:14,
        background:"linear-gradient(90deg,#E8832A,#C9902A)" }} />
      <div style={{ fontFamily:"'Inter',sans-serif", fontWeight:700,
        fontSize:"1.3rem", color:"#3a2410", marginBottom:10, lineHeight:1.3 }}>
        {heading}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:18 }}>
        {lines.map((l,i)=>(
          <div key={i} style={{ display:"flex", gap:9, alignItems:"baseline" }}>
            <span style={{ color:"#C9902A", flexShrink:0, fontSize:"1.0rem" }}>•</span>
            <span style={{ fontSize:"1.08rem", color:"#4a3d2a",
              fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{l}</span>
          </div>
        ))}
      </div>
      <button onClick={onOpen} style={{ width:"100%", padding:"15px 20px",
        background:"linear-gradient(135deg,#E8832A,#D06B1A)", border:"none",
        borderRadius:14, color:"#ffffff", fontFamily:"'Inter',sans-serif",
        fontWeight:700, fontSize:"1.12rem", cursor:"pointer",
        boxShadow:"0 6px 22px rgba(232,131,42,0.3)" }}>
        Read my full written reading →
      </button>
      <button onClick={() => window.dispatchEvent(new CustomEvent("vs:navigate", { detail: "consult" }))}
        style={{ width:"100%", marginTop:10, padding:"14px 20px", background:"#ffffff",
          border:"1.5px solid #E8832A", borderRadius:14, color:"#8a4a10", fontFamily:"'Inter',sans-serif",
          fontWeight:700, fontSize:"1.08rem", cursor:"pointer" }}>
        Or ask a real astrologer about this
      </button>
    </div>
  );
}

// Clean geometric line-icons for the guest-home value grid (replaces emoji).
function JyotishGridIcon({ name, color = "#C9902A", size = 28, strokeWidth = 1.75 }) {
  const common = {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: color, strokeWidth,
    strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (name) {
    case "archetype": // lotus / trishul-like mark
      return (
        <svg {...common}>
          <path d="M12 21c-4-1.5-6.5-4.5-6.5-8" />
          <path d="M12 21c4-1.5 6.5-4.5 6.5-8" />
          <path d="M12 21V8" />
          <path d="M12 8c-1.6 0-3 1.6-3 3.6M12 8c1.6 0 3 1.6 3 3.6" />
          <path d="M12 8l0-4M9 6l3 2 3-2" />
        </svg>
      );
    case "horoscope": // sunrise: half-sun + rays + horizon
      return (
        <svg {...common}>
          <path d="M3 18h18" />
          <path d="M7 18a5 5 0 0 1 10 0" />
          <path d="M12 5v2M5.6 8.6l1.4 1.4M18.4 8.6l-1.4 1.4M2.5 14h1.5M20 14h1.5" />
        </svg>
      );
    case "nakshatra": // 5-point star outline
      return (
        <svg {...common}>
          <path d="M12 3l2.35 5.4 5.9.5-4.47 3.86 1.36 5.74L12 15.9l-5.14 2.5 1.36-5.74L3.75 8.9l5.9-.5z" />
        </svg>
      );
    case "chart": // planet with orbit ring
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="5" />
          <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(-24 12 12)" />
        </svg>
      );
    case "dasha": // ascending bars / timeline
      return (
        <svg {...common}>
          <path d="M4 20V13M10 20V9M16 20V6M20 20V15" />
          <path d="M3 20h18" />
        </svg>
      );
    case "compatibility": // two interlocking rings
      return (
        <svg {...common}>
          <circle cx="9" cy="12" r="5" />
          <circle cx="15" cy="12" r="5" />
        </svg>
      );
    default:
      return null;
  }
}

export default function JyotishPage({ user, onBack, appLang, appScript }) {
  const [lang, setLang] = useState(() => appLang || localStorage.getItem("panchang_lang") || "te");
  const [isSubscribed,  setIsSubscribed]  = useState(false);
  const [userTier,      setUserTier]      = useState(null); // "drishti"|"saathi"|"darshan"
  const [scriptPref,    setScriptPref]    = useState(() => appScript || localStorage.getItem("jyotish_script_pref") || "native");
  const [subLoading,    setSubLoading]    = useState(true);
  const [view,       setView]       = useState("home"); // "home" | feature id
  const [arcData,    setArcData]    = useState(null);
  const [arcLoad,    setArcLoad]    = useState(true);
  const [noBirth,    setNoBirth]    = useState(false);
  const [correcting,   setCorrecting]   = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("saathi");
  const [planets,    setPlanets]    = useState([]);
  const [sadhana,    setSadhana]    = useState(null);
  const [sadLoad,    setSadLoad]    = useState(true);
  const [horoscope,      setHoroscope]      = useState(null);
  const [horoLoad,       setHoroLoad]       = useState(false);
  const [personalHoro,   setPersonalHoro]   = useState(null);
  const [personalLoad,   setPersonalLoad]   = useState(false);
  const [weeklyReading,  setWeeklyReading]  = useState(null);
  const [weeklyLoad,     setWeeklyLoad]     = useState(false);
  const [summary,        setSummary]        = useState(null);
  const [chatInitMsg,    setChatInitMsg]    = useState("");
  const [ishtaData,      setIshtaData]      = useState(null);
  const [ishtaLoad,      setIshtaLoad]      = useState(false);
  const [ishtaErr,       setIshtaErr]       = useState(null);
  const [shaniData,      setShaniData]      = useState(null);
  const [shaniLoad,      setShaniLoad]      = useState(false);
  const [shaniErr,       setShaniErr]       = useState(null);
  const [horaData,       setHoraData]       = useState(null);
  const [horaLoad,       setHoraLoad]       = useState(false);
  const [horaNow,        setHoraNow]        = useState(new Date());
  const [pujaAlertDays,  setPujaAlertDays]  = useState(null);
  const [pujaAlertLoad,  setPujaAlertLoad]  = useState(false);
  const [dashaNavData,   setDashaNavData]   = useState(null);
  const [dashaNavLoad,   setDashaNavLoad]   = useState(false);
  const [doshaFullData,  setDoshaFullData]  = useState(null);
  const [doshaFullLoad,  setDoshaFullLoad]  = useState(false);

  // ── Jyotish onboarding (shown once after birth details saved) ─────────────
  const ONBOARD_KEY   = `jyotish_onboarded_${user.id}`;
  const CONCERN_KEY   = `jyotish_concern_${user.id}`;
  const [onboarded,     setOnboarded]     = useState(() => !!localStorage.getItem(ONBOARD_KEY));
  const [onboardPhase,  setOnboardPhase]  = useState("focus"); // "focus" | "marriage_followup"
  const [onboardFocus,  setOnboardFocus]  = useState(null);
  // Problem-first onboarding: concern selected before birth data entry
  const SUB_KEY = `jyotish_subconcern_${user.id}`;
  const [concern,       setConcern]       = useState(() => localStorage.getItem(CONCERN_KEY) || null);
  const [subConcern,    setSubConcern]    = useState(() => localStorage.getItem(SUB_KEY) || null);
  const [concernBanner,     setConcernBanner]     = useState(false);
  const [showConcernResult, setShowConcernResult] = useState(false);
  const pricingRef    = useRef(null);
  const horoscopeRef  = useRef(null);
  const [lightMode,   setLightMode]   = useState(true);
  // Plans render inline, below all the free value. Hiding them behind a
  // toggle at the bottom of a 6000px page meant nobody ever saw the pricing.
  const [showPlans,   setShowPlans]   = useState(true);
  const [viewRefreshKey, setViewRefreshKey] = useState(0);

  const onBirthSavedInFeature = () => {
    setNoBirth(false);
    fetchArchetype();
    fetchSummary();
    setViewRefreshKey(k => k + 1);
  };

  const changeLang = (l) => {
    setLang(l);
    localStorage.setItem("panchang_lang", l);
    fetch(`${API}/user/language?user_id=${encodeURIComponent(user.id)}&lang_code=${l}`, { method:"POST" }).catch(() => {});
  };

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [view]);

  useEffect(() => {
    if (view !== "ishta_devata" || ishtaData || ishtaLoad) return;
    setIshtaLoad(true);
    setIshtaErr(null);
    fetch(`${API}/jyotish/ishta_devata/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setIshtaErr(d.message || d.error);
        else setIshtaData(d);
        setIshtaLoad(false);
      })
      .catch(() => { setIshtaErr("Could not load Ishta Devata."); setIshtaLoad(false); });
  }, [view, user.id]);

  useEffect(() => {
    if (!["shani","shani_kavach"].includes(view) || shaniData || shaniLoad) return;
    setShaniLoad(true);
    setShaniErr(null);
    fetch(`${API}/jyotish/shani/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setShaniErr(d.message || d.error);
        else setShaniData(d);
        setShaniLoad(false);
      })
      .catch(() => { setShaniErr("Could not load Shani status."); setShaniLoad(false); });
  }, [view, user.id]);

  useEffect(() => {
    if (view !== "dasha_navigator" || dashaNavData || dashaNavLoad) return;
    setDashaNavLoad(true);
    fetch(`${API}/jyotish/dasha/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setDashaNavData(d.error ? null : d); setDashaNavLoad(false); })
      .catch(() => setDashaNavLoad(false));
  }, [view, user.id]);

  useEffect(() => {
    if (view !== "dosha_remedy" || doshaFullData || doshaFullLoad) return;
    setDoshaFullLoad(true);
    fetch(`${API}/jyotish/doshas/full/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setDoshaFullData(d.error ? null : d); setDoshaFullLoad(false); })
      .catch(() => setDoshaFullLoad(false));
  }, [view, user.id]);

  useEffect(() => {
    if (view !== "hora" || horaData || horaLoad) return;
    setHoraLoad(true);
    fetch(`${API}/panchang/hora`)
      .then(r => r.json())
      .then(d => { setHoraData(d); setHoraLoad(false); })
      .catch(() => setHoraLoad(false));
  }, [view]);

  useEffect(() => {
    if (view !== "puja_alerts" || pujaAlertDays !== null || pujaAlertLoad) return;
    setPujaAlertLoad(true);
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth() + 1;
    const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
    const todayStr = today.toISOString().slice(0, 10);
    Promise.all([
      fetch(`${API}/panchang/month/${y}/${m}`).then(r => r.json()),
      fetch(`${API}/panchang/month/${next.y}/${next.m}`).then(r => r.json()),
    ]).then(([cur, nxt]) => {
      const all = [...(cur.days || []), ...(nxt.days || [])];
      const SACRED = ["ekadashi","amavasya","trayodashi","purnima"];
      const days = all
        .filter(d => d.date >= todayStr && SACRED.some(s => (d.tithi_en||"").toLowerCase().includes(s)))
        .slice(0, 10)
        .map(d => {
          const t = (d.tithi_en || "").toLowerCase();
          const cat = t.includes("ekadashi") ? "ekadashi"
            : t.includes("amavasya") ? "amavasya"
            : t.includes("trayodashi") ? "pradosham"
            : "purnima";
          const diff = Math.round((new Date(d.date) - today) / 86400000);
          return { ...d, cat, diff };
        });
      setPujaAlertDays(days);
      setPujaAlertLoad(false);
    }).catch(() => setPujaAlertLoad(false));
  }, [view, pujaAlertDays, pujaAlertLoad]);

  useEffect(() => {
    if (view !== "hora") return;
    const id = setInterval(() => setHoraNow(new Date()), 60000);
    return () => clearInterval(id);
  }, [view]);

  useEffect(() => {
    const fn = () => setWinW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const [payLoading,    setPayLoading]    = useState(false);
  const [payError,      setPayError]      = useState("");
  const [compareDrawer, setCompareDrawer] = useState(null); // null | "drishti"|"saathi"|"darshan"
  const [activePlanTab,    setActivePlanTab]    = useState("saathi");
  const [showCompareTable, setShowCompareTable] = useState(true);
  const [winW,             setWinW]             = useState(() => window.innerWidth);

  const handleUpgrade = () => {
    setView("home");
    setShowPlans(true);
    setTimeout(() => {
      pricingRef.current?.scrollIntoView({ behavior:"smooth", block:"start" });
    }, 80);
  };

  const loadRazorpayScript = () => new Promise(resolve => {
    if (document.getElementById("rzp-script")) { resolve(true); return; }
    const s = document.createElement("script");
    s.id  = "rzp-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const PLAN_PRICES = { drishti: 299, saathi: 499, darshan: 699 };
  const PLAN_NAMES  = { drishti: "Navagraha Drishti", saathi: "Jyotish Saathi", darshan: "Guru Darshan" };

  const handleSubscribe = async (planId) => {
    const plan = planId || selectedPlan;
    setSelectedPlan(plan);
    setPayError("");
    setPayLoading(true);
    const loaded = await loadRazorpayScript();
    if (!loaded) { setPayError("Could not load payment SDK. Check your internet."); setPayLoading(false); return; }

    let order;
    try {
      const res = await fetch(`${API}/jyotish/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, user_name: user.name, user_email: user.email, plan }),
      });
      order = await res.json();
      if (!order.order_id) throw new Error(order.detail || "Order creation failed");
    } catch (e) {
      setPayError(e.message);
      setPayLoading(false);
      return;
    }

    setPayLoading(false);

    const rzp = new window.Razorpay({
      key:         order.key_id,
      amount:      order.amount,
      currency:    order.currency,
      order_id:    order.order_id,
      name:        "VedSanga",
      description: `${PLAN_NAMES[plan] || "Jyotish"} · 1 Month`,
      image:       "/logo192.png",
      prefill: {
        name:    user.name  || "",
        email:   user.email || "",
        contact: "",
      },
      method: {
        upi:        true,
        card:       true,
        netbanking: true,
        wallet:     true,
      },
      theme: { color: "#c9a84c" },
      handler: async (response) => {
        try {
          const vRes = await fetch(`${API}/jyotish/payment/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id:             user.id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vData.ok) {
            setIsSubscribed(true);
            setUserTier(selectedPlan);
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            setPayError("Payment received but verification failed. Contact support.");
          }
        } catch {
          setPayError("Verification error. Contact support with your payment ID.");
        }
      },
      modal: {
        ondismiss: () => setPayLoading(false),
      },
    });
    rzp.open();
  };

  const handleFocusPick = (focusId) => {
    if (focusId === "love_family") {
      setOnboardFocus(focusId);
      setOnboardPhase("marriage_followup");
    } else {
      completeOnboarding(focusId, null, null);
    }
  };

  const completeOnboarding = (focusId, maritalStatus, employmentStatus) => {
    localStorage.setItem(ONBOARD_KEY, "1");
    setOnboarded(true);
    const payload = { user_id: user.id };
    if (focusId)          payload.life_focus        = focusId;
    if (maritalStatus)    payload.marital_status    = maritalStatus;
    if (employmentStatus) payload.employment_status = employmentStatus;
    if (Object.keys(payload).length > 1) {
      fetch(`${API}/jyotish/life-context`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
  };

  const fetchArchetype = () => {
    setArcLoad(true);
    fetch(`${API}/jyotish/profile/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error === "no_birthdate") { setNoBirth(true); } else { setArcData(d); }
        setArcLoad(false);
      })
      .catch(() => setArcLoad(false));
  };

  const fetchSummary = () => {
    fetch(`${API}/jyotish/summary/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setSummary(d); })
      .catch(() => {});
  };

  const fetchSadhana = () => {
    setSadLoad(true);
    fetch(`${API}/sadhana/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setSadhana(d); setSadLoad(false); })
      .catch(() => setSadLoad(false));
  };

  useEffect(() => {
    fetch(`${API}/jyotish/subscription/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setIsSubscribed(!!d.subscribed); if (d.plan && d.plan !== "annual") setUserTier(d.plan); })
      .catch(() => setIsSubscribed(false))
      .finally(() => setSubLoading(false));
  }, [user.id]);

  useEffect(() => {
    fetchArchetype();
    fetch(`${API}/sadhana/planets`).then(r=>r.json()).then(setPlanets).catch(()=>{});
    fetchSadhana();
    fetch(`${API}/jyotish/summary/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setSummary(d); })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    if (arcData?.rashi_idx == null) return;
    const horoLang = lang === "ta" ? (scriptPref === "roman" ? "ta_roman" : "ta") : "en";
    setHoroLoad(true);
    fetch(`${API}/horoscope/${arcData.rashi_idx}?lang=${horoLang}`)
      .then(r => r.json())
      .then(d => { setHoroscope(d.error ? null : d); setHoroLoad(false); })
      .catch(() => setHoroLoad(false));
    // Also fetch personalized horoscope (Layer 2)
    setPersonalLoad(true);
    fetch(`${API}/horoscope/personal/${encodeURIComponent(user.id)}?lang=${horoLang}`)
      .then(r => r.json())
      .then(d => { setPersonalHoro(d.error ? null : d); setPersonalLoad(false); })
      .catch(() => setPersonalLoad(false));
    // Also fetch weekly reading (Layer 2)
    setWeeklyLoad(true);
    fetch(`${API}/jyotish/weekly/${encodeURIComponent(user.id)}?lang=${horoLang}`)
      .then(r => r.json())
      .then(d => { setWeeklyReading(d.error ? null : d); setWeeklyLoad(false); })
      .catch(() => setWeeklyLoad(false));
  }, [arcData?.rashi_idx, lang, scriptPref]);

  const handleShare = () => {
    if (!arcData) return;
    const arch = arcData.archetype;
    const rashiName = arcData.rashi?.en || "";
    const text = `🔱 My Vedic Archetype on VedSanga:\n\nI am ${arch.en} (${rashiName})\n\n"${arch.dharma}"\n\n${arch.gita_ref}\n\nDiscover yours → vedsanga.com`;
    if (navigator.share) { navigator.share({ title:`I am ${arch.en}`, text }).catch(()=>{}); }
    else { navigator.clipboard.writeText(text).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2500); }); }
  };

  const startSadhana = (planet) => {
    fetch(`${API}/sadhana/start`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ user_id:user.id, planet }),
    }).then(() => fetchSadhana());
  };

  const completeSadhana = () => {
    fetch(`${API}/sadhana/${encodeURIComponent(user.id)}/complete`, { method:"POST" })
      .then(() => fetchSadhana());
  };

  const abandonSadhana = () => {
    fetch(`${API}/sadhana/${encodeURIComponent(user.id)}`, { method:"DELETE" })
      .then(() => fetchSadhana());
  };

  const shimmer = <div style={{ height:360, borderRadius:"20px", marginBottom:"16px",
    background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
    backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />;

  const currentFeature = JYOTISH_FEATURES.find(f => f.id === view);
  const goHome = () => { setView("home"); setCorrecting(false); };

  // Warm the page only while Jyotish is open. The app's light theme paints
  // --bg, --bg2 and --bg3 all pure white, so page and cards are the same tone
  // and nothing separates — the reason this section reads flat. Warming the
  // page (and leaving cards white) is how AstroTalk/AstroSage get depth.
  // Restored on unmount so the rest of the app is untouched.
  React.useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "#FFFBF3";
    return () => { document.body.style.background = prev; };
  }, []);

  return (
    <div className="page" style={{ maxWidth:1050, "--bg3":"#FDF6EA" }}>
      {/* Back button */}
      <button onClick={view === "home" ? onBack : goHome}
        style={{ background:"none", border:"none", cursor:"pointer",
          color:"var(--text-dim)", fontFamily:"'Inter',sans-serif",
          fontSize:"1rem", padding:"0", marginBottom:"20px",
          display:"flex", alignItems:"center", gap:"6px" }}>
        ← {view === "home"
          ? (lang === "te" ? "వెనక్కి" : lang === "hi" ? "वापस" : "Back")
          : "All Features"}
      </button>

      {/* ── JYOTISH ONBOARDING (shown once, after birth details saved) ── */}
      {view === "home" && arcData && !noBirth && !onboarded && (
        <div style={{ padding:"8px 0" }}>
          {onboardPhase === "focus" && (
            <>
              <div style={{ textAlign:"center", marginBottom:"28px" }}>
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.7rem",
                  fontWeight:700, color:"var(--text)", marginBottom:"10px", lineHeight:1.25 }}>
                  Personalise your reading
                </div>
                <div style={{ fontSize:"1.12rem", color:"var(--text-dim)", lineHeight:1.7,
                  fontFamily:"'Inter',sans-serif", maxWidth:310, margin:"0 auto" }}>
                  Tell us what matters most to you right now.
                  Your horoscope, weekly reading, and chat will all be shaped around this.
                </div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:"11px", marginBottom:"22px" }}>
                {JYOTISH_FOCUSES.map(f => (
                  <button key={f.id} onClick={() => handleFocusPick(f.id)} style={{
                    background:"var(--bg2)",
                    border:"1px solid var(--border)",
                    borderRadius:"16px", padding:"17px 20px",
                    display:"flex", alignItems:"center", gap:"16px",
                    cursor:"pointer", textAlign:"left", transition:"border-color 0.18s", width:"100%",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="var(--saffron)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; }}
                  >
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:"1.15rem", color:"var(--text)", fontWeight:700,
                        fontFamily:"'Inter',sans-serif", marginBottom:"3px" }}>{f.title}</div>
                      <div style={{ fontSize:"1.02rem", color:"var(--text-dim)", lineHeight:1.4,
                        fontFamily:"'Inter',sans-serif" }}>{f.sub}</div>
                    </div>
                    <span style={{ color:"var(--saffron)", fontSize:"1.3rem", flexShrink:0 }}>›</span>
                  </button>
                ))}
              </div>

              <div style={{ textAlign:"center" }}>
                <button onClick={() => completeOnboarding(null, null, null)} style={{
                  background:"none", border:"none", color:"var(--text-dim)",
                  fontSize:"1.05rem", cursor:"pointer", fontFamily:"'Inter',sans-serif",
                  textDecoration:"underline",
                }}>Skip — I'll explore on my own</button>
              </div>
            </>
          )}

          {onboardPhase === "marriage_followup" && (
            <>
              <div style={{ textAlign:"center", marginBottom:"28px" }}>
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.5rem",
                  fontWeight:700, color:"var(--text)", marginBottom:"10px" }}>
                  Love & family
                </div>
                <div style={{ fontSize:"1.12rem", color:"var(--text-dim)", lineHeight:1.7,
                  fontFamily:"'Inter',sans-serif", maxWidth:310, margin:"0 auto" }}>
                  One more thing — this helps me give you the most accurate reading.
                </div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:"9px",
                maxWidth:340, margin:"0 auto 20px" }}>
                {[
                  { label:"Looking for marriage", marital:"single"       },
                  { label:"In a relationship",    marital:"relationship"  },
                  { label:"Already married",       marital:"married"       },
                ].map(opt => (
                  <button key={opt.marital}
                    onClick={() => completeOnboarding(onboardFocus, opt.marital, null)}
                    style={{
                      background:"var(--bg2)",
                      border:"1px solid var(--border)",
                      borderRadius:"14px", padding:"15px 20px",
                      color:"var(--text)", fontSize:"1.12rem", fontWeight:600,
                      fontFamily:"'Inter',sans-serif",
                      cursor:"pointer", textAlign:"left", transition:"border-color 0.15s", width:"100%",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="var(--saffron)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; }}
                  >{opt.label}</button>
                ))}
              </div>

              <div style={{ textAlign:"center" }}>
                <button onClick={() => { setOnboardPhase("focus"); }} style={{
                  background:"none", border:"none", color:"var(--text-dim)",
                  fontSize:"1.0rem", cursor:"pointer", marginRight:"16px",
                }}>← Back</button>
                <button onClick={() => completeOnboarding(onboardFocus, null, null)} style={{
                  background:"none", border:"none", color:"var(--text-dim)",
                  fontSize:"1.0rem", cursor:"pointer",
                }}>Skip this question</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── HOME VIEW ── */}
      {view === "home" && (onboarded || noBirth || !arcData) && (
        <>
          {/* Page header — hidden for new users (they see WelcomeEntry instead) */}
          {/* Plain heading, not a card. The white card here duplicated the page
              title above it and pushed the kundali below the fold; letting it sit
              on the warm page makes the kundali the first real object you see. */}
          {!noBirth && <div style={{ marginBottom:"16px", padding:"0 2px" }}>
            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"2rem",
              fontWeight:800, color:"#1a1a1a", letterSpacing:"-0.01em",
              marginBottom:"4px", lineHeight:1.15 }}>
              ज्योतिष · Jyotish
            </div>
            <div style={{ fontSize:"1.1rem", color:"var(--text-dim)",
              fontFamily:"'Inter',sans-serif", lineHeight:1.5 }}>
              Authentic Vedic astrology — your stars, your dharma
            </div>
          </div>}

          {/* Language selector — hidden for new users */}
          {!noBirth && <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"14px" }}>
            {["te","hi","en","ta","kn","mr"].map(l => (
              <button key={l} onClick={() => changeLang(l)} style={{
                background: lang === l ? "var(--saffron)" : "var(--bg2)",
                color: lang === l ? "#0f0e0d" : "var(--text-dim)",
                border: `1px solid ${lang === l ? "var(--saffron)" : "var(--border)"}`,
                borderRadius:"20px", padding:"5px 14px",
                fontFamily:"'Inter',sans-serif", fontSize:"1.0rem",
                fontWeight: lang === l ? 700 : 400,
                cursor:"pointer", transition:"all 0.15s",
                boxShadow: lang === l ? "0 2px 10px rgba(232,131,42,0.3)" : "none",
              }}>
                {LANG_CHIPS[l]}
              </button>
            ))}
          </div>}

          {/* ── Concern Result — shown immediately after concern-based birth entry ── */}
          {showConcernResult && concern && !noBirth && (
            <>
              <ConcernResultCard
                concern={concern}
                subConcern={subConcern}
                arcData={arcData}
                summary={summary}
                setView={setView}
                onDismiss={() => setShowConcernResult(false)}
              />
            </>
          )}

          {/* ── Entry summary card — full picture at one glance ── */}
          {!noBirth && !showConcernResult && (
            <JyotishSummaryCard summary={summary} setView={setView} archetype={arcData?.archetype} lang={lang} />
          )}

          {/* ── Daily retention: psychological triggers + return visitor ── */}
          {!noBirth && arcData && summary && (
            <DailyRetentionBanner summary={summary} setView={setView} user={user} />
          )}

          {/* ── New user entry: beautiful single-screen star reveal ── */}
          {noBirth && <JyotishWelcomeEntry userId={user.id} onSaved={onBirthSavedInFeature} />}

          {/* ── Problem-first onboarding — disabled, replaced by WelcomeEntry above ── */}
          {false && noBirth && (() => {
            const activeConcern = CONCERNS.find(c => c.id === concern);

            const pickConcern = (id) => {
              localStorage.setItem(CONCERN_KEY, id);
              setConcern(id);
              setSubConcern(null);
              localStorage.removeItem(SUB_KEY);
            };

            const pickSubConcern = (id) => {
              localStorage.setItem(SUB_KEY, id);
              setSubConcern(id);
            };

            const clearConcern = () => {
              localStorage.removeItem(CONCERN_KEY);
              localStorage.removeItem(SUB_KEY);
              setConcern(null);
              setSubConcern(null);
            };

            const clearSubConcern = () => {
              localStorage.removeItem(SUB_KEY);
              setSubConcern(null);
            };

            const onBirthSavedWithConcern = () => {
              setNoBirth(false);
              fetchArchetype();
              fetchSummary();
              localStorage.setItem(ONBOARD_KEY, "1");
              setOnboarded(true);
              if (concern) {
                const ctx = CONCERNS.find(c => c.id === concern);
                if (ctx) {
                  fetch(`${API}/jyotish/life-context`, {
                    method:"POST", headers:{"Content-Type":"application/json"},
                    body: JSON.stringify({ user_id:user.id, life_focus: ctx.lifeContext }),
                  }).catch(() => {});
                  setShowConcernResult(true);
                }
              }
            };

            const subs = CONCERN_SUBS[concern] || [];
            const activeSub = subs.find(s => s.id === subConcern);

            // ── Phase 1.5: concern selected, no sub-concern yet — show sub-question ──
            if (activeConcern && subs.length > 0 && !activeSub) {
              return (
                <div style={{ marginBottom:"24px", animation:"fadeInUp 0.35s ease" }}>
                  <button onClick={clearConcern} style={{
                    background:"none", border:"none", cursor:"pointer",
                    color:"#666666", fontSize:"1.1rem",
                    fontFamily:"'Inter',sans-serif", padding:"0 0 12px",
                    display:"flex", alignItems:"center", gap:"6px",
                  }}>← Change my concern</button>

                  <div style={{
                    position:"relative", overflow:"hidden",
                    background:"#ffffff",
                    border:"1px solid rgba(212,175,55,0.3)", borderRadius:"22px",
                    padding:"26px 22px 22px", marginBottom:"14px",
                    boxShadow:"0 12px 48px rgba(0,0,0,0.5)",
                  }}>
                    <Stars />
                    <div style={{ position:"relative", zIndex:1 }}>
                      {/* Concern label */}
                      <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"20px" }}>
                        <span style={{ fontSize:"1.8rem" }}>{activeConcern.emoji}</span>
                        <div style={{ fontFamily:JT.heading, fontSize:"1rem",
                          color:"#d4a853" }}>{activeConcern.title}</div>
                      </div>

                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem",
                        fontWeight:600, color:"#2b2b2b", lineHeight:1.6, marginBottom:"22px" }}>
                        One more thing — so your reading is accurate for your situation:
                      </div>

                      <div style={{ display:"flex", flexDirection:"column", gap:"9px" }}>
                        {subs.map(s => (
                          <button key={s.id} onClick={() => pickSubConcern(s.id)} style={{
                            background:JT.card,
                            border:`1px solid ${JT.border}`, boxShadow:JT.shadow,
                            borderRadius:"14px", padding:"14px 18px",
                            display:"flex", alignItems:"center", gap:"14px",
                            cursor:"pointer", textAlign:"left", transition:"all 0.18s", width:"100%",
                          }}
                            onMouseEnter={e => { e.currentTarget.style.background="rgba(180,130,50,0.1)"; e.currentTarget.style.borderColor="rgba(180,130,50,0.38)"; e.currentTarget.style.transform="translateX(3px)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background=JT.card; e.currentTarget.style.borderColor=JT.border; e.currentTarget.style.transform="none"; }}
                          >
                            <span style={{ fontSize:"1.5rem", flexShrink:0 }}>{s.emoji}</span>
                            <div style={{ flex:1 }}>
                              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem",
                                fontWeight:600, color:"#2b2b2b" }}>{s.label}</div>
                            </div>
                            <span style={{ color:"rgba(212,175,55,0.4)", fontSize:"1rem", flexShrink:0 }}>›</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // ── Phase 2: concern + sub-concern selected — show hook + birth form ──
            if (activeConcern) {
              return (
                <div style={{ marginBottom:"24px", animation:"fadeInUp 0.35s ease" }}>
                  {/* Back button */}
                  <button onClick={subs.length > 0 ? clearSubConcern : clearConcern} style={{
                    background:"none", border:"none", cursor:"pointer",
                    color:"#666666", fontSize:"1.1rem",
                    fontFamily:"'Inter',sans-serif", padding:"0 0 12px",
                    display:"flex", alignItems:"center", gap:"6px",
                  }}>← {subs.length > 0 ? "Change my situation" : "Change my concern"}</button>

                  {/* Hook card */}
                  <div style={{
                    position:"relative", overflow:"hidden",
                    background:"#ffffff",
                    border:"1px solid rgba(212,175,55,0.35)", borderRadius:"22px",
                    padding:"26px 22px 24px", marginBottom:"20px",
                    boxShadow:"0 16px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(212,175,55,0.12)",
                  }}>
                    <Stars />
                    <div style={{ position:"relative", zIndex:1 }}>
                      {/* Concern + sub label */}
                      <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"16px" }}>
                        <span style={{ fontSize:"2rem",
                          filter:"drop-shadow(0 0 14px rgba(212,175,55,0.6))" }}>
                          {activeSub?.emoji || activeConcern.emoji}
                        </span>
                        <div>
                          <div style={{ fontSize:"1.0rem", letterSpacing:"0.16em",
                            color:"rgba(212,175,55,0.6)", fontFamily:JT.heading,
                             }}>
                            {activeConcern.title}
                          </div>
                          {activeSub && (
                            <div style={{ fontSize:"1.0rem", color:"#d4a853",
                              fontFamily:"'Inter',sans-serif", marginTop:"2px" }}>
                              {activeSub.label}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Main hook — sub-concern specific if available */}
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.35rem",
                        fontWeight:600, color:"#2b2b2b",
                        lineHeight:1.85, marginBottom:"14px" }}>
                        {activeSub ? activeSub.intro : activeConcern.hook}
                      </div>

                      {/* Urgency line */}
                      <div style={{
                        background:"rgba(212,175,55,0.07)", border:"1px solid rgba(212,175,55,0.22)",
                        borderLeft:"3px solid #d4a853",
                        borderRadius:"10px", padding:"10px 14px", marginBottom:"22px",
                      }}>
                        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.1rem",
                          color:"rgba(212,175,55,0.85)", fontStyle:"italic", lineHeight:1.7 }}>
                          {activeConcern.urgent}
                        </div>
                      </div>

                      {/* Divider */}
                      <div style={{ height:1, background:"linear-gradient(90deg,transparent,rgba(212,175,55,0.25),transparent)",
                        marginBottom:"22px" }} />

                      {/* Inline birth form — concern-specific CTA */}
                      <div style={{ fontSize:"1.0rem", letterSpacing:"0.16em",
                        color:"#666666", fontFamily:JT.heading,
                         marginBottom:"14px" }}>
                        Add your birth details to unlock this
                      </div>
                      <BirthForm
                        userId={user.id}
                        onSaved={onBirthSavedWithConcern}
                        inline={true}
                        buttonLabel={`✦ ${activeConcern.cta} →`}
                      />
                    </div>
                  </div>

                  {/* Free tools strip */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {[
                      { icon:"⭐", label:"Nakshatra", id:"nakshatra" },
                      { icon:"⚠️", label:"Manglik",   id:"dosha"    },
                      { icon:"📊", label:"Dasha",     id:"dasha"    },
                      { icon:"💑", label:"Vivah Yog", id:"milan"    },
                    ].map(t => (
                      <button key={t.id} onClick={() => setView(t.id)} style={{
                        background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:12, padding:"9px 16px", display:"flex", alignItems:"center",
                        gap:7, cursor:"pointer", color:"#666666", fontSize:"1.0rem",
                        transition:"all 0.15s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(201,149,58,0.1)"; e.currentTarget.style.borderColor="rgba(201,149,58,0.3)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.1)"; }}
                      >
                        <span>{t.icon}</span> {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }

            // ── Phase 1: no concern — show concern picker ──
            return (
              <div style={{ marginBottom:"24px", animation:"fadeInUp 0.35s ease" }}>
                {/* Header */}
                <div style={{
                  position:"relative", overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(212,175,55,0.3)", borderRadius:"22px",
                  padding:"26px 22px 22px", marginBottom:"14px",
                  boxShadow:"0 12px 48px rgba(120,90,30,0.16), inset 0 1px 0 rgba(212,175,55,0.1)",
                }}>
                  <Stars />
                  <div style={{ position:"relative", zIndex:1, textAlign:"center", marginBottom:"24px" }}>

                    {/* ── Animated Kundali visual ── */}
                    <div style={{ position:"relative", width:160, height:160, margin:"0 auto 18px", flexShrink:0 }}>
                      {/* Outer ring — slow CW */}
                      <div style={{
                        position:"absolute", inset:0,
                        borderRadius:"50%",
                        border:"1px solid rgba(212,175,55,0.18)",
                        animation:"spin-slow 22s linear infinite",
                      }}>
                        {/* 4 tick marks on ring */}
                        {[0,90,180,270].map(deg => (
                          <div key={deg} style={{
                            position:"absolute", top:"50%", left:"50%",
                            width:6, height:6, borderRadius:"50%",
                            background:"rgba(212,175,55,0.55)",
                            transform:`rotate(${deg}deg) translateX(78px) translateY(-3px)`,
                          }} />
                        ))}
                      </div>

                      {/* Middle ring — slow CCW */}
                      <div style={{
                        position:"absolute", inset:12,
                        borderRadius:"50%",
                        border:"1px dashed rgba(212,175,55,0.12)",
                        animation:"spin-slow-r 18s linear infinite",
                      }} />

                      {/* North-Indian Kundali diamond SVG */}
                      <div style={{
                        position:"absolute", inset:0,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        animation:"kundali-glow 3s ease-in-out infinite",
                      }}>
                        <svg width="108" height="108" viewBox="0 0 108 108" fill="none">
                          {/* Outer square rotated 45° */}
                          <rect x="14" y="14" width="80" height="80" rx="3"
                            stroke="rgba(212,175,55,0.55)" strokeWidth="1.2"
                            transform="rotate(45 54 54)" fill="none" />
                          {/* Inner diamond lines */}
                          <line x1="54" y1="14" x2="54" y2="94" stroke="rgba(212,175,55,0.22)" strokeWidth="0.8"/>
                          <line x1="14" y1="54" x2="94" y2="54" stroke="rgba(212,175,55,0.22)" strokeWidth="0.8"/>
                          <line x1="20" y1="20" x2="88" y2="88" stroke="rgba(212,175,55,0.18)" strokeWidth="0.8"/>
                          <line x1="88" y1="20" x2="20" y2="88" stroke="rgba(212,175,55,0.18)" strokeWidth="0.8"/>
                          {/* Center square */}
                          <rect x="38" y="38" width="32" height="32" rx="2"
                            stroke="rgba(212,175,55,0.4)" strokeWidth="1" fill="rgba(212,175,55,0.04)" />
                          {/* OM symbol center */}
                          <text x="54" y="60" textAnchor="middle" fontSize="18"
                            fill="#b07818"
                            style={{ fontFamily:"serif" }}>ॐ</text>
                        </svg>
                      </div>

                      {/* Orbiting planets */}
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {/* Sun — outer orbit CW */}
                        <div style={{ position:"absolute",
                          animation:"orbit-cw 8s linear infinite" }}>
                          <div style={{ width:10, height:10, borderRadius:"50%",
                            background:"radial-gradient(circle,#fbbf24,#d97706)",
                            boxShadow:"0 0 10px rgba(251,191,36,0.8)" }} />
                        </div>
                        {/* Moon — inner orbit CCW */}
                        <div style={{ position:"absolute",
                          animation:"orbit-ccw 6s linear infinite" }}>
                          <div style={{ width:8, height:8, borderRadius:"50%",
                            background:"radial-gradient(circle,#e0e7ff,#a5b4fc)",
                            boxShadow:"0 0 8px rgba(165,180,252,0.7)" }} />
                        </div>
                        {/* Mars — medium orbit */}
                        <div style={{ position:"absolute",
                          animation:"orbit-med 12s linear infinite" }}>
                          <div style={{ width:7, height:7, borderRadius:"50%",
                            background:"radial-gradient(circle,#f87171,#dc2626)",
                            boxShadow:"0 0 8px rgba(248,113,113,0.6)" }} />
                        </div>
                      </div>
                    </div>

                    <div style={{ fontFamily:JT.heading, fontSize:"1.3rem",
                      background:"linear-gradient(90deg,#d4a853,#f5d17a,#fbbf24)",
                      WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                      backgroundClip:"text", marginBottom:"10px", lineHeight:1.3 }}>
                      What's weighing on you?
                    </div>
                    <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem",
                      fontWeight:500, color:"#666666", lineHeight:1.7, maxWidth:340, margin:"0 auto" }}>
                      Tell me what's troubling you. I'll show you exactly what your stars say about it.
                    </div>
                  </div>

                  {/* Concern cards */}
                  <div style={{ display:"flex", flexDirection:"column", gap:"9px", position:"relative", zIndex:1 }}>
                    {CONCERNS.map(c => (
                      <button key={c.id} onClick={() => pickConcern(c.id)} style={{
                        background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:"16px", padding:"16px 18px",
                        display:"flex", alignItems:"center", gap:"14px",
                        cursor:"pointer", textAlign:"left", transition:"all 0.18s", width:"100%",
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background="rgba(180,130,50,0.1)";
                          e.currentTarget.style.borderColor="rgba(180,130,50,0.38)";
                          e.currentTarget.style.transform="translateX(3px)";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background="rgba(255,255,255,0.03)";
                          e.currentTarget.style.borderColor="rgba(255,255,255,0.1)";
                          e.currentTarget.style.transform="none";
                        }}
                      >
                        <span style={{ fontSize:"1.7rem", flexShrink:0,
                          filter:"drop-shadow(0 0 8px rgba(212,175,55,0.35))" }}>{c.emoji}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:"1.3rem", color:"#2b2b2b",
                            fontFamily:"'Inter',sans-serif", fontWeight:600, marginBottom:"3px" }}>
                            {c.title}
                          </div>
                          <div style={{ fontSize:"1rem", color:"#666666",
                            fontFamily:"'Inter',sans-serif", lineHeight:1.4 }}>
                            {c.sub}
                          </div>
                        </div>
                        <span style={{ color:"rgba(212,175,55,0.4)", fontSize:"1.1rem", flexShrink:0 }}>›</span>
                      </button>
                    ))}
                  </div>

                  {/* Skip */}
                  <div style={{ textAlign:"center", marginTop:"18px", position:"relative", zIndex:1 }}>
                    <button onClick={() => setView("archetype")} style={{
                      background:"none", border:"none",
                      color:"#5a5a5a", fontSize:"1.05rem",
                      cursor:"pointer", fontFamily:"'Inter',sans-serif",
                      textDecoration:"underline", textDecorationColor:"rgba(255,255,255,0.12)",
                    }}>
                      Skip — I'll explore on my own
                    </button>
                  </div>
                </div>

                {/* Free tools strip */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {[
                    { icon:"⭐", label:"Nakshatra Finder", id:"nakshatra" },
                    { icon:"⚠️", label:"Manglik Check",    id:"dosha"    },
                    { icon:"📊", label:"Dasha Calculator", id:"dasha"    },
                    { icon:"💑", label:"Vivah Yog Check",  id:"milan"    },
                  ].map(t => (
                    <button key={t.id} onClick={() => setView(t.id)} style={{
                      background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.1)",
                      borderRadius:12, padding:"9px 16px", display:"flex", alignItems:"center",
                      gap:7, cursor:"pointer", color:"#666666", fontSize:"1.0rem",
                      transition:"all 0.15s",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background="rgba(201,149,58,0.1)"; e.currentTarget.style.borderColor="rgba(201,149,58,0.3)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.1)"; }}
                    >
                      <span>{t.icon}</span> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Daily Mantra View placeholder (moved outside home block) ── */}
          {false && (() => {
            const MANTRA_META = [
              { translit:"Om Ashwini Kumarabhyam Namah",    meaning:"Salutations to the Divine Physician twins — bringers of healing and swift restoration",                           chant:108, time:"Sunrise"      },
              { translit:"Om Yamaya Namah",                  meaning:"Salutations to Yama — lord of truth, transformation and righteous passage",                                        chant:108, time:"Dusk"         },
              { translit:"Om Agnaye Namah",                  meaning:"Salutations to Agni — the sacred fire that purifies all it touches",                                              chant:108, time:"Sunrise"      },
              { translit:"Om Brahmanaye Namah",              meaning:"Salutations to Brahma — creator and nurturer of all living forms",                                                chant:108, time:"Morning"      },
              { translit:"Om Somaya Namah",                  meaning:"Salutations to Soma — the Moon, source of nourishment and gentle seeking",                                        chant:27,  time:"Evening"      },
              { translit:"Om Rudraya Namah",                 meaning:"Salutations to Rudra — the storm that clears, renews and dissolves what is finished",                            chant:108, time:"Before sleep" },
              { translit:"Om Adityai Namah",                 meaning:"Salutations to Aditi — boundless mother, giver of infinite light and renewal",                                   chant:108, time:"Sunrise"      },
              { translit:"Om Brihaspataye Namah",            meaning:"Salutations to Brihaspati — Jupiter, guru of the gods and bestower of wisdom",                                   chant:108, time:"Morning"      },
              { translit:"Om Nagaya Namah",                  meaning:"Salutations to the Serpent — master of instinct, kundalini and ancient knowing",                                 chant:54,  time:"Night"        },
              { translit:"Om Pitribhyo Namah",               meaning:"Salutations to the Ancestors — honouring the lineage and blessings that flow through you",                       chant:108, time:"Noon"         },
              { translit:"Om Bhagaya Namah",                 meaning:"Salutations to Bhaga — deity of love, joy and divine abundance in all its forms",                                chant:108, time:"Morning"      },
              { translit:"Om Aryamanaye Namah",              meaning:"Salutations to Aryaman — lord of noble contracts, friendship and honourable unions",                              chant:108, time:"Noon"         },
              { translit:"Om Savitre Namah",                 meaning:"Salutations to Savitar — the radiant Sun that awakens all skill held within your hands",                         chant:108, time:"Sunrise"      },
              { translit:"Om Tvashtrae Namah",               meaning:"Salutations to Tvashtar — divine architect of all beautiful and perfect forms in creation",                       chant:108, time:"Morning"      },
              { translit:"Om Vayave Namah",                  meaning:"Salutations to Vayu — the wind, the life breath and the force of all true independence",                         chant:108, time:"Sunrise"      },
              { translit:"Om Indragniabhyam Namah",          meaning:"Salutations to Indra and Agni — lightning and fire, the twin powers of focused victory",                         chant:108, time:"Morning"      },
              { translit:"Om Mitraya Namah",                 meaning:"Salutations to Mitra — lord of sacred friendship, alliances and heartfelt devotion",                             chant:108, time:"Morning"      },
              { translit:"Om Indraya Namah",                 meaning:"Salutations to Indra — king of the gods, lord of courage, storms and righteous power",                           chant:108, time:"Sunrise"      },
              { translit:"Om Nirritaye Namah",               meaning:"Salutations to Nirriti — the goddess of dissolution, root-cutter and deep liberator",                            chant:108, time:"Dusk"         },
              { translit:"Om Adbhyo Namah",                  meaning:"Salutations to the Waters — source of all purification, flow and invincible grace",                              chant:108, time:"Evening"      },
              { translit:"Om Vishwebhyo Devebhyo Namah",    meaning:"Salutations to all the Universal Gods — the all-pervading forces of cosmic dharma",                               chant:108, time:"Morning"      },
              { translit:"Om Vishnave Namah",                meaning:"Salutations to Vishnu — the preserver, the great listener and sustainer of all worlds",                          chant:108, time:"Morning"      },
              { translit:"Om Vasubhyo Namah",                meaning:"Salutations to the Eight Vasus — elemental gods of abundance and all material blessings",                        chant:108, time:"Morning"      },
              { translit:"Om Varunaya Namah",                meaning:"Salutations to Varuna — cosmic guardian of truth, sacred water and karmic law",                                   chant:108, time:"Night"        },
              { translit:"Om Aja Ekapadaya Namah",           meaning:"Salutations to Aja Ekapada — the one-footed unborn fire, pillar of all cosmic order",                           chant:54,  time:"Before sleep" },
              { translit:"Om Ahir Budhnyaya Namah",          meaning:"Salutations to Ahir Budhnya — the serpent of the deep, ancient wisdom beneath all creation",                    chant:108, time:"Night"        },
              { translit:"Om Pushne Namah",                  meaning:"Salutations to Pushan — the gentle nourisher who guides every soul safely home",                                  chant:108, time:"Morning"      },
            ];

            const nak  = arcData.nakshatra;
            const meta = MANTRA_META[nak.nak_idx] || MANTRA_META[0];
            const nakName = nak.name?.[lang] || nak.name?.en || "";

            return (
              <div style={{ borderRadius:22,
                background:"#ffffff",
                border:"1.5px solid rgba(59,130,246,0.35)",
                boxShadow:"0 8px 40px rgba(59,130,246,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
                overflow:"hidden" }}>

                {/* Top accent */}
                <div style={{ height:3, background:"linear-gradient(90deg,transparent,#93c5fd,rgba(59,130,246,0.5),#93c5fd,transparent)" }} />

                <div style={{ padding:"22px 22px 24px" }}>
                  {/* Header row */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontSize:"1.8rem",
                        filter:"drop-shadow(0 0 10px rgba(59,130,246,0.7))" }}>📿</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                          color:"#93c5fd", fontWeight:700, letterSpacing:"0.06em" }}>
                          Today's Sacred Mantra
                        </div>
                        <div style={{ fontSize:"1rem", color:"#666666", marginTop:2 }}>
                          {nak.symbol} {nakName} Nakshatra · {nak.deity?.split("·")[0]?.trim()}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:"1rem", color:"rgba(59,130,246,0.8)",
                        fontFamily:JT.heading, letterSpacing:"0.06em" }}>BEST TIME</div>
                      <div style={{ fontSize:"1.2rem", color:"#93c5fd", fontWeight:600 }}>{meta.time}</div>
                    </div>
                  </div>

                  {/* Devanagari mantra — large */}
                  <div style={{ textAlign:"center", marginBottom:14,
                    padding:"20px 16px", borderRadius:16,
                    background:"rgba(59,130,246,0.08)",
                    border:"1px solid rgba(59,130,246,0.2)" }}>
                    <div style={{ fontSize:"1.5rem", lineHeight:1.7, color:"#1a1a1a",
                      fontFamily:"'Noto Sans Devanagari',serif",
                      textShadow:"0 0 20px rgba(59,130,246,0.4)",
                      letterSpacing:"0.04em", marginBottom:10 }}>
                      {nak.mantra}
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"rgba(59,130,246,0.9)",
                      fontStyle:"italic", letterSpacing:"0.03em", marginBottom:8 }}>
                      {meta.translit}
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#2b2b2b",
                      lineHeight:1.6, maxWidth:340, margin:"0 auto" }}>
                      {meta.meaning}
                    </div>
                  </div>

                  {/* Chant guide row */}
                  <div style={{ display:"flex", gap:10 }}>
                    <div style={{ flex:1, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.07)", border:"1px solid rgba(59,130,246,0.18)",
                      textAlign:"center" }}>
                      <div style={{ fontSize:"1.4rem", fontWeight:800, color:"#93c5fd",
                        fontFamily:"'Inter',sans-serif" }}>{meta.chant}</div>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        letterSpacing:"0.06em", fontFamily:JT.heading }}>TIMES</div>
                    </div>
                    <div style={{ flex:2, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.07)", border:"1px solid rgba(59,130,246,0.18)" }}>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        fontFamily:JT.heading, letterSpacing:"0.05em", marginBottom:3 }}>PRACTICE</div>
                      <div style={{ fontSize:"1.2rem", color:"#2b2b2b", lineHeight:1.4 }}>
                        Sit quietly facing east. Breathe 3 times. Chant {meta.chant} times. Offer to {nak.deity?.split("·")[0]?.trim()}.
                      </div>
                    </div>
                    <div style={{ flex:1, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.07)", border:"1px solid rgba(59,130,246,0.18)",
                      textAlign:"center", display:"flex", flexDirection:"column",
                      alignItems:"center", justifyContent:"center", gap:4 }}>
                      <span style={{ fontSize:"1.4rem" }}>🔔</span>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        fontFamily:JT.heading, letterSpacing:"0.04em" }}>REMIND</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Subscribed Home Dashboard ── */}
          {isSubscribed && arcData && !noBirth && (() => {
            const TIER_RANK  = { drishti:1, saathi:2, darshan:3 };
            const tierRank   = TIER_RANK[userTier] || 0;
            const TIER_META  = {
              drishti: { label:"Graha Raksha", emoji:"🛡️", color:"#93c5fd", rgb:"59,130,246"  },
              saathi:  { label:"Jyotish Saathi",emoji:"🧭", color:"#f5d17a", rgb:"212,175,55" },
              darshan: { label:"Guru Kripa",   emoji:"🔱", color:"#fdba74", rgb:"249,115,22"  },
            };
            const tier = TIER_META[userTier];
            const uLang = lang;

            const FEATURE_NAMES = {
              en: { hora:"Daily Hora", mantra:"Daily Mantra", archetype:"Archetype", nakshatra:"Nakshatra", kundali:"Birth Chart", dasha:"Dasha", dosha:"Dosha Check", milan:"Compatibility", shani:"Shani Status", ishta_devata:"Ishta Devata", planet_alerts:"Planet Alerts", puja_alerts:"Sacred Alerts", jyotish_weekly:"Weekly Energy", dasha_navigator:"Dasha Navigator", dosha_remedy:"Dosha Remedies", shani_kavach:"Shani Kavach", eclipse:"Eclipse Reports", retrograde:"Retrograde", festival:"Festival Guide", varshaphal:"Varshaphal", muhurta:"Muhurta Picker", jyotish_chat:"AI Pandit", sadhana:"Graha Sadhana", beej_mantra:"Beej Mantra", gemstone:"Gemstone Guide", health_alerts:"Health Alerts", prakriti:"Ayurvedic Read", family:"Family Charts", puja_calendar:"Puja Calendar", quarterly:"Quarterly Report" },
              hi: { hora:"होरा चक्र", mantra:"नक्षत्र मंत्र", archetype:"लग्न शक्ति", nakshatra:"जन्म नक्षत्र", kundali:"कुंडली", dasha:"महादशा", dosha:"ग्रह दोष", milan:"कुंडली मिलान", shani:"शनि स्थिति", ishta_devata:"इष्ट देवता", planet_alerts:"ग्रह प्रवेश", puja_alerts:"तिथि सूचना", jyotish_weekly:"साप्ताहिक फल", dasha_navigator:"दशा विचार", dosha_remedy:"दोष निवारण", shani_kavach:"शनि कवच", eclipse:"ग्रहण फल", retrograde:"वक्री ग्रह", festival:"पर्व फल", varshaphal:"वर्षफल", muhurta:"शुभ मुहूर्त", jyotish_chat:"AI पंडित", sadhana:"ग्रह साधना", beej_mantra:"बीज मंत्र", gemstone:"रत्न विचार", health_alerts:"आरोग्य सूचना", prakriti:"प्रकृति विचार", family:"परिवार कुंडली", puja_calendar:"पूजा तिथि", quarterly:"त्रैमासिक फल" },
              hi_roman: { hora:"Hora Chakra", mantra:"Nakshatra Mantra", archetype:"Lagna Shakti", nakshatra:"Janma Nakshatra", kundali:"Janma Kundali", dasha:"Mahadasha", dosha:"Graha Dosha", milan:"Kundali Milan", shani:"Shani Sthiti", ishta_devata:"Ishta Devata", planet_alerts:"Graha Pravesh", puja_alerts:"Tithi Suchana", jyotish_weekly:"Saptahik Fal", dasha_navigator:"Dasha Vichar", dosha_remedy:"Dosha Nivaran", shani_kavach:"Shani Kavach", eclipse:"Grahan Fal", retrograde:"Vakri Graha", festival:"Parva Fal", varshaphal:"Varshaphal", muhurta:"Shubh Muhurta", jyotish_chat:"AI Pandit", sadhana:"Graha Sadhana", beej_mantra:"Beej Mantra", gemstone:"Ratna Vichar", health_alerts:"Arogya Suchana", prakriti:"Prakriti Vichar", family:"Parivar Kundali", puja_calendar:"Puja Tithi", quarterly:"Traimasik Fal" },
              te: { hora:"శుభ ఘడి", mantra:"నక్షత్ర మంత్రం", archetype:"లగ్న స్వభావం", nakshatra:"జన్మ నక్షత్రం", kundali:"జాతకం", dasha:"మహాదశ", dosha:"జాతక దోషాలు", milan:"వివాహ పొందిక", shani:"శని ప్రభావం", ishta_devata:"ఇష్ట దేవత", planet_alerts:"గ్రహ మార్పులు", puja_alerts:"తిథుల సూచన", jyotish_weekly:"వారఫలాలు", dasha_navigator:"దశా మార్గదర్శి", dosha_remedy:"దోష పరిహారం", shani_kavach:"శని కవచం", eclipse:"గ్రహణ ప్రభావం", retrograde:"వక్రి గ్రహాలు", festival:"పర్వదిన ఫలాలు", varshaphal:"వార్షిక ఫలాలు", muhurta:"శుభముహూర్తం", jyotish_chat:"AI పండితుడు", sadhana:"గ్రహ సాధన", beej_mantra:"బీజ మంత్రం", gemstone:"రత్న సూచన", health_alerts:"గ్రహ ఆరోగ్య హెచ్చరిక", prakriti:"ఆయుర్వేద ప్రకృతి", family:"కుటుంబ జాతకాలు", puja_calendar:"పూజా పంచాంగం", quarterly:"త్రైమాసిక ఫలాలు" },
              te_roman: { hora:"Shubha Ghadi", mantra:"Nakshatra Mantram", archetype:"Lagna Swabhavam", nakshatra:"Janma Nakshatram", kundali:"Jatakam", dasha:"Mahadasha", dosha:"Jataka Dosham", milan:"Vivaha Porika", shani:"Shani Prabhavam", ishta_devata:"Ishta Devata", planet_alerts:"Graha Marpulu", puja_alerts:"Tithula Suchana", jyotish_weekly:"Varaphalam", dasha_navigator:"Dasha Margadarshi", dosha_remedy:"Dosha Parihaaram", shani_kavach:"Shani Kavacham", eclipse:"Grahana Prabhavam", retrograde:"Vakri Grahalu", festival:"Parvadina Phalalu", varshaphal:"Varshika Phalalu", muhurta:"Shubha Muhurtam", jyotish_chat:"AI Panditulu", sadhana:"Graha Sadhana", beej_mantra:"Bija Mantram", gemstone:"Ratna Suchana", health_alerts:"Graha Arogya Alert", prakriti:"Ayurveda Prakriti", family:"Kutumba Jatakalu", puja_calendar:"Puja Panchangam", quarterly:"Traimasika Phalalu" },
              ta: { hora:"நல்ல நேரம் · ஹோரை", mantra:"நட்சத்திர மந்திரம்", archetype:"உங்கள் ஜோதிட குணம்", nakshatra:"ஜன்ம நட்சத்திரம்", kundali:"ஜாதகம்", dasha:"தசை", dosha:"தோஷம்", milan:"ஜோடி பொருத்தம்", shani:"சனி பார்வை", ishta_devata:"இஷ்ட தெய்வம்", planet_alerts:"கிரக நிலை மாற்றம்", puja_alerts:"திதி நல்நேரம்", jyotish_weekly:"வார ராசி பலன்", dasha_navigator:"தசை புக்தி", dosha_remedy:"தோஷ பரிகாரம்", shani_kavach:"சனி கவசம்", eclipse:"கிரகண பலன்", retrograde:"வக்ர கிரகம்", festival:"பண்டிகை பலன்", varshaphal:"வருட ராசி பலன்", muhurta:"சுப முகூர்த்தம்", jyotish_chat:"AI ஜோதிடர்", sadhana:"கிரக சாதனை", beej_mantra:"பீஜ மந்திரம்", gemstone:"ரத்ன விசாரம்", health_alerts:"ஆரோக்கிய எச்சரிக்கை", prakriti:"பிரகிருதி விசாரம்", family:"குடும்ப ஜாதகம்", puja_calendar:"பூஜை திதி", quarterly:"முக்கால் வருட பலன்" },
              ta_roman: { hora:"Nalla Neram", mantra:"Natchatiram Mantiram", archetype:"Ungal Jothida Gunam", nakshatra:"Janma Natchatiram", kundali:"Jatakam", dasha:"Dasai", dosha:"Dosham", milan:"Jodi Porutham", shani:"Sani Parvai", ishta_devata:"Ishta Deivam", planet_alerts:"Kiraka Nilai Maatram", puja_alerts:"Tithi Nalla Neram", jyotish_weekly:"Vara Rasi Palan", dasha_navigator:"Dasai Bukthi", dosha_remedy:"Dosha Parikaaram", shani_kavach:"Sani Kavacham", eclipse:"Grahana Palan", retrograde:"Vakra Kirakam", festival:"Tiruvizha Palan", varshaphal:"Varusha Rasi Palan", muhurta:"Subha Muhurtam", jyotish_chat:"AI Jothidar", sadhana:"Kiraka Sadanai", beej_mantra:"Bija Mantiram", gemstone:"Ratna Vichar", health_alerts:"Aarogya Echcharikkai", prakriti:"Prakriti Vichar", family:"Kudumba Jatakam", puja_calendar:"Puja Tithi", quarterly:"Mukkal Varusha Palan" },
              kn: { hora:"ಹೋರಾ ಚಕ್ರ", mantra:"ನಕ್ಷತ್ರ ಮಂತ್ರ", archetype:"ಲಗ್ನ ಶಕ್ತಿ", nakshatra:"ಜನ್ಮ ನಕ್ಷತ್ರ", kundali:"ಜಾತಕ", dasha:"ದಶಾ", dosha:"ದೋಷ", milan:"ಕೂಟ ಮಿಲನ", shani:"ಶನಿ ಸ್ಥಿತಿ", ishta_devata:"ಇಷ್ಟ ದೇವತೆ", planet_alerts:"ಗ್ರಹ ಪ್ರವೇಶ", puja_alerts:"ತಿಥಿ ಸೂಚನೆ", jyotish_weekly:"ವಾರ ಫಲ", dasha_navigator:"ದಶಾ ವಿಚಾರ", dosha_remedy:"ದೋಷ ನಿವಾರಣ", shani_kavach:"ಶನಿ ಕವಚ", eclipse:"ಗ್ರಹಣ ಫಲ", retrograde:"ವಕ್ರಿ ಗ್ರಹ", festival:"ಹಬ್ಬದ ಫಲ", varshaphal:"ವರ್ಷಫಲ", muhurta:"ಶುಭ ಮುಹೂರ್ತ", jyotish_chat:"AI ಪಂಡಿತ", sadhana:"ಗ್ರಹ ಸಾಧನ", beej_mantra:"ಬೀಜ ಮಂತ್ರ", gemstone:"ರತ್ನ ವಿಚಾರ", health_alerts:"ಆರೋಗ್ಯ ಸೂಚನೆ", prakriti:"ಪ್ರಕೃತಿ ವಿಚಾರ", family:"ಕುಟುಂಬ ಜಾತಕ", puja_calendar:"ಪೂಜಾ ತಿಥಿ", quarterly:"ತ್ರೈಮಾಸಿಕ ಫಲ" },
              kn_roman: { hora:"Hora Chakra", mantra:"Nakshatra Mantra", archetype:"Lagna Shakti", nakshatra:"Janma Nakshatra", kundali:"Janma Jataka", dasha:"Mahadasha", dosha:"Graha Dosha", milan:"Koota Milana", shani:"Shani Sthiti", ishta_devata:"Ishta Devate", planet_alerts:"Graha Pravesh", puja_alerts:"Tithi Suchane", jyotish_weekly:"Vara Phala", dasha_navigator:"Dasha Vichar", dosha_remedy:"Dosha Nivarana", shani_kavach:"Shani Kavach", eclipse:"Grahana Phala", retrograde:"Vakri Graha", festival:"Habba Phala", varshaphal:"Varshaphala", muhurta:"Shubha Muhurta", jyotish_chat:"AI Pandita", sadhana:"Graha Sadhane", beej_mantra:"Bija Mantra", gemstone:"Ratna Vichar", health_alerts:"Arogya Suchane", prakriti:"Prakriti Vichar", family:"Kutumba Jataka", puja_calendar:"Puja Tithi", quarterly:"Trimasa Phala" },
              ml: { hora:"ഹോര ചക്രം", mantra:"നക്ഷത്ര മന്ത്രം", archetype:"ലഗ്ന ശക്തി", nakshatra:"ജന്മ നക്ഷത്രം", kundali:"ജാതകം", dasha:"ദശ", dosha:"ദോഷം", milan:"കൂടൽ", shani:"ശനി സ്ഥിതി", ishta_devata:"ഇഷ്ട ദേവത", planet_alerts:"ഗ്രഹ പ്രവേശം", puja_alerts:"തിഥി അറിയിപ്പ്", jyotish_weekly:"വാര ഫലം", dasha_navigator:"ദശ വിചാരം", dosha_remedy:"ദോഷ നിവാരണം", shani_kavach:"ശനി കവചം", eclipse:"ഗ്രഹണ ഫലം", retrograde:"വക്രി ഗ്രഹം", festival:"ഉൽസവ ഫലം", varshaphal:"വർഷഫലം", muhurta:"ശുഭ മുഹൂർത്തം", jyotish_chat:"AI പണ്ഡിതൻ", sadhana:"ഗ്രഹ സാധന", beej_mantra:"ബീജ മന്ത്രം", gemstone:"രത്ന വിചാരം", health_alerts:"ആരോഗ്യ അറിയിപ്പ്", prakriti:"പ്രകൃതി വിചാരം", family:"കുടുംബ ജാതകം", puja_calendar:"പൂജ തിഥി", quarterly:"ത്രൈമാസിക ഫലം" },
              mr: { hora:"होरा चक्र", mantra:"नक्षत्र मंत्र", archetype:"लग्न शक्ती", nakshatra:"जन्म नक्षत्र", kundali:"जन्म पत्रिका", dasha:"महादशा", dosha:"ग्रह दोष", milan:"कुंडली जुळणी", shani:"शनि स्थिती", ishta_devata:"इष्ट देवता", planet_alerts:"ग्रह प्रवेश", puja_alerts:"तिथी सूचना", jyotish_weekly:"साप्ताहिक फल", dasha_navigator:"दशा विचार", dosha_remedy:"दोष निवारण", shani_kavach:"शनि कवच", eclipse:"ग्रहण फल", retrograde:"वक्री ग्रह", festival:"सण फल", varshaphal:"वर्षफल", muhurta:"शुभ मुहूर्त", jyotish_chat:"AI पंडित", sadhana:"ग्रह साधना", beej_mantra:"बीज मंत्र", gemstone:"रत्न विचार", health_alerts:"आरोग्य सूचना", prakriti:"प्रकृती विचार", family:"कुटुंब पत्रिका", puja_calendar:"पूजा तिथी", quarterly:"त्रैमासिक फल" },
              mr_roman: { hora:"Hora Chakra", mantra:"Nakshatra Mantra", archetype:"Lagna Shakti", nakshatra:"Janma Nakshatra", kundali:"Janma Patrika", dasha:"Mahadasha", dosha:"Graha Dosha", milan:"Kundali Julani", shani:"Shani Sthiti", ishta_devata:"Ishta Devata", planet_alerts:"Graha Pravesh", puja_alerts:"Tithi Suchana", jyotish_weekly:"Saptahik Phal", dasha_navigator:"Dasha Vichar", dosha_remedy:"Dosha Nivaran", shani_kavach:"Shani Kavach", eclipse:"Grahan Phal", retrograde:"Vakri Graha", festival:"San Phal", varshaphal:"Varshaphal", muhurta:"Shubh Muhurta", jyotish_chat:"AI Pandit", sadhana:"Graha Sadhana", beej_mantra:"Bij Mantra", gemstone:"Ratna Vichar", health_alerts:"Arogya Suchana", prakriti:"Prakriti Vichar", family:"Kutumb Patrika", puja_calendar:"Puja Tithi", quarterly:"Traimasik Phal" },
            };
            const CAT_LABELS = {
              en:       { today:"Today", yourchart:"Your Chart",          timing:"Timing & Life Chapters",              doshaprotection:"Doshas & Protection",   alerts:"Alerts",              relationships:"Relationships & Family",        dailypractice:"Daily Practice"       },
              hi:       { today:"आज का", yourchart:"आपकी कुंडली",         timing:"समय और जीवन चरण",                     doshaprotection:"दोष और सुरक्षा",         alerts:"सूचनाएं",             relationships:"रिश्ते और परिवार",              dailypractice:"दैनिक साधना"          },
              hi_roman: { today:"Aaj Ka", yourchart:"Aapki Kundali",       timing:"Samay Aur Jeevan Charan",             doshaprotection:"Dosh Aur Suraksha",      alerts:"Suchnayen",           relationships:"Rishte Aur Parivar",            dailypractice:"Dainik Sadhana"       },
              te:       { today:"ఈరోజు", yourchart:"మీ జాతకం",             timing:"కాలం & జీవిత దశలు",                  doshaprotection:"దోషాలు & రక్షణ",         alerts:"సూచనలు",              relationships:"సంబంధాలు & కుటుంబం",           dailypractice:"దైనందిన సాధన"         },
              te_roman: { today:"Eeroju", yourchart:"Mee Jatakam",         timing:"Kaalam & Jeevita Dashalu",            doshaprotection:"Doshaalu & Rakshana",    alerts:"Suchanalu",           relationships:"Sambandhaalu & Kutumbam",       dailypractice:"Dainandina Sadhana"   },
              ta:       { today:"இன்றைய பலன்", yourchart:"உங்கள் ஜாதகம்", timing:"காலமும் வாழ்க்கை கட்டங்களும்",       doshaprotection:"தோஷங்கள் & பாதுகாப்பு",  alerts:"அறிவிப்புகள்",        relationships:"உறவுகள் & குடும்பம்",           dailypractice:"தினசரி சாதனை"         },
              ta_roman: { today:"Indraiya Palan", yourchart:"Ungal Jatakam", timing:"Kaalamum Vaazhkkai Kattangalum",   doshaprotection:"Doshangal & Paadhukaapu", alerts:"Arivippugal",        relationships:"Uravugal & Kudumbam",           dailypractice:"Thinasari Saadhanai"  },
              kn:       { today:"ಇಂದು", yourchart:"ನಿಮ್ಮ ಜಾತಕ",           timing:"ಕಾಲ ಮತ್ತು ಜೀವನ ಹಂತಗಳು",              doshaprotection:"ದೋಷಗಳು ಮತ್ತು ರಕ್ಷಣೆ",     alerts:"ಸೂಚನೆಗಳು",           relationships:"ಸಂಬಂಧಗಳು ಮತ್ತು ಕುಟುಂಬ",        dailypractice:"ದೈನಂದಿನ ಸಾಧನೆ"        },
              kn_roman: { today:"Indu", yourchart:"Nimma Jataka",          timing:"Kaala Mattu Jeevana Hantagalu",       doshaprotection:"Doshagalu Mattu Rakshane", alerts:"Suchanegalu",       relationships:"Sambandhagalu Mattu Kutumba",   dailypractice:"Dainandina Sadhane"   },
              ml:       { today:"ഇന്ന്", yourchart:"നിങ്ങളുടെ ജാതകം",     timing:"കാലവും ജീവിത ഘട്ടങ്ങളും",            doshaprotection:"ദോഷങ്ങളും സംരക്ഷണവും",   alerts:"അറിയിപ്പുകൾ",         relationships:"ബന്ധങ്ങളും കുടുംബവും",          dailypractice:"ദൈനംദിന സാധന"         },
              mr:       { today:"आजचे", yourchart:"तुमची पत्रिका",         timing:"काळ आणि जीवन टप्पे",                  doshaprotection:"दोष आणि रक्षण",          alerts:"सूचना",               relationships:"नाती आणि कुटुंब",               dailypractice:"दैनंदिन साधना"        },
              mr_roman: { today:"Aajche", yourchart:"Tumchi Patrika",      timing:"Kaal Ani Jeevan Tappe",               doshaprotection:"Dosh Ani Rakshan",       alerts:"Suchana",             relationships:"Naati Ani Kutumb",              dailypractice:"Dainandin Sadhana"    },
            };
            const ROMAN_LANGS = new Set(["hi","te","ta","kn","mr"]);
            const resolvedLang = (ROMAN_LANGS.has(uLang) && scriptPref === "roman") ? `${uLang}_roman` : uLang;
            const fNames  = FEATURE_NAMES[resolvedLang] || FEATURE_NAMES.en;
            const cLabels = CAT_LABELS[resolvedLang]     || CAT_LABELS.en;

            const CATS = [
              {
                // Quick daily glance — kept separate from the rest for its distinct
                // "check right now" style, not because of pricing tier.
                label:cLabels.today, key:"today",
                features:[
                  { id:"hora",   icon:"⏱️", title:"Daily Hora",  color:"#93c5fd", rgb:"59,130,246",  minTier:"drishti" },
                  { id:"mantra", icon:"📿", title:"Daily Mantra", color:"#a78bfa", rgb:"167,139,250", minTier:"drishti" },
                ],
              },
              {
                // Core chart identity — who you are astrologically.
                label:cLabels.yourchart, key:"yourchart",
                features:[
                  { id:"archetype", icon:"🔱", title:"Archetype",   color:"#d4a853", rgb:"212,168,83", minTier:null },
                  { id:"nakshatra", icon:"⭐", title:"Nakshatra",   color:"#f59e0b", rgb:"245,158,11", minTier:null },
                  { id:"kundali",   icon:"🪐", title:"Birth Chart", color:"#c9a84c", rgb:"201,168,76", minTier:null },
                ],
              },
              {
                // Everything about "when" — planetary periods and life cycles.
                label:cLabels.timing, key:"timing",
                features:[
                  { id:"dasha",           icon:"🌀", title:"Dasha",           color:"#22c55e", rgb:"34,197,94",  minTier:null },
                  { id:"dasha_navigator", icon:"⏳", title:"Dasha Navigator", color:"#22c55e", rgb:"34,197,94",  minTier:"saathi" },
                  { id:"varshaphal",      icon:"📆", title:"Varshaphal",      color:"#22c55e", rgb:"34,197,94",  minTier:"saathi" },
                  { id:"muhurta",         icon:"🕐", title:"Muhurta Picker",  color:"#f5d17a", rgb:"212,175,55", minTier:"saathi" },
                  { id:"jyotish_weekly",  icon:"📅", title:"Weekly Energy",   color:"#14b8a6", rgb:"20,184,166", minTier:"saathi" },
                  { id:"quarterly",       icon:"📜", title:"Quarterly Report",color:"#fbbf24", rgb:"251,191,36", minTier:"darshan" },
                ],
              },
              {
                // Problem + remedy together — a dosha and its fix now live in
                // the same section instead of being split by price tier.
                label:cLabels.doshaprotection, key:"doshaprotection",
                features:[
                  { id:"dosha",        icon:"⚠️", title:"Dosha Check",   color:"#ef4444", rgb:"239,68,68",   minTier:null },
                  { id:"dosha_remedy", icon:"💊", title:"Dosha Remedies",color:"#ef4444", rgb:"239,68,68",   minTier:"saathi" },
                  { id:"shani",        icon:"🪐", title:"Shani Status",  color:"#64748b", rgb:"100,116,139", minTier:"drishti" },
                  { id:"shani_kavach", icon:"🪐", title:"Shani Kavach",  color:"#64748b", rgb:"100,116,139", minTier:"saathi" },
                  { id:"ishta_devata", icon:"🪔", title:"Ishta Devata",  color:"#f59e0b", rgb:"245,158,11",  minTier:"drishti" },
                ],
              },
              {
                // Timely notifications — planetary/sky events and health flags.
                label:cLabels.alerts, key:"alerts",
                features:[
                  { id:"planet_alerts", icon:"🪐", title:"Planet Alerts", color:"#93c5fd", rgb:"59,130,246",  minTier:"drishti" },
                  { id:"puja_alerts",   icon:"🌙", title:"Sacred Alerts", color:"#a78bfa", rgb:"167,139,250", minTier:"drishti" },
                  { id:"eclipse",       icon:"🌘", title:"Eclipse Reports",color:"#a855f7", rgb:"168,85,247", minTier:"saathi" },
                  { id:"retrograde",    icon:"🔄", title:"Retrograde",    color:"#06b6d4", rgb:"6,182,212",   minTier:"saathi" },
                  { id:"festival",      icon:"🎉", title:"Festival Guide",color:"#f97316", rgb:"249,115,22",  minTier:"saathi" },
                  { id:"health_alerts", icon:"🏥", title:"Health Alerts", color:"#ef4444", rgb:"239,68,68",   minTier:"darshan" },
                ],
              },
              {
                label:cLabels.relationships, key:"relationships",
                features:[
                  { id:"milan",  icon:"💑", title:"Compatibility", color:"#ec4899", rgb:"236,72,153", minTier:null },
                  { id:"family", icon:"👨‍👩‍👧", title:"Family Charts", color:"#ec4899", rgb:"236,72,153", minTier:"darshan" },
                ],
              },
              {
                // Ongoing spiritual tools you'd come back to regularly.
                label:cLabels.dailypractice, key:"dailypractice",
                features:[
                  { id:"jyotish_chat",  icon:"💬", title:"AI Pandit",      color:"#c9a84c", rgb:"201,168,76",  minTier:"darshan" },
                  { id:"sadhana",       icon:"🔱", title:"Graha Sadhana",  color:"#f97316", rgb:"249,115,22",  minTier:"darshan" },
                  { id:"beej_mantra",   icon:"📿", title:"Beej Mantra",    color:"#a78bfa", rgb:"167,139,250", minTier:"darshan" },
                  { id:"gemstone",      icon:"💎", title:"Gemstone Guide", color:"#34d399", rgb:"52,211,153",  minTier:"darshan" },
                  { id:"prakriti",      icon:"🌿", title:"Ayurvedic Read", color:"#22c55e", rgb:"34,197,94",   minTier:"darshan" },
                  { id:"puja_calendar", icon:"🙏", title:"Puja Calendar",  color:"#f59e0b", rgb:"245,158,11",  minTier:"darshan" },
                ],
              },
            ];

            // showcase cards always visible (conversion); other features gated by tier
            const SHOWCASE_IDS = new Set(["planet_alerts","puja_alerts","jyotish_chat"]);
            const visibleCats = CATS.map(cat => ({
              ...cat,
              features: cat.features.filter(f =>
                SHOWCASE_IDS.has(f.id) || !f.minTier || (TIER_RANK[f.minTier] || 0) <= tierRank
              ),
            })).filter(cat => cat.features.length > 0);

            return (
              <div style={{ marginBottom:24 }}>

                {/* Plan badge + script toggle */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
                  {tier && (
                    <div style={{ display:"inline-flex", alignItems:"center", gap:8,
                      padding:"6px 14px", borderRadius:20,
                      background:`rgba(${tier.rgb},0.12)`,
                      border:`1px solid rgba(${tier.rgb},0.35)` }}>
                      <span style={{ fontSize:"1.1rem" }}>{tier.emoji}</span>
                      <span style={{ fontFamily:JT.label, fontSize:"0.95rem",
                        color:tier.color, letterSpacing:"0.02em", fontWeight:600 }}>
                        {tier.label}
                      </span>
                    </div>
                  )}
                  {ROMAN_LANGS.has(uLang) && (
                    <div style={{ display:"flex", borderRadius:10, overflow:"hidden",
                      border:"1px solid #999999" }}>
                      {[["roman","Aa"],["native", LANG_CHIPS[uLang] || "अ"]].map(([val,label]) => (
                        <button key={val} onClick={() => { setScriptPref(val); localStorage.setItem("jyotish_script_pref", val); }}
                          style={{ padding:"5px 13px", fontSize:"1.0rem", border:"none", cursor:"pointer",
                            fontFamily: val === "native" ? "inherit" : "sans-serif",
                            background: scriptPref === val ? "#999999" : "transparent",
                            color: scriptPref === val ? "#1a1a1a" : "#666666",
                            transition:"all 0.15s" }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Categorized feature sections */}
                {visibleCats.map(cat => (
                  <div key={cat.key} style={{ marginBottom:20 }}>
                    <div style={{ fontSize:"0.95rem", letterSpacing:"0.14em",
                      color:JT.textDim, fontFamily:JT.label, fontWeight:600,
                       marginBottom:10 }}>
                      {cat.label}
                    </div>
                    <div style={{ display:"grid",
                      gridTemplateColumns: cat.key === "today" ? "1fr 1fr" : "repeat(3,1fr)",
                      gap:10 }}>
                      {cat.features.map(f => (
                        cat.key === "today"
                          ? (
                            <div key={f.id} onClick={() => setView(f.id)}
                              style={{ display:"flex", alignItems:"center", gap:12,
                                padding:"14px 16px", borderRadius:14, cursor:"pointer",
                                background:`rgba(${f.rgb},0.09)`,
                                border:`1px solid rgba(${f.rgb},0.28)`,
                                transition:"all 0.18s" }}
                              onMouseEnter={e => { e.currentTarget.style.background=`rgba(${f.rgb},0.17)`; e.currentTarget.style.borderColor=`rgba(${f.rgb},0.5)`; }}
                              onMouseLeave={e => { e.currentTarget.style.background=`rgba(${f.rgb},0.09)`; e.currentTarget.style.borderColor=`rgba(${f.rgb},0.28)`; }}
                            >
                              <span style={{ fontSize:"1.8rem", filter:`drop-shadow(0 0 8px rgba(${f.rgb},0.5))`, flexShrink:0 }}>{f.icon}</span>
                              <div>
                                <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
                                  color:f.color, fontWeight:700, marginBottom:2 }}>{fNames[f.id] || f.title}</div>
                                <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                  fontFamily:JT.body, fontStyle:"italic" }}>
                                  {f.id === "hora"
                                    ? ({ en:"Best hour right now", hi:"अभी का शुभ समय", hi_roman:"Abhi ka shubh samay", te:"ఇప్పటి శుభ వేళ", te_roman:"Ippati shubha vela", ta:"இப்போதைய நல்ல நேரம்", ta_roman:"Ippodaya Nalla Neram", kn:"ಈಗಿನ ಶುಭ ಸಮಯ", kn_roman:"Eegina Shubha Samaya", ml:"ഇപ്പോഴത്തെ ശുഭ നേരം", mr:"आत्ताचा शुभ काळ", mr_roman:"Aattacha Shubh Kaal" }[resolvedLang] || "Best hour right now")
                                    : ({ en:"Your Nakshatra mantra", hi:"आपका नक्षत्र मंत्र", hi_roman:"Aapka nakshatra mantra", te:"మీ నక్షత్ర మంత్రం", te_roman:"Mee nakshatra mantram", ta:"உங்கள் ஜன்ம நட்சத்திர மந்திரம்", ta_roman:"Ungal Janma Natchatiram Mantiram", kn:"ನಿಮ್ಮ ನಕ್ಷತ್ರ ಮಂತ್ರ", kn_roman:"Nimma nakshatra mantra", ml:"നിങ്ങളുടെ നക്ഷത്ര മന്ത്രം", mr:"तुमचा नक्षत्र मंत्र", mr_roman:"Tumcha nakshatra mantra" }[resolvedLang] || "Your Nakshatra mantra")
                                  }
                                </div>
                              </div>
                            </div>
                          ) : f.id === "puja_alerts" && (TIER_RANK[f.minTier] || 0) > tierRank ? (
                            /* ── Sacred Moon Alerts — Heaven Card ── */
                            <div key="puja_alerts" style={{ gridColumn:"1 / -1", borderRadius:20, overflow:"hidden",
                              border:"1px solid rgba(167,139,250,0.28)",
                              background:"#ffffff",
                              boxShadow:"0 10px 32px rgba(124,58,237,0.16)",
                              position:"relative" }}>
                              {/* Top shimmer */}
                              <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(167,139,250,0.7),rgba(216,180,254,0.9),rgba(167,139,250,0.7),transparent)" }} />
                              {/* Ambient moon glow */}
                              <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180,
                                borderRadius:"50%", background:"radial-gradient(circle,rgba(167,139,250,0.08) 0%,transparent 70%)", pointerEvents:"none" }} />
                              <div style={{ padding:"18px 20px 20px" }}>
                                {/* Header */}
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                                    <div style={{ width:44, height:44, borderRadius:"50%",
                                      background:"linear-gradient(135deg,rgba(167,139,250,0.2),rgba(109,40,217,0.15))",
                                      border:"1px solid rgba(167,139,250,0.35)",
                                      display:"flex", alignItems:"center", justifyContent:"center",
                                      boxShadow:"0 0 16px rgba(167,139,250,0.25), inset 0 1px 0 rgba(255,255,255,0.1)",
                                      flexShrink:0 }}>
                                      <span style={{ fontSize:"1.4rem", filter:"drop-shadow(0 0 8px rgba(216,180,254,0.8))" }}>🌙</span>
                                    </div>
                                    <div>
                                      <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                                        color:"#7c3aed", fontWeight:700, letterSpacing:"0.01em", marginBottom:2 }}>
                                        {fNames["puja_alerts"] || "Sacred Alerts"}
                                      </div>
                                      <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                        fontFamily:JT.body, fontStyle:"italic" }}>
                                        { {en:"Never miss a sacred day", hi:"कोई तिथि न चूकें", te:"ఏ తిథి మిస్ కావద్దు", ta:"எந்த திதியும் தவறாதீர்கள்", kn:"ಯಾವ ತಿಥಿಯನ್ನೂ ತಪ್ಪಿಸಬೇಡಿ", mr:"कोणती तिथी चुकवू नका", ml:"ഒരു തിഥിയും നഷ്ടമാക്കരുത്" }[uLang] || "Never miss a sacred day" }
                                      </div>
                                    </div>
                                  </div>
                                  <button onClick={handleUpgrade} style={{
                                    background:"linear-gradient(135deg,#7c3aed,#a855f7,#7c3aed)",
                                    backgroundSize:"200% 100%",
                                    color:"#fff", border:"none", borderRadius:20,
                                    padding:"9px 18px", cursor:"pointer",
                                    fontFamily:JT.label, fontSize:"0.95rem",
                                    letterSpacing:"0.02em", fontWeight:700, flexShrink:0,
                                    boxShadow:"0 4px 18px rgba(124,58,237,0.5)",
                                    transition:"all 0.2s",
                                  }}
                                    onMouseEnter={e=>{ e.currentTarget.style.boxShadow="0 6px 24px rgba(168,85,247,0.7)"; e.currentTarget.style.transform="translateY(-1px)"; }}
                                    onMouseLeave={e=>{ e.currentTarget.style.boxShadow="0 4px 18px rgba(124,58,237,0.5)"; e.currentTarget.style.transform="none"; }}
                                  >
                                    { {en:"Unlock", hi:"अनलॉक करें", te:"అన్‌లాక్", ta:"திறக்க", kn:"ಅನ್‌ಲಾಕ್", mr:"उघडा", ml:"അൺലോക്ക്" }[uLang] || "Unlock" } 🔓
                                  </button>
                                </div>
                                {/* Preview rows — blurred FOMO */}
                                <div style={{ position:"relative" }}>
                                  <div style={{ display:"flex", flexDirection:"column", gap:8,
                                    filter:"blur(2.5px)", opacity:0.55, pointerEvents:"none", userSelect:"none" }}>
                                    {[
                                      { icon:"🌑", days:"3", name:{en:"Amavasya",hi:"अमावस्या",te:"అమావాస్య",ta:"அமாவாசை",kn:"ಅಮಾವಾಸ್ಯೆ",mr:"अमावस्या",ml:"അമാവാസ്യ"}[uLang]||"Amavasya", action:{en:"Ancestor Tarpan · Lamp offering",hi:"पितृ तर्पण · दीप दान",te:"పితృ తర్పణం · దీప దానం",ta:"பிதுர் தர்ப்பணம்",kn:"ಪಿತೃ ತರ್ಪಣ",mr:"पितृ तर्पण",ml:"പിതൃ തർപ്പണം"}[uLang]||"Ancestor Tarpan", color:"#94a3b8" },
                                      { icon:"🌿", days:"8", name:{en:"Ekadashi",hi:"एकादशी",te:"ఏకాదశి",ta:"ஏகாதசி",kn:"ಏಕಾದಶಿ",mr:"एकादशी",ml:"ഏകാദശി"}[uLang]||"Ekadashi", action:{en:"Vishnu Puja · Full fast",hi:"विष्णु पूजा · निर्जला व्रत",te:"విష్ణు పూజ · నిర్జల ఉపవాసం",ta:"விஷ்ணு பூஜை · விரதம்",kn:"ವಿಷ್ಣು ಪೂಜೆ · ಉಪವಾಸ",mr:"विष्णू पूजा · उपवास",ml:"വിഷ്ണു പൂജ · ഉപവാസം"}[uLang]||"Vishnu Puja · Fast", color:"#86efac" },
                                      { icon:"🔱", days:"12", name:{en:"Pradosham",hi:"प्रदोष",te:"ప్రదోషం",ta:"பிரதோஷம்",kn:"ಪ್ರದೋಷ",mr:"प्रदोष",ml:"പ്രദോഷം"}[uLang]||"Pradosham", action:{en:"Shiva Abhishek at twilight",hi:"त्रिकाल शिव अभिषेक",te:"సాయంకాలం శివ అభిషేకం",ta:"சந்தியா சிவ அபிஷேகம்",kn:"ಸಂಧ್ಯಾ ಶಿವ ಅಭಿಷೇಕ",mr:"संध्याकाळ शिव अभिषेक",ml:"പ്രദോഷ ശിവ അഭിഷേകം"}[uLang]||"Shiva Abhishek at twilight", color:"#fbbf24" },
                                    ].map((row,i) => (
                                      <div key={i} style={{ display:"flex", alignItems:"center", gap:12,
                                        background:"rgba(167,139,250,0.07)", border:"1px solid rgba(167,139,250,0.14)",
                                        borderRadius:12, padding:"10px 14px" }}>
                                        <span style={{ fontSize:"1.3rem", width:30, textAlign:"center", flexShrink:0 }}>{row.icon}</span>
                                        <div style={{ flex:1, minWidth:0 }}>
                                          <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
                                            color:"#2b2b2b", fontWeight:600 }}>{row.name}</div>
                                          <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                            fontFamily:"'Inter',sans-serif", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.action}</div>
                                        </div>
                                        <div style={{ background:"rgba(167,139,250,0.15)",
                                          border:"1px solid rgba(167,139,250,0.3)", borderRadius:20,
                                          padding:"4px 12px", fontSize:"1.0rem", color:row.color,
                                          fontFamily:JT.heading, fontWeight:700, flexShrink:0 }}>
                                          {row.days}d
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Lock veil */}
                                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
                                    justifyContent:"center", borderRadius:12,
                                    background:"linear-gradient(transparent 0%,rgba(7,9,26,0.35) 50%,transparent 100%)" }}>
                                    <div style={{ background:"rgba(7,9,26,0.75)", backdropFilter:"blur(4px)",
                                      border:"1px solid rgba(167,139,250,0.3)", borderRadius:20,
                                      padding:"8px 20px", display:"flex", alignItems:"center", gap:8 }}>
                                      <span style={{ fontSize:"1rem" }}>🔒</span>
                                      <span style={{ fontFamily:JT.label, fontWeight:600, fontSize:"0.95rem",
                                        color:"rgba(216,180,254,0.85)",  }}>
                                        { {en:"Subscribe to receive alerts",hi:"सदस्यता लें",te:"సభ్యత్వం తీసుకోండి",ta:"பலன் பெற இப்போதே சேருங்கள்",kn:"ಚಂದಾ ಮಾಡಿಕೊಳ್ಳಿ",mr:"सदस्यत्व घ्या",ml:"സബ്‌സ്‌ക്രൈബ് ചെయ്യൂ"}[uLang]||"Subscribe to receive alerts" }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : f.id === "planet_alerts" && (TIER_RANK[f.minTier] || 0) > tierRank ? (
                            /* ── Planet Guardian Alerts — Heaven Card ── */
                            <div key="planet_alerts" style={{ gridColumn:"1 / -1", borderRadius:20, overflow:"hidden",
                              border:"1px solid rgba(59,130,246,0.28)",
                              background:"#ffffff",
                              boxShadow:"0 10px 32px rgba(29,78,216,0.16)",
                              position:"relative" }}>
                              {/* Top shimmer */}
                              <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(59,130,246,0.7),rgba(147,197,253,0.9),rgba(59,130,246,0.7),transparent)" }} />
                              {/* Orbital glow */}
                              <div style={{ position:"absolute", bottom:-50, left:-30, width:200, height:200,
                                borderRadius:"50%", background:"radial-gradient(circle,rgba(59,130,246,0.07) 0%,transparent 70%)", pointerEvents:"none" }} />
                              <div style={{ padding:"18px 20px 20px" }}>
                                {/* Header */}
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                                    <div style={{ width:44, height:44, borderRadius:"50%",
                                      background:"linear-gradient(135deg,rgba(59,130,246,0.2),rgba(29,78,216,0.15))",
                                      border:"1px solid rgba(59,130,246,0.4)",
                                      display:"flex", alignItems:"center", justifyContent:"center",
                                      boxShadow:"0 0 16px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                                      flexShrink:0 }}>
                                      <span style={{ fontSize:"1.3rem", filter:"drop-shadow(0 0 8px rgba(147,197,253,0.9))" }}>🪐</span>
                                    </div>
                                    <div>
                                      <div style={{ fontFamily:JT.heading, fontSize:"1.05rem",
                                        color:"#1d4ed8", fontWeight:700, letterSpacing:"0.01em", marginBottom:2 }}>
                                        {fNames["planet_alerts"] || "Planet Alerts"}
                                      </div>
                                      <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                        fontFamily:JT.body, fontStyle:"italic" }}>
                                        { {en:"Planets move. Your life shifts.",hi:"ग्रह चलते हैं, भाग्य बदलता है",te:"గ్రహాలు కదులుతాయి, జీవితం మారుతుంది",ta:"கிரகங்கள் நகர்கின்றன, வாழ்க்கை மாறுகிறது",kn:"ಗ್ರಹಗಳು ಚಲಿಸುತ್ತವೆ, ಜೀವನ ಬದಲಾಗುತ್ತದೆ",mr:"ग्रह बदलतात, नशीब बदलते",ml:"ഗ്രഹങ്ങൾ നീങ്ങുന്നു, ജീവിതം മാറുന്നു"}[uLang]||"Planets move. Your life shifts." }
                                      </div>
                                    </div>
                                  </div>
                                  <button onClick={handleUpgrade} style={{
                                    background:"linear-gradient(135deg,#1d4ed8,#3b82f6,#1d4ed8)",
                                    backgroundSize:"200% 100%",
                                    color:"#fff", border:"none", borderRadius:20,
                                    padding:"9px 18px", cursor:"pointer",
                                    fontFamily:JT.label, fontSize:"0.95rem",
                                    letterSpacing:"0.02em", fontWeight:700, flexShrink:0,
                                    boxShadow:"0 4px 18px rgba(29,78,216,0.55)",
                                    transition:"all 0.2s",
                                  }}
                                    onMouseEnter={e=>{ e.currentTarget.style.boxShadow="0 6px 24px rgba(59,130,246,0.75)"; e.currentTarget.style.transform="translateY(-1px)"; }}
                                    onMouseLeave={e=>{ e.currentTarget.style.boxShadow="0 4px 18px rgba(29,78,216,0.55)"; e.currentTarget.style.transform="none"; }}
                                  >
                                    { {en:"Unlock", hi:"अनलॉक करें", te:"అన్‌లాక్", ta:"திறக்க", kn:"ಅನ್‌ಲಾಕ್", mr:"उघडा", ml:"അൺലോക്ക്" }[uLang] || "Unlock" } 🔓
                                  </button>
                                </div>
                                {/* Planet transit preview rows */}
                                <div style={{ position:"relative" }}>
                                  <div style={{ display:"flex", flexDirection:"column", gap:8,
                                    filter:"blur(2.5px)", opacity:0.5, pointerEvents:"none", userSelect:"none" }}>
                                    {[
                                      { symbol:"♃", planet:{en:"Jupiter",hi:"बृहस्पति",te:"బృహస్పతి",ta:"குரு",kn:"ಗುರು",mr:"गुरू",ml:"ഗുരു"}[uLang]||"Jupiter", event:{en:"enters Gemini",hi:"मिथुन प्रवेश",te:"మిథున ప్రవేశం",ta:"மிதுனம் பிரவேசம்",kn:"ಮಿಥುನ ಪ್ರವೇಶ",mr:"मिथुन प्रवेश",ml:"മിഥുന പ്രവേശം"}[uLang]||"enters Gemini", timing:"6d", urgency:"rgba(251,191,36,0.9)", bg:"rgba(251,191,36,0.08)", border:"rgba(251,191,36,0.2)" },
                                      { symbol:"♄", planet:{en:"Saturn",hi:"शनि",te:"శని",ta:"சனி",kn:"ಶನಿ",mr:"शनी",ml:"ശനി"}[uLang]||"Saturn", event:{en:"retrograde — caution period",hi:"वक्री — सावधानी काल",te:"వక్రి — జాగ్రత్త కాలం",ta:"வக்ரம் — சனி எச்சரிக்கை",kn:"ವಕ್ರಿ — ಎಚ್ಚರಿಕೆ ಕಾಲ",mr:"वक्री — सावधानतेचा काळ",ml:"വക്രി — ജാഗ്രതാ കാലം"}[uLang]||"retrograde — caution", timing:"NOW", urgency:"rgba(239,68,68,0.9)", bg:"rgba(239,68,68,0.07)", border:"rgba(239,68,68,0.2)" },
                                      { symbol:"☿", planet:{en:"Mercury",hi:"बुध",te:"బుధుడు",ta:"புதன்",kn:"ಬುಧ",mr:"बुध",ml:"ബുധൻ"}[uLang]||"Mercury", event:{en:"enters Aries — sharp thinking",hi:"मेष प्रवेश — तेज बुद्धि",te:"మేష ప్రవేశం — తీక్ష్ణ మేధ",ta:"மேஷம் பிரவேசம் — கூர்மையான சிந்தனை",kn:"ಮೇಷ ಪ್ರವೇಶ — ಚುರುಕಿನ ಬುದ್ಧಿ",mr:"मेष प्रवेश — तीक्ष्ण बुद्धी",ml:"മേഷ പ്രവേശം — ശക്തമായ ബുദ്ധി"}[uLang]||"enters Aries — sharp thinking", timing:"2d", urgency:"rgba(52,211,153,0.9)", bg:"rgba(52,211,153,0.07)", border:"rgba(52,211,153,0.2)" },
                                    ].map((row,i) => (
                                      <div key={i} style={{ display:"flex", alignItems:"center", gap:12,
                                        background:row.bg, border:`1px solid ${row.border}`,
                                        borderRadius:12, padding:"10px 14px" }}>
                                        <div style={{ width:36, height:36, borderRadius:"50%",
                                          background:`rgba(${row.border.match(/[\d.]+/g).slice(0,3).join(",")},0.15)`,
                                          border:`1px solid ${row.border}`,
                                          display:"flex", alignItems:"center", justifyContent:"center",
                                          fontSize:"1.1rem", color:row.urgency, fontWeight:700,
                                          fontFamily:"serif", flexShrink:0,
                                          boxShadow:`0 0 10px ${row.border}` }}>
                                          {row.symbol}
                                        </div>
                                        <div style={{ flex:1, minWidth:0 }}>
                                          <div style={{ fontFamily:JT.heading, fontSize:"1.0rem",
                                            color:"#2b2b2b", fontWeight:700 }}>{row.planet}</div>
                                          <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                            fontFamily:"'Inter',sans-serif", marginTop:2,
                                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.event}</div>
                                        </div>
                                        <div style={{ background:`rgba(${row.border.match(/[\d.]+/g).slice(0,3).join(",")},0.15)`,
                                          border:`1px solid ${row.border}`, borderRadius:20,
                                          padding:"4px 10px", fontSize:"1.0rem", color:row.urgency,
                                          fontFamily:JT.heading, fontWeight:700, flexShrink:0 }}>
                                          {row.timing}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Lock veil */}
                                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
                                    justifyContent:"center", borderRadius:12,
                                    background:"linear-gradient(transparent 0%,rgba(4,10,22,0.35) 50%,transparent 100%)" }}>
                                    <div style={{ background:"rgba(4,10,22,0.8)", backdropFilter:"blur(4px)",
                                      border:"1px solid rgba(59,130,246,0.3)", borderRadius:20,
                                      padding:"8px 20px", display:"flex", alignItems:"center", gap:8 }}>
                                      <span style={{ fontSize:"1rem" }}>🔒</span>
                                      <span style={{ fontFamily:JT.label, fontWeight:600, fontSize:"0.95rem",
                                        color:"rgba(147,197,253,0.85)",  }}>
                                        { {en:"Subscribe to track transits",hi:"सदस्यता लें",te:"సభ్యత్వం తీసుకోండి",ta:"பலன் பெற இப்போதே சேருங்கள்",kn:"ಚಂದಾ ಮಾಡಿಕೊಳ್ಳಿ",mr:"सदस्यत्व घ्या",ml:"സബ്‌സ്‌ക്രൈബ് ചെയ്യൂ"}[uLang]||"Subscribe to track transits" }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : f.soon ? (
                            <div key={f.id}
                              style={{ display:"flex", flexDirection:"column", alignItems:"center",
                                textAlign:"center", gap:6,
                                padding:"14px 8px", borderRadius:14, cursor:"default",
                                background:JT.cardCream,
                                border:`1px solid rgba(${f.rgb},0.15)`,
                                opacity:0.55, position:"relative" }}
                            >
                              <span style={{ fontSize:"1.5rem", filter:"grayscale(0.6)" }}>{f.icon}</span>
                              <div style={{ fontSize:"0.95rem", color:JT.textDim,
                                fontFamily:JT.heading, lineHeight:1.2, letterSpacing:"0.01em" }}>{fNames[f.id] || f.title}</div>
                              <div style={{ fontSize:"0.9rem", letterSpacing:"0.05em",
                                color:JT.textDim, fontFamily:JT.label, fontWeight:600,
                                background:JT.card, border:`1px solid ${JT.border}`,
                                borderRadius:6, padding:"2px 7px", marginTop:2 }}>SOON</div>
                            </div>
                          ) : (
                            <div key={f.id} onClick={() => setView(f.id)}
                              style={{ display:"flex", flexDirection:"column", alignItems:"center",
                                textAlign:"center", gap:6,
                                padding:"14px 8px", borderRadius:14, cursor:"pointer",
                                background:JT.card,
                                border:`1px solid rgba(${f.rgb},0.3)`, boxShadow:JT.shadow,
                                transition:"all 0.18s" }}
                              onMouseEnter={e => { e.currentTarget.style.background=`rgba(${f.rgb},0.1)`; e.currentTarget.style.borderColor=`rgba(${f.rgb},0.55)`; }}
                              onMouseLeave={e => { e.currentTarget.style.background=JT.card; e.currentTarget.style.borderColor=`rgba(${f.rgb},0.3)`; }}
                            >
                              <span style={{ fontSize:"1.5rem" }}>{f.icon}</span>
                              <div style={{ fontSize:"0.95rem", color:JT.text,
                                fontFamily:JT.heading, lineHeight:1.2, letterSpacing:"0.01em" }}>{fNames[f.id] || f.title}</div>
                              {f.id === "puja_alerts" && uLang === "te" && (
                                <div style={{ fontSize:"1.0rem", color:JT.textDim,
                                  fontFamily:JT.body, fontStyle:"italic", lineHeight:1.2, marginTop:1 }}>
                                  ఏకాదశి · అమావాస్య · ప్రదోషం
                                </div>
                              )}
                            </div>
                          )
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}


          {/* ── 3-Tier Plan Overview ── non-subscribed only ── */}
          {!isSubscribed && (<div style={{ marginBottom:32 }}>
            <div style={{ fontSize:"2rem", color:"#1a1a1a",
              marginBottom:6, fontFamily:"'Inter',sans-serif", fontWeight:800,
              letterSpacing:"-0.01em", lineHeight:1.15 }}>
              Choose what to explore
            </div>
            <div style={{ fontSize:"1.1rem", color:"var(--text-dim)", marginBottom:18,
              fontFamily:"'Inter',sans-serif" }}>
              Everything below is free — just tap to begin.
            </div>

            {/* FREE value grid — big tap cards, no pricing */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",
              gap:12, marginBottom:20 }}>
              {[
                { iconKey:"archetype",     label:"Vedic Archetype", sub:"Who you are",        id:"archetype" },
                { iconKey:"horoscope",     label:"Daily Horoscope", sub:"Today for you",      id:"horoscope" },
                { iconKey:"nakshatra",     label:"Nakshatra",       sub:"Your birth star",    id:"nakshatra" },
                { iconKey:"chart",         label:"Birth Chart",     sub:"Your full kundali",  id:"kundali"   },
                { iconKey:"dasha",         label:"Dasha Periods",   sub:"Your life chapters", id:"dasha"     },
                { iconKey:"compatibility", label:"Compatibility",   sub:"Match two charts",   id:"milan" },
                { iconKey:"horoscope",     label:"Dosha Check",     sub:"Manglik, Kaal Sarp", id:"dosha"     },
              ].map(f => (
                <div key={f.id} onClick={() => setView(f.id)}
                  style={{ display:"flex", flexDirection:"column", gap:10, cursor:"pointer",
                    padding:"18px 16px", borderRadius:18,
                    background:"var(--bg2)",
                    border:"1px solid var(--border)", boxShadow:"var(--card-shadow)",
                    transition:"border-color 0.18s, box-shadow 0.18s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor="var(--saffron)"; e.currentTarget.style.boxShadow="var(--card-shadow-hover)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.boxShadow="var(--card-shadow)"; }}
                >
                  {/* Solid saffron tile — a hollow grey ring reads unfinished
                      next to the filled icon tiles Indian users see elsewhere. */}
                  <div style={{ width:48, height:48, borderRadius:14,
                    background:"linear-gradient(150deg,#F5A03C 0%,#E8832A 55%,#D2701C 100%)",
                    boxShadow:"0 3px 10px rgba(210,112,28,0.28)",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <JyotishGridIcon name={f.iconKey} color="#FFFFFF" strokeWidth={2} />
                  </div>
                  <div style={{ fontSize:"1.12rem", fontWeight:700, color:"var(--text)",
                    fontFamily:"'Inter',sans-serif" }}>{f.label}</div>
                  <div style={{ fontSize:"1rem", color:"var(--text-dim)",
                    fontFamily:"'Inter',sans-serif" }}>{f.sub}</div>
                </div>
              ))}
            </div>

            {/* ── Problem-first: what's weighing on you ── */}
            <div style={{ margin:"34px 0 18px" }}>
              <div style={{ fontSize:"2rem", color:"#1a1a1a", marginBottom:6,
                fontFamily:"'Inter',sans-serif", fontWeight:800,
                letterSpacing:"-0.01em", lineHeight:1.15 }}>
                What's weighing on your mind?
              </div>
              <div style={{ fontSize:"1.1rem", color:"var(--text-dim)", marginBottom:18,
                fontFamily:"'Inter',sans-serif" }}>
                Your Kundali has an answer — and a remedy. Choose what matters today.
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[
                  { title:"Marriage & relationships",   sub:"Timing, harmony, the right match",        view:"milan",     question:"What does my birth chart say about marriage timing and relationship harmony?" },
                  { title:"Career feels stuck",         sub:"Which planet blocks you — and till when", view:"dasha",     question:"Which planet is blocking my career right now, and when does it lift?" },
                  { title:"Money isn't flowing",        sub:"Your wealth periods, and when they open", view:"dasha",     question:"What do my wealth periods look like, and when do they open up?" },
                  { title:"Health worries",             sub:"Planetary patterns and their remedies",   view:"dosha",     question:"What planetary patterns affect my health, and what remedies help?" },
                  { title:"Feeling lost, no direction", sub:"What your soul is built for",             view:"archetype", question:"What is my soul's purpose according to my chart?" },
                ].map((c,i) => (
                  <div key={i}
                    style={{ padding:"16px 18px", borderRadius:16,
                      background:"var(--bg2)",
                      border:"1px solid var(--border)", boxShadow:"var(--card-shadow)",
                      transition:"border-color 0.18s, box-shadow 0.18s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="var(--saffron)"; e.currentTarget.style.boxShadow="var(--card-shadow-hover)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.boxShadow="var(--card-shadow)"; }}
                  >
                    <div onClick={() => setView(c.view)} style={{ display:"flex", alignItems:"center", gap:14, cursor:"pointer" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"1.15rem", fontWeight:700, color:"var(--text)",
                          fontFamily:"'Inter',sans-serif", marginBottom:3 }}>{c.title}</div>
                        <div style={{ fontSize:"1.02rem", color:"var(--text-dim)",
                          fontFamily:"'Inter',sans-serif" }}>{c.sub}</div>
                        <div style={{ fontSize:"1.0rem", color:"var(--saffron)", marginTop:6,
                          fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                          See what your chart says — free →
                        </div>
                      </div>
                      <div style={{ color:"var(--saffron)", fontSize:"1.3rem", flexShrink:0 }}>›</div>
                    </div>
                    {/* Ask the Pandit — a distinct button, not a link: signals "starts a conversation" */}
                    <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid var(--border)" }}>
                      {/* Solid, and capped in width — a pale outline stretched the
                          full card reads as disabled, not as the main action. */}
                      <button onClick={() => { setChatInitMsg(c.question); setView("jyotish_chat"); }}
                        style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                          width:"100%", maxWidth:380, minHeight:48, padding:"12px 20px",
                          borderRadius:12, cursor:"pointer", border:"none",
                          // Deep saffron: white on mid-tone #E8832A is only 2.7:1.
                          // These stops all clear 4.5:1.
                          background:"linear-gradient(150deg,#C2560F 0%,#B04E0C 55%,#A8450B 100%)",
                          boxShadow:"0 3px 12px rgba(168,69,11,0.28)",
                          color:"#ffffff", fontSize:"1.05rem",
                          fontFamily:"'Inter',sans-serif", fontWeight:700,
                          transition:"filter 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.filter="brightness(1.06)"; }}
                        onMouseLeave={e => { e.currentTarget.style.filter="none"; }}
                      >
                        💬 Ask the Pandit about this
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* REPORTS — written readings; single, unrepeated one-time-purchase CTA */}
            <div onClick={() => setView("reports")}
              style={{ borderRadius:20, padding:"20px 22px", cursor:"pointer", marginBottom:20,
                background:"var(--bg2)",
                border:"1px solid var(--border)", boxShadow:"var(--card-shadow)",
                transition:"border-color 0.22s, box-shadow 0.22s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor="var(--saffron)"; e.currentTarget.style.boxShadow="var(--card-shadow-hover)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.boxShadow="var(--card-shadow)"; }}
            >
              <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.35rem",
                fontWeight:700, color:"var(--saffron)", marginBottom:8 }}>
                What does your chart know that you don't yet?
              </div>
              <p style={{ color:"var(--text)", fontSize:"1.05rem", lineHeight:1.75,
                fontFamily:"'Inter',sans-serif", margin:"0 0 14px" }}>
                Your Janma Kundali holds exact dasha windows, timing, and remedies —
                written once, permanently yours, like a pandit's letter addressed only to you.
              </p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:14 }}>
                {["Your True Self","Power & Purpose","Career","Year Ahead",
                  "Relationship","Karmic Blueprint","Compatibility"].map(r => (
                  <span key={r} style={{ padding:"6px 14px", borderRadius:22,
                    background:"var(--bg2)",
                    border:"1px solid var(--border)",
                    fontSize:"1.0rem", color:"var(--text-dim)",
                    fontFamily:"'Inter',sans-serif", fontWeight:500 }}>
                    {r}
                  </span>
                ))}
                <span style={{ padding:"6px 14px", borderRadius:22,
                  background:"var(--bg2)",
                  border:"1px solid var(--border)",
                  fontSize:"1.0rem", color:"var(--text-dim)" }}>
                  +2 more
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ color:"var(--saffron)", fontSize:"1.05rem", fontWeight:700,
                  fontFamily:"'Inter',sans-serif" }}>
                  Read yours →
                </span>
              </div>
            </div>

            {/* Premium toggle — pricing stays hidden until asked */}
            {!showPlans && (
              <button onClick={() => { setShowPlans(true); setTimeout(() => pricingRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 60); }}
                style={{ width:"100%", maxWidth:520, margin:"0 auto 16px",
                  padding:"15px 20px", borderRadius:14, minHeight:48,
                  background:"#FFFFFF",
                  border:"2px solid #C2560F", cursor:"pointer",
                  color:"#A8450B", fontSize:"1.05rem", fontWeight:700,
                  fontFamily:"'Inter',sans-serif",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                Want daily guidance & remedies? See premium plans — from ₹299/yr ⌄
              </button>
            )}

            {/* 3-Tier Premium Plans. pricingRef lives here, not on the whole
                section above — every "See plans" link was scrolling users to
                the free features grid and never reaching the prices. */}
            <div ref={pricingRef} />
            {showPlans && (<div style={{ marginBottom:8 }}>
              <div style={{ fontSize:"2rem", color:"#1a1a1a", fontWeight:800,
                letterSpacing:"-0.01em", lineHeight:1.15, marginBottom:6 }}>
                Go deeper with a plan
              </div>
              <div style={{ fontSize:"1.1rem", color:"var(--text-dim)",
                fontFamily:"'Inter',sans-serif", marginBottom:18 }}>
                Everything above stays free. These add daily guidance and remedies.
              </div>
            </div>)}
            {showPlans && (() => {
              const PLANS = [
                {
                  id:"drishti", emoji:"🛡️", name:"Graha Raksha", price:299,
                  perDay:"₹0.82/day", perDayNote:"Less than one chai",
                  tag:"PROTECTION", tagColor:"#93c5fd", tagColorDark:"#2563eb", rgb:"59,130,246",
                  tagBg:"rgba(59,130,246,0.15)", tagBorder:"rgba(59,130,246,0.4)",
                  border:"rgba(59,130,246,0.25)",
                  trigger:"Daily cosmic armor — your mantra, your best hours, your planet alerts. Never start the day blind.",
                  features:[
                    { icon:"🌟", label:"Daily Nakshatra Energy — which star rules today", free:true },
                    { icon:"⏱️", label:"Daily Hora tracker — best hour for money, work & health", free:true, view:"hora" },
                    { icon:"📿", label:"Your daily mantra — personal to your birth Nakshatra", free:true, view:"mantra" },
                    { icon:"🕉️", label:"Ishta Devata reveal — your personal deity from your chart", view:"ishta_devata" },
                    { icon:"⚠️", label:"Planet entry & exit alerts — know when Shani, Rahu shift" },
                    { icon:"📅", label:"Ekadashi, Pradosham & Amavasya alerts with what to do" },
                    { icon:"🪐", label:"Sade Sati & Shani Dhaiya status — know if you are in it", view:"shani" },
                  ],
                },
                {
                  id:"saathi", emoji:"🧭", name:"Jyotish Saathi", price:499,
                  perDay:"₹1.36/day", perDayNote:"Less than an autorickshaw ride",
                  tag:"✦ MOST POPULAR", tagColor:"#f5d17a", tagColorDark:"#b07818", rgb:"212,175,55",
                  tagBg:"rgba(212,175,55,0.2)", tagBorder:"rgba(212,175,55,0.55)",
                  border:"rgba(212,175,55,0.5)",
                  trigger:"Know what is coming before it arrives — and have your remedy ready before it hits.",
                  features:[
                    { icon:"✦", label:"Everything in Graha Raksha" },
                    { icon:"🪐", label:"Shani Kavach — full Sade Sati remedies + weekly Shani alerts" },
                    { icon:"⏳", label:"Dasha Navigator — your current life chapter & what comes next" },
                    { icon:"💊", label:"Full dosha remedy plans — Mangal, Kaal Sarp, Pitra & more" },
                    { icon:"🌘", label:"Eclipse impact reports — how each eclipse hits YOUR chart" },
                    { icon:"🔄", label:"Retrograde alerts — Mercury, Jupiter, Venus with your guidance" },
                    { icon:"🎉", label:"Festival guidance — how Diwali & Navratri affect your chart" },
                    { icon:"📆", label:"Varshaphal — what this full year holds for you" },
                    { icon:"🕐", label:"Weekly muhurta picks — best days for your decisions" },
                  ],
                  recommended: true,
                },
                {
                  id:"darshan", emoji:"🔱", name:"Guru Kripa", price:699,
                  perDay:"₹1.91/day", perDayNote:"Less than a pandit visit per year",
                  tag:"GURU'S GRACE", tagColor:"#fdba74", tagColorDark:"#c2570c", rgb:"249,115,22",
                  tagBg:"rgba(249,115,22,0.15)", tagBorder:"rgba(249,115,22,0.4)",
                  border:"rgba(249,115,22,0.28)",
                  trigger:"Your personal AI Pandit — replaces ₹500+ consultation visits. Ask about your chart every day.",
                  features:[
                    { icon:"✦", label:"Everything in Jyotish Saathi" },
                    { icon:"💬", label:"More AI Pandit questions — up to 25 a day about YOUR chart" },
                    { icon:"📿", label:"Beej Mantra initiation + 40-day sadhana guide for your chart" },
                    { icon:"💎", label:"Gemstone & yantra guidance — based on YOUR chart weaknesses" },
                    { icon:"🏥", label:"Planetary health alerts — which body systems need care monthly" },
                    { icon:"🌿", label:"Ayurvedic Prakriti reading from your birth chart" },
                    { icon:"👨‍👩‍👧", label:"Add family members — parents, spouse, children charts" },
                    { icon:"🙏", label:"Annual puja calendar — which pujas, when & why for YOUR doshas" },
                    { icon:"📜", label:"Quarterly prediction report — next 3 months specific to you" },
                    { icon:"🔱", label:"Graha Sadhana weekly practice — planetary worship guide" },
                  ],
                },
              ];

              const PlanFeaturePills = ({ plan }) => (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
                  {plan.features.map(f => (
                    <div key={f.label}
                      onClick={() => f.view && setView(f.view)}
                      style={{
                        display:"flex", alignItems:"center", gap:12,
                        padding:"11px 16px", borderRadius:13,
                        background:`rgba(${plan.rgb},0.09)`,
                        border:`1px solid rgba(${plan.rgb},0.25)`,
                        fontSize:"1.0rem", color:"#2b2b2b",
                        fontWeight:400, lineHeight:1.35,
                        transition:"all 0.18s",
                        cursor: f.view ? "pointer" : "default",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background=`rgba(${plan.rgb},0.18)`; e.currentTarget.style.borderColor=`rgba(${plan.rgb},0.5)`; e.currentTarget.style.boxShadow=`0 0 12px rgba(${plan.rgb},0.22)`; e.currentTarget.style.color="#1a1a1a"; }}
                      onMouseLeave={e => { e.currentTarget.style.background=`rgba(${plan.rgb},0.09)`; e.currentTarget.style.borderColor=`rgba(${plan.rgb},0.25)`; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.color="#2b2b2b"; }}
                    >
                      <span style={{ fontSize:"1.1rem", flexShrink:0 }}>{f.icon}</span>
                      <span style={{ flex:1 }}>{f.label}</span>
                      {f.view && <span style={{ fontSize:"1.0rem", opacity:0.5 }}>›</span>}
                    </div>
                  ))}
                </div>
              );

              const SubscribeBtn = ({ plan, compact }) => !isSubscribed ? (
                <button
                  onClick={e => { e.stopPropagation(); handleSubscribe(plan.id); }}
                  disabled={payLoading}
                  style={{ width:"100%", padding:"14px 16px", minHeight:48,
                    borderRadius:14, cursor: payLoading ? "default" : "pointer",
                    fontFamily:"'Inter',sans-serif",
                    // Was 0.72-0.85rem (11-13px) on the purchase button.
                    fontSize:"1.05rem",
                    fontWeight:700, letterSpacing:"0.01em", transition:"all 0.22s",
                    border:"none",
                    // Recommended keeps near-black on gold (already high contrast).
                    // The other two were a pale same-hue tint; solid dark + white.
                    color: plan.recommended ? "#241a02" : "#ffffff",
                    background: payLoading && selectedPlan === plan.id
                      ? "#8a8a8a"
                      : plan.recommended
                        ? "linear-gradient(135deg,#b8922e,#d4a853,#f5d17a,#d4a853)"
                        : ({ drishti:"#1D4ED8", darshan:"#A8450B" }[plan.id] || "#334155"),
                    boxShadow: plan.recommended ? "0 4px 20px rgba(212,175,55,0.35)" : "0 3px 12px rgba(0,0,0,0.14)",
                  }}>
                  {payLoading && selectedPlan === plan.id
                    ? "Opening…"
                    : `Choose ${plan.name}`}
                </button>
              ) : (
                <div style={{ textAlign:"center", fontSize:"1.0rem",
                  color:"#34d399", fontFamily:"'Inter',sans-serif", letterSpacing:"0.08em" }}>
                  ✓ Subscribed
                </div>
              );

              const isMobile = winW < 860;

              const COMPARE_ROWS = [
                { group:"Daily Cosmic Guidance", rows:[
                  { icon:"🌅", label:"Daily Rashi Phal",             free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"⭐", label:"Nakshatra Energy today",        free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"📅", label:"Daily Panchang",                free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"🕐", label:"Rahu Kaal timing",              free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"📿", label:"Personal daily mantra",         free:0, drishti:1, saathi:1, darshan:1 },
                  { icon:"⏱️", label:"Daily Hora tracker",            free:0, drishti:1, saathi:1, darshan:1 },
                  { icon:"🕉️", label:"Ishta Devata reveal",           free:0, drishti:1, saathi:1, darshan:1 },
                  { icon:"⚠️", label:"Planet entry & exit alerts",    free:0, drishti:1, saathi:1, darshan:1 },
                  { icon:"📅", label:"Ekadashi & festival alerts",    free:0, drishti:1, saathi:1, darshan:1 },
                  { icon:"🪐", label:"Sade Sati & Shani Dhaiya",      free:0, drishti:1, saathi:1, darshan:1 },
                ]},
                { group:"Deep Chart Analysis", rows:[
                  { icon:"🪐", label:"Birth Chart (Kundali)",         free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"📊", label:"Dasha Periods",                 free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"⚠️", label:"Dosha Detection",               free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"💑", label:"Compatibility check",           free:1, drishti:1, saathi:1, darshan:1 },
                  { icon:"🪐", label:"Shani Kavach & remedies",       free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"⏳", label:"Dasha Navigator",               free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"💊", label:"Full dosha remedy plans",       free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"🌘", label:"Eclipse impact reports",        free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"🔄", label:"Retrograde guidance",           free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"📆", label:"Varshaphal — yearly forecast",  free:0, drishti:0, saathi:1, darshan:1 },
                  { icon:"🕐", label:"Weekly muhurta picks",          free:0, drishti:0, saathi:1, darshan:1 },
                ]},
                { group:"Premium Personal Guidance", rows:[
                  { icon:"💬", label:"AI Pandit — 25 questions a day",      free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"📿", label:"Beej Mantra + 40-day sadhana",  free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"💎", label:"Gemstone & yantra guidance",    free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"🏥", label:"Planetary health alerts",       free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"🌿", label:"Ayurvedic Prakriti reading",    free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"👨‍👩‍👧", label:"Family members charts",      free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"🙏", label:"Annual puja calendar",          free:0, drishti:0, saathi:0, darshan:1 },
                  { icon:"📜", label:"Quarterly prediction report",   free:0, drishti:0, saathi:0, darshan:1 },
                ]},
              ];

              const colDefs = [
                { key:"free",    label:"Free",           color:"#16a34a" },
                { key:"drishti", label:"Graha\nRaksha",  color:"#2563eb" },
                { key:"saathi",  label:"Jyotish\nSaathi",color:"#b07818" },
                { key:"darshan", label:"Guru\nKripa",    color:"#c2570c" },
              ];


              const PlanCardInner = ({ plan }) => {
                const headerBg = `radial-gradient(ellipse at 50% 100%, rgba(${plan.rgb},0.1) 0%, transparent 62%), linear-gradient(180deg,#ffffff,#fff9ef)`;
                const featureBg = "linear-gradient(180deg,#ffffff,#ffffff)";
                return (
                  <div style={{
                    width:"100%", borderRadius:26, overflow:"hidden",
                    border: plan.recommended
                      ? `2px solid ${plan.tagBorder}`
                      : `1.5px solid ${plan.border}`,
                    boxShadow: plan.recommended
                      ? `0 0 0 1px rgba(${plan.rgb},0.2), 0 24px 70px rgba(${plan.rgb},0.22)`
                      : `0 10px 44px rgba(0,0,0,0.55)`,
                    display:"flex", flexDirection:"column",
                    transition:"box-shadow 0.25s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow=`0 0 0 1px rgba(${plan.rgb},0.5), 0 28px 80px rgba(${plan.rgb},0.3)`; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow=plan.recommended?`0 0 0 1px rgba(${plan.rgb},0.2), 0 24px 70px rgba(${plan.rgb},0.22)`:`0 10px 44px rgba(0,0,0,0.55)`; }}
                  >
                    {/* ── HEADER: centered, atmospheric ── */}
                    <div style={{ background:headerBg, textAlign:"center",
                      padding: plan.recommended ? "32px 28px 26px" : "26px 24px 22px",
                      position:"relative",
                      borderBottom:`1px solid rgba(${plan.rgb},0.18)` }}>

                      {plan.recommended && (
                        <div style={{ position:"absolute", top:0, left:"50%",
                          transform:"translateX(-50%)",
                          background:"linear-gradient(90deg,#a07828,#c9a84c,#f5d17a,#c9a84c,#a07828)",
                          color:"#0a0700", padding:"5px 26px",
                          borderRadius:"0 0 14px 14px",
                          fontSize:"1.0rem", fontFamily:"'Inter',sans-serif",
                          fontWeight:800, letterSpacing:"0.14em", whiteSpace:"nowrap" }}>
                          MOST POPULAR
                        </div>
                      )}

                      {/* Emoji with radial glow */}
                      <div style={{ position:"relative", display:"inline-block",
                        marginTop: plan.recommended ? 18 : 4, marginBottom:16 }}>
                        <div style={{ position:"absolute", inset:"-20px", borderRadius:"50%",
                          background:`radial-gradient(circle, rgba(${plan.rgb},0.6) 0%, transparent 70%)`,
                          filter:"blur(14px)" }} />
                        <span style={{ fontSize:"3.6rem", lineHeight:1, position:"relative",
                          filter:`drop-shadow(0 0 12px ${plan.tagColor})` }}>
                          {plan.emoji}
                        </span>
                      </div>

                      {/* Plan name */}
                      <div style={{ fontFamily:"'Inter',sans-serif", fontWeight:700,
                        fontSize:"1.3rem", color:"#2a2a2a",
                        letterSpacing:"0.02em", marginBottom:8 }}>
                        {plan.name}
                      </div>

                      {/* Badge */}
                      <div style={{ marginBottom:18 }}>
                        <span style={{ background:plan.tagBg,
                          border:`1px solid ${plan.tagBorder}`,
                          borderRadius:6, padding:"3px 14px",
                          fontSize:"1.0rem", color:plan.tagColorDark,
                          fontWeight:700, letterSpacing:"0.12em",
                          fontFamily:"'Inter',sans-serif" }}>
                          {plan.tag}
                        </span>
                      </div>

                      {/* Big price */}
                      <div style={{ lineHeight:1, marginBottom:8 }}>
                        <span style={{ fontSize:"3.2rem", fontWeight:800, color:"#1a1a1a",
                          fontFamily:"'Inter',sans-serif",
                          textShadow:`0 0 36px rgba(${plan.rgb},0.5)` }}>
                          ₹{plan.price}
                        </span>
                        <span style={{ fontSize:"1.0rem",
                          color:"#5a5a5a", marginLeft:5 }}>/yr</span>
                      </div>
                      <div style={{ fontSize:"1.05rem", color:`rgba(${plan.rgb},0.95)`,
                        marginBottom:4 }}>{plan.perDay}</div>
                      <div style={{ fontSize:"1.1rem",
                        color:"#666666", fontStyle:"italic",
                        fontFamily:"'Inter',sans-serif" }}>
                        {plan.perDayNote}
                      </div>
                    </div>

                    {/* ── FEATURES: glassmorphism cards ── */}
                    <div style={{ background:featureBg, padding:"14px 20px 22px" }}>
                      {plan.features.map((f, i) => (
                        <div key={i}
                          onClick={() => f.view && setView(f.view)}
                          style={{
                            display:"flex", alignItems:"center", gap:14,
                            padding:"13px 16px",
                            marginBottom:8,
                            borderRadius:13,
                            background: f.free ? "rgba(52,211,153,0.06)" : "#fafafa",
                            border: f.free
                              ? "1px solid rgba(52,211,153,0.22)"
                              : `1px solid ${JT.border}`,
                            cursor: f.view ? "pointer" : "default",
                            transition:"all 0.18s",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = f.free ? "rgba(52,211,153,0.12)" : "#f0f0f0";
                            e.currentTarget.style.borderColor = f.free ? "rgba(52,211,153,0.4)" : JT.borderStrong;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = f.free ? "rgba(52,211,153,0.06)" : "#fafafa";
                            e.currentTarget.style.borderColor = f.free ? "rgba(52,211,153,0.22)" : JT.border;
                          }}
                        >
                          <span style={{ fontSize:"1.25rem", flexShrink:0,
                            width:28, textAlign:"center" }}>{f.icon}</span>
                          <span style={{ fontSize:"1rem",
                            color:"#2b2b2b",
                            fontFamily:"'Inter',sans-serif", lineHeight:1.45, flex:1 }}>
                            {f.label}
                          </span>
                          {f.free && (
                            <span style={{
                              background:"rgba(52,211,153,0.15)",
                              border:"1px solid rgba(52,211,153,0.38)",
                              borderRadius:20, padding:"3px 11px",
                              fontSize:"1.0rem", color:"#0d9668",
                              fontWeight:700, letterSpacing:"0.1em",
                              fontFamily:"'Inter',sans-serif", flexShrink:0,
                            }}>FREE</span>
                          )}
                          {f.view && (
                            <span style={{ fontSize:"1.08rem",
                              color: f.free ? "rgba(13,150,104,0.55)" : JT.textDim,
                              flexShrink:0 }}>›</span>
                          )}
                        </div>
                      ))}
                      <div style={{ marginTop:18 }}>
                        <SubscribeBtn plan={plan} />
                      </div>
                    </div>
                  </div>
                );
              };

              const stickyBgEven = "#f8f6f0";
              const stickyBgOdd  = "#ffffff";

              const CompareTable = () => (
                <div style={{ marginTop:20, borderRadius:18, overflow:"hidden",
                  border:"1px solid rgba(0,0,0,0.1)",
                  boxShadow:"0 8px 40px #f7f7f7" }}>
                  <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
                    <table style={{ width:"100%", minWidth:520, borderCollapse:"collapse",
                      tableLayout:"fixed" }}>
                      <colgroup>
                        <col style={{ width:"42%" }} />
                        <col style={{ width:"14.5%" }} />
                        <col style={{ width:"14.5%" }} />
                        <col style={{ width:"14.5%" }} />
                        <col style={{ width:"14.5%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ padding:"13px 14px", textAlign:"left",
                            fontSize:"1.0rem", color:"#5a5a5a",
                            fontFamily:"'Inter',sans-serif",
                            background:"#f7f7f7", fontWeight:600,
                            position:"sticky", left:0, zIndex:3,
                            borderBottom:"1px solid rgba(0,0,0,0.08)" }}>
                            Feature
                          </th>
                          {colDefs.map(c => (
                            <th key={c.key} style={{ padding:"13px 6px", textAlign:"center",
                              fontSize:"1.0rem", fontFamily:"'Inter',sans-serif",
                              color:c.color, fontWeight:700, letterSpacing:"0.05em",
                              lineHeight:1.45, background:"#f7f7f7",
                              borderBottom:"1px solid rgba(0,0,0,0.08)" }}>
                              {c.label.split("\n").map((w,wi) => <div key={wi}>{w}</div>)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {COMPARE_ROWS.flatMap((group, gi) => [
                          <tr key={`g${gi}`}>
                            <td colSpan={5} style={{
                              padding:"8px 14px 5px",
                              fontSize:"1.0rem",
                              color:"#5a5a5a", fontWeight:600,
                              fontFamily:"'Inter',sans-serif",
                              background:"rgba(0,0,0,0.035)",
                              borderBottom:"1px solid rgba(0,0,0,0.06)",
                              borderTop:"1px solid rgba(0,0,0,0.06)",
                            }}>
                              {group.group}
                            </td>
                          </tr>,
                          ...group.rows.map((row, ri) => {
                            const bg = ri%2===0 ? "rgba(0,0,0,0.015)" : "transparent";
                            const stickyBg = ri%2===0 ? stickyBgEven : stickyBgOdd;
                            return (
                              <tr key={`${gi}-${ri}`} style={{
                                background:bg,
                                borderBottom:"1px solid rgba(0,0,0,0.05)",
                              }}>
                                <td style={{
                                  padding:"9px 14px", fontSize:"1.05rem",
                                  color:"#2b2b2b", lineHeight:1.35,
                                  position:"sticky", left:0, zIndex:1,
                                  background:stickyBg,
                                  borderRight:"1px solid rgba(0,0,0,0.06)",
                                }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                    <span style={{ fontSize:"1.0rem", flexShrink:0 }}>{row.icon}</span>
                                    <span>{row.label}</span>
                                  </div>
                                </td>
                                {colDefs.map(c => (
                                  <td key={c.key} style={{ padding:"9px 4px", textAlign:"center" }}>
                                    {row[c.key]
                                      ? <span style={{ color:c.color, fontSize:"1.1rem", fontWeight:700 }}>✓</span>
                                      : <span style={{ color:"rgba(0,0,0,0.22)", fontSize:"1.08rem" }}>—</span>
                                    }
                                  </td>
                                ))}
                              </tr>
                            );
                          }),
                        ])}
                        <tr style={{ background:"#f7f7f7",
                          borderTop:"1px solid rgba(0,0,0,0.1)" }}>
                          <td style={{ padding:"13px 14px", fontSize:"1.0rem",
                            color:"#5a5a5a", fontFamily:"'Inter',sans-serif",
                            fontStyle:"italic", position:"sticky", left:0, zIndex:1,
                            background:"#f7f7f7",
                            borderRight:"1px solid rgba(0,0,0,0.06)" }}>
                            Choose your path
                          </td>
                          <td style={{ padding:"10px 6px", textAlign:"center" }}>
                            <span style={{ fontSize:"1.0rem", color:"#16a34a",
                              fontFamily:"'Inter',sans-serif", fontWeight:700 }}>Free</span>
                          </td>
                          {PLANS.map(plan => (
                            <td key={plan.id} style={{ padding:"10px 6px", textAlign:"center" }}>
                              <button onClick={() => handleSubscribe(plan.id)}
                                disabled={payLoading || isSubscribed}
                                style={{ width:"86%", padding:"8px 4px", borderRadius:10,
                                  background:plan.tagBg, border:`1px solid ${plan.tagBorder}`,
                                  color:plan.tagColorDark, fontSize:"1.0rem", cursor:"pointer",
                                  fontFamily:"'Inter',sans-serif", fontWeight:700,
                                  letterSpacing:"0.04em" }}>
                                ₹{plan.price}
                              </button>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Mobile: plan tab selector */}
                  {isMobile && (
                    <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                      {PLANS.map(p => {
                        const active = activePlanTab === p.id;
                        return (
                          <button key={p.id} onClick={() => setActivePlanTab(p.id)}
                            style={{
                              flex:1, padding:"10px 6px", borderRadius:14, cursor:"pointer",
                              background: active ? p.tagBg : "rgba(0,0,0,0.02)",
                              border:`2px solid ${active ? p.tagBorder : "rgba(0,0,0,0.08)"}`,
                              boxShadow: active ? `0 0 16px rgba(${p.rgb},0.28)` : "none",
                              transition:"all 0.22s",
                              display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                            }}>
                            <span style={{ fontSize:"1.3rem",
                              filter: active ? `drop-shadow(0 0 6px ${p.tagColor})` : "none",
                              transition:"filter 0.22s" }}>
                              {p.emoji}
                            </span>
                            <span style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.0rem",
                              fontWeight:700, letterSpacing:"0.04em",
                              color: active ? p.tagColorDark : "#666666" }}>
                              {p.name.split(" ").pop()}
                            </span>
                            <span style={{ fontSize:"1.0rem", fontWeight:700,
                              fontFamily:"'Inter',sans-serif",
                              color: active ? p.tagColorDark : "#666666" }}>
                              ₹{p.price}
                            </span>
                            {active && <div style={{ width:18, height:2, borderRadius:1,
                              background:p.tagColorDark, boxShadow:`0 0 5px ${p.tagColor}` }} />}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Desktop: vertical stack  |  Mobile: single active card + swipe */}
                  {isMobile ? (() => {
                    const plan = PLANS.find(p => p.id === activePlanTab) || PLANS[1];
                    let touchX = 0;
                    const idx = PLANS.findIndex(p => p.id === activePlanTab);
                    return (
                      <div
                        onTouchStart={e => { touchX = e.touches[0].clientX; }}
                        onTouchEnd={e => {
                          const d = e.changedTouches[0].clientX - touchX;
                          if(d < -50 && idx < PLANS.length-1) setActivePlanTab(PLANS[idx+1].id);
                          else if(d > 50 && idx > 0) setActivePlanTab(PLANS[idx-1].id);
                        }}
                      >
                        <PlanCardInner plan={plan} />
                      </div>
                    );
                  })() : (
                    // Side by side, not stacked: the three plans sat ~900px apart
                    // vertically, so comparing them meant scrolling ~1850px —
                    // and comparison is the whole decision. Falls back to one
                    // column under ~950px.
                    <div style={{ display:"grid", gap:16, alignItems:"start",
                      gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))" }}>
                      {PLANS.map(plan => <PlanCardInner key={plan.id} plan={plan} />)}
                    </div>
                  )}

                  {/* Swipe hint — mobile only */}
                  {isMobile && (
                    <div style={{ textAlign:"center", marginTop:8, fontSize:"1.0rem",
                      color:"#5a5a5a", fontFamily:"'Inter',sans-serif",
                      letterSpacing:"0.04em" }}>
                      ← swipe to switch plans →
                    </div>
                  )}

                  {/* Compare all plans toggle */}
                  <div style={{ textAlign:"center", marginTop:16, marginBottom:2 }}>
                    <button onClick={() => setShowCompareTable(o => !o)}
                      style={{
                        background:"transparent",
                        border:"1px solid #999999",
                        borderRadius:20, padding:"7px 22px",
                        color:"#5a5a5a",
                        cursor:"pointer", fontSize:"1.05rem",
                        fontFamily:"'Inter',sans-serif", transition:"all 0.2s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor="#b07818"; e.currentTarget.style.color="#b07818"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor="#999999"; e.currentTarget.style.color="#999999"; }}>
                      {showCompareTable ? "↑ Hide comparison" : "↗ Compare all plans"}
                    </button>
                  </div>

                  {showCompareTable && <CompareTable />}

                  {false && (
                    <div style={{
                      position:"fixed", inset:0, zIndex:2000,
                      background:"#ffffff",
                      display:"flex", flexDirection:"column",
                    }}>
                      {/* Per-plan ambient glows — always visible, always rich */}
                      <div style={{ position:"fixed", top:"20%", left:"8%", width:360, height:360, borderRadius:"50%",
                        background:"radial-gradient(circle,rgba(59,130,246,0.13) 0%,transparent 65%)",
                        pointerEvents:"none", zIndex:0 }} />
                      <div style={{ position:"fixed", top:"30%", left:"50%", transform:"translateX(-50%)", width:400, height:400, borderRadius:"50%",
                        background:"radial-gradient(circle,rgba(212,175,55,0.11) 0%,transparent 65%)",
                        pointerEvents:"none", zIndex:0 }} />
                      <div style={{ position:"fixed", top:"20%", right:"8%", width:360, height:360, borderRadius:"50%",
                        background:"radial-gradient(circle,rgba(249,115,22,0.11) 0%,transparent 65%)",
                        pointerEvents:"none", zIndex:0 }} />

                      {/* Header */}
                      <div style={{ padding:"22px 22px 0", flexShrink:0, position:"relative", zIndex:1 }}>
                        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
                          <div>
                            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem",
                              color:"#1a1a1a", fontWeight:700, letterSpacing:"0.07em", lineHeight:1.2 }}>
                              Choose Your Sacred Path
                            </div>
                          </div>
                          <button onClick={() => setCompareDrawer(null)}
                            style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.18)",
                              borderRadius:12, padding:"9px 18px", color:"#2b2b2b",
                              cursor:"pointer", fontSize:"1.1rem", fontFamily:"'Inter',sans-serif",
                              letterSpacing:"0.06em", flexShrink:0, transition:"all 0.18s", marginLeft:12 }}
                            onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.13)"; e.currentTarget.style.color="#1a1a1a"; }}
                            onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; e.currentTarget.style.color="#2b2b2b"; }}
                          >✕ Close</button>
                        </div>

                        {/* ── Plan Tab Buttons — big, labeled, one-tap navigation ── */}
                        <div style={{ display:"flex", gap:10 }}>
                          {PLANS.map(p => {
                            const active = compareDrawer === p.id;
                            return (
                              <button key={p.id}
                                onClick={() => setCompareDrawer(p.id)}
                                style={{
                                  flex:1, padding:"12px 6px", borderRadius:14, cursor:"pointer",
                                  background: active ? p.tagBg : "rgba(255,255,255,0.04)",
                                  border:`2px solid ${active ? p.tagBorder : "rgba(255,255,255,0.1)"}`,
                                  boxShadow: active ? `0 0 18px rgba(${p.rgb},0.35)` : "none",
                                  transition:"all 0.22s",
                                  display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                                }}>
                                <span style={{ fontSize:"1.4rem",
                                  filter: active ? `drop-shadow(0 0 8px ${p.tagColor})` : "none",
                                  transition:"filter 0.22s" }}>{p.emoji}</span>
                                <span style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.0rem",
                                  fontWeight:700, letterSpacing:"0.06em", lineHeight:1.2,
                                  color: active ? p.tagColor : "#2b2b2b" }}>
                                  {p.name.split(" ").slice(-1)[0]}
                                </span>
                                <span style={{ fontSize:"1.02rem", fontWeight:700,
                                  color: active ? p.tagColor : "#2b2b2b",
                                  fontFamily:"'Inter',sans-serif" }}>
                                  ₹{p.price}/yr
                                </span>
                                {active && (
                                  <div style={{ width:20, height:2, borderRadius:1,
                                    background:p.tagColor,
                                    boxShadow:`0 0 6px ${p.tagColor}` }} />
                                )}
                              </button>
                            );
                          })}
                        </div>

                        <div style={{ height:1, background:"linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent)", margin:"16px 0 0" }} />
                      </div>

                      {/* ── Single active plan card + swipe support ── */}
                      {(() => {
                        const activePlan = PLANS.find(p => p.id === compareDrawer);
                        let touchStartX = 0;
                        const planIdx = PLANS.findIndex(p => p.id === compareDrawer);
                        const goPrev = () => { if(planIdx > 0) setCompareDrawer(PLANS[planIdx-1].id); };
                        const goNext = () => { if(planIdx < PLANS.length-1) setCompareDrawer(PLANS[planIdx+1].id); };
                        return activePlan ? (
                          <div style={{ flex:1, overflowY:"auto", position:"relative", zIndex:1 }}
                            onTouchStart={e => { touchStartX = e.touches[0].clientX; }}
                            onTouchEnd={e => {
                              const delta = e.changedTouches[0].clientX - touchStartX;
                              if(delta < -50) goNext();
                              else if(delta > 50) goPrev();
                            }}>
                            <div style={{ padding:"18px 20px 32px", display:"flex", justifyContent:"center" }}>
                              <div style={{
                                width:"100%", maxWidth:480,
                                borderRadius:26,
                                background:activePlan.bg,
                                border:`2px solid ${activePlan.tagBorder}`,
                                boxShadow:`0 0 0 1px ${activePlan.tagBorder}, 0 24px 80px ${activePlan.glowColor}, inset 0 1px 0 rgba(255,255,255,0.08)`,
                                overflow:"hidden", position:"relative",
                              }}>
                                <div style={{ position:"absolute", inset:0, borderRadius:26, pointerEvents:"none",
                                  boxShadow:`inset 0 0 50px rgba(${activePlan.rgb},0.07)`, zIndex:0 }} />

                                {/* Top bar */}
                                <div style={{ height:4,
                                  background:`linear-gradient(90deg,transparent,${activePlan.tagColor},rgba(${activePlan.rgb},0.6),${activePlan.tagColor},transparent)` }} />

                                <div style={{ padding:"24px 24px 28px", position:"relative", zIndex:1 }}>
                                  {/* Emoji + name */}
                                  <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
                                    <span style={{ fontSize:"3rem",
                                      filter:`drop-shadow(0 0 16px ${activePlan.tagColor}) drop-shadow(0 0 30px rgba(${activePlan.rgb},0.4))` }}>
                                      {activePlan.emoji}
                                    </span>
                                    <div>
                                      <div style={{ fontFamily:"'Inter',sans-serif",
                                        fontSize:"1.15rem", fontWeight:700, lineHeight:1.2,
                                        color:activePlan.tagColor,
                                        textShadow:`0 0 20px rgba(${activePlan.rgb},0.5)` }}>
                                        {activePlan.name}
                                      </div>
                                      <span style={{
                                        display:"inline-block", marginTop:6,
                                        background:activePlan.tagBg, border:`1px solid ${activePlan.tagBorder}`,
                                        borderRadius:8, padding:"3px 12px", fontSize:"1.0rem",
                                        color:activePlan.tagColor, fontWeight:700, letterSpacing:"0.1em",
                                        fontFamily:"'Inter',sans-serif"
                                      }}>{activePlan.tag}</span>
                                    </div>
                                  </div>

                                  {/* Price block */}
                                  <div style={{ padding:"18px 20px", borderRadius:18, marginBottom:20,
                                    background:`rgba(${activePlan.rgb},0.12)`,
                                    border:`1px solid rgba(${activePlan.rgb},0.35)`,
                                    boxShadow:`0 4px 20px rgba(${activePlan.rgb},0.1)` }}>
                                    <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:4 }}>
                                      <span style={{ fontFamily:"'Inter',sans-serif", fontWeight:800,
                                        fontSize:"3.5rem", lineHeight:1, color:activePlan.tagColor,
                                        textShadow:`0 0 30px rgba(${activePlan.rgb},0.6)` }}>
                                        ₹{activePlan.price}
                                      </span>
                                      <span style={{ fontSize:"1rem", color:"#666666",
                                        fontFamily:"'Inter',sans-serif" }}>/year</span>
                                    </div>
                                    {/* Daily cost reframe */}
                                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                                      <span style={{ fontSize:"1.0rem", fontWeight:700,
                                        color:activePlan.tagColor,
                                        fontFamily:"'Inter',sans-serif" }}>
                                        {activePlan.perDay}
                                      </span>
                                      <span style={{ fontSize:"1.02rem", color:"#666666",
                                        fontStyle:"italic" }}>— {activePlan.perDayNote}</span>
                                    </div>
                                    <div style={{ fontSize:"1.0rem", color:"#666666",
                                      fontStyle:"italic", lineHeight:1.5 }}>
                                      {activePlan.trigger}
                                    </div>
                                  </div>

                                  {/* Feature pills */}
                                  <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                                    {activePlan.features.map(f => (
                                      <div key={f.label}
                                        onClick={() => { if(f.view){ setCompareDrawer(null); setView(f.view); }}}
                                        style={{
                                          display:"flex", alignItems:"center", gap:14,
                                          padding:"14px 18px", borderRadius:14,
                                          background:`rgba(${activePlan.rgb},0.1)`,
                                          border:`1px solid rgba(${activePlan.rgb},0.3)`,
                                          boxShadow:`0 2px 10px rgba(${activePlan.rgb},0.08)`,
                                          cursor: f.view ? "pointer" : "default",
                                          transition:"all 0.18s",
                                        }}
                                        onMouseEnter={e => { if(f.view){ e.currentTarget.style.background=`rgba(${activePlan.rgb},0.2)`; e.currentTarget.style.borderColor=`rgba(${activePlan.rgb},0.55)`; }}}
                                        onMouseLeave={e => { e.currentTarget.style.background=`rgba(${activePlan.rgb},0.1)`; e.currentTarget.style.borderColor=`rgba(${activePlan.rgb},0.3)`; }}
                                      >
                                        <span style={{ fontSize:"1.3rem", flexShrink:0,
                                          filter:`drop-shadow(0 0 6px rgba(${activePlan.rgb},0.7))` }}>
                                          {f.icon}
                                        </span>
                                        <span style={{ fontSize:"1rem", lineHeight:1.4,
                                          color:"#2b2b2b", flex:1 }}>{f.label}</span>
                                        {f.view && <span style={{ fontSize:"1.08rem", opacity:0.45 }}>›</span>}
                                      </div>
                                    ))}
                                  </div>

                                  <SubscribeBtn plan={activePlan} />

                                  {/* Prev / Next arrows */}
                                  <div style={{ display:"flex", justifyContent:"space-between",
                                    alignItems:"center", marginTop:18, gap:12 }}>
                                    <button onClick={goPrev} disabled={planIdx === 0}
                                      style={{ flex:1, padding:"12px", borderRadius:12, cursor: planIdx===0 ? "default":"pointer",
                                        background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)",
                                        color: planIdx===0 ? "#999999" : "#2b2b2b",
                                        fontSize:"1.0rem", fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em",
                                        transition:"all 0.18s" }}
                                      onMouseEnter={e => { if(planIdx>0){ e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.color="#1a1a1a"; }}}
                                      onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.05)"; e.currentTarget.style.color=planIdx===0?"#999999":"#2b2b2b"; }}
                                    >← Previous</button>
                                    <span style={{ fontSize:"1.0rem", color:"#5a5a5a",
                                      fontFamily:"'Inter',sans-serif" }}>{planIdx+1} / {PLANS.length}</span>
                                    <button onClick={goNext} disabled={planIdx === PLANS.length-1}
                                      style={{ flex:1, padding:"12px", borderRadius:12, cursor: planIdx===PLANS.length-1 ? "default":"pointer",
                                        background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)",
                                        color: planIdx===PLANS.length-1 ? "#999999" : "#2b2b2b",
                                        fontSize:"1.0rem", fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em",
                                        transition:"all 0.18s" }}
                                      onMouseEnter={e => { if(planIdx<PLANS.length-1){ e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.color="#1a1a1a"; }}}
                                      onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.05)"; e.currentTarget.style.color=planIdx===PLANS.length-1?"#999999":"#2b2b2b"; }}
                                    >Next →</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </>
              );
            })()}
            {payError && (
              <div style={{ fontSize:"1.1rem", color:"#f87171", marginBottom:12,
                textAlign:"center", lineHeight:1.5 }}>{payError}</div>
            )}
          </div>
          )}

          {/* Layer 2: Personalized horoscope */}
          {(arcData || personalLoad) && (
            <PersonalHoroscopeCard
              horoscope={personalHoro}
              loading={personalLoad}
              onUpgrade={isSubscribed ? null : handleUpgrade}
            />
          )}

          {/* Layer 2: Weekly Mood & Energy */}
          {(arcData || weeklyLoad) && (
            <WeeklyReadingCard
              reading={weeklyReading}
              loading={weeklyLoad}
              onUpgrade={isSubscribed ? null : handleUpgrade}
            />
          )}

          {/* Layer 1: Generic daily rashi horoscope */}
          {(arcData || horoLoad) && (
            <div ref={horoscopeRef} style={{ marginBottom:"18px" }}>
              <HoroscopeCard
                horoscope={horoscope}
                rashi={arcData ? RASHIS_META[arcData.rashi_idx] : null}
                loading={horoLoad}
              />
            </div>
          )}
        </>
      )}

      {/* ── FEATURE VIEWS ── */}
      {view !== "home" && (
        <>
          {/* Feature page header */}
          {currentFeature && (
            <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"22px",
              paddingBottom:"14px", borderBottom:"1px solid rgba(245,240,232,0.08)" }}>
              <div style={{ fontSize:"1.6rem", lineHeight:1,
                filter:`drop-shadow(0 0 6px ${currentFeature.color}50)`, flexShrink:0 }}>
                {currentFeature.icon}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.4rem",
                  color:"var(--saffron)",
                  fontWeight:700, marginBottom:"4px" }}>
                  {currentFeature.title}
                </div>
                <div style={{ fontSize:"1.1rem", color:"#2b2b2b",
                  fontFamily:"'Inter',sans-serif", fontStyle:"italic" }}>
                  {currentFeature.subtitle}
                </div>
              </div>
              {/* Light theme is locked on across the section — no dark toggle. */}
            </div>
          )}

          {/* ARCHETYPE */}
          {view === "archetype" && (
            <>
              {arcLoad && shimmer}
              {!arcLoad && noBirth && (
                <BirthForm userId={user.id} onSaved={() => { setNoBirth(false); fetchArchetype(); }} />
              )}
              {!arcLoad && arcData && !correcting && (
                <>
                  {!arcData.has_birth_time && (
                    <div style={{
                      background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                      borderRadius:"14px", padding:"14px 18px", marginBottom:"16px",
                      display:"flex", gap:"10px", alignItems:"center",
                    }}>
                      <span style={{ fontSize:"1.2rem", flexShrink:0 }}>⚠️</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"1.0rem", color:"var(--cream)", lineHeight:1.6 }}>
                          <b>Rashi may be approximate</b> — no birth time saved.
                          If your pujari told you a different Rashi, add your birth time.
                        </div>
                      </div>
                      <button onClick={() => setCorrecting(true)} style={{
                        background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.4)",
                        borderRadius:"10px", padding:"8px 14px",
                        color:"#f87171", fontSize:"1.05rem", cursor:"pointer",
                        fontFamily:JT.heading, whiteSpace:"nowrap", flexShrink:0,
                      }}>
                        Fix it →
                      </button>
                    </div>
                  )}
                  <ArchetypeCard data={arcData} lang={lang} onShare={handleShare} />
                  <div style={{ textAlign:"center", marginTop:"12px" }}>
                    <button onClick={() => setCorrecting(true)} style={{
                      background:"transparent", border:"1px solid var(--border)",
                      borderRadius:"10px", padding:"8px 18px",
                      color:"var(--text-dim)", fontSize:"1.08rem", cursor:"pointer",
                    }}>
                      ✏️ Edit Birth Details
                    </button>
                  </div>
                </>
              )}
              {!arcLoad && arcData && correcting && (
                <BirthForm
                  userId={user.id}
                  isCorrection={true}
                  initialDob={arcData.dob || ""}
                  initialTime={arcData.birth_time || ""}
                  initialCity={arcData.birth_city || ""}
                  onSaved={() => { setCorrecting(false); fetchArchetype(); }}
                />
              )}
            </>
          )}
          {view === "archetype" && !noBirth && !correcting && !arcLoad && arcData && (
            <ReportBridge
              heading="This is who you are. Now read where you're going."
              lines={[
                "Your soul's purpose in work, love, and family",
                "The strengths your chart asks you to lean on",
                "Your path ahead, written chapter by chapter",
              ]}
              onOpen={() => setView("reports")}
            />
          )}

          {/* NAKSHATRA */}
          {view === "nakshatra" && (
            <>
              {arcLoad && shimmer}
              {!arcLoad && noBirth && <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Discover Your Birth Star" buttonLabel="✨ Reveal My Nakshatra" />}
              {!arcLoad && arcData?.nakshatra && (
                <NakshatraCard nak={arcData.nakshatra} lang={lang} />
              )}
            </>
          )}

          {/* ISHTA DEVATA */}
          {view === "ishta_devata" && (
            <>
              {ishtaLoad && shimmer}
              {!ishtaLoad && noBirth && (
                <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Discover Your Ishta Devata" buttonLabel="🪔 Reveal My Deity" />
              )}
              {!ishtaLoad && ishtaErr && !noBirth && (
                <div style={{
                  background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                  borderRadius:"14px", padding:"20px", textAlign:"center",
                }}>
                  <div style={{ fontSize:"1.5rem", marginBottom:"8px" }}>⚠️</div>
                  <div style={{ color:"var(--text-dim)", fontSize:"1.0rem", lineHeight:1.6 }}>
                    {ishtaErr === "no_birth_city" || ishtaErr?.includes("city")
                      ? "Birth city is required to calculate your Lagna and Ishta Devata. Please add it in your profile."
                      : ishtaErr}
                  </div>
                </div>
              )}
              {!ishtaLoad && ishtaData && (
                <IshtaDevataCard data={ishtaData} lang={lang} />
              )}
            </>
          )}

          {/* SHANI STATUS */}
          {view === "shani" && (
            <>
              {shaniLoad && shimmer}
              {!shaniLoad && noBirth && (
                <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Check Your Shani Status" buttonLabel="🪐 Check Sade Sati" />
              )}
              {!shaniLoad && shaniErr && !noBirth && (
                <div style={{
                  background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                  borderRadius:"14px", padding:"20px", textAlign:"center",
                }}>
                  <div style={{ fontSize:"1.5rem", marginBottom:"8px" }}>⚠️</div>
                  <div style={{ color:"var(--text-dim)", fontSize:"1.0rem", lineHeight:1.6 }}>
                    {shaniErr}
                  </div>
                </div>
              )}
              {!shaniLoad && shaniData && (
                <ShaniStatusCard data={shaniData} />
              )}
            </>
          )}

          {/* ── SACRED ALERTS (puja_alerts) ── */}
          {view === "puja_alerts" && (() => {
            const uLang = lang;
            const CAT_META = {
              ekadashi:  { icon:"🌿", color:"#15803d", rgb:"134,239,172", label:{ en:"Ekadashi", hi:"एकादशी", te:"ఏకాదశి", ta:"ஏகாதசி", kn:"ಏಕಾದಶಿ", mr:"एकादशी", ml:"ഏകാദശി" }, action:{ en:"Fast · Vishnu Puja", hi:"उपवास · विष्णु पूजा", te:"ఉపవాసం · విష్ణు పూజ", ta:"விரதம் · விஷ்ணு பூஜை", kn:"ಉಪವಾಸ · ವಿಷ್ಣು ಪೂಜೆ", mr:"उपवास · विष्णू पूजा", ml:"ഉപവാസം · വിഷ്ണു പൂജ" } },
              amavasya:  { icon:"🌑", color:"#475569", rgb:"148,163,184", label:{ en:"Amavasya", hi:"अमावस्या", te:"అమావాస్య", ta:"அமாவாசை", kn:"ಅಮಾವಾಸ್ಯೆ", mr:"अमावस्या", ml:"അമാവാസ്യ" }, action:{ en:"Ancestor Tarpan · Lamp", hi:"पितृ तर्पण · दीप दान", te:"పితృ తర్పణం · దీప దానం", ta:"பிதுர் தர்ப்பணம் · விளக்கு", kn:"ಪಿತೃ ತರ್ಪಣ · ದೀಪ", mr:"पितृ तर्पण · दीप", ml:"പിതൃ തർപ്പണം · ദീപം" } },
              pradosham: { icon:"🔱", color:"#b45309", rgb:"251,191,36",  label:{ en:"Pradosham", hi:"प्रदोष", te:"ప్రదోషం", ta:"பிரதோஷம்", kn:"ಪ್ರದೋಷ", mr:"प्रदोष", ml:"പ്രദോഷം" }, action:{ en:"Shiva Abhishek at dusk", hi:"संध्या शिव अभिषेक", te:"సాయంకాలం శివ అభిషేకం", ta:"மாலை சிவ அபிஷேகம்", kn:"ಸಂಧ್ಯಾ ಶಿವ ಅಭಿಷೇಕ", mr:"संध्याकाळ शिव अभिषेक", ml:"പ്രദോഷ ശിവ അഭിഷേകം" } },
              purnima:   { icon:"🌕", color:"#a16207", rgb:"245,209,122", label:{ en:"Purnima", hi:"पूर्णिमा", te:"పౌర్ణమి", ta:"பௌர்ணமி", kn:"ಹುಣ್ಣಿಮೆ", mr:"पौर्णिमा", ml:"പൂർണ്ണിമ" }, action:{ en:"Satya Narayan Puja · Dana", hi:"सत्यनारायण पूजा · दान", te:"సత్యనారాయణ పూజ · దానం", ta:"சத்யநாராயண பூஜை · தானம்", kn:"ಸತ್ಯನಾರಾಯಣ ಪೂಜೆ · ದಾನ", mr:"सत्यनारायण पूजा · दान", ml:"സത്യനാരായണ പൂജ · ദാനം" } },
            };
            const days = pujaAlertDays || [];
            return (
              <div>
                {/* Header banner */}
                <div style={{ borderRadius:18, overflow:"hidden", marginBottom:18,
                  background:"#ffffff",
                  border:"1px solid rgba(167,139,250,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(167,139,250,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(167,139,250,0.8),rgba(216,180,254,1),rgba(167,139,250,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(167,139,250,0.25),rgba(109,40,217,0.15))",
                      border:"1px solid rgba(167,139,250,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(167,139,250,0.3)" }}>
                      <span style={{ fontSize:"1.6rem", filter:"drop-shadow(0 0 8px rgba(216,180,254,0.9))" }}>🌙</span>
                    </div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                        color:"#7e22ce", marginBottom:4, letterSpacing:"0.04em" }}>
                        { {en:"Your Sacred Moon Calendar", hi:"आपका पवित्र चंद्र कैलेंडर", te:"మీ పవిత్ర చంద్ర పంచాంగం", ta:"உங்கள் புனித சந்திர காலண்டர்", kn:"ನಿಮ್ಮ ಪವಿತ್ರ ಚಂದ್ರ ಕ್ಯಾಲೆಂಡರ್", mr:"तुमचे पवित्र चंद्र पंचांग", ml:"നിങ്ങളുടെ പവിത്ര ചന്ദ്ര കലണ്ടർ" }[uLang] || "Your Sacred Moon Calendar" }
                      </div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim,
                        fontFamily:"'Inter',sans-serif" }}>
                        { {en:"Upcoming Ekadashi · Amavasya · Pradosham · Purnima", hi:"आगामी एकादशी · अमावस्या · प्रदोष · पूर्णिमा", te:"రాబోయే ఏకాదశి · అమావాస్య · ప్రదోషం · పౌర్ణమి", ta:"வரவிருக்கும் ஏகாதசி · அமாவாசை · பிரதோஷம் · பௌர்ணமி", kn:"ಮುಂಬರುವ ಏಕಾದಶಿ · ಅಮಾವಾಸ್ಯೆ · ಪ್ರದೋಷ · ಹುಣ್ಣಿಮೆ", mr:"येणारी एकादशी · अमावस्या · प्रदोष · पौर्णिमा", ml:"വരാനിരിക്കുന്ന ഏകാദശി · അമാവാസ്യ · പ്രദോഷം · പൂർണ്ണിമ" }[uLang] || "Upcoming Ekadashi · Amavasya · Pradosham · Purnima" }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loading */}
                {pujaAlertLoad && <div style={{ height:200, borderRadius:16, background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />}

                {/* Sacred days list */}
                {!pujaAlertLoad && days.length > 0 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {days.map((d, i) => {
                      const m = CAT_META[d.cat] || CAT_META.ekadashi;
                      const dateObj = new Date(d.date);
                      const dayName = dateObj.toLocaleDateString("en-IN", { weekday:"long" });
                      const dateStr = dateObj.toLocaleDateString("en-IN", { day:"numeric", month:"long" });
                      const isToday = d.diff === 0;
                      const isTomorrow = d.diff === 1;
                      const daysLabel = isToday ? "TODAY" : isTomorrow ? "TOMORROW" : `${d.diff}d`;
                      return (
                        <div key={i} style={{
                          borderRadius:16, overflow:"hidden",
                          background:`linear-gradient(135deg,rgba(${m.rgb},0.08),rgba(${m.rgb},0.03))`,
                          border:`1px solid rgba(${m.rgb},${isToday ? "0.55" : "0.2"})`,
                          boxShadow: isToday ? `0 0 20px rgba(${m.rgb},0.2)` : "none",
                          transition:"all 0.2s",
                        }}>
                          {isToday && <div style={{ height:2, background:`linear-gradient(90deg,transparent,rgba(${m.rgb},0.9),transparent)` }} />}
                          <div style={{ padding:"14px 18px", display:"flex", alignItems:"center", gap:14 }}>
                            <div style={{ width:46, height:46, borderRadius:"50%", flexShrink:0,
                              background:`rgba(${m.rgb},0.12)`, border:`1px solid rgba(${m.rgb},0.3)`,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:"1.4rem", boxShadow:`0 0 12px rgba(${m.rgb},0.2)` }}>
                              {m.icon}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3, flexWrap:"wrap" }}>
                                <span style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                                  color:m.color, fontWeight:700 }}>
                                  {m.label[uLang] || m.label.en}
                                </span>
                                {d.festival && (
                                  <span style={{ fontSize:"1.2rem", color:"#666666",
                                    fontFamily:"'Inter',sans-serif" }}>
                                    · {d.festival}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize:"1.2rem", color:"#666666",
                                fontFamily:"'Inter',sans-serif", marginBottom:4 }}>
                                {m.action[uLang] || m.action.en}
                              </div>
                              <div style={{ fontSize:"1.2rem", color:"#5a5a5a",
                                fontFamily:JT.heading, letterSpacing:"0.06em" }}>
                                {dayName} · {dateStr}
                              </div>
                            </div>
                            <div style={{
                              background: isToday ? `rgba(${m.rgb},0.2)` : "#f5f5f5",
                              border:`1px solid rgba(${m.rgb},${isToday ? "0.5" : "0.2"})`,
                              borderRadius:20, padding:"6px 14px", flexShrink:0, textAlign:"center",
                              boxShadow: isToday ? `0 0 12px rgba(${m.rgb},0.3)` : "none",
                            }}>
                              <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                                color:m.color, fontWeight:700 }}>{daysLabel}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!pujaAlertLoad && days.length === 0 && (
                  <div style={{ textAlign:"center", padding:"40px 20px",
                    color:"#666666", fontFamily:"'Inter',sans-serif", fontSize:"1.2rem" }}>
                    No upcoming sacred days found. Try again later.
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── PLANET ALERTS (planet_alerts) ── */}
          {view === "planet_alerts" && (() => {
            const uLang = lang;
            const PLANETS_INFO = [
              { sym:"☉", name:{ en:"Sun",     hi:"सूर्य",     te:"సూర్యుడు", ta:"சூரியன்", kn:"ಸೂರ್ಯ", mr:"सूर्य",    ml:"സൂര്യൻ" }, color:"#b45309", rgb:"251,191,36",  effect:{ en:"Authority · Career · Vitality",    hi:"अधिकार · करियर · जीवनशक्ति", te:"అధికారం · కెరీర్ · జీవశక్తి",    ta:"அதிகாரம் · தொழில்",   kn:"ಅಧಿಕಾರ · ವೃತ್ತಿ" } },
              { sym:"☽", name:{ en:"Moon",    hi:"चंद्र",     te:"చంద్రుడు",  ta:"சந்திரன்",kn:"ಚಂದ್ರ", mr:"चंद्र",    ml:"ചന്ദ്രൻ" }, color:"#475569", rgb:"226,232,240", effect:{ en:"Mind · Emotions · Home",           hi:"मन · भावनाएं · घर",           te:"మనసు · భావాలు · ఇల్లు",          ta:"மனம் · உணர்வுகள்",    kn:"ಮನಸ್ಸು · ಭಾವನೆ" } },
              { sym:"♂", name:{ en:"Mars",    hi:"मंगल",     te:"కుజుడు",    ta:"செவ்வாய்",kn:"ಮಂಗಳ", mr:"मंगळ",    ml:"ചൊவ്വ" }, color:"#dc2626", rgb:"248,113,113",  effect:{ en:"Energy · Courage · Conflict",      hi:"ऊर्जा · साहस · संघर्ष",        te:"శక్తి · ధైర్యం · సంఘర్షణ",      ta:"ஆற்றல் · தைரியம்",   kn:"ಶಕ್ತಿ · ಧೈರ್ಯ" } },
              { sym:"☿", name:{ en:"Mercury", hi:"बुध",       te:"బుధుడు",    ta:"புதன்",    kn:"ಬುಧ",   mr:"बुध",      ml:"ബുധൻ" }, color:"#059669", rgb:"52,211,153",   effect:{ en:"Intellect · Speech · Business",   hi:"बुद्धि · वाणी · व्यापार",      te:"బుద్ధి · వ్యాపారం · వాణి",      ta:"புத்தி · பேச்சு",     kn:"ಬುದ್ಧಿ · ಮಾತು" } },
              { sym:"♃", name:{ en:"Jupiter", hi:"बृहस्पति",  te:"బృహస్పతి",  ta:"குரு",     kn:"ಗುರು",  mr:"गुरू",     ml:"ഗുരു" }, color:"#a16207", rgb:"245,158,11",   effect:{ en:"Wisdom · Wealth · Blessings",     hi:"ज्ञान · धन · आशीर्वाद",        te:"జ్ఞానం · సంపద · ఆశీర్వాదం",    ta:"ஞானம் · செல்வம்",    kn:"ಜ್ಞಾನ · ಸಂಪತ್ತು" } },
              { sym:"♀", name:{ en:"Venus",   hi:"शुक्र",     te:"శుక్రుడు",  ta:"சுக்கிரன்",kn:"ಶುಕ್ರ", mr:"शुक्र",    ml:"ശുക്രൻ" }, color:"#be185d", rgb:"249,168,212",  effect:{ en:"Love · Beauty · Luxury",          hi:"प्रेम · सौंदर्य · भोग",        te:"ప్రేమ · అందం · సౌఖ్యం",         ta:"காதல் · அழகு",        kn:"ಪ್ರೀತಿ · ಸೌಂದರ್ಯ" } },
              { sym:"♄", name:{ en:"Saturn",  hi:"शनि",       te:"శని",       ta:"சனி",      kn:"ಶನಿ",   mr:"शनी",      ml:"ശനി" }, color:"#475569", rgb:"148,163,184",  effect:{ en:"Discipline · Karma · Delays",     hi:"अनुशासन · कर्म · विलंब",       te:"క్రమశిక్షణ · కర్మ · ఆలస్యాలు", ta:"ஒழுக்கம் · கர்மம்",  kn:"ಶಿಸ್ತು · ಕರ್ಮ" } },
              { sym:"☊", name:{ en:"Rahu",    hi:"राहु",      te:"రాహువు",    ta:"ராகு",     kn:"ರಾಹು",  mr:"राहू",     ml:"രാഹു" }, color:"#6d28d9", rgb:"167,139,250",  effect:{ en:"Ambition · Illusion · Obsession", hi:"महत्वाकांक्षा · माया",          te:"ఆశలు · మాయ · అభిలాష",          ta:"லட்சியம் · மாயை",    kn:"ಮಹತ್ವಾಕಾಂಕ್ಷೆ" } },
              { sym:"☋", name:{ en:"Ketu",    hi:"केतु",      te:"కేతువు",    ta:"கேது",     kn:"ಕೇತು",  mr:"केतू",     ml:"കേതു" }, color:"#047857", rgb:"110,231,183",  effect:{ en:"Liberation · Detachment · Moksha",hi:"मोक्ष · वैराग्य",              te:"మోక్షం · వైరాగ్యం",            ta:"மோட்சம் · வைராக்யம்", kn:"ಮೋಕ್ಷ · ವೈರಾಗ್ಯ" } },
            ];
            return (
              <div>
                {/* Header */}
                <div style={{ borderRadius:18, overflow:"hidden", marginBottom:18,
                  background:"#ffffff",
                  border:"1px solid rgba(59,130,246,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(59,130,246,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(59,130,246,0.8),rgba(147,197,253,1),rgba(59,130,246,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(59,130,246,0.25),rgba(29,78,216,0.15))",
                      border:"1px solid rgba(59,130,246,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(59,130,246,0.35)" }}>
                      <span style={{ fontSize:"1.6rem", filter:"drop-shadow(0 0 8px rgba(147,197,253,0.9))" }}>🪐</span>
                    </div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                        color:"#1d4ed8", marginBottom:4, letterSpacing:"0.04em" }}>
                        { {en:"The Nine Grahas", hi:"नव ग्रह — आपके भाग्य के स्वामी", te:"నవ గ్రహాలు — మీ భాగ్య నిర్ణేతలు", ta:"நவ கிரகங்கள்", kn:"ನವ ಗ್ರಹಗಳು", mr:"नव ग्रह", ml:"നവ ഗ്രഹങ്ങൾ" }[uLang] || "The Nine Grahas" }
                      </div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim,
                        fontFamily:"'Inter',sans-serif" }}>
                        { {en:"Planets move. Your life energy shifts.", hi:"ग्रह बदलते हैं, जीवन ऊर्जा बदलती है", te:"గ్రహాలు కదులుతాయి, జీవనశక్తి మారుతుంది", ta:"கிரகங்கள் நகர்கின்றன, உயிர் ஆற்றல் மாறுகிறது", kn:"ಗ್ರಹಗಳು ಚಲಿಸುತ್ತವೆ, ಜೀವ ಶಕ್ತಿ ಬದಲಾಗುತ್ತದೆ", mr:"ग्रह बदलतात, जीवनशक्ती बदलते", ml:"ഗ്രഹങ്ങൾ നീങ്ങുന്നു, ജീവനൂർജ്ജം മാറുന്നു" }[uLang] || "Planets move. Your life energy shifts." }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Planet grid */}
                <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
                  {PLANETS_INFO.map((p, i) => (
                    <div key={i} style={{
                      borderRadius:14, padding:"13px 16px",
                      background:`linear-gradient(135deg,rgba(${p.rgb},0.07),rgba(${p.rgb},0.02))`,
                      border:`1px solid rgba(${p.rgb},0.18)`,
                      display:"flex", alignItems:"center", gap:14,
                      transition:"all 0.2s",
                    }}>
                      <div style={{ width:42, height:42, borderRadius:"50%", flexShrink:0,
                        background:`rgba(${p.rgb},0.12)`, border:`1px solid rgba(${p.rgb},0.3)`,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:"1.25rem", color:p.color, fontWeight:700, fontFamily:"serif",
                        boxShadow:`0 0 10px rgba(${p.rgb},0.2)` }}>
                        {p.sym}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                          color:p.color, fontWeight:700, marginBottom:2 }}>
                          {p.name[uLang] || p.name.en}
                        </div>
                        <div style={{ fontSize:"1.2rem", color:"#666666",
                          fontFamily:"'Inter',sans-serif",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {p.effect[uLang] || p.effect.en}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Coming soon banner */}
                <div style={{ borderRadius:16, padding:"18px 20px", textAlign:"center",
                  background:"rgba(59,130,246,0.05)", border:"1px solid rgba(59,130,246,0.2)",
                  boxShadow:JT.shadow }}>
                  <div style={{ fontSize:"1.5rem", marginBottom:8 }}>🔔</div>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                    color:"#1d4ed8", marginBottom:6, letterSpacing:"0.05em" }}>
                    { {en:"Real-time Transit Alerts — Launching Soon", hi:"रियल-टाइम ग्रह सूचनाएं — शीघ्र आ रही हैं", te:"రియల్-టైమ్ గ్రహ అప్రమత్తాలు — త్వరలో", ta:"நிகழ்நேர கிரக எச்சரிக்கைகள் — விரைவில்", kn:"ರಿಯಲ್-ಟೈಮ್ ಗ್ರಹ ಎಚ್ಚರಿಕೆಗಳು — ಶೀಘ್ರದಲ್ಲಿ", mr:"रिअल-टाइम ग्रह सूचना — लवकरच", ml:"തത്സമയ ഗ്രഹ മുന്നറിയിപ്പ് — ഉടൻ" }[uLang] || "Real-time Transit Alerts — Launching Soon" }
                  </div>
                  <div style={{ fontSize:"1.2rem", color:"#666666",
                    fontFamily:"'Inter',sans-serif", lineHeight:1.6, maxWidth:320, margin:"0 auto" }}>
                    { {en:"You'll receive a push notification whenever a planet enters a new sign, goes retrograde, or creates a significant transit affecting your Rashi.", hi:"जब कोई ग्रह राशि बदले, वक्री हो या आपकी राशि को प्रभावित करे — आपको तुरंत सूचना मिलेगी।", te:"గ్రహం రాశి మారినప్పుడు, వక్రి అయినప్పుడు లేదా మీ రాశిని ప్రభావితం చేసినప్పుడు — వెంటనే నోటిఫికేషన్ వస్తుంది.", ta:"கிரகம் ராசி மாறும்போது, வக்கிரம் ஆகும்போது — உடனடியாக அறிவிப்பு கிடைக்கும்.", kn:"ಗ್ರಹ ರಾಶಿ ಬದಲಿಸಿದಾಗ, ವಕ್ರಿಯಾದಾಗ — ತಕ್ಷಣ ಅಧಿಸೂಚನೆ ಬರುತ್ತದೆ.", mr:"ग्रह राशी बदलल्यावर, वक्री झाल्यावर — लगेच सूचना मिळेल.", ml:"ഗ്രഹം രാശി മാറുമ്പോൾ, വക്രരാകുമ്പോൾ — ഉടൻ അറിയിപ്പ് ലഭിക്കും." }[uLang] || "You'll receive a push notification whenever a planet enters a new sign, goes retrograde, or creates a significant transit affecting your Rashi." }
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── DASHA NAVIGATOR ── */}
          {view === "dasha_navigator" && (() => {
            if (noBirth) return <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Map Your Life Chapters" buttonLabel="⏳ Show My Dasha Map" />;
            if (dashaNavLoad) return <div style={{ height:300, borderRadius:16, background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />;
            if (!dashaNavData) return null;
            const { maha_dasha, antardasha, timeline } = dashaNavData;
            const accent = maha_dasha.color;
            const rgb = hexToRgb(accent);
            const curIdx = (timeline || []).findIndex(t => t.is_current);
            const upcoming = (timeline || []).slice(curIdx + 1);
            const nextDasha = upcoming[0];
            const DASHA_MEANINGS_BRIEF = {
              ketu:    { feel:"Spiritual surrender, release of old structures", prep:"Begin a meditation practice now. Simplify." },
              venus:   { feel:"Abundance, love, and creative flowering", prep:"Invest in relationships. Pursue creative work." },
              sun:     { feel:"Authority, recognition, ego refinement", prep:"Build leadership skills. Heal father relationship." },
              moon:    { feel:"Emotional depth, intuition, home", prep:"Strengthen family bonds. Tend your inner world." },
              mars:    { feel:"Energy, ambition, property, courage", prep:"Pursue bold goals now. Build physical strength." },
              rahu:    { feel:"Obsession, worldly ambition, rapid change", prep:"Stay grounded. Avoid shortcuts and illusions." },
              jupiter: { feel:"Wisdom, wealth, expansion, blessings", prep:"Study, teach, be generous. Dharma pays now." },
              saturn:  { feel:"Discipline, karma, delays that teach", prep:"Do the work. Pay debts. Build slow foundations." },
              mercury: { feel:"Intellect, speech, commerce, networking", prep:"Learn new skills. Communicate clearly. Write." },
            };
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {/* Header */}
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(34,197,94,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(34,197,94,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(34,197,94,0.8),rgba(134,239,172,1),rgba(34,197,94,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(34,197,94,0.25),rgba(21,128,61,0.15))",
                      border:"1px solid rgba(34,197,94,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(34,197,94,0.3)", fontSize:"1.8rem" }}>⏳</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#15803d", marginBottom:4 }}>Your Life Chapter Map</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>120-year Vimshottari journey · where you are · what comes next</div>
                    </div>
                  </div>
                </div>

                {/* Full timeline */}
                <div style={{ borderRadius:16, border:"1px solid rgba(34,197,94,0.15)", overflow:"hidden",
                  background:"linear-gradient(135deg,rgba(34,197,94,0.04),rgba(0,0,0,0))" }}>
                  <div style={{ padding:"14px 18px 6px", fontSize:"1rem", letterSpacing:"0.14em",
                    color:"#5a5a5a", fontFamily:JT.heading,  }}>Complete Dasha Timeline</div>
                  <div style={{ display:"flex", flexDirection:"column" }}>
                    {(timeline || []).map((d, i) => {
                      const isCur = d.is_current;
                      const isPast = i < curIdx;
                      const dRgb = hexToRgb(d.color);
                      return (
                        <div key={i} style={{
                          display:"flex", alignItems:"center", gap:14,
                          padding:"12px 18px",
                          background: isCur ? `rgba(${dRgb},0.1)` : "transparent",
                          borderLeft: isCur ? `3px solid ${d.color}` : "3px solid transparent",
                          opacity: isPast ? 0.4 : 1,
                        }}>
                          <span style={{ fontSize:"1.5rem", filter: isCur ? `drop-shadow(0 0 8px ${d.color})` : "none" }}>{d.emoji}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontFamily:JT.heading, fontSize:"1.1rem",
                              color: isCur ? d.color : "#2b2b2b", fontWeight: isCur ? 700 : 400 }}>
                              {d.name?.en} Dasha {isCur ? "← Now" : ""}
                            </div>
                            <div style={{ fontSize:"1rem", color:"#666666", fontFamily:"'Inter',sans-serif" }}>
                              Age {d.age_start}–{d.age_end} · {new Date(d.start).getFullYear()}–{new Date(d.end).getFullYear()} · {d.years} yrs
                            </div>
                          </div>
                          {isCur && (
                            <div style={{ background:`rgba(${dRgb},0.18)`, border:`1px solid rgba(${dRgb},0.4)`,
                              borderRadius:20, padding:"4px 12px", fontSize:"1rem", color:d.color, fontFamily:JT.heading }}>
                              Active
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Next dasha preview */}
                {nextDasha && (() => {
                  const briefKey = nextDasha.name?.en?.toLowerCase();
                  const brief = DASHA_MEANINGS_BRIEF[briefKey] || {};
                  const nRgb = hexToRgb(nextDasha.color);
                  const startsIn = Math.round((new Date(nextDasha.start) - new Date()) / (1000 * 60 * 60 * 24 * 365.25) * 10) / 10;
                  return (
                    <div style={{ borderRadius:16, border:`1px solid rgba(${nRgb},0.3)`,
                      background:`linear-gradient(135deg,rgba(${nRgb},0.08),rgba(${nRgb},0.02))`,
                      padding:"18px 20px", boxShadow:`0 4px 20px rgba(${nRgb},0.1)` }}>
                      <div style={{ fontSize:"1rem", letterSpacing:"0.14em", color:"#5a5a5a",
                        fontFamily:JT.heading,  marginBottom:12 }}>Your Next Chapter</div>
                      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                        <span style={{ fontSize:"2.2rem", filter:`drop-shadow(0 0 12px ${nextDasha.color})` }}>{nextDasha.emoji}</span>
                        <div>
                          <div style={{ fontFamily:JT.heading, fontSize:"1.3rem", color:nextDasha.color }}>{nextDasha.name?.en} Dasha</div>
                          <div style={{ fontSize:"1rem", color:"#666666", fontFamily:"'Inter',sans-serif" }}>Begins in ~{startsIn} years · lasts {nextDasha.years} years · Age {nextDasha.age_start}–{nextDasha.age_end}</div>
                        </div>
                      </div>
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem", color:"#2b2b2b",
                        fontStyle:"italic", lineHeight:1.7, marginBottom:10, borderLeft:`3px solid ${nextDasha.color}55`, paddingLeft:14 }}>
                        "{brief.feel}"
                      </div>
                      <div style={{ background:`rgba(${nRgb},0.06)`, border:`1px solid rgba(${nRgb},0.15)`,
                        borderRadius:12, padding:"12px 14px" }}>
                        <div style={{ fontSize:"1rem", color:nextDasha.color, fontFamily:JT.heading,
                          fontWeight:700, marginBottom:6 }}>🌱 Prepare now</div>
                        <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{brief.prep}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Antardasha current */}
                {antardasha && (
                  <div style={{ borderRadius:14, padding:"14px 18px",
                    background:"#faf9f6", border:`1px solid ${JT.border}` }}>
                    <div style={{ fontSize:"1rem", letterSpacing:"0.12em", color:"#5a5a5a",
                      fontFamily:JT.heading,  marginBottom:8 }}>Current Antardasha</div>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:"1.6rem" }}>{antardasha.emoji}</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.1rem", color:antardasha.color }}>
                          {maha_dasha.name?.en}–{antardasha.name?.en} Antardasha
                        </div>
                        <div style={{ fontSize:"1rem", color:"#666666", fontFamily:"'Inter',sans-serif" }}>
                          Ends {new Date(antardasha.end).toLocaleDateString("en-IN",{month:"long",year:"numeric"})}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── DOSHA REMEDIES ── */}
          {view === "dosha_remedy" && (() => {
            if (noBirth) return <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Get Your Dosha Remedies" buttonLabel="💊 Show My Remedies" />;
            if (doshaFullLoad) return <div style={{ height:300, borderRadius:16, background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />;
            const d = doshaFullData;
            if (!d) return null;
            const hasDosha = d.manglik || d.kaal_sarp || d.sadhe_sati || d.pitra_dosha;
            const DOSHAS = [
              {
                key:"manglik", label:"Manglik Dosha", icon:"🔴", color:"#dc2626", rgb:"248,113,113",
                active: d.manglik,
                severity: d.manglik_severity,
                mars_h: d.manglik_mars_h_lagna,
                mars_rashi: d.manglik_mars_rashi,
                impact: d.manglik_impact,
                cancels: d.manglik_cancellations || [],
                remedies: d.manglik_remedies || [],
              },
              {
                key:"kaal_sarp", label: d.kaal_sarp_type || "Kaal Sarp Dosha", icon:"🐍", color:"#6d28d9", rgb:"167,139,250",
                active: d.kaal_sarp,
                severity: d.kaal_sarp_severity,
                mars_rashi: d.kaal_sarp_rahu_rashi,
                impact: d.kaal_sarp_impact,
                cancels: d.kaal_sarp_cancellations || [],
                remedies: d.kaal_sarp_remedies || [],
              },
              {
                key:"sadhe_sati", label:"Sadhe Sati", icon:"🪐", color:"#475569", rgb:"148,163,184",
                active: d.sadhe_sati,
                severity: d.shani_severity,
                impact: d.shani_impact,
                cancels: [],
                remedies: d.shani_remedies || [],
              },
              {
                key:"pitra_dosha", label:"Pitra Dosha", icon:"🌑", color:"#b45309", rgb:"251,191,36",
                active: d.pitra_dosha,
                severity: "moderate",
                impact: d.pitra_impact || "Ancestral karmic debt may manifest as delays, family tensions, or recurring obstacles in key life areas.",
                cancels: d.pitra_cancellations || [],
                remedies: d.pitra_remedies || ["Perform Shraddha rituals on Pitru Paksha (Mahalaya Amavasya)","Offer water (jal) to ancestors daily — mix til (sesame) and barley","Feed crows and Brahmins on Amavasya","Light a sesame oil lamp at dusk facing south on new moon days","Visit Gaya, Prayagraj or Kashi for Pitra Tarpan if remedies are needed urgently"],
              },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {/* Header */}
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(239,68,68,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(239,68,68,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(239,68,68,0.8),rgba(252,165,165,1),rgba(239,68,68,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(239,68,68,0.25),rgba(153,27,27,0.15))",
                      border:"1px solid rgba(239,68,68,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(239,68,68,0.3)", fontSize:"1.8rem" }}>💊</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#b91c1c", marginBottom:4 }}>Your Dosha Remedy Plan</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Severity · Cancellations · Step-by-step remedies</div>
                    </div>
                  </div>
                </div>

                {!hasDosha && (
                  <div style={{ borderRadius:16, padding:"30px 20px", textAlign:"center",
                    background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)" }}>
                    <div style={{ fontSize:"2.5rem", marginBottom:10 }}>✨</div>
                    <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#15803d", marginBottom:8 }}>No Major Doshas Detected</div>
                    <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.7 }}>Your chart is free of the four major doshas. This is a rare and fortunate placement — protect it with daily sadhana and gratitude.</div>
                  </div>
                )}

                {DOSHAS.filter(dos => dos.active).map(dos => (
                  <div key={dos.key} style={{ borderRadius:16, border:`1px solid rgba(${dos.rgb},0.25)`,
                    background:`linear-gradient(135deg,rgba(${dos.rgb},0.07),rgba(${dos.rgb},0.02))`,
                    overflow:"hidden" }}>
                    <div style={{ height:2, background:`linear-gradient(90deg,transparent,rgba(${dos.rgb},0.8),transparent)` }} />
                    <div style={{ padding:"16px 18px" }}>
                      {/* Title row */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:"1.6rem" }}>{dos.icon}</span>
                          <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:dos.color, fontWeight:700 }}>{dos.label}</div>
                        </div>
                        {dos.severity && (
                          <div style={{ background:`rgba(${dos.rgb},0.15)`, border:`1px solid rgba(${dos.rgb},0.3)`,
                            borderRadius:20, padding:"3px 12px", fontSize:"1rem",
                            color:dos.color, fontFamily:JT.heading,  }}>
                            {dos.severity}
                          </div>
                        )}
                      </div>
                      {/* Impact */}
                      <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif",
                        lineHeight:1.7, marginBottom:12,
                        borderLeft:`3px solid rgba(${dos.rgb},0.4)`, paddingLeft:12 }}>
                        {dos.impact}
                      </div>
                      {/* Cancellations */}
                      {dos.cancels.length > 0 && (
                        <div style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.22)",
                          borderRadius:12, padding:"10px 14px", marginBottom:12 }}>
                          <div style={{ fontSize:"1rem", color:"#15803d", fontFamily:JT.heading,
                            fontWeight:700, marginBottom:6 }}>✓ Cancellation factors in your chart</div>
                          {dos.cancels.map((c, i) => (
                            <div key={i} style={{ fontSize:"1.1rem", color:"#2b2b2b",
                              fontFamily:"'Inter',sans-serif", lineHeight:1.6, marginBottom:3 }}>· {c}</div>
                          ))}
                        </div>
                      )}
                      {/* Remedies */}
                      <div style={{ background:`rgba(${dos.rgb},0.06)`, border:`1px solid rgba(${dos.rgb},0.18)`,
                        borderRadius:12, padding:"12px 14px" }}>
                        <div style={{ fontSize:"1rem", color:dos.color, fontFamily:JT.heading,
                          fontWeight:700, marginBottom:8 }}>🪔 Remedies</div>
                        {dos.remedies.map((r, i) => (
                          <div key={i} style={{ display:"flex", gap:8, marginBottom:8, alignItems:"flex-start" }}>
                            <span style={{ color:`rgba(${dos.rgb},0.7)`, fontWeight:700, flexShrink:0, marginTop:2 }}>{i + 1}.</span>
                            <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{r}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── SHANI KAVACH ── */}
          {view === "shani_kavach" && (() => {
            if (noBirth) return <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Get Your Shani Kavach" buttonLabel="🪐 Build My Shield" />;
            if (shaniLoad) return <div style={{ height:300, borderRadius:16, background:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />;
            const sd = shaniData;
            const inSadeSati = sd && (sd.phase === "rising" || sd.phase === "peak" || sd.phase === "setting");
            const inDhaiya = sd && sd.phase === "dhaiya";
            const activeProtection = inSadeSati || inDhaiya;
            const WEEKLY = [
              { day:"Saturday", practice:"Fast from sunrise to sunset. One meal — no salt, no meat. Visit Shani temple.", mantra:"Om Shanicharaya Namah", count:108 },
              { day:"Daily", practice:"Light a sesame oil lamp facing west at dusk. Chant before sleep.", mantra:"Om Sham Shanicharaya Namah", count:27 },
              { day:"Amavasya", practice:"Donate black sesame (til), mustard oil, and black cloth. Feed black dogs.", mantra:"Om Nilanjana Samabhasam", count:108 },
            ];
            const REMEDIES = [
              { icon:"🛢️", title:"Sesame Oil Lamp", desc:"Light a diya with sesame (til) oil every Saturday evening facing west. Shani's direction is west — this is the most effective daily remedy." },
              { icon:"⚫", title:"Black Donations", desc:"Donate black sesame, mustard oil, black lentils (urad dal), or black cloth to someone in need every Saturday. Shani rewards generosity instantly." },
              { icon:"🐦‍⬛", title:"Feed Crows", desc:"Crows are Shani's vahana. Feed them rice mixed with sesame every Saturday morning. This pacifies Saturn's karmic weight on your lineage." },
              { icon:"💎", title:"Blue Sapphire / Neelam", desc:"If your chart supports it, wear a natural blue sapphire in silver on the middle finger of the right hand. Consult a pandit first — this stone amplifies both good and bad Saturn." },
              { icon:"📿", title:"Hanuman Chalisa", desc:"Hanuman is the antidote to Saturn's adversity. Recite Hanuman Chalisa every Saturday — Shanidev cannot trouble those under Hanuman's protection." },
              { icon:"🕉️", title:"Shani Stotra", desc:"Recite the Shani Stotra ('Nilanjana Samabhasam...') daily during Sade Sati. 7 verses, 7 minutes, 7 weeks minimum." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {/* Header */}
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(100,116,139,0.35)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(100,116,139,0.12)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(100,116,139,0.8),rgba(203,213,225,1),rgba(100,116,139,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(100,116,139,0.3),rgba(51,65,85,0.2))",
                      border:"1px solid rgba(100,116,139,0.5)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(100,116,139,0.3)", fontSize:"1.8rem" }}>🛡️</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#334155", marginBottom:4 }}>Shani Kavach</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>
                        {activeProtection ? (inSadeSati ? "Sade Sati Active — Full Protection Protocol" : "Shani Dhaiya Active — Shield Practice") : "Shani Stable — Maintain Your Armour"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Kavach Mantra */}
                <div style={{ borderRadius:16, padding:"18px 20px",
                  background:"#ffffff", border:`1px solid ${JT.border}` }}>
                  <div style={{ fontSize:"1rem", color:JT.textDim,
                    fontFamily:JT.heading, fontWeight:700, marginBottom:12 }}>🪐 Shani Kavach Mantra</div>
                  <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem", color:"#1a1a1a",
                    lineHeight:1.9, textAlign:"center", marginBottom:10 }}>
                    ॐ नीलञ्जन समाभासं<br/>रविपुत्रं यमाग्रजम्<br/>छाया मार्तण्ड सम्भूतं<br/>तं नमामि शनैश्चरम्
                  </div>
                  <div style={{ fontSize:"1.1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif",
                    fontStyle:"italic", textAlign:"center", lineHeight:1.7 }}>
                    "I bow to Saturn, dark as collyrium,<br/>son of the Sun, elder brother of Yama,<br/>born of Chaya and Martanda."
                  </div>
                  <div style={{ display:"flex", justifyContent:"center", marginTop:12 }}>
                    <div style={{ background:"rgba(100,116,139,0.1)", border:"1px solid rgba(100,116,139,0.3)",
                      borderRadius:20, padding:"5px 18px", fontSize:"1.1rem", color:"#334155",
                      fontFamily:JT.heading }}>108× daily · Best time: sunrise or sunset</div>
                  </div>
                </div>

                {/* Weekly ritual */}
                <div style={{ borderRadius:16, border:`1px solid ${JT.border}`, overflow:"hidden", background:"#ffffff" }}>
                  <div style={{ padding:"12px 18px 4px", fontSize:"1rem",
                    color:JT.textDim, fontFamily:JT.heading }}>Weekly Practice</div>
                  {WEEKLY.map((w, i) => (
                    <div key={i} style={{ padding:"12px 18px", borderTop: i ? `1px solid ${JT.border}` : "none",
                      display:"flex", flexDirection:"column", gap:4 }}>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.1rem", color:"#334155" }}>{w.day}</div>
                      <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{w.practice}</div>
                      <div style={{ fontSize:"1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>{w.mantra} · {w.count}×</div>
                    </div>
                  ))}
                </div>

                {/* 6 remedies */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {REMEDIES.map((r, i) => (
                    <div key={i} style={{ borderRadius:14, padding:"14px", border:`1px solid ${JT.border}`,
                      background:"#ffffff" }}>
                      <div style={{ fontSize:"1.6rem", marginBottom:6 }}>{r.icon}</div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:"#334155", marginBottom:6 }}>{r.title}</div>
                      <div style={{ fontSize:"1.1rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{r.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── RETROGRADE GUIDE ── */}
          {view === "retrograde" && (() => {
            const PLANETS_R = [
              { name:"Mercury", sym:"☿", color:"#059669", rgb:"5,150,105", freq:"3–4× per year · ~21 days each",
                what:"Contracts, communication, devices, travel, and past connections resurface.",
                do_: "Review, revise, reconnect. Back up data. Avoid signing contracts.",
                avoid:"Launching new products, major purchases of electronics, starting new projects.",
                gita:"BG 6.5 — 'Let the mind be a friend, not an enemy — retrograde is the mind turning inward.'" },
              { name:"Venus", sym:"♀", color:"#be185d", rgb:"190,24,93", freq:"Every 18 months · ~40 days",
                what:"Relationships, values, beauty, finances, and past lovers return.",
                do_: "Reassess what you value. Heal old relationships. Don't rush new love.",
                avoid:"Major cosmetic procedures, new relationships, large luxury purchases.",
                gita:"BG 3.16 — 'The wheel of creation — when Venus retrogrades, love asks: what is real?'" },
              { name:"Mars", sym:"♂", color:"#dc2626", rgb:"220,38,38", freq:"Every ~2 years · ~72 days",
                what:"Energy, ambition, conflict, and unfinished battles return for resolution.",
                do_: "Reflect on anger patterns. Slow down physical action. Finish old projects.",
                avoid:"Starting new physical conflicts, risky ventures, major surgery if possible.",
                gita:"BG 2.14 — 'Sensations of pleasure and pain come and go — in retrograde, Mars teaches patience.'" },
              { name:"Jupiter", sym:"♃", color:"#b45309", rgb:"180,83,9", freq:"Yearly · ~4 months",
                what:"Expansion pauses. Wisdom turns inward. Philosophy, dharma, and beliefs are reviewed.",
                do_: "Study, reflect, and refine your beliefs. Past gurus or teachers may return.",
                avoid:"Starting new major ventures, gambling, risky speculation.",
                gita:"BG 4.33 — 'Knowledge is the highest offering — Jupiter retrograde deepens this truth.'" },
              { name:"Saturn", sym:"♄", color:"#475569", rgb:"71,85,105", freq:"Yearly · ~4.5 months",
                what:"Karmic patterns resurface. Structures built on weak foundations are tested.",
                do_: "Pay karmic debts. Do the inner work. Review long-term commitments.",
                avoid:"Shortcuts, cutting corners, avoiding responsibilities.",
                gita:"BG 18.23 — 'Work done without desire for reward, done without attachment — this is Saturn's highest lesson.'" },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(6,182,212,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(6,182,212,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(6,182,212,0.8),rgba(103,232,249,1),rgba(6,182,212,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(6,182,212,0.25),rgba(8,145,178,0.15))",
                      border:"1px solid rgba(6,182,212,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(6,182,212,0.3)", fontSize:"1.8rem" }}>🔄</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#0e7490", marginBottom:4 }}>Retrograde Guide</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>When planets reverse — what it means · what to do · what to avoid</div>
                    </div>
                  </div>
                </div>
                {PLANETS_R.map((p, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid ${JT.border}`,
                    background:"#ffffff",
                    padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:"1.4rem", color:p.color, fontFamily:"serif" }}>{p.sym}</span>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:p.color, fontWeight:700 }}>{p.name} Retrograde</div>
                      <div style={{ marginLeft:"auto", fontSize:"1rem", color:JT.textDim,
                        fontFamily:"'Inter',sans-serif" }}>{p.freq}</div>
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.65, marginBottom:10 }}>{p.what}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                      <div style={{ background:"rgba(34,197,94,0.07)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:"1rem", color:"#15803d", fontFamily:JT.heading, marginBottom:4 }}>✓ DO</div>
                        <div style={{ fontSize:"1.1rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.55 }}>{p.do_}</div>
                      </div>
                      <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.22)", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:"1rem", color:"#b91c1c", fontFamily:JT.heading, marginBottom:4 }}>✗ AVOID</div>
                        <div style={{ fontSize:"1.1rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.55 }}>{p.avoid}</div>
                      </div>
                    </div>
                    <div style={{ fontSize:"1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif", fontStyle:"italic", lineHeight:1.6 }}>{p.gita}</div>
                  </div>
                ))}
                <DlBtn label="📥 Download Retrograde Guide PDF" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const rLord = rashiLords[arcData?.rashi_idx ?? 0];
                  genJyotishPDF({
                    title: "Retrograde Planet Guide", filename: `VedSanga_Retrograde_${rashiName}`,
                    badge: "Saathi Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob, extraLines: [`Rashi Lord: ${rLord}`],
                    sections: [
                      { heading: "About Planetary Retrogrades", lines: [
                        "A retrograde planet appears to move backward in the sky. In Jyotish, retrograde intensifies planetary energy — the planet turns inward. Old patterns resurface to be re-examined, completed, or released.",
                        `Your Moon sign (Rashi) is ${rashiName}. Your Rashi lord is ${rLord}. When ${rLord} goes retrograde, the effect on you is heightened — treat it with extra care.`,
                      ]},
                      ...PLANETS_R.map(p => ({ heading: `${p.name} Retrograde  (${p.freq})`, lines: [
                        p.what, "---",
                        "DO during retrograde:", ...p.do_.split(". ").filter(Boolean).map(s => `  • ${s.trim()}`), "---",
                        "AVOID during retrograde:", ...p.avoid.split(". ").filter(Boolean).map(s => `  • ${s.trim()}`), "---",
                        `Gita wisdom: ${p.gita}`,
                        ...(p.name === rLord ? [`★ Special note: ${rLord} is your Rashi lord — this retrograde is especially significant for you. Review, rest, and reflect rather than initiate.`] : []),
                      ]})),
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── ECLIPSE REPORTS ── */}
          {view === "eclipse" && (() => {
            const ECLIPSE_TYPES = [
              { type:"Solar Eclipse", icon:"🌑", color:"#7e22ce", rgb:"168,85,247",
                desc:"The Sun (ego, career, authority) is blocked. New beginnings are seeded in darkness — powerful for sankalpa and new intentions.",
                rules:["Do not eat during the eclipse window","Sit in meditation or silent prayer","Place water in moonlight after — it becomes charged","Begin a new intention (sankalpa) at the moment of maximum eclipse","Avoid sleeping during the eclipse"],
              },
              { type:"Lunar Eclipse", icon:"🌕", color:"#b45309", rgb:"245,158,11",
                desc:"The Moon (mind, emotions, mother) is shadowed by Earth. Deep emotions surface. Past karma completes. What no longer serves you falls away.",
                rules:["Do not eat cooked food during the eclipse","Take a bath before and after — purification is essential","Release what no longer serves you consciously","Avoid starting new ventures or relationships","Chant mantras — the energy is amplified 1000× during eclipses"],
              },
            ];
            const RASHI_ECLIPSE = [
              { rashi:"Aries · Libra axis", impact:"Relationships and self-identity are activated. Eclipse on this axis reshapes how you show up in partnerships." },
              { rashi:"Taurus · Scorpio axis", impact:"Finances, values, and deep transformation are triggered. Material security vs. inner depth." },
              { rashi:"Gemini · Sagittarius axis", impact:"Beliefs, learning, and long journeys are shaken. Your philosophy of life updates." },
              { rashi:"Cancer · Capricorn axis", impact:"Home, mother, and career are in tension. What you build vs. where you belong." },
              { rashi:"Leo · Aquarius axis", impact:"Creative expression and community purpose collide. Individual glory vs. collective dharma." },
              { rashi:"Virgo · Pisces axis", impact:"Health, service, and spiritual dissolution. Daily discipline vs. transcendence." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(168,85,247,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(168,85,247,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(168,85,247,0.8),rgba(216,180,254,1),rgba(168,85,247,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(168,85,247,0.25),rgba(109,40,217,0.15))",
                      border:"1px solid rgba(168,85,247,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(168,85,247,0.3)", fontSize:"1.8rem" }}>🌘</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#7e22ce", marginBottom:4 }}>Eclipse Guide</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Solar & lunar eclipse wisdom · house activations · rules of conduct</div>
                    </div>
                  </div>
                </div>
                {ECLIPSE_TYPES.map((e, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${e.rgb},0.25)`,
                    background:`linear-gradient(135deg,rgba(${e.rgb},0.07),rgba(${e.rgb},0.02))`, padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:"1.8rem" }}>{e.icon}</span>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:e.color, fontWeight:700 }}>{e.type}</div>
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.7, marginBottom:12, borderLeft:`3px solid rgba(${e.rgb},0.4)`, paddingLeft:12 }}>{e.desc}</div>
                    <div style={{ background:`rgba(${e.rgb},0.06)`, border:`1px solid rgba(${e.rgb},0.18)`, borderRadius:12, padding:"12px 14px" }}>
                      <div style={{ fontSize:"1rem", color:e.color, fontFamily:JT.heading, fontWeight:700, marginBottom:8 }}>📿 Rules of conduct</div>
                      {e.rules.map((r, j) => (
                        <div key={j} style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.6, marginBottom:4 }}>· {r}</div>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{ borderRadius:14, border:`1px solid ${JT.border}`, overflow:"hidden", background:"#ffffff" }}>
                  <div style={{ padding:"12px 18px 4px", fontSize:"1rem",
                    color:JT.textDim, fontFamily:JT.heading }}>Eclipse Axis Effects</div>
                  {RASHI_ECLIPSE.map((r, i) => (
                    <div key={i} style={{ padding:"10px 18px", borderTop: i ? `1px solid ${JT.border}` : "none" }}>
                      <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:"#7e22ce", marginBottom:3 }}>{r.rashi}</div>
                      <div style={{ fontSize:"1.1rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{r.impact}</div>
                    </div>
                  ))}
                </div>
                <DlBtn label="📥 Download Eclipse Impact Report" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const axisMap = [
                    "Aries-Libra axis — self-identity and partnerships are activated",
                    "Taurus-Scorpio axis — finances, values, and deep transformation",
                    "Gemini-Sagittarius axis — beliefs, learning, and long journeys",
                    "Cancer-Capricorn axis — home, mother, and career in tension",
                    "Leo-Aquarius axis — creative expression and community purpose",
                    "Virgo-Pisces axis — health, service, and spiritual dissolution",
                    "Aries-Libra axis — self-identity and partnerships are activated",
                    "Taurus-Scorpio axis — finances, values, and deep transformation",
                    "Gemini-Sagittarius axis — beliefs, learning, and long journeys",
                    "Cancer-Capricorn axis — home, mother, and career in tension",
                    "Leo-Aquarius axis — creative expression and community purpose",
                    "Virgo-Pisces axis — health, service, and spiritual dissolution",
                  ];
                  const yourAxis = axisMap[arcData?.rashi_idx ?? 0];
                  genJyotishPDF({
                    title: "Eclipse Impact Report", filename: `VedSanga_Eclipse_${rashiName}`,
                    badge: "Saathi Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob, extraLines: [`Eclipse Axis: ${yourAxis.split(" —")[0]}`],
                    sections: [
                      { heading: `Your Eclipse Axis — ${rashiName}`, lines: [
                        `Your Moon sign ${rashiName} falls on the ${yourAxis}.`,
                        "Every solar and lunar eclipse falls somewhere on the zodiac. Eclipses on or near YOUR Rashi or its opposite sign have a direct and powerful effect on your life.",
                        "When an eclipse activates your axis, old structures complete and new chapters begin — often suddenly and irreversibly.",
                      ]},
                      ...ECLIPSE_TYPES.map(e => ({ heading: `${e.type}`, lines: [
                        e.desc, "---",
                        "Rules of Conduct:", ...e.rules.map(r => `  • ${r}`),
                      ]})),
                      { heading: "Eclipse Axis Effects — All 6 Pairs", lines:
                        RASHI_ECLIPSE.flatMap(r => [`${r.rashi}:`, `  ${r.impact}`, "---"]).slice(0,-1)
                      },
                      { heading: "Your Preparation", lines: [
                        `Since your Rashi is ${rashiName}, the ${yourAxis}. Use every eclipse on this axis as a powerful reset window.`,
                        "  • Set a new sankalpa (intention) at each solar eclipse",
                        "  • Release what no longer serves you at each lunar eclipse",
                        "  • Perform a 108-repetition mantra chanting session on eclipse day",
                        "  • Fast and meditate during the eclipse window",
                        "  • Avoid important new beginnings for 3 days post-eclipse — let the energy settle",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── FESTIVAL GUIDE ── */}
          {view === "festival" && (() => {
            const FESTIVALS = [
              { name:"Navratri", icon:"🔱", color:"#f97316", rgb:"249,115,22",
                when:"9 nights (March/April & Sept/Oct)",
                energy:"Shakti activation — the feminine divine removes obstacles and grants power.",
                rashi_power:["Aries","Scorpio","Cancer"],
                rashi_watch:["Libra","Capricorn","Taurus"],
                ritual:"Fast on all 9 days. Recite Durga Saptashati. Offer red flowers to Devi. Light a lamp that burns all 9 nights.",
                gita:"BG 10.34 — 'Among the Shaktis I am fame, prosperity, speech, memory, wisdom, steadfastness, and forgiveness.'" },
              { name:"Diwali", icon:"🪔", color:"#f59e0b", rgb:"245,158,11",
                when:"Amavasya of Kartik (Oct/Nov)",
                energy:"Lakshmi descends. Darkness is conquered. New wealth and light enter the home.",
                rashi_power:["Taurus","Libra","Pisces"],
                rashi_watch:["Virgo","Gemini","Sagittarius"],
                ritual:"Clean the home completely. Light 108 diyas at dusk. Offer lotus flowers to Lakshmi. Keep all lights on through the night.",
                gita:"BG 9.16 — 'I am the ritual, I am the sacrifice, I am the offering to the ancestors...'" },
              { name:"Maha Shivaratri", icon:"🕉️", color:"#6366f1", rgb:"99,102,241",
                when:"Chaturdashi of Magha (Feb/March)",
                energy:"Shiva's grace destroys what must be destroyed. Liberation is accessible. Karma can be dissolved in one night.",
                rashi_power:["Capricorn","Aquarius","Scorpio"],
                rashi_watch:["Cancer","Leo","Taurus"],
                ritual:"Stay awake through the night. Fast completely. Offer bael leaves, milk, and Ganga jal to Shiva lingam. Chant Om Namah Shivaya.",
                gita:"BG 14.19 — 'When the seer sees no agent other than the Gunas — and knows what is higher than the Gunas — he attains my Being.'" },
              { name:"Ekadashi (monthly)", icon:"🌿", color:"#22c55e", rgb:"34,197,94",
                when:"11th tithi of every lunar fortnight (twice monthly)",
                energy:"Vishnu's energy is maximum. The mind is clearest. Spiritual merit from fasting is multiplied.",
                rashi_power:["All Rashis benefit equally"],
                rashi_watch:[],
                ritual:"Fast from sunrise to next day sunrise. Avoid grains and pulses. Read Vishnu Sahasranam. Break fast next morning with Dwadashi.",
                gita:"BG 9.26 — 'Whoever offers a leaf, flower, fruit, or water with devotion — I accept that offering made with a pure heart.'" },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(249,115,22,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(249,115,22,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(249,115,22,0.8),rgba(253,186,116,1),rgba(249,115,22,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(249,115,22,0.25),rgba(194,65,12,0.15))",
                      border:"1px solid rgba(249,115,22,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(249,115,22,0.3)", fontSize:"1.8rem" }}>🎉</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#c2410c", marginBottom:4 }}>Festival Guide</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>How major festivals affect your Rashi — and what to do</div>
                    </div>
                  </div>
                </div>
                {FESTIVALS.map((f, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${f.rgb},0.25)`,
                    background:`linear-gradient(135deg,rgba(${f.rgb},0.07),rgba(${f.rgb},0.02))`, padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                      <span style={{ fontSize:"1.8rem" }}>{f.icon}</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:f.color, fontWeight:700 }}>{f.name}</div>
                        <div style={{ fontSize:"1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>{f.when}</div>
                      </div>
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.7, marginBottom:10, borderLeft:`3px solid rgba(${f.rgb},0.4)`, paddingLeft:12 }}>{f.energy}</div>
                    {f.rashi_power[0] !== "All Rashis benefit equally" && (
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                        <div style={{ background:"rgba(34,197,94,0.07)", border:"1px solid rgba(34,197,94,0.22)", borderRadius:10, padding:"8px 12px" }}>
                          <div style={{ fontSize:"1rem", color:"#15803d", fontFamily:JT.heading, marginBottom:4 }}>⭐ Power Rashis</div>
                          {f.rashi_power.map((r, j) => <div key={j} style={{ fontSize:"1.1rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif" }}>· {r}</div>)}
                        </div>
                        <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.22)", borderRadius:10, padding:"8px 12px" }}>
                          <div style={{ fontSize:"1rem", color:"#b91c1c", fontFamily:JT.heading, marginBottom:4 }}>⚡ Watch</div>
                          {f.rashi_watch.map((r, j) => <div key={j} style={{ fontSize:"1.1rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif" }}>· {r}</div>)}
                        </div>
                      </div>
                    )}
                    <div style={{ background:`rgba(${f.rgb},0.06)`, border:`1px solid rgba(${f.rgb},0.18)`, borderRadius:12, padding:"10px 14px", marginBottom:10 }}>
                      <div style={{ fontSize:"1rem", color:f.color, fontFamily:JT.heading, fontWeight:700, marginBottom:6 }}>🪔 Ritual</div>
                      <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{f.ritual}</div>
                    </div>
                    <div style={{ fontSize:"1rem", color:`rgba(${f.rgb},0.55)`, fontFamily:"'Inter',sans-serif", fontStyle:"italic", lineHeight:1.6 }}>{f.gita}</div>
                  </div>
                ))}
                <DlBtn label="📥 Download Festival Guide PDF" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  genJyotishPDF({
                    title: "Personalised Festival Guide", filename: `VedSanga_Festival_${rashiName}`,
                    badge: "Saathi Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    sections: [
                      { heading: "Your Festival Power Windows", lines: [
                        `For ${rashiName} Rashi, the festivals where your energy peaks and where extra care is needed are identified below. Power Rashi = your energy is amplified. Watch Rashi = this festival's energies challenge your natal pattern — remedies help.`,
                      ]},
                      ...FESTIVALS.map(f => {
                        const isPower = f.rashi_power.some(r => r.toLowerCase().includes(rashiName.toLowerCase()));
                        const isWatch = f.rashi_watch.some(r => r.toLowerCase().includes(rashiName.toLowerCase()));
                        const status = isPower ? `★ POWER RASHI — ${rashiName} gains maximum benefit from ${f.name}` :
                                       isWatch  ? `⚠ WATCH RASHI — ${rashiName} should perform extra remedies during ${f.name}` :
                                       `${f.name} benefits all Rashis equally. Standard practice applies.`;
                        return { heading: `${f.name}  (${f.when})`, lines: [
                          status, "---",
                          f.energy, "---",
                          "Ritual:", `  ${f.ritual}`, "---",
                          `Gita: ${f.gita}`,
                          ...(isPower ? ["Power Rashis: " + f.rashi_power.join(", ")] : []),
                          ...(isWatch && f.rashi_watch.length ? ["Watch Rashis: " + f.rashi_watch.join(", ")] : []),
                        ]};
                      }),
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── VARSHAPHAL ── */}
          {view === "varshaphal" && (() => {
            const PILLARS = [
              { icon:"💼", title:"Career & Dharma", color:"#b45309", rgb:"245,158,11", desc:"The 10th house lord in your Solar Return chart reveals which career actions will bear fruit this year. Strong 10th = promotion and recognition." },
              { icon:"💑", title:"Relationships", color:"#ec4899", rgb:"236,72,153", desc:"7th house energy in the Solar Return shows the quality of partnerships this year — whether love deepens, a new bond forms, or distance must be healed." },
              { icon:"🏥", title:"Health", color:"#22c55e", rgb:"34,197,94", desc:"6th and 8th house lords in the Solar Return chart reveal which body systems need attention — and when to schedule rest vs. activity." },
              { icon:"💰", title:"Wealth", color:"#a16207", rgb:"245,209,122", desc:"2nd and 11th house rulers in the Solar Return show income potential and gains — whether savings grow or sudden expenses arise." },
              { icon:"🧘", title:"Dharma & Spirituality", color:"#6d28d9", rgb:"167,139,250", desc:"9th house energy reveals pilgrimage potential, guru connections, and whether this is a year of outward action or inward transformation." },
              { icon:"👨‍👩‍👧", title:"Family", color:"#059669", rgb:"52,211,153", desc:"4th house in the Solar Return shows whether the home brings peace or upheaval — property matters, mother's health, and inner security." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(134,239,172,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(134,239,172,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(134,239,172,0.8),rgba(187,247,208,1),rgba(134,239,172,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(134,239,172,0.25),rgba(21,128,61,0.15))",
                      border:"1px solid rgba(134,239,172,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(134,239,172,0.3)", fontSize:"1.8rem" }}>📆</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#15803d", marginBottom:4 }}>Varshaphal</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Vedic Solar Return · Your complete year ahead</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius:14, padding:"16px 18px",
                  background:"#faf9f6", border:`1px solid ${JT.border}` }}>
                  <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem", color:"#2b2b2b", lineHeight:1.8 }}>
                    Varshaphal — the "fruit of the year" — is a Vedic Solar Return chart cast for the exact moment the Sun returns to its birth degree each year. It reveals a fresh horoscope for that specific year of your life — more precise than a Western Solar Return, because it uses Vedic house division and your natal Dasha as context.
                  </div>
                </div>
                <div style={{ fontSize:"1rem", color:JT.textDim,
                  fontFamily:JT.heading, paddingLeft:4 }}>What Varshaphal Reveals</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {PILLARS.map((p, i) => (
                    <div key={i} style={{ borderRadius:14, padding:"14px",
                      border:`1px solid rgba(${p.rgb},0.2)`,
                      background:`rgba(${p.rgb},0.05)` }}>
                      <div style={{ fontSize:"1.5rem", marginBottom:6 }}>{p.icon}</div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:p.color, marginBottom:6 }}>{p.title}</div>
                      <div style={{ fontSize:"1.1rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{p.desc}</div>
                    </div>
                  ))}
                </div>
                <DlBtn label="📥 Download Varshaphal Report" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const dasha = dashaNavData?.maha_dasha;
                  genJyotishPDF({
                    title: "Varshaphal — Year Ahead Report", filename: `VedSanga_Varshaphal_${rashiName}`,
                    badge: "Saathi Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: dasha ? [`Current Maha Dasha: ${dasha.planet} Dasha (until ${dasha.end})`] : [],
                    sections: [
                      { heading: "What is Varshaphal?", lines: [
                        "Varshaphal (\"fruit of the year\") is a Vedic Solar Return chart cast for the exact moment the Sun returns to its birth degree each year. It reveals a fresh horoscope for that specific year of life — more precise than Western solar returns because it uses Vedic house division and integrates your natal Dasha as context.",
                        `For ${rashiName} Rashi, the Solar Return activates new house energies each year depending on the Lagna of the return chart. Below are the 6 life areas and what to watch this year.`,
                      ]},
                      ...(dasha ? [{ heading: `Dasha Context — ${dasha.planet} Maha Dasha`, lines: [
                        `You are currently in ${dasha.planet} Maha Dasha, running until ${dasha.end}. The Varshaphal year must be read in the context of this Dasha — the Dasha defines the overall theme; the Varshaphal shows the yearly texture.`,
                        `${dasha.planet} Dasha theme: ${({ketu:"Spiritual surrender and release of old structures",venus:"Abundance, love, and creative flowering",sun:"Authority, recognition, and ego refinement",moon:"Emotional depth, intuition, and home",mars:"Energy, ambition, property, and courage",rahu:"Worldly ambition, obsession, and rapid change",jupiter:"Wisdom, wealth, expansion, and blessings",saturn:"Discipline, karma, and delays that teach",mercury:"Intellect, speech, commerce, and networking"})[dasha.planet?.toLowerCase()] || ""}`,
                      ]}] : []),
                      ...PILLARS.map(p => ({ heading: `${p.icon} ${p.title}`, lines: [p.desc] })),
                      { heading: "How to Use This Report", lines: [
                        "  • Read this guide at the start of each new birth year (your birthday)",
                        "  • Cross-reference with your current Dasha and Antardasha for precision",
                        "  • Perform Surya puja on your birthday to honour the Solar Return",
                        "  • Set clear intentions (sankalpa) for each of the 6 life areas above",
                        "  • Review this guide every 3 months and update your priorities",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* DAILY MANTRA */}
          {view === "mantra" && arcData?.nakshatra && (() => {
            const MANTRA_META = [
              { translit:"Om Ashwini Kumarabhyam Namah",    meaning:"Salutations to the Divine Physician twins — bringers of healing and swift restoration",                           chant:108, time:"Sunrise"      },
              { translit:"Om Yamaya Namah",                  meaning:"Salutations to Yama — lord of truth, transformation and righteous passage",                                        chant:108, time:"Dusk"         },
              { translit:"Om Agnaye Namah",                  meaning:"Salutations to Agni — the sacred fire that purifies all it touches",                                              chant:108, time:"Sunrise"      },
              { translit:"Om Brahmanaye Namah",              meaning:"Salutations to Brahma — creator and nurturer of all living forms",                                                chant:108, time:"Morning"      },
              { translit:"Om Somaya Namah",                  meaning:"Salutations to Soma — the Moon, source of nourishment and gentle seeking",                                        chant:27,  time:"Evening"      },
              { translit:"Om Rudraya Namah",                 meaning:"Salutations to Rudra — the storm that clears, renews and dissolves what is finished",                            chant:108, time:"Before sleep" },
              { translit:"Om Adityai Namah",                 meaning:"Salutations to Aditi — boundless mother, giver of infinite light and renewal",                                   chant:108, time:"Sunrise"      },
              { translit:"Om Brihaspataye Namah",            meaning:"Salutations to Brihaspati — Jupiter, guru of the gods and bestower of wisdom",                                   chant:108, time:"Morning"      },
              { translit:"Om Nagaya Namah",                  meaning:"Salutations to the Serpent — master of instinct, kundalini and ancient knowing",                                 chant:54,  time:"Night"        },
              { translit:"Om Pitribhyo Namah",               meaning:"Salutations to the Ancestors — honouring the lineage and blessings that flow through you",                       chant:108, time:"Noon"         },
              { translit:"Om Bhagaya Namah",                 meaning:"Salutations to Bhaga — deity of love, joy and divine abundance in all its forms",                                chant:108, time:"Morning"      },
              { translit:"Om Aryamanaye Namah",              meaning:"Salutations to Aryaman — lord of noble contracts, friendship and honourable unions",                              chant:108, time:"Noon"         },
              { translit:"Om Savitre Namah",                 meaning:"Salutations to Savitar — the radiant Sun that awakens all skill held within your hands",                         chant:108, time:"Sunrise"      },
              { translit:"Om Tvashtrae Namah",               meaning:"Salutations to Tvashtar — divine architect of all beautiful and perfect forms in creation",                       chant:108, time:"Morning"      },
              { translit:"Om Vayave Namah",                  meaning:"Salutations to Vayu — the wind, the life breath and the force of all true independence",                         chant:108, time:"Sunrise"      },
              { translit:"Om Indragniabhyam Namah",          meaning:"Salutations to Indra and Agni — lightning and fire, the twin powers of focused victory",                         chant:108, time:"Morning"      },
              { translit:"Om Mitraya Namah",                 meaning:"Salutations to Mitra — lord of sacred friendship, alliances and heartfelt devotion",                             chant:108, time:"Morning"      },
              { translit:"Om Indraya Namah",                 meaning:"Salutations to Indra — king of the gods, lord of courage, storms and righteous power",                           chant:108, time:"Sunrise"      },
              { translit:"Om Nirritaye Namah",               meaning:"Salutations to Nirriti — the goddess of dissolution, root-cutter and deep liberator",                            chant:108, time:"Dusk"         },
              { translit:"Om Adbhyo Namah",                  meaning:"Salutations to the Waters — source of all purification, flow and invincible grace",                              chant:108, time:"Evening"      },
              { translit:"Om Vishwebhyo Devebhyo Namah",    meaning:"Salutations to all the Universal Gods — the all-pervading forces of cosmic dharma",                               chant:108, time:"Morning"      },
              { translit:"Om Vishnave Namah",                meaning:"Salutations to Vishnu — the preserver, the great listener and sustainer of all worlds",                          chant:108, time:"Morning"      },
              { translit:"Om Vasubhyo Namah",                meaning:"Salutations to the Eight Vasus — elemental gods of abundance and all material blessings",                        chant:108, time:"Morning"      },
              { translit:"Om Varunaya Namah",                meaning:"Salutations to Varuna — cosmic guardian of truth, sacred water and karmic law",                                   chant:108, time:"Night"        },
              { translit:"Om Aja Ekapadaya Namah",           meaning:"Salutations to Aja Ekapada — the one-footed unborn fire, pillar of all cosmic order",                           chant:54,  time:"Before sleep" },
              { translit:"Om Ahir Budhnyaya Namah",          meaning:"Salutations to Ahir Budhnya — the serpent of the deep, ancient wisdom beneath all creation",                    chant:108, time:"Night"        },
              { translit:"Om Pushne Namah",                  meaning:"Salutations to Pushan — the gentle nourisher who guides every soul safely home",                                  chant:108, time:"Morning"      },
            ];
            const nak  = arcData.nakshatra;
            const meta = MANTRA_META[nak.nak_idx] || MANTRA_META[0];
            const nakName = nak.name?.[lang] || nak.name?.en || "";
            return (
              <div style={{ borderRadius:22,
                background:"#ffffff",
                border:"1.5px solid rgba(59,130,246,0.35)",
                boxShadow:"0 8px 40px rgba(59,130,246,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
                overflow:"hidden" }}>
                <div style={{ height:3, background:"linear-gradient(90deg,transparent,#93c5fd,rgba(59,130,246,0.5),#93c5fd,transparent)" }} />
                <div style={{ padding:"22px 22px 24px" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontSize:"1.8rem", filter:"drop-shadow(0 0 10px rgba(59,130,246,0.7))" }}>📿</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem",
                          color:"#1d4ed8", fontWeight:700 }}>
                          Today's Sacred Mantra
                        </div>
                        <div style={{ fontSize:"1rem", color:"#666666", marginTop:2 }}>
                          {nak.symbol} {nakName} Nakshatra · {nak.deity?.split("·")[0]?.trim()}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:"1rem", color:JT.textDim,
                        fontFamily:JT.heading }}>Best time</div>
                      <div style={{ fontSize:"1.2rem", color:"#1d4ed8", fontWeight:600 }}>{meta.time}</div>
                    </div>
                  </div>
                  <div style={{ textAlign:"center", marginBottom:14, padding:"20px 16px", borderRadius:16,
                    background:"rgba(59,130,246,0.07)", border:"1px solid rgba(59,130,246,0.22)" }}>
                    <div style={{ fontSize:"1.5rem", lineHeight:1.7, color:"#1a1a1a",
                      fontFamily:"'Noto Sans Devanagari',serif",
                      letterSpacing:"0.04em", marginBottom:10 }}>
                      {nak.mantra}
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#1d4ed8",
                      fontStyle:"italic", letterSpacing:"0.03em", marginBottom:8 }}>
                      {meta.translit}
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#2b2b2b",
                      lineHeight:1.6, maxWidth:340, margin:"0 auto" }}>
                      {meta.meaning}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:10 }}>
                    <div style={{ flex:1, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.22)",
                      textAlign:"center" }}>
                      <div style={{ fontSize:"1.4rem", fontWeight:800, color:"#1d4ed8",
                        fontFamily:"'Inter',sans-serif" }}>{meta.chant}</div>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        fontFamily:JT.heading }}>Times</div>
                    </div>
                    <div style={{ flex:2, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.22)" }}>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        fontFamily:JT.heading, marginBottom:3 }}>Practice</div>
                      <div style={{ fontSize:"1.2rem", color:"#2b2b2b", lineHeight:1.4 }}>
                        Sit quietly facing east. Breathe 3 times. Chant {meta.chant} times. Offer to {nak.deity?.split("·")[0]?.trim()}.
                      </div>
                    </div>
                    <div style={{ flex:1, padding:"12px 14px", borderRadius:12,
                      background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.22)",
                      textAlign:"center", display:"flex", flexDirection:"column",
                      alignItems:"center", justifyContent:"center", gap:4 }}>
                      <span style={{ fontSize:"1.4rem" }}>🔔</span>
                      <div style={{ fontSize:"1rem", color:"#666666",
                        fontFamily:JT.heading }}>Remind</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* DAILY HORA */}
          {view === "hora" && (() => {
            const HORA_Q = {
              excellent: { label:"Excellent", color:"#b45309", bg:"rgba(251,191,36,0.12)", border:"rgba(251,191,36,0.35)" },
              good:      { label:"Good",      color:"#15803d", bg:"rgba(34,197,94,0.10)",  border:"rgba(34,197,94,0.3)"  },
              neutral:   { label:"Neutral",   color:"#475569", bg:"rgba(148,163,184,0.10)",border:"rgba(148,163,184,0.3)"},
              avoid:     { label:"Avoid",     color:"#dc2626", bg:"rgba(248,113,113,0.10)",border:"rgba(248,113,113,0.3)" },
            };
            if (horaLoad) return shimmer;
            if (!horaData) return null;
            const curMin = horaNow.getHours() * 60 + horaNow.getMinutes();
            let currentIdx = horaData.current_hora_index;
            for (const h of horaData.horas) {
              const { start_min: s, end_min: e } = h;
              if (e < s) { if (curMin >= s || curMin < e) { currentIdx = h.index; break; } }
              else        { if (s <= curMin && curMin < e) { currentIdx = h.index; break; } }
            }
            const current = horaData.horas[currentIdx] || horaData.horas[0];
            const qs = HORA_Q[current?.quality] || HORA_Q.neutral;
            const HoraRow = ({ h, isCur }) => {
              const q = HORA_Q[h.quality] || HORA_Q.neutral;
              return (
                <div style={{
                  display:"flex", alignItems:"center", gap:12,
                  background: isCur ? q.bg : "#ffffff",
                  border:`1px solid ${isCur ? q.border : JT.border}`,
                  borderRadius:10, padding:"10px 14px",
                  boxShadow: isCur ? `0 0 10px ${q.border}` : "none",
                }}>
                  <span style={{ fontSize:"1.3rem", minWidth:26 }}>{h.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:"1.2rem", fontWeight:700,
                        color: isCur ? q.color : "#2b2b2b" }}>{h.ruler}</span>
                      {isCur && <span style={{ fontSize:"1.0rem", color:q.color, fontWeight:700,
                        background:q.bg, border:`1px solid ${q.border}`,
                        borderRadius:6, padding:"1px 6px" }}>NOW</span>}
                    </div>
                    <div style={{ fontSize:"1rem", color:"#666666", marginTop:1 }}>
                      {h.start} – {h.end}
                    </div>
                  </div>
                  <div style={{ fontSize:"1rem", color:q.color, fontWeight:600, minWidth:56, textAlign:"right" }}>
                    {q.label}
                  </div>
                </div>
              );
            };
            return (
              <>
                <div style={{
                  background:`linear-gradient(135deg,${qs.bg},#ffffff)`,
                  border:`2px solid ${qs.border}`, borderRadius:18, padding:"20px 22px",
                  marginBottom:20, boxShadow:JT.shadow,
                }}>
                  <div style={{ fontSize:"1.0rem", color:"#666666",
                    fontFamily:JT.heading, fontWeight:700, marginBottom:8 }}>Current Hora</div>
                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                    <span style={{ fontSize:"2.6rem" }}>{current?.icon}</span>
                    <div>
                      <div style={{ fontSize:"1.4rem", color:qs.color, fontWeight:700,
                        fontFamily:JT.heading }}>{current?.ruler}</div>
                      <div style={{ fontSize:"1.2rem", color:"#2b2b2b", marginTop:2 }}>
                        {current?.start} – {current?.end} IST</div>
                    </div>
                    <div style={{ marginLeft:"auto" }}>
                      <span style={{ fontSize:"1rem", fontWeight:700, color:qs.color,
                        background:qs.bg, border:`1px solid ${qs.border}`,
                        borderRadius:8, padding:"4px 10px" }}>{qs.label}</span>
                    </div>
                  </div>
                  <div style={{ marginTop:10, fontSize:"1.2rem", color:"#666666" }}>{current?.ideal_en}</div>
                  <div style={{ marginTop:8, display:"flex", gap:16, fontSize:"1rem",
                    color:"#5a5a5a" }}>
                    <span>🌅 {horaData.sunrise}</span><span>🌇 {horaData.sunset}</span>
                  </div>
                </div>
                <div style={{ fontSize:"1.0rem", color:"#5a5a5a",
                  marginBottom:8, fontWeight:600 }}>☀️ Daytime · {horaData.sunrise} – {horaData.sunset}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                  {horaData.horas.filter(h => h.type === "day").map(h =>
                    <HoraRow key={h.index} h={h} isCur={h.index === currentIdx} />)}
                </div>
                <div style={{ fontSize:"1.0rem", color:"#5a5a5a",
                  marginBottom:8, fontWeight:600 }}>🌙 Nighttime · {horaData.sunset} onwards</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:16 }}>
                  {horaData.horas.filter(h => h.type === "night").map(h =>
                    <HoraRow key={h.index} h={h} isCur={h.index === currentIdx} />)}
                </div>
                <div style={{ padding:"12px 14px", background:"#faf9f6",
                  border:`1px solid ${JT.border}`, borderRadius:12,
                  fontSize:"1.1rem", color:"#666666", lineHeight:1.5 }}>
                  Each Hora is 1/12th of daylight (or nighttime) from sunrise. Chaldean sequence:
                  Sun → Venus → Mercury → Moon → Saturn → Jupiter → Mars.
                </div>
              </>
            );
          })()}

          {/* DASHA */}
          {view === "dasha" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Unlock Your Dasha Timeline" buttonLabel="✨ Show My Dasha" />
              : <>
                  <DashaTab
                    key={viewRefreshKey}
                    userId={user.id}
                    noBirth={false}
                    onNeedBirth={() => setView("archetype")}
                    lightMode={lightMode}
                  />
                  <ReportBridge
                    heading="You've seen which chapter you're in. The full story is written."
                    lines={[
                      "The exact months when your current period turns",
                      "What to start — and what to avoid — in each phase",
                      "The remedy for your ruling planet, step by step",
                    ]}
                    onOpen={() => setView("reports")}
                  />
                </>
          )}

          {/* SADHANA */}
          {view === "sadhana" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Start Your Graha Sadhana" buttonLabel="✨ Begin Sadhana" />
              : <>
                  {sadLoad && shimmer}
                  {!sadLoad && sadhana?.active && (
                    <ActiveSadhana
                      sadhana={sadhana}
                      onComplete={completeSadhana}
                      onAbandon={abandonSadhana}
                    />
                  )}
                  {!sadLoad && !sadhana?.active && (
                    <PlanetSelector planets={planets} onSelect={startSadhana} />
                  )}
                </>
          )}

          {/* KUNDALI */}
          {view === "kundali" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Generate Your Birth Chart" buttonLabel="✨ Draw My Kundali" />
              : <>
                  <KundaliTab
                    key={viewRefreshKey}
                    userId={user.id}
                    noBirth={false}
                    onNeedBirth={() => setView("archetype")}
                    lightMode={lightMode}
                  />
                  <ReportBridge
                    heading="Your chart holds more than planets in houses."
                    lines={[
                      "Every house explained for your own life — not generic meanings",
                      "Your strongest yogas, and what they promise",
                      "Gentle remedies for the weak spots in your chart",
                    ]}
                    onOpen={() => setView("reports")}
                  />
                </>
          )}

          {/* WEEKLY ENERGY */}
          {view === "jyotish_weekly" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Get Your Weekly Energy Forecast" buttonLabel="✨ Show My Week" />
              : <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                  <WeeklyReadingCard
                    reading={weeklyReading}
                    loading={weeklyLoad}
                    onUpgrade={isSubscribed ? null : handleUpgrade}
                  />
                </div>
          )}

          {/* JYOTISH CHAT */}
          {view === "jyotish_chat" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Talk to Your Jyotishi" buttonLabel="✨ Start My Reading" />
              : <JyotishChatTab
                  userId={user.id}
                  noBirth={false}
                  onNeedBirth={() => setView("archetype")}
                  arcData={arcData}
                  isSubscribed={isSubscribed}
                  initialMessage={chatInitMsg}
                  onInitialMessageConsumed={() => setChatInitMsg("")}
                  onOpenReports={() => setView("reports")}
                  onUpgrade={() => {
                    setView("home");
                    setShowPlans(true);
                    setTimeout(() => pricingRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 150);
                  }}
                />
          )}

          {/* DOSHA */}
          {view === "dosha" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Check Your Doshas" buttonLabel="✨ Scan My Doshas" />
              : <>
                  <DoshaTab
                    userId={user.id}
                    noBirth={false}
                    onNeedBirth={() => setView("archetype")}
                    isSubscribed={isSubscribed}
                    lightMode={lightMode}
                    onUpgrade={handleUpgrade}
                    onChatAbout={(label) => {
                      setChatInitMsg(`Tell me everything about my ${label} — severity, what it means for me personally, and what I should do.`);
                      setView("jyotish_chat");
                    }}
                  />
                  <ReportBridge
                    heading="Knowing the dosha is the first step. The remedy is the rest."
                    lines={[
                      "How strongly each dosha acts in your chart — not everyone's is equal",
                      "The exact remedy, mantra, and the right day to begin",
                      "What softens it this year, and what to avoid",
                    ]}
                    onOpen={() => setView("reports")}
                  />
                </>
          )}

          {/* MILAN */}
          {view === "milan" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Kundali Milan Compatibility" buttonLabel="✨ Match Our Kundalis" />
              : <>
                  <KundaliMilanTab
                    userId={user.id}
                    userDob={arcData?.dob || null}
                    userBirthTime={arcData?.birth_time || null}
                    userCity={arcData?.birth_city || ""}
                  />
                  <ReportBridge
                    heading="A score is a number. Your bond deserves the full reading."
                    lines={[
                      "What your 7th house says about the partnership itself",
                      "Timing windows for marriage decisions in your chart",
                      "What strengthens this match — written for both of you",
                    ]}
                    onOpen={() => setView("reports")}
                  />
                </>
          )}
          {/* MUHURTA */}
          {view === "muhurta" && (
            noBirth
              ? <BirthForm userId={user.id} onSaved={onBirthSavedInFeature} title="Find Your Auspicious Dates" buttonLabel="✨ Calculate Muhurta" />
              : <MuhurtaTab
                  userId={user.id}
                  noBirth={false}
                  onNeedBirth={() => setView("archetype")}
                  isSubscribed={isSubscribed}
                  onUpgrade={() => {
                    setView("home");
                    setShowPlans(true);
                    setTimeout(() => pricingRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 150);
                  }}
                />
          )}

          {/* ── BEEJ MANTRA ── */}
          {view === "beej_mantra" && (() => {
            const BEEJ = [
              { planet:"Sun",     sym:"☉", beej:"ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः", roman:"Om Hraam Hreem Hraum Sah Suryaya Namah", color:"#b45309", rgb:"251,191,36", count:7, day:"Sunday",   benefit:"Confidence, authority, career recognition, father relationship, eye and heart health." },
              { planet:"Moon",    sym:"☽", beej:"ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः", roman:"Om Shraam Shreem Shraum Sah Chandramase Namah", color:"#475569", rgb:"226,232,240", count:11, day:"Monday",  benefit:"Peace of mind, emotional healing, intuition, mother relationship, sleep and fluids." },
              { planet:"Mars",    sym:"♂", beej:"ॐ क्रां क्रीं क्रौं सः भौमाय नमः",    roman:"Om Kraam Kreem Kraum Sah Bhaumaya Namah", color:"#dc2626", rgb:"248,113,113", count:10, day:"Tuesday",  benefit:"Courage, energy, property, protection from accidents and enemies." },
              { planet:"Mercury", sym:"☿", beej:"ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः",   roman:"Om Braam Breem Braum Sah Budhaya Namah", color:"#059669", rgb:"52,211,153",  count:9,  day:"Wednesday", benefit:"Intellect, speech, business success, nervous system health, learning." },
              { planet:"Jupiter", sym:"♃", beej:"ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः",   roman:"Om Graam Greem Graum Sah Guruve Namah", color:"#a16207", rgb:"245,158,11",  count:19, day:"Thursday",  benefit:"Wisdom, wealth, children, guru blessings, dharma and spiritual growth." },
              { planet:"Venus",   sym:"♀", beej:"ॐ द्रां द्रीं द्रौं सः शुक्राय नमः", roman:"Om Draam Dreem Draum Sah Shukraya Namah", color:"#be185d", rgb:"249,168,212", count:16, day:"Friday",    benefit:"Love, beauty, luxury, creative gifts, marital harmony." },
              { planet:"Saturn",  sym:"♄", beej:"ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः",roman:"Om Praam Preem Praum Sah Shanaischaraya Namah", color:"#475569", rgb:"148,163,184", count:19, day:"Saturday",  benefit:"Discipline, karmic relief, longevity, justice, delayed but certain success." },
              { planet:"Rahu",    sym:"☊", beej:"ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः",  roman:"Om Bhraam Bhreem Bhraum Sah Rahave Namah", color:"#6d28d9", rgb:"167,139,250", count:18, day:"Saturday",  benefit:"Removes Rahu afflictions, confusion, obsession. Brings sudden positive transformation." },
              { planet:"Ketu",    sym:"☋", beej:"ॐ स्त्रां स्त्रीं स्त्रौं सः केतवे नमः",roman:"Om Straam Streem Straum Sah Ketave Namah", color:"#047857", rgb:"110,231,183", count:17, day:"Tuesday",   benefit:"Moksha, detachment, spiritual liberation, healing psychic wounds." },
            ];
            const SADHANA_40 = [
              { week:"Week 1 (Days 1–7)", title:"Sankalpa & Purification", desc:"Set your intention (sankalpa) before a deity or flame. Begin with bath at sunrise. Chant your beej 108× facing east. No meat, no intoxicants." },
              { week:"Week 2 (Days 8–14)", title:"Deepen the Practice", desc:"Add a mala (rosary) to count chants. Visualise the planet's colour and symbol as you chant. Begin a simple fast on the planet's day." },
              { week:"Week 3 (Days 15–21)", title:"Offering & Devotion", desc:"Offer flowers, incense, or fruit at a small altar. Chant before both sunrise and sunset. Keep a practice journal — note what shifts." },
              { week:"Week 4–6 (Days 22–40)", title:"Integration & Completion", desc:"The mantra should now feel natural. Chant while walking, cooking, or bathing. On Day 40: perform a full puja and offer the fruits of the practice to the planet." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(167,139,250,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(167,139,250,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(167,139,250,0.8),rgba(216,180,254,1),rgba(167,139,250,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(167,139,250,0.25),rgba(109,40,217,0.15))",
                      border:"1px solid rgba(167,139,250,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(167,139,250,0.3)", fontSize:"1.8rem" }}>📿</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#7e22ce", marginBottom:4 }}>Planetary Beej Mantras</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Seed mantras for all 9 grahas · 40-day initiation guide</div>
                    </div>
                  </div>
                </div>
                {BEEJ.map((b, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${b.rgb},0.25)`,
                    background:`linear-gradient(135deg,rgba(${b.rgb},0.07),rgba(${b.rgb},0.02))`, padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:"1.4rem", color:b.color, fontFamily:"serif", fontWeight:700 }}>{b.sym}</span>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:b.color, fontWeight:700, flex:1 }}>{b.planet}</div>
                      <div style={{ fontSize:"1rem", color:JT.textDim, fontFamily:JT.heading }}>{b.day} · {b.count}× mala</div>
                    </div>
                    <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.3rem", color:"#2b2b2b",
                      lineHeight:1.8, marginBottom:6, textAlign:"center",
                      background:`rgba(${b.rgb},0.08)`, borderRadius:10, padding:"10px 14px" }}>{b.beej}</div>
                    <div style={{ fontSize:"1.1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif",
                      fontStyle:"italic", textAlign:"center", marginBottom:8 }}>{b.roman}</div>
                    <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{b.benefit}</div>
                  </div>
                ))}
                <div style={{ borderRadius:16, border:`1px solid ${JT.border}`, overflow:"hidden", background:"#ffffff" }}>
                  <div style={{ padding:"14px 18px 6px", fontFamily:JT.heading, fontSize:"1.2rem", color:"#7e22ce" }}>40-Day Sadhana Guide</div>
                  {SADHANA_40.map((s, i) => (
                    <div key={i} style={{ padding:"12px 18px", borderTop: i ? `1px solid ${JT.border}` : "none" }}>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.1rem", color:"#7e22ce", marginBottom:4 }}>{s.week} — {s.title}</div>
                      <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.65 }}>{s.desc}</div>
                    </div>
                  ))}
                </div>
                <DlBtn label="📥 Download Beej Mantra Initiation PDF" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const nakRuler = arcData?.nakshatra?.ruler || "Sun";
                  const nakName  = arcData?.nakshatra?.name?.en || "";
                  const myBeej   = BEEJ.find(b => b.planet.toLowerCase() === nakRuler.toLowerCase()) || BEEJ[0];
                  genJyotishPDF({
                    title: "Beej Mantra Initiation Guide", filename: `VedSanga_BeejMantra_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: nakName,
                    dob: arcData?.dob, extraLines: [`Nakshatra Ruler: ${nakRuler}`, `Your Primary Beej: ${myBeej.roman}`],
                    sections: [
                      { heading: `Your Primary Planet — ${nakRuler}`, lines: [
                        `Your birth nakshatra ${nakName} is ruled by ${nakRuler}. This makes ${nakRuler} your primary planetary influence — the planet whose energy shapes your soul's current journey. Strengthening ${nakRuler} through its Beej Mantra is the most direct way to enhance your natal chart's gifts.`,
                      ]},
                      { heading: `${myBeej.planet} Beej Mantra — Your Primary Practice`, lines: [
                        { label: "Devanagari", value: myBeej.devanagari },
                        { label: "Roman", value: myBeej.roman },
                        { label: "Benefit", value: myBeej.benefit },
                        "---",
                        "Chant count: 108 repetitions per session",
                        "Best time: Sunrise (Brahma Muhurta preferred)",
                        `Best day: ${({Sun:"Sunday",Moon:"Monday",Mars:"Tuesday",Mercury:"Wednesday",Jupiter:"Thursday",Venus:"Friday",Saturn:"Saturday",Rahu:"Saturday",Ketu:"Tuesday"})[myBeej.planet] || "Any day"}`,
                        "Sit facing East. Use rudraksha mala. Maintain silence before and after for 10 minutes.",
                      ]},
                      { heading: "All 9 Planetary Beej Mantras for Reference", lines:
                        BEEJ.flatMap(b => [`${b.planet}: ${b.roman}`, `  ${b.benefit}`, "---"]).slice(0,-1)
                      },
                      ...SADHANA_40.map(s => ({ heading: `${s.week} — ${s.title}`, lines: [s.desc] })),
                      { heading: "Rules of Practice", lines: [
                        "  • Begin on a Sunday, Monday, or the day of your Rashi lord",
                        "  • Do not break the 40-day cycle — if you miss a day, restart from Day 1",
                        "  • Maintain brahmacharya (restraint) during the 40 days for maximum potency",
                        "  • Offer water, sandalwood, and a flower to the planet's deity image before chanting",
                        "  • Record your inner experiences in a journal — patterns will emerge by Week 2",
                        "  • After 40 days, reduce to 27 repetitions daily as ongoing maintenance",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── GEMSTONE GUIDE ── */}
          {view === "gemstone" && (() => {
            const GEMS = [
              { planet:"Sun",     gem:"Ruby (Manik)",        alt:"Red Garnet / Red Spinel",  color:"#b45309", rgb:"251,191,36",  metal:"Gold",   finger:"Ring finger — right hand", day:"Sunday",   caution:"Avoid if Sun is debilitated or afflicted — amplifies aggression." },
              { planet:"Moon",    gem:"Pearl (Moti)",        alt:"Moonstone / White Coral",  color:"#475569", rgb:"226,232,240", metal:"Silver", finger:"Ring finger — right hand", day:"Monday",  caution:"Check Moon strength. Avoid during Ketu periods — can cause confusion." },
              { planet:"Mars",    gem:"Red Coral (Moonga)",  alt:"Carnelian",                color:"#dc2626", rgb:"248,113,113", metal:"Gold/Copper",finger:"Ring finger — right hand",day:"Tuesday", caution:"Strong stone — avoid if Mars rules 3rd/6th/8th/12th from Lagna." },
              { planet:"Mercury", gem:"Emerald (Panna)",     alt:"Green Tourmaline / Peridot",color:"#059669",rgb:"52,211,153",  metal:"Gold",   finger:"Little finger — right hand",day:"Wednesday",caution:"Avoid during Rahu periods. Not for those with Mercury as malefic lord." },
              { planet:"Jupiter", gem:"Yellow Sapphire (Pukhraj)",alt:"Citrine / Yellow Topaz",color:"#a16207",rgb:"245,158,11",metal:"Gold",   finger:"Index finger — right hand", day:"Thursday", caution:"Most universally beneficial gemstone. Avoid only if Jupiter rules 3rd/8th." },
              { planet:"Venus",   gem:"Diamond (Heera)",     alt:"White Sapphire / Zircon",  color:"#be185d", rgb:"249,168,212", metal:"Platinum/Silver",finger:"Middle finger",day:"Friday", caution:"Very powerful. Avoid if Venus rules difficult houses. Consult pandit." },
              { planet:"Saturn",  gem:"Blue Sapphire (Neelam)",alt:"Amethyst / Blue Topaz",  color:"#475569", rgb:"148,163,184", metal:"Silver/Iron",finger:"Middle finger — right hand",day:"Saturday",caution:"Most powerful and dangerous stone — must test for 3 days before wearing permanently." },
              { planet:"Rahu",    gem:"Hessonite (Gomed)",   alt:"Zircon / Spessartite",     color:"#6d28d9", rgb:"167,139,250", metal:"Silver", finger:"Middle finger — right hand", day:"Saturday", caution:"Test for 3 days. Not recommended during Saturn periods." },
              { planet:"Ketu",    gem:"Cat's Eye (Lehsunia)",alt:"Tiger's Eye",              color:"#047857", rgb:"110,231,183", metal:"Silver", finger:"Ring finger — right hand",    day:"Tuesday",  caution:"Powerful spiritual stone. Test before wearing. Avoid during Venus periods." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(52,211,153,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(52,211,153,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(52,211,153,0.8),rgba(110,231,183,1),rgba(52,211,153,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(52,211,153,0.25),rgba(6,78,59,0.15))",
                      border:"1px solid rgba(52,211,153,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(52,211,153,0.3)", fontSize:"1.8rem" }}>💎</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#047857", marginBottom:4 }}>Gemstone Guide</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Planetary gems, metals, fingers · cautions for YOUR chart</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius:12, padding:"12px 16px", background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.25)" }}>
                  <div style={{ fontSize:"1.2rem", color:"#b91c1c", fontFamily:"'Inter',sans-serif", lineHeight:1.65 }}>
                    ⚠️ Gemstones amplify planetary energy — both positive and negative. Always consult an experienced Jyotishi before wearing. Test every new stone for 3 days on the body before permanent wear.
                  </div>
                </div>
                {GEMS.map((g, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${g.rgb},0.22)`,
                    background:"#ffffff", padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:"1.5rem" }}>💎</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:g.color, fontWeight:700 }}>{g.gem}</div>
                        <div style={{ fontSize:"1.1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>for {g.planet} · Alt: {g.alt}</div>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                      {[["Metal", g.metal], ["Finger", g.finger], ["Best day", g.day]].map(([k,v]) => (
                        <div key={k} style={{ background:`rgba(${g.rgb},0.06)`, border:`1px solid rgba(${g.rgb},0.18)`, borderRadius:10, padding:"8px 12px" }}>
                          <div style={{ fontSize:"1rem", color:JT.textDim, fontFamily:JT.heading, marginBottom:2 }}>{k}</div>
                          <div style={{ fontSize:"1.1rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.22)", borderRadius:10, padding:"8px 12px" }}>
                      <div style={{ fontSize:"1.2rem", color:"#b91c1c", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>⚠ {g.caution}</div>
                    </div>
                  </div>
                ))}
                <DlBtn label="📥 Download Gemstone Prescription PDF" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const nakRuler  = arcData?.nakshatra?.ruler || "Sun";
                  const rLord     = rashiLords[arcData?.rashi_idx ?? 0];
                  const primaryGem   = GEMS.find(g => g.planet.toLowerCase() === nakRuler.toLowerCase()) || GEMS[0];
                  const secondaryGem = GEMS.find(g => g.planet.toLowerCase() === rLord.toLowerCase());
                  genJyotishPDF({
                    title: "Gemstone Prescription Report", filename: `VedSanga_Gemstone_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: [`Nakshatra Ruler: ${nakRuler}`, `Rashi Lord: ${rLord}`, `Primary Gem: ${primaryGem.gem}`, `Secondary Gem: ${secondaryGem?.gem || "—"}`],
                    sections: [
                      { heading: "Important Caution", lines: [
                        "Gemstones amplify planetary energy — both positive and negative. A wrong gemstone can worsen the problems it was intended to cure. ALWAYS test a new stone for 3 days (wear it on the body, sleep with it nearby) before permanent wear. Consult an experienced Jyotishi before making final decisions.",
                      ]},
                      { heading: `Primary Gem — ${primaryGem.gem}  (for ${primaryGem.planet})`, lines: [
                        `Your birth nakshatra ruler is ${nakRuler}. Strengthening ${nakRuler} is your primary gemstone prescription.`,
                        { label: "Gem", value: primaryGem.gem },
                        { label: "Alternative", value: primaryGem.alt },
                        { label: "Metal", value: primaryGem.metal },
                        { label: "Finger", value: primaryGem.finger },
                        { label: "Best Day to Put On", value: primaryGem.day },
                        { label: "Caution", value: primaryGem.caution },
                      ]},
                      ...(secondaryGem && secondaryGem.planet !== primaryGem.planet ? [{ heading: `Secondary Gem — ${secondaryGem.gem}  (for ${secondaryGem.planet})`, lines: [
                        `Your Rashi lord is ${rLord}. This is your secondary gemstone consideration.`,
                        { label: "Gem", value: secondaryGem.gem },
                        { label: "Alternative", value: secondaryGem.alt },
                        { label: "Metal", value: secondaryGem.metal },
                        { label: "Finger", value: secondaryGem.finger },
                        { label: "Best Day", value: secondaryGem.day },
                        { label: "Caution", value: secondaryGem.caution },
                      ]}] : []),
                      { heading: "Complete Planetary Gemstone Reference", lines:
                        GEMS.flatMap(g => [`${g.planet}: ${g.gem} (alt: ${g.alt})`, `  Metal: ${g.metal}  |  Finger: ${g.finger}  |  Day: ${g.day}`, `  Caution: ${g.caution}`, "---"]).slice(0,-1)
                      },
                      { heading: "How to Energize Your Gemstone", lines: [
                        `  1. Choose ${primaryGem.best_day || primaryGem.day} morning, during Brahma Muhurta (before sunrise)`,
                        "  2. Place the ring/pendant in a bowl of raw milk or rose water for 30 minutes",
                        "  3. Remove and place on a clean yellow/white cloth",
                        `  4. Chant the Beej mantra of ${nakRuler} 108 times while holding the stone`,
                        "  5. Wear the stone touching your skin — never with a closed back",
                        "  6. Observe for 3 days: dreams, mood, events. Discomfort = remove immediately",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── HEALTH ALERTS ── */}
          {view === "health_alerts" && (() => {
            const PLANET_HEALTH = [
              { planet:"Sun",     color:"#b45309", rgb:"251,191,36",  systems:["Heart","Spine","Eyes","Vitality","Immune system"], season:"Summer transit afflictions", watch:"When Sun transits Libra (debilitated) or is afflicted by Saturn — heart and eye strain increase." },
              { planet:"Moon",    color:"#475569", rgb:"226,232,240", systems:["Mind","Lymph","Fluids","Stomach","Breasts"], season:"New moon & full moon peaks", watch:"Moon afflicted by Rahu/Ketu causes mental anxiety, sleep disorders, and hormonal imbalance." },
              { planet:"Mars",    color:"#dc2626", rgb:"248,113,113", systems:["Blood","Muscles","Surgery risk","Accidents","Fevers"], season:"Mars retrograde periods", watch:"Mars afflicted by Saturn increases accident risk and inflammatory conditions. Avoid surgery during retrograde." },
              { planet:"Mercury", color:"#059669", rgb:"52,211,153",  systems:["Nervous system","Lungs","Skin","Speech","Thyroid"], season:"Mercury retrograde (3× per year)", watch:"Mercury retrograde triggers respiratory issues, nervous tension, and miscommunication-related stress." },
              { planet:"Jupiter", color:"#a16207", rgb:"245,158,11",  systems:["Liver","Hips","Thighs","Fat metabolism","Pancreas"], season:"Jupiter in 6th/8th house transit", watch:"Weak Jupiter in the chart correlates with liver sluggishness and weight management challenges." },
              { planet:"Venus",   color:"#be185d", rgb:"249,168,212", systems:["Kidneys","Reproductive","Skin","Throat","Diabetes risk"], season:"Venus retrograde (~every 18 months)", watch:"Venus afflicted by malefics increases kidney and reproductive system sensitivity." },
              { planet:"Saturn",  color:"#475569", rgb:"148,163,184", systems:["Bones","Joints","Teeth","Chronic conditions","Longevity"], season:"Sade Sati & Shani Dhaiya periods", watch:"Saturn transiting 8th house from Moon increases joint pain, dental issues, and chronic fatigue." },
              { planet:"Rahu",    color:"#6d28d9", rgb:"167,139,250", systems:["Mystery diseases","Toxins","Allergies","Phobias","Nervous disorders"], season:"Rahu-Ketu axis transits", watch:"Rahu afflictions are hard to diagnose — they mimic other conditions. Seek second opinions." },
              { planet:"Ketu",    color:"#047857", rgb:"110,231,183", systems:["Spiritual illness","Viral infections","Hidden causes","Legs/feet","Liberation through illness"], season:"Ketu on natal planets", watch:"Ketu periods often bring inexplicable health changes that resolve through spiritual practice." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(239,68,68,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(239,68,68,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(239,68,68,0.8),rgba(252,165,165,1),rgba(239,68,68,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(239,68,68,0.25),rgba(153,27,27,0.15))",
                      border:"1px solid rgba(239,68,68,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(239,68,68,0.3)", fontSize:"1.8rem" }}>🏥</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#b91c1c", marginBottom:4 }}>Planetary Health Guide</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Each planet governs body systems · know when to be vigilant</div>
                    </div>
                  </div>
                </div>
                {PLANET_HEALTH.map((p, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${p.rgb},0.22)`,
                    background:"#ffffff", padding:"16px 18px" }}>
                    <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:p.color, fontWeight:700, marginBottom:8 }}>{p.planet}</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                      {p.systems.map((s, j) => (
                        <span key={j} style={{ fontSize:"1.1rem", padding:"4px 10px", borderRadius:20,
                          background:`rgba(${p.rgb},0.12)`, border:`1px solid rgba(${p.rgb},0.3)`,
                          color:p.color, fontFamily:"'Inter',sans-serif" }}>{s}</span>
                      ))}
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.65 }}>⚠ {p.watch}</div>
                  </div>
                ))}
                <DlBtn label="📥 Download Planetary Health Guide PDF" onClick={() => {
                  const rashiName = arcData?.rashi?.en || "Unknown";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const prakritiMap = ["Pitta","Kapha","Vata","Kapha","Pitta","Vata","Vata","Pitta","Pitta","Vata","Vata","Kapha"];
                  const rLord      = rashiLords[arcData?.rashi_idx ?? 0];
                  const prakriti   = prakritiMap[arcData?.rashi_idx ?? 0];
                  const nakRuler   = arcData?.nakshatra?.ruler || "Sun";
                  const primaryPlanet  = PLANET_HEALTH.find(p => p.planet.toLowerCase() === rLord.toLowerCase());
                  const secondPlanet   = PLANET_HEALTH.find(p => p.planet.toLowerCase() === nakRuler.toLowerCase());
                  genJyotishPDF({
                    title: "Planetary Health Guide", filename: `VedSanga_Health_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: [`Rashi Lord: ${rLord}  |  Nakshatra Ruler: ${nakRuler}`, `Ayurvedic Prakriti (from Rashi): ${prakriti}`],
                    sections: [
                      { heading: "How Jyotish Guides Your Health", lines: [
                        "Each planet governs specific body systems. When a planet is weak in your natal chart, or when transiting planets afflict it, the corresponding body systems become vulnerable. Knowing this allows you to take preventative action before symptoms appear.",
                        `Your Rashi lord is ${rLord} — the body systems governed by ${rLord} deserve your primary attention. Your nakshatra ruler ${nakRuler} governs your secondary health focus.`,
                      ]},
                      ...(primaryPlanet ? [{ heading: `PRIMARY FOCUS — ${primaryPlanet.planet} (Your Rashi Lord)`, lines: [
                        `Body systems: ${primaryPlanet.systems.join(", ")}`,
                        `Watch period: ${primaryPlanet.season}`, "---",
                        primaryPlanet.watch,
                      ]}] : []),
                      ...(secondPlanet && secondPlanet.planet !== primaryPlanet?.planet ? [{ heading: `SECONDARY FOCUS — ${secondPlanet.planet} (Nakshatra Ruler)`, lines: [
                        `Body systems: ${secondPlanet.systems.join(", ")}`,
                        `Watch period: ${secondPlanet.season}`, "---",
                        secondPlanet.watch,
                      ]}] : []),
                      { heading: "Complete Planetary Health Reference", lines:
                        PLANET_HEALTH.flatMap(p => [`${p.planet}: ${p.systems.join(" · ")}`, `Watch: ${p.watch}`, "---"]).slice(0,-1)
                      },
                      { heading: `Ayurvedic Prakriti — ${prakriti} (from ${rashiName} Rashi)`, lines: [
                        `Your Moon sign ${rashiName} indicates a primarily ${prakriti} constitution.`,
                        ...({
                          Pitta:  ["  • Favour cool, sweet, bitter foods. Avoid chilli and fried foods","  • Exercise in cool morning hours — avoid noon sun","  • Practice Sheetali pranayama to cool excess heat","  • Moon fasting (Ekadashi fast) balances Pitta"],
                          Kapha:  ["  • Favour light, dry, spicy foods. Avoid dairy excess and sweets","  • Wake before sunrise — exercise vigorously daily","  • Dry brushing and regular sauna clear Kapha accumulation","  • Sun salutations (Surya Namaskar) daily"],
                          Vata:   ["  • Favour warm, oily, grounding foods. Ghee, sesame, root vegetables","  • Sleep before 10pm. Oil massage (abhyanga) daily","  • Avoid raw salads and cold drinks — cook everything","  • Gentle yoga and warmth are your medicine"],
                        }[prakriti] || []),
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── AYURVEDIC PRAKRITI ── */}
          {view === "prakriti" && (() => {
            const DOSHAS_AYU = [
              { name:"Vata", color:"#1d4ed8", rgb:"59,130,246", element:"Air + Ether", planets:"Saturn · Rahu · Mercury",
                traits:["Creative and imaginative","Quick thinking, easily distracted","Light, thin build","Cold hands and feet","Irregular digestion","Sensitive nervous system","Loves movement and change"],
                diet:"Warm, oily, grounding foods. Ghee, sesame oil, root vegetables, cooked grains. Avoid raw salads, caffeine, cold drinks.",
                routine:"Sleep before 10pm. Oil massage (abhyanga) daily. Gentle yoga. Warmth and stillness are medicine.",
                chart_sign:"Strong Saturn or Rahu, prominent Gemini/Virgo/Aquarius/Capricorn Lagna" },
              { name:"Pitta", color:"#dc2626", rgb:"248,113,113", element:"Fire + Water", planets:"Sun · Mars · Jupiter",
                traits:["Sharp intellect and focus","Natural leaders","Medium build, muscular","Prone to heat, inflammation, anger","Strong digestion","Competitive","Perfectionist tendencies"],
                diet:"Cool, sweet, bitter, astringent foods. Coconut water, cucumber, leafy greens, dairy. Avoid chilli, alcohol, fried foods.",
                routine:"Exercise in the cool hours. Moonbathing. Swimming. Avoid overwork. Meditate to cool the mind.",
                chart_sign:"Strong Sun or Mars, prominent Aries/Leo/Scorpio/Sagittarius Lagna" },
              { name:"Kapha", color:"#15803d", rgb:"134,239,172", element:"Earth + Water", planets:"Moon · Venus · Jupiter",
                traits:["Steady, calm, loyal","Strong memory, slow to learn but retains","Heavy, solid build","Excellent stamina","Slow but strong digestion","Tendency to hold weight","Deep sleeper"],
                diet:"Light, dry, spicy foods. Ginger, honey, bitter greens, lentils. Avoid dairy excess, sweets, cold foods, heavy meals.",
                routine:"Wake before sunrise. Vigorous exercise daily. Dry brushing. Variety and stimulation prevent stagnation.",
                chart_sign:"Strong Moon or Venus, prominent Taurus/Cancer/Pisces/Libra Lagna" },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(34,197,94,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(34,197,94,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(34,197,94,0.8),rgba(134,239,172,1),rgba(34,197,94,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(34,197,94,0.25),rgba(21,128,61,0.15))",
                      border:"1px solid rgba(34,197,94,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(34,197,94,0.3)", fontSize:"1.8rem" }}>🌿</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#15803d", marginBottom:4 }}>Ayurvedic Prakriti</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Vata · Pitta · Kapha — your body-mind constitution from your chart</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius:12, padding:"14px 16px", background:"#faf9f6", border:`1px solid ${JT.border}` }}>
                  <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.75 }}>
                    Ayurveda and Jyotish are sister sciences — both born from the same Vedic root. Your Lagna, Moon sign, and planetary strengths reveal your dominant Prakriti (constitution). Knowing this is the foundation of personalised health, diet, and daily rhythm.
                  </div>
                </div>
                {DOSHAS_AYU.map((d, i) => (
                  <div key={i} style={{ borderRadius:16, border:`1px solid rgba(${d.rgb},0.25)`,
                    background:"#ffffff", padding:"16px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.4rem", color:d.color }}>{d.name}</div>
                      <div style={{ fontSize:"1.1rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>· {d.element} · {d.planets}</div>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                      {d.traits.map((t, j) => (
                        <span key={j} style={{ fontSize:"1.1rem", padding:"4px 10px", borderRadius:20,
                          background:`rgba(${d.rgb},0.08)`, border:`1px solid rgba(${d.rgb},0.25)`,
                          color:"#2b2b2b", fontFamily:"'Inter',sans-serif" }}>{t}</span>
                      ))}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <div style={{ background:`rgba(${d.rgb},0.06)`, border:`1px solid rgba(${d.rgb},0.18)`, borderRadius:10, padding:"10px 14px" }}>
                        <div style={{ fontSize:"1rem", color:d.color, fontFamily:JT.heading, fontWeight:700, marginBottom:4 }}>🥗 Diet</div>
                        <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{d.diet}</div>
                      </div>
                      <div style={{ background:`rgba(${d.rgb},0.06)`, border:`1px solid rgba(${d.rgb},0.18)`, borderRadius:10, padding:"10px 14px" }}>
                        <div style={{ fontSize:"1rem", color:d.color, fontFamily:JT.heading, fontWeight:700, marginBottom:4 }}>🌅 Daily routine</div>
                        <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{d.routine}</div>
                      </div>
                    </div>
                  </div>
                ))}
                <DlBtn label="📥 Download Ayurvedic Prakriti Report" onClick={() => {
                  const rashiName  = arcData?.rashi?.en || "Unknown";
                  const nakRuler   = arcData?.nakshatra?.ruler || "Sun";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const rLord      = rashiLords[arcData?.rashi_idx ?? 0];
                  const prakritiMap   = ["Pitta","Kapha","Vata","Kapha","Pitta","Vata","Vata","Pitta","Pitta","Vata","Vata","Kapha"];
                  const nakPrakritiMap = {Sun:"Pitta",Moon:"Kapha",Mars:"Pitta",Mercury:"Vata",Jupiter:"Kapha",Venus:"Vata",Saturn:"Vata",Rahu:"Vata",Ketu:"Pitta"};
                  const moonPrak  = prakritiMap[arcData?.rashi_idx ?? 0];
                  const nakPrak   = nakPrakritiMap[nakRuler] || moonPrak;
                  const combined  = moonPrak === nakPrak ? moonPrak : `${moonPrak}-${nakPrak}`;
                  const myDosha   = DOSHAS_AYU.find(d => d.name === moonPrak) || DOSHAS_AYU[0];
                  genJyotishPDF({
                    title: "Ayurvedic Prakriti Report", filename: `VedSanga_Prakriti_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: [`Rashi Lord: ${rLord}  |  Nakshatra Ruler: ${nakRuler}`, `Prakriti (from Moon sign): ${moonPrak}`, `Prakriti (from Nakshatra ruler): ${nakPrak}`, `Combined Prakriti: ${combined}`],
                    sections: [
                      { heading: "How Your Prakriti is Determined", lines: [
                        "Ayurveda and Jyotish are sister sciences — both born from the same Vedic root. Your Moon sign (Rashi) reveals your emotional and mental constitution. Your Nakshatra ruler reveals your energetic pattern. Together they define your dominant Prakriti blend.",
                        `Your Moon sign ${rashiName} gives a ${moonPrak} dominant constitution. Your Nakshatra ruler ${nakRuler} adds a ${nakPrak} quality. Your combined Prakriti is ${combined}.`,
                      ]},
                      { heading: `${myDosha.name} — Your Primary Dosha`, lines: [
                        `Element: ${myDosha.element}  |  Governing Planets: ${myDosha.planets}`,
                        "---",
                        "Traits:", ...myDosha.traits.map(t => `  • ${t}`),
                        "---",
                        "Chart indicator:", myDosha.chart_sign,
                      ]},
                      { heading: `${myDosha.name} — Diet Guidance`, lines: [myDosha.diet] },
                      { heading: `${myDosha.name} — Daily Routine`, lines: [myDosha.routine] },
                      { heading: "All Three Doshas — Reference", lines:
                        DOSHAS_AYU.flatMap(d => [
                          `${d.name} (${d.element}):`,
                          `  Planets: ${d.planets}`,
                          `  Diet: ${d.diet}`,
                          `  Routine: ${d.routine}`, "---"
                        ]).slice(0,-1)
                      },
                      { heading: "Seasonal Health Recommendations", lines: [
                        `Based on your ${combined} Prakriti, here are your seasonal priorities:`,
                        ...(moonPrak === "Pitta"  ? ["  • Summer (Grisma): reduce heat, favour coconut water, rose, coriander","  • Autumn (Sharad): Pitta peaks — fast on Ekadashi, avoid sun exposure","  • Winter (Hemanta): You thrive now — use this season for intense projects","  • Spring (Vasanta): Light cleanse with bitter herbs to clear Pitta build-up"] : []),
                        ...(moonPrak === "Vata"   ? ["  • Autumn (Sharad): Vata peaks — abhyanga, sesame oil, warm soups daily","  • Winter (Hemanta): Stay warm, avoid raw food, sleep by 9:30pm","  • Summer (Grisma): Best season for Vata — enjoy the warmth and lightness","  • Spring (Vasanta): Transition carefully — avoid sudden temperature changes"] : []),
                        ...(moonPrak === "Kapha"  ? ["  • Spring (Vasanta): Kapha peaks — vigorous exercise, dry-brushing, light diet","  • Winter (Hemanta): Most challenging — add spices, avoid cold dairy, move daily","  • Summer (Grisma): Detox season — fasting and sun exposure help clear Kapha","  • Autumn (Sharad): Use this season to build warmth practices for winter"] : []),
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── FAMILY CHARTS ── */}
          {view === "family" && (() => {
            const RELATIONS = [
              { icon:"👨", label:"Father", desc:"Sun governs the father. His chart reveals the energy between you and your dharmic lineage." },
              { icon:"👩", label:"Mother", desc:"Moon governs the mother. Her chart shows the emotional and nurturing bond between you." },
              { icon:"💑", label:"Spouse / Partner", desc:"Venus and the 7th house. Compatibility reveals strengths, growth edges, and life karma shared." },
              { icon:"👦", label:"Child", desc:"Jupiter and the 5th house govern children. Their chart shows their soul purpose and your role as guide." },
              { icon:"👧", label:"Sibling", desc:"Mars and the 3rd house. Sibling dynamics are encoded in both charts — where they challenge and support." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(236,72,153,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(236,72,153,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(236,72,153,0.8),rgba(249,168,212,1),rgba(236,72,153,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(236,72,153,0.25),rgba(157,23,77,0.15))",
                      border:"1px solid rgba(236,72,153,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(236,72,153,0.3)", fontSize:"1.8rem" }}>👨‍👩‍👧</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#be185d", marginBottom:4 }}>Family Charts</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Store your family's birth details · see their Rashi & Dasha</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius:12, padding:"14px 16px", background:"#faf9f6", border:`1px solid ${JT.border}` }}>
                  <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.75 }}>
                    Your family members' charts complete the picture of your own. Jyotish teaches that family karma is shared — understanding each person's planetary nature transforms how you relate, support, and grow together.
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {RELATIONS.map((r, i) => (
                    <div key={i} style={{ borderRadius:14, padding:"14px 18px",
                      background:"rgba(236,72,153,0.05)", border:"1px solid rgba(236,72,153,0.22)",
                      display:"flex", alignItems:"flex-start", gap:14 }}>
                      <span style={{ fontSize:"2rem", flexShrink:0 }}>{r.icon}</span>
                      <div>
                        <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#be185d", marginBottom:4 }}>{r.label}</div>
                        <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.65 }}>{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderRadius:14, padding:"20px 18px", textAlign:"center",
                  background:"rgba(236,72,153,0.06)", border:"1px solid rgba(236,72,153,0.25)" }}>
                  <div style={{ fontSize:"2rem", marginBottom:8 }}>👨‍👩‍👧</div>
                  <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#be185d", marginBottom:8 }}>Family Chart Storage — Coming Soon</div>
                  <div style={{ fontSize:"1.2rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>Add birth details for up to 5 family members. See their Rashi, Nakshatra, current Dasha, and a one-click compatibility reading between your chart and theirs.</div>
                </div>
              </div>
            );
          })()}

          {/* ── PUJA CALENDAR ── */}
          {view === "puja_calendar" && (() => {
            const MONTHLY_PUJAS = [
              { month:"Chaitra (March–April)",   color:"#f97316", rgb:"249,115,22",  icon:"🔱", pujas:["Navratri Puja (9 days) — Goddess Durga","Ram Navami — Lord Rama","Hanuman Jayanti — Hanuman puja + Chalisa recitation"] },
              { month:"Vaishakha (April–May)",    color:"#f59e0b", rgb:"245,158,11",  icon:"🌸", pujas:["Akshaya Tritiya — Lakshmi puja + gold offering","Buddha Purnima — meditation and dana","Parashuram Jayanti — Vishnu sahasranam"] },
              { month:"Jyeshtha (May–June)",      color:"#b45309", rgb:"251,191,36",  icon:"☀️", pujas:["Ganga Dussehra — Ganga puja and holy dip","Nirjala Ekadashi — strict Vishnu fast","Vat Savitri — women's puja for marital dharma"] },
              { month:"Ashadha (June–July)",      color:"#059669", rgb:"52,211,153",  icon:"🌧️", pujas:["Guru Purnima — honour your guru tradition","Chaturmas begins — increased spiritual practice for 4 months","Jagannath Rath Yatra — Vishnu darshan"] },
              { month:"Shravana (July–August)",   color:"#6366f1", rgb:"99,102,241",  icon:"🌿", pujas:["Shravan Somvar — Shiva puja every Monday","Nag Panchami — serpent worship, Rahu-Ketu remedy","Raksha Bandhan — protection ritual","Krishna Janmashtami — midnight Vishnu celebration"] },
              { month:"Bhadrapada (Aug–Sept)",    color:"#dc2626", rgb:"248,113,113", icon:"🐘", pujas:["Ganesh Chaturthi (10 days) — new beginnings","Onam — harvest gratitude","Pitru Paksha (15 days) — ancestor tarpan daily"] },
              { month:"Ashwayuja (Sept–Oct)",     color:"#6d28d9", rgb:"167,139,250", icon:"🔱", pujas:["Navratri (9 nights) — Goddess Shakti","Dussehra — victory of dharma over adharma","Kojagarit Purnima — Lakshmi invocation on full moon"] },
              { month:"Kartika (Oct–Nov)",        color:"#b45309", rgb:"252,211,77",  icon:"🪔", pujas:["Dhanteras — Lakshmi and Kubera puja","Diwali — 108 diyas, Lakshmi puja, new beginnings","Tulsi Vivah — Vishnu's sacred marriage"] },
              { month:"Margashira (Nov–Dec)",     color:"#1d4ed8", rgb:"147,197,253", icon:"❄️", pujas:["Gita Jayanti — read/hear Bhagavad Gita","Dattatreya Jayanti — Guru tradition worship","Monthly Ekadashis — Vishnu fasts"] },
              { month:"Pushya (Dec–Jan)",         color:"#15803d", rgb:"134,239,172", icon:"🌙", pujas:["Makara Sankranti prep — sesame donations","Saphala Ekadashi — important Vishnu fast","Annual dosha remedies if needed"] },
              { month:"Magha (Jan–Feb)",          color:"#be185d", rgb:"249,168,212", icon:"🕉️", pujas:["Maha Shivaratri — all-night Shiva worship","Ratha Saptami — Surya puja","Mauni Amavasya — silence and meditation"] },
              { month:"Phalguna (Feb–March)",     color:"#b45309", rgb:"251,191,36",  icon:"🌈", pujas:["Holi — fire ritual (Holika Dahan) the night before","Amalaki Ekadashi — Vishnu + Amla tree worship","Mahashivratri if in this month — Shiva"] },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  background:"#ffffff",
                  border:"1px solid rgba(245,158,11,0.3)",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(245,158,11,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(245,158,11,0.8),rgba(253,211,77,1),rgba(245,158,11,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(245,158,11,0.25),rgba(180,83,9,0.15))",
                      border:"1px solid rgba(245,158,11,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(245,158,11,0.3)", fontSize:"1.8rem" }}>🙏</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#b45309", marginBottom:4 }}>Annual Puja Calendar</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>12 months · key pujas · Vedic lunar masa calendar</div>
                    </div>
                  </div>
                </div>
                {MONTHLY_PUJAS.map((m, i) => (
                  <div key={i} style={{ borderRadius:14, border:`1px solid rgba(${m.rgb},0.25)`,
                    background:"#ffffff", padding:"14px 18px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                      <span style={{ fontSize:"1.4rem" }}>{m.icon}</span>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.1rem", color:m.color, fontWeight:700 }}>{m.month}</div>
                    </div>
                    {m.pujas.map((p, j) => (
                      <div key={j} style={{ fontSize:"1.2rem", color:"#666666",
                        fontFamily:"'Inter',sans-serif", lineHeight:1.65, marginBottom:4 }}>· {p}</div>
                    ))}
                  </div>
                ))}
                <DlBtn label="📥 Download Annual Puja Calendar PDF" onClick={() => {
                  const rashiName  = arcData?.rashi?.en || "Unknown";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const rashiDeity = {
                    Mars:"Hanuman & Kartikeya", Venus:"Lakshmi & Lalita", Mercury:"Vishnu & Saraswati",
                    Moon:"Shiva & Durga", Sun:"Surya & Ram", Jupiter:"Brihaspati & Vishnu",
                    Saturn:"Shani & Bhairava"
                  };
                  const rLord      = rashiLords[arcData?.rashi_idx ?? 0];
                  const nakRuler   = arcData?.nakshatra?.ruler || "Sun";
                  const myDeity    = rashiDeity[rLord] || "Vishnu";
                  const dasha      = dashaNavData?.maha_dasha;
                  genJyotishPDF({
                    title: "Annual Puja Calendar", filename: `VedSanga_PujaCalendar_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: [
                      `Rashi Lord: ${rLord}  |  Nakshatra Ruler: ${nakRuler}`,
                      `Primary Deity for ${rashiName}: ${myDeity}`,
                      ...(dasha ? [`Current Dasha: ${dasha.planet} Maha Dasha`] : []),
                    ],
                    sections: [
                      { heading: `Your Personal Deity — ${myDeity}`, lines: [
                        `Your Rashi lord ${rLord} is propitiated through ${myDeity}. Regular puja to ${myDeity} strengthens your natal Rashi lord and brings flow, protection, and dharmic clarity. Prioritise these pujas above all others in your calendar.`,
                        ...(dasha ? [`Your current ${dasha.planet} Maha Dasha also prescribes specific pujas — these are marked below.`] : []),
                      ]},
                      ...MONTHLY_PUJAS.map(m => ({ heading: m.month, lines: m.pujas.map(p => `  • ${p}`) })),
                      { heading: "Dasha-Prescribed Pujas", lines: dasha ? [
                        `In ${dasha.planet} Maha Dasha, prioritise:`,
                        ...({
                          Sun:     ["  • Surya puja every Sunday — offer red flowers, jaggery, and wheat","  • Ram Navami — most powerful day in Sun Dasha","  • Ratha Saptami — Sun chariot festival"],
                          Moon:    ["  • Shiva puja on Mondays — offer milk and bael leaves","  • Purnima (full moon) fast and Lakshmi puja every month","  • Varalakshmi Vratam — key Lakshmi festival"],
                          Mars:    ["  • Hanuman puja on Tuesdays — offer sindur and red flowers","  • Navratri Puja — most powerful for Mars Dasha","  • Subrahmanya (Kartikeya) puja on Sashti"],
                          Mercury: ["  • Vishnu puja on Wednesdays — Sahasranama recitation","  • Saraswati Puja — for knowledge, speech, Mercury clarity","  • Ekadashi fasts — Vishnu's mercy in Mercury Dasha"],
                          Jupiter: ["  • Guru (Jupiter) puja on Thursdays — offer yellow flowers and turmeric","  • Guru Purnima — most important day of the year in Jupiter Dasha","  • Dakshinamurti puja — guru lineage blessings"],
                          Venus:   ["  • Lakshmi puja on Fridays — offer lotus, rice, and white sweets","  • Diwali — peak Venus festival","  • Varalakshmi Vratam and Lalita Sahasranama recitation"],
                          Saturn:  ["  • Shani puja on Saturdays — sesame oil lamp, black sesame donations","  • Amavasya (new moon) ancestor tarpan — reduces Saturn karma","  • Hanuman Chalisa — most powerful Saturn remedy"],
                          Rahu:    ["  • Durga puja — Rahu surrenders to Shakti","  • Rahu Kaal daily awareness — avoid major decisions","  • Sarpa dosha puja if Rahu is afflicted in chart"],
                          Ketu:    ["  • Ganesha puja for Ketu's spiritual doorway","  • Meditation retreats — Ketu responds to silence","  • Pilgrimage — Ketu is activated by sacred travel"],
                        }[dasha.planet] || ["  • Follow the monthly Ekadashi and Purnima fasts","  • Offer water to Surya daily at sunrise"]),
                      ] : ["Please load your Dasha data first to see Dasha-prescribed pujas."]},
                      { heading: "Weekly Minimum Practice", lines: [
                        "  • Monday: Shiva puja — Om Namah Shivaya 108 times",
                        `  • ${({Sun:"Sunday",Moon:"Monday",Mars:"Tuesday",Mercury:"Wednesday",Jupiter:"Thursday",Venus:"Friday",Saturn:"Saturday",Rahu:"Saturday",Ketu:"Tuesday"})[rLord] || "Thursday"}: ${rLord} puja — your most important weekly ritual`,
                        "  • Ekadashi (twice monthly): Vishnu fast — grain-free, Vishnu Sahasranama",
                        "  • Amavasya (new moon): Ancestor tarpan — water offering to pitru",
                        "  • Purnima (full moon): Satyanarayan Katha or Lakshmi puja",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {/* ── QUARTERLY REPORT ── */}
          {view === "quarterly" && (() => {
            const SECTIONS = [
              { icon:"💼", title:"Career & Purpose", color:"#a16207", rgb:"245,158,11", desc:"Which professional actions will bear fruit in the next 3 months. Based on 10th house transits, current Dasha sub-period, and Jupiter's position relative to your natal chart." },
              { icon:"💑", title:"Relationships", color:"#be185d", rgb:"236,72,153", desc:"The quality of partnerships this quarter — whether existing bonds deepen, new connections form, or distance must be intentionally healed. Venus and 7th house transits." },
              { icon:"💰", title:"Wealth & Finances", color:"#b45309", rgb:"251,191,36", desc:"Income and savings potential. Whether this is a quarter to invest boldly or consolidate carefully. 2nd, 11th house transits + Dasha timing." },
              { icon:"🏥", title:"Health Focus", color:"#dc2626", rgb:"239,68,68", desc:"Which body systems need extra attention this quarter based on afflicted transits. Preventative guidance from the 6th house and current planetary positions." },
              { icon:"🧘", title:"Dharma & Spirituality", color:"#6d28d9", rgb:"167,139,250", desc:"Is this a quarter for outward action or inward deepening? 9th house and Jupiter show whether pilgrimages, study, or seva will bear the most fruit." },
              { icon:"⚡", title:"Key Dates", color:"#0e7490", rgb:"103,232,249", desc:"Specific dates when planetary energy peaks — windows for major decisions, rituals, and actions. Eclipse periods, retrogrades, and Ekadashi alignments with your chart." },
            ];
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ borderRadius:18, overflow:"hidden",
                  border:"1px solid rgba(251,191,36,0.3)",
                  background:"#ffffff",
                  boxShadow:"0 8px 40px rgba(120,90,30,0.16), inset 0 1px 0 rgba(251,191,36,0.1)" }}>
                  <div style={{ height:2, background:"linear-gradient(90deg,transparent,rgba(251,191,36,0.8),rgba(253,224,71,1),rgba(251,191,36,0.8),transparent)" }} />
                  <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg,rgba(251,191,36,0.25),rgba(180,83,9,0.15))",
                      border:"1px solid rgba(251,191,36,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow:"0 0 20px rgba(251,191,36,0.3)", fontSize:"1.8rem" }}>📜</div>
                    <div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1.2rem", color:"#a16207", marginBottom:4 }}>Quarterly Prediction</div>
                      <div style={{ fontSize:"1.2rem", color:JT.textDim, fontFamily:"'Inter',sans-serif" }}>Next 3 months — career · relationships · health · dharma</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius:12, padding:"14px 16px", background:"#faf9f6", border:`1px solid ${JT.border}` }}>
                  <div style={{ fontSize:"1.2rem", color:"#2b2b2b", fontFamily:"'Inter',sans-serif", lineHeight:1.75 }}>
                    A quarterly report reads your Dasha sub-period, all current planetary transits, and upcoming eclipse and retrograde windows — then synthesises what this specific 3-month window means for each life area. More precise than an annual forecast, more practical than a daily reading.
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {SECTIONS.map((s, i) => (
                    <div key={i} style={{ borderRadius:14, padding:"14px",
                      border:`1px solid rgba(${s.rgb},0.22)`,
                      background:"#ffffff" }}>
                      <div style={{ fontSize:"1.5rem", marginBottom:6 }}>{s.icon}</div>
                      <div style={{ fontFamily:JT.heading, fontSize:"1rem", color:s.color, marginBottom:6 }}>{s.title}</div>
                      <div style={{ fontSize:"1.1rem", color:"#666666", fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>{s.desc}</div>
                    </div>
                  ))}
                </div>
                <DlBtn label="📥 Download Q2 2026 Quarterly Report" onClick={() => {
                  const rashiName  = arcData?.rashi?.en || "Unknown";
                  const rashiLords = ["Mars","Venus","Mercury","Moon","Sun","Mercury","Venus","Mars","Jupiter","Saturn","Saturn","Jupiter"];
                  const rLord      = rashiLords[arcData?.rashi_idx ?? 0];
                  const nakRuler   = arcData?.nakshatra?.ruler || "Sun";
                  const dasha      = dashaNavData?.maha_dasha;
                  const doshas     = doshaFullData?.doshas || [];
                  // Q2 2026 = April–June 2026
                  const rashiQ2 = {
                    Aries:       { career:"Jupiter transits your 1st house — new beginnings in career, bold moves pay off", relationships:"Venus enters your 2nd house in May — harmony and financial flow in partnerships", wealth:"Mars in your 1st house gives energy to earn — act boldly in April", health:"Watch your head and eyes — Aries rules the head; avoid stress spikes", dharma:"A powerful quarter for personal dharma — new practices begun now stick", dates:"April 8 eclipse activates your axis. May 5 Mercury direct — sign contracts then. June 21 solstice — powerful ritual day" },
                    Taurus:      { career:"Saturn stabilises your 10th house — steady, diligent work rewarded. No shortcuts", relationships:"Venus, your lord, is strong in Q2 — relationships deepen and beautify", wealth:"Akshaya Tritiya in May is your peak wealth ritual — Lakshmi puja on this day", health:"Watch throat and neck — Taurus rules these; avoid cold and voice strain", dharma:"Earth-based practices: gardening, offering to land and trees, seva through food", dates:"May Akshaya Tritiya — most important wealth ritual of the year for you. June solstice — plant new intentions" },
                    Gemini:      { career:"Mercury retrograde in April — review and refine existing projects rather than launch", relationships:"Communication is the gift and test this quarter — speak with precision", wealth:"Avoid signing contracts in April (Mercury retro). Excellent May–June for income", health:"Nervous system and lungs need care — pranayama daily, screen time limits", dharma:"Study and writing are your dharma practices this quarter — write what you learn", dates:"April 25 Mercury direct — resume launches and negotiations. June 3 Mercury-Jupiter conjunction — peak clarity" },
                    Cancer:      { career:"Moon rules you — follow your intuition on career moves. Home-based work thrives", relationships:"Your 4th house is activated — deep bonds with mother, home, family strengthen", wealth:"Real estate and home investments are favoured this quarter", health:"Stomach and chest — your primary focus. Avoid emotional eating and cold liquids", dharma:"Ancestor honouring (Pitru Tarpan) is your highest dharma act this quarter", dates:"Amavasya each month — perform tarpan. Guru Purnima in July — plan now for this" },
                    Leo:         { career:"Sun is exalted in Q2 — your leadership shines. Step forward, be visible", relationships:"Your natural magnetism is at peak — existing partnerships deepen in warmth", wealth:"Creative ventures and investments in gold/performance are favoured", health:"Heart and spine are primary — adequate rest between intense activity periods", dharma:"Teaching, performing, and giving recognition to others is your dharma now", dates:"Ratha Saptami (April) — most important Surya festival. Ram Navami — peak power day for Leo" },
                    Virgo:       { career:"Mercury retrograde in April requires review — prepare thoroughly before presenting", relationships:"Service to partner is the key — practical acts of love over grand gestures", wealth:"Systematic savings and debt reduction outperform investment this quarter", health:"Digestive system focus — Ayurvedic cleanse in May is highly beneficial", dharma:"Precision service: volunteer, organise, assist those who need expertise you have", dates:"May Mercury direct — launch new work routines. June Vat Savitri — women's dharma ritual" },
                    Libra:       { career:"Venus is your lord and Q2 is Venus-strong — creative, aesthetic, and collaborative work", relationships:"Partnership energy is peak — business partnerships and marriage decisions favoured", wealth:"Luxury goods, creative industries, and partnerships generate wealth now", health:"Kidneys and lower back — your watch zone. Stay hydrated and avoid excess sugar", dharma:"Diplomacy, beauty-making, and bringing harmony between people is your dharma", dates:"Akshaya Tritiya — Lakshmi puja for Libra is especially powerful. June 21 — Venus-Sun alignment" },
                    Scorpio:     { career:"Mars (your lord) is direct in Q2 — drive forward on stalled projects now", relationships:"Depth over breadth — one genuine connection beats many surface bonds", wealth:"Hidden assets, insurance, and inheritance matters may surface. Investigate finances", health:"Reproductive system and elimination — detox practices and adequate water", dharma:"Transformation is your dharma — let what must die, die. No clinging this quarter", dates:"April 30 Mars trine Jupiter — peak ambition window. Scorpio full moon in May — emotional release ritual" },
                    Sagittarius: { career:"Jupiter is your lord and Q2 is Jupiter-exalted — expansion in all forms", relationships:"International connections and teacher-student bonds are highlighted", wealth:"Higher education investments and long-distance ventures yield returns", health:"Hips, thighs, liver — avoid excess and overindulgence especially in May", dharma:"Teaching, pilgrimage, and philosophy are your core dharma practices now", dates:"Guru Purnima prep begins now — July event but plant seeds in Q2. June pilgrimage window opens" },
                    Capricorn:   { career:"Saturn is your lord — slow, systematic, disciplined work is the only path. No shortcuts", relationships:"Professional relationships and long-term committed bonds are highlighted", wealth:"Capricorn season favours real estate, structures, long-term instruments", health:"Bones, joints, and knees — yoga, calcium, regular physio check if pain exists", dharma:"Responsibility fulfilled with integrity is your dharma — hold your line", dates:"Makara Sankranti (Jan resonance continues) — sesame rituals. Shani Jayanti in May — most important day" },
                    Aquarius:    { career:"Saturn's discipline meets Aquarius vision — community projects and innovation", relationships:"Friendships and collective bonds are more nourishing than romantic intensity now", wealth:"Technology, community ventures, and future-oriented investments are favoured", health:"Circulation, ankles, nervous system — warm oil massage and grounding practices", dharma:"Seva (selfless service) to communities and causes larger than yourself", dates:"Shani Jayanti in May — most important Shani festival for Aquarius. June 21 solstice — community ceremony" },
                    Pisces:      { career:"Jupiter's grace softens obstacles — go with the flow, trust timing", relationships:"Spiritual and artistic connections are deepened this quarter", wealth:"Intuitive investment decisions pay off — trust your gut in May", health:"Feet, lymphatic system, and sleep — protect your boundaries and rest deeply", dharma:"Meditation, creative expression, and silent retreats — your peak dharma window", dates:"Guru Purnima (July) — begin preparations now. June 21 solstice — meditation retreat day" },
                  };
                  const q2 = rashiQ2[rashiName] || rashiQ2.Aries;
                  genJyotishPDF({
                    title: "Q2 2026 Quarterly Prediction Report", filename: `VedSanga_Q2_2026_${rashiName}`,
                    badge: "Guru Kripa Feature", userName: user.name || "Devotee",
                    rashi: rashiName, nakshatra: arcData?.nakshatra?.name?.en || "",
                    dob: arcData?.dob,
                    extraLines: [
                      "Quarter: April 1 – June 30, 2026",
                      `Rashi Lord: ${rLord}  |  Nakshatra Ruler: ${nakRuler}`,
                      ...(dasha ? [`Current Dasha: ${dasha.planet} Maha Dasha (until ${dasha.end})`] : []),
                    ],
                    sections: [
                      { heading: "Q2 2026 Overview for " + rashiName, lines: [
                        "This quarterly report synthesises your Moon sign tendencies with the planetary configuration of April–June 2026. Major planetary positions: Jupiter in Taurus, Saturn in Aquarius, Rahu in Pisces / Ketu in Virgo.",
                        ...(dasha ? [`Your ${dasha.planet} Maha Dasha provides the overall life theme. Q2 2026 is a chapter within that larger story.`] : []),
                      ]},
                      ...SECTIONS.map(s => ({ heading: `${s.icon} ${s.title}`, lines: [
                        q2[s.title.toLowerCase().replace(/[^a-z]/g,"").slice(0,6)] || s.desc,
                        "---", s.desc,
                      ]})),
                      { heading: "Key Dates & Windows", lines: [
                        q2.dates || "Track Ekadashi and Amavasya dates for your most powerful ritual windows.",
                        "---",
                        "April 2026 key events:",
                        "  • April 8: Mercury retrograde ends — resume launches and signings",
                        "  • April 13: Baisakhi / Mesha Sankranti — solar new year energy",
                        "  • April 14-22: Navratri window — 9 nights of Shakti practice",
                        "  • April 23: Ram Navami — Vishnu / Sun energy peak",
                        "May 2026 key events:",
                        "  • May 3: Akshaya Tritiya — most auspicious wealth day of the year",
                        "  • May 10-12: Shani Jayanti — most important Saturn ritual day",
                        "  • May 23: Budha Purnima — Buddha and Mercury blessings",
                        "June 2026 key events:",
                        "  • June 5-6: Ganga Dussehra — purification window",
                        "  • June 13: Nirjala Ekadashi — most powerful Ekadashi of the year",
                        "  • June 21: Summer Solstice — Surya is strongest; Surya puja",
                      ]},
                      ...(doshas.length > 0 ? [{ heading: "Active Doshas — Q2 Remedies", lines:
                        doshas.flatMap(d => [`${d.dosha}: ${d.severity || "present"}`, ...(d.remedies || []).slice(0,3).map(r => `  • ${r}`), "---"]).slice(0,-1)
                      }] : []),
                      { heading: "Your Q2 Sadhana Plan", lines: [
                        "Daily: Chant your Rashi lord's mantra 108 times at sunrise",
                        `Weekly: ${rLord} puja on ${({Sun:"Sunday",Moon:"Monday",Mars:"Tuesday",Mercury:"Wednesday",Jupiter:"Thursday",Venus:"Friday",Saturn:"Saturday",Rahu:"Saturday",Ketu:"Tuesday"})[rLord] || "Thursday"}`,
                        "Ekadashi: Full Vishnu fast — grain-free, Sahasranama recitation",
                        "Monthly: Amavasya ancestor tarpan — water offering at noon",
                        "Q2 Special: Akshaya Tritiya (May 3) — Lakshmi puja + gold purchase or donation",
                        "Q2 Special: Nirjala Ekadashi (June 13) — most powerful fast of the year",
                      ]},
                    ]
                  });
                }} />
              </div>
            );
          })()}

          {view === "reports" && (
            <ReportsStorePage
              userId={user.id}
              noBirth={noBirth}
              onNeedBirth={() => setView("archetype")}
              arcData={arcData}
              onBack={() => setView("home")}
              isSubscribed={isSubscribed}
              onUpgrade={handleUpgrade}
            />
          )}
        </>
      )}

      {copied && (
        <div style={{ position:"fixed", bottom:90, left:"50%", transform:"translateX(-50%)",
          background:"var(--bg2)", border:"1px solid var(--saffron)", borderRadius:"20px",
          padding:"10px 20px", color:"var(--saffron)", fontSize:"1.1rem", zIndex:999,
          fontFamily:JT.heading }}>
          {L("copied", lang)}
        </div>
      )}

      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes twinkle { 0%,100%{opacity:0.3} 50%{opacity:1} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes chatDot { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1);opacity:1} }
        @keyframes orbit-cw  { from{transform:rotate(0deg)  translateX(52px) rotate(0deg)}   to{transform:rotate(360deg)  translateX(52px) rotate(-360deg)} }
        @keyframes orbit-ccw { from{transform:rotate(0deg)  translateX(38px) rotate(0deg)}   to{transform:rotate(-360deg) translateX(38px) rotate(360deg)} }
        @keyframes orbit-med { from{transform:rotate(60deg) translateX(70px) rotate(-60deg)} to{transform:rotate(420deg)  translateX(70px) rotate(-420deg)} }
        @keyframes kundali-glow { 0%,100%{filter:drop-shadow(0 0 18px rgba(212,175,55,0.55))} 50%{filter:drop-shadow(0 0 36px rgba(212,175,55,0.9)) drop-shadow(0 0 60px rgba(212,175,55,0.35))} }
        @keyframes spin-slow  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes spin-slow-r{ from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
      `}</style>
    </div>
  );
}
